import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Target, FileText, ChevronLeft, ChevronRight, ChevronDown, Plus, X, Check, Edit2, Trash2, LayoutGrid, CalendarRange, Clock, Zap, Battery, BatteryLow, ArrowLeft, ArrowRight, Brain, ListTodo, Palette, BookOpen, Save, LogOut, TrendingUp, BarChart3, Award, Flame, Activity, XCircle, GripVertical, SkipForward, Square, CheckSquare, Search, RefreshCw, Wallet, CreditCard, Mic, MicOff, MessageSquare, Receipt, ArrowUpRight, ArrowDownLeft, AlertTriangle, AlertCircle, Mail, Lock, Unlock, Sparkles, Trophy, Compass, Lightbulb, Scale, Heart, Star, Upload, ClipboardList } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export default function WeeklyPlanner({ onSignOut, user }) {
  const getCurrentWeekNumber = () => {
    const now = new Date();
    const startDate = new Date(2026, 0, 1);
    const startDay = startDate.getDay();
    const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
    const firstMonday = new Date(2026, 0, daysToMonday);

    if (now < firstMonday) return 1; // Before week 1, default to week 1

    const diffTime = now - firstMonday;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(diffDays / 7) + 1;

    return weekNumber > 52 ? 52 : weekNumber; // Cap at week 52
  };

  const getWeekNumForDate = (date) => {
    const startDate = new Date(2026, 0, 1);
    const startDay = startDate.getDay();
    const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
    const firstMonday = new Date(2026, 0, daysToMonday);

    if (date < firstMonday) return 1;

    const diffTime = date - firstMonday;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(diffDays / 7) + 1;

    return weekNumber > 52 ? 52 : weekNumber;
  };

  const [currentWeek, setCurrentWeek] = useState(getCurrentWeekNumber());
  const [weekData, setWeekData] = useState({});
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('planner-viewMode') || 'week');
  useEffect(() => { sessionStorage.setItem('planner-viewMode', viewMode); }, [viewMode]);
  const [brainDump, setBrainDump] = useState({ text: '', cards: [] });
  const [todoList, setTodoList] = useState([]);
  const [newCardText, setNewCardText] = useState('');
  const [newCardColor, setNewCardColor] = useState('yellow');
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingCardId, setResizingCardId] = useState(null);
  const [cardMaxZ, setCardMaxZ] = useState(1);
  const [newTodoText, setNewTodoText] = useState('');
  const [todoFilter, setTodoFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(2026);
  const [dayHighlights, setDayHighlights] = useState({});
  const [calendarBanners, setCalendarBanners] = useState([]);
  const [dayNotes, setDayNotes] = useState({});
  const [monthlyGoals, setMonthlyGoals] = useState({});
  const [dailyHours, setDailyHours] = useState({});
  const [habits, setHabits] = useState([]);
  const [habitTracking, setHabitTracking] = useState({});
  const [daySchedule, setDaySchedule] = useState({});
  const [dayTasks, setDayTasks] = useState({});
  const [activeDayView, setActiveDayView] = useState(null); // { year, month, day }
  const [journalInputDay, setJournalInputDay] = useState(null);
  const [journalInputText, setJournalInputText] = useState('');
  const [expandedJournalWeeks, setExpandedJournalWeeks] = useState([currentWeek]);
  const [expandedJournalDays, setExpandedJournalDays] = useState(() => {
    const t = new Date();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return [`${t.getFullYear()}-${m}-${d}`];
  });
  const [isSavingBrainDump, setIsSavingBrainDump] = useState(false);
  const [brainDumpSaveSuccess, setBrainDumpSaveSuccess] = useState(false);
  const [productivityLog, setProductivityLog] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [recurringSchedule, setRecurringSchedule] = useState([]);
  const [userMantra, setUserMantra] = useState('');
  const [lifeWheel, setLifeWheel] = useState({});
  const [openaiApiKey, setOpenaiApiKey] = useState(() => {
    try { return localStorage.getItem('openai-api-key') || ''; } catch { return ''; }
  });
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const [cmdInput, setCmdInput] = useState('');
  const [zenMode, setZenMode] = useState(false);
  const [brainDumpTab, setBrainDumpTab] = useState('notes');
  const [editingCover, setEditingCover] = useState(false);
  const [dailyHealth, setDailyHealth] = useState({});
  const [gratitudeLog, setGratitudeLog] = useState({});
  const [expenseData, setExpenseData] = useState({
    income: [], expenses: [], creditCards: [], budgets: {},
    settings: { currency: 'INR', currencySymbol: '\u20B9', locale: 'en-IN', monthStartDay: 1 }
  });
  const [expenseSelectedMonth, setExpenseSelectedMonth] = useState(() => {
    const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [expenseActiveTab, setExpenseActiveTab] = useState('dashboard');
  // expenseChatInput removed — using ref inside ExpenseManagerView to avoid re-render focus loss
  const [expenseShowTxnPage, setExpenseShowTxnPage] = useState(false);
  const [emiData, setEmiData] = useState([]);
  const [showYearReport, setShowYearReport] = useState(false);

  // --- Life Lab & New Features State ---
  const [futureLetters, setFutureLetters] = useState([]);
  const [dreamBoard, setDreamBoard] = useState([]);
  const [ideaIncubator, setIdeaIncubator] = useState([]);
  const [decisionJournal, setDecisionJournal] = useState([]);
  const [onePercentBetter, setOnePercentBetter] = useState([]);
  const [antiTodo, setAntiTodo] = useState([]);
  const [microAdventures, setMicroAdventures] = useState([]);
  const [lifeWeeksBirthDate, setLifeWeeksBirthDate] = useState(null);
  const [lifeLabTab, setLifeLabTab] = useState('letters');
  const [todoViewTab, setTodoViewTab] = useState('tasks');
  const [journalTab, setJournalTab] = useState('entries');

  // Initialize week data from storage
  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await window.storage.get('planner-data');
        if (result && result.value) {
          setWeekData(JSON.parse(result.value));
        }
      } catch (error) {
        console.log('No existing data found, starting fresh');
      }

      try {
        const highlightsResult = await window.storage.get('day-highlights');
        if (highlightsResult && highlightsResult.value) {
          setDayHighlights(JSON.parse(highlightsResult.value));
        }
      } catch (error) {
        console.log('No existing highlights found');
      }

      try {
        const cb = await window.storage.get('calendar-banners');
        if (cb && cb.value) setCalendarBanners(JSON.parse(cb.value));
      } catch (e) {
        console.log('No existing calendar banners found');
      }

      try {
        const notesResult = await window.storage.get('day-notes');
        if (notesResult && notesResult.value) {
          setDayNotes(JSON.parse(notesResult.value));
        }
      } catch (error) {
        console.log('No existing day notes found');
      }

      try {
        const monthlyGoalsResult = await window.storage.get('monthly-goals');
        if (monthlyGoalsResult && monthlyGoalsResult.value) {
          setMonthlyGoals(JSON.parse(monthlyGoalsResult.value));
        }
      } catch (error) {
        console.log('No existing monthly goals found');
      }

      try {
        const dailyHoursResult = await window.storage.get('daily-hours');
        if (dailyHoursResult && dailyHoursResult.value) {
          setDailyHours(JSON.parse(dailyHoursResult.value));
        }
      } catch (error) {
        console.log('No existing daily hours found');
      }

      try {
        const habitsResult = await window.storage.get('habits');
        if (habitsResult && habitsResult.value) {
          setHabits(JSON.parse(habitsResult.value));
        }
      } catch (error) {
        console.log('No existing habits found');
      }

      try {
        const habitTrackingResult = await window.storage.get('habit-tracking');
        if (habitTrackingResult && habitTrackingResult.value) {
          setHabitTracking(JSON.parse(habitTrackingResult.value));
        }
      } catch (error) {
        console.log('No existing habit tracking found');
      }

      try {
        const dayScheduleResult = await window.storage.get('day-schedule');
        if (dayScheduleResult && dayScheduleResult.value) {
          setDaySchedule(JSON.parse(dayScheduleResult.value));
        }
      } catch (error) {
        console.log('No existing day schedule found');
      }

      try {
        const dayTasksResult = await window.storage.get('day-tasks');
        if (dayTasksResult && dayTasksResult.value) {
          setDayTasks(JSON.parse(dayTasksResult.value));
        }
      } catch (error) {
        console.log('No existing day tasks found');
      }

      try {
        const brainDumpResult = await window.storage.get('brain-dump');
        if (brainDumpResult && brainDumpResult.value) {
          setBrainDump(JSON.parse(brainDumpResult.value));
        }
      } catch (error) {
        console.log('No existing brain dump found');
      }

      try {
        const todoListResult = await window.storage.get('todo-list');
        if (todoListResult && todoListResult.value) {
          setTodoList(JSON.parse(todoListResult.value));
        }
      } catch (error) {
        console.log('No existing todo list found');
      }

      try {
        const productivityLogResult = await window.storage.get('productivity-log');
        if (productivityLogResult && productivityLogResult.value) {
          setProductivityLog(JSON.parse(productivityLogResult.value));
        }
      } catch (error) {
        console.log('No existing productivity log found');
      }
      const recurring = await window.storage.get('recurring-schedule');
      if (recurring && recurring.value) setRecurringSchedule(JSON.parse(recurring.value));

      const mantra = await window.storage.get('user-mantra');
      if (mantra && mantra.value) setUserMantra(JSON.parse(mantra.value));

      const lw = await window.storage.get('life-wheel');
      if (lw && lw.value) setLifeWheel(JSON.parse(lw.value));

      const dh = await window.storage.get('daily-health');
      if (dh && dh.value) setDailyHealth(JSON.parse(dh.value));

      const gl = await window.storage.get('gratitude-log');
      if (gl && gl.value) setGratitudeLog(JSON.parse(gl.value));

      const ed = await window.storage.get('expense-manager');
      if (ed && ed.value) {
        const parsed = JSON.parse(ed.value);
        // One-time migration: fix isCredit/isRepayment flags on imported CC transactions
        // Migration: RESET and re-derive isCredit/isRepayment flags on ALL imported CC transactions
        // Previous migrations may have set incorrect flags — this re-evaluates from scratch
        let migrated = false;
        if (parsed.expenses) {
          parsed.expenses = parsed.expenses.map(e => {
            if (e.creditCardId && (e.smartLogSource === 'cc-statement' || (e.tags || []).includes('cc-import'))) {
              const desc = (e.description || '') + ' ' + (e.notes || '');
              const shouldBeRepayment = /\bCARD\s*PAYMENT/i.test(desc) || /\bbill payment/i.test(desc);
              // Credit patterns: word "credit"/"refund"/"reversal"/"waiver"/"cashback" in desc/notes, or HDFC "+ C" indicator, or AGGREGATOR OFFUS (known refund pattern)
              const shouldBeCredit = /\bcredit\b/i.test(desc) || /\brefund\b/i.test(desc) || /\breversal\b/i.test(desc) || /\bwaiver\b/i.test(desc) || /\bcashback\b/i.test(desc) || /\+\s*C\b/.test(desc) || /\bAGGREGATOR\b.*\bOFFUS\b/i.test(desc) || shouldBeRepayment;
              // Re-derive: correct both false negatives AND false positives
              if (!!e.isCredit !== shouldBeCredit) { e.isCredit = shouldBeCredit || undefined; migrated = true; }
              if (!!e.isRepayment !== shouldBeRepayment) { e.isRepayment = shouldBeRepayment || undefined; migrated = true; }
            }
            return e;
          });
        }
        // Migration: assign ccStatementMonth to imported CC transactions that don't have it
        // Group by createdAt to identify import batches, then compute statement month from max date per batch
        const ccNeedStmtMonth = (parsed.expenses || []).filter(e => !e.ccStatementMonth && e.creditCardId && (e.smartLogSource === 'cc-statement' || (e.tags || []).includes('cc-import')));
        if (ccNeedStmtMonth.length > 0) {
          const sorted = [...ccNeedStmtMonth].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          const batches = []; let batch = [sorted[0]];
          for (let i = 1; i < sorted.length; i++) {
            if (Math.abs(new Date(sorted[i].createdAt || 0) - new Date(sorted[i-1].createdAt || 0)) < 5000) { batch.push(sorted[i]); }
            else { batches.push(batch); batch = [sorted[i]]; }
          }
          batches.push(batch);
          batches.forEach(b => {
            const card = (parsed.creditCards || []).find(c => c.id === b[0].creditCardId);
            const bd = card ? (card.billingDate || 15) : 15;
            const maxD = new Date(Math.max(...b.map(e => new Date(e.date).getTime())));
            let sm = maxD.getMonth(), sy = maxD.getFullYear();
            if (maxD.getDate() > bd) { sm++; if (sm > 11) { sm = 0; sy++; } }
            const stmtMonth = `${sy}-${String(sm + 1).padStart(2, '0')}`;
            b.forEach(e => { e.ccStatementMonth = stmtMonth; });
          });
          migrated = true;
          console.log('[Migration] Assigned ccStatementMonth to', ccNeedStmtMonth.length, 'imported CC transactions');
        }
        if (migrated) console.log('[Migration] Updated imported CC transaction flags');
        setExpenseData(parsed);
        if (migrated) window.storage.set('expense-manager', JSON.stringify(parsed));
      }

      const emiRaw = await window.storage.get('emi-tracker');
      if (emiRaw && emiRaw.value) setEmiData(JSON.parse(emiRaw.value));

      // Life Lab & new features
      try { const fl = await window.storage.get('future-letters'); if (fl && fl.value) setFutureLetters(JSON.parse(fl.value)); } catch(e) {}
      try { const db = await window.storage.get('dream-board'); if (db && db.value) setDreamBoard(JSON.parse(db.value)); } catch(e) {}
      try { const ii = await window.storage.get('idea-incubator'); if (ii && ii.value) setIdeaIncubator(JSON.parse(ii.value)); } catch(e) {}
      try { const dj = await window.storage.get('decision-journal'); if (dj && dj.value) setDecisionJournal(JSON.parse(dj.value)); } catch(e) {}
      try { const op = await window.storage.get('one-percent-better'); if (op && op.value) setOnePercentBetter(JSON.parse(op.value)); } catch(e) {}
      try { const at = await window.storage.get('anti-todo'); if (at && at.value) setAntiTodo(JSON.parse(at.value)); } catch(e) {}
      try { const ma = await window.storage.get('micro-adventures'); if (ma && ma.value) setMicroAdventures(JSON.parse(ma.value)); } catch(e) {}
      try { const lw = await window.storage.get('life-weeks-birth'); if (lw && lw.value) setLifeWeeksBirthDate(JSON.parse(lw.value)); } catch(e) {}

      setIsLoaded(true);
    };
    loadData();
  }, []);

  // One-time migration: sync existing weekly finance entries → Expense Manager
  useEffect(() => {
    if (!isLoaded) return;
    if (expenseData?.settings?.weeklyMigrated) return; // already done
    const existingIncomeIds = new Set((expenseData.income || []).map(i => i.id));
    const existingExpenseIds = new Set((expenseData.expenses || []).map(e => e.id));
    const newIncome = [];
    const newExpenses = [];
    Object.keys(weekData).forEach(weekNum => {
      const data = weekData[weekNum];
      if (data?.revenueEntries) {
        data.revenueEntries.forEach(entry => {
          if (!existingIncomeIds.has(entry.id)) {
            newIncome.push({ id: entry.id, amount: entry.amount, source: entry.description || 'Weekly Revenue', category: 'salary', date: entry.date, recurring: false, recurringFrequency: null, nextDueDate: null, notes: '', smartLogSource: 'weekly', createdAt: entry.timestamp || entry.date });
          }
        });
      }
      if (data?.expenseEntries) {
        data.expenseEntries.forEach(entry => {
          if (!existingExpenseIds.has(entry.id)) {
            newExpenses.push({ id: entry.id, amount: entry.amount, description: entry.description || 'Weekly Expense', category: 'other', type: 'variable', date: entry.date, tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, recurringFrequency: null, nextDueDate: null, merchant: '', notes: '', smartLogSource: 'weekly', createdAt: entry.timestamp || entry.date });
          }
        });
      }
    });
    if (newIncome.length > 0 || newExpenses.length > 0) {
      setExpenseData(prev => ({
        ...prev,
        income: [...newIncome, ...(prev.income || [])],
        expenses: [...newExpenses, ...(prev.expenses || [])],
        settings: { ...(prev.settings || {}), weeklyMigrated: true }
      }));
      console.log(`Migrated ${newIncome.length} income + ${newExpenses.length} expense entries from weekly finance`);
    } else {
      setExpenseData(prev => ({ ...prev, settings: { ...(prev.settings || {}), weeklyMigrated: true } }));
    }
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoaded) return;
    const saveData = async () => {
      if (Object.keys(weekData).length > 0) {
        try {
          await window.storage.set('planner-data', JSON.stringify(weekData));
        } catch (error) {
          console.error('Error saving data:', error);
        }
      }
    };
    saveData();
  }, [weekData, isLoaded]);

  // Save day highlights
  useEffect(() => {
    if (!isLoaded) return;
    const saveHighlights = async () => {
      try {
        await window.storage.set('day-highlights', JSON.stringify(dayHighlights));
      } catch (error) {
        console.error('Error saving highlights:', error);
      }
    };
    saveHighlights();
  }, [dayHighlights, isLoaded]);

  // Save calendar banners
  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('calendar-banners', JSON.stringify(calendarBanners));
  }, [calendarBanners, isLoaded]);

  // Save day notes
  useEffect(() => {
    if (!isLoaded) return;
    const saveNotes = async () => {
      try {
        await window.storage.set('day-notes', JSON.stringify(dayNotes));
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    };
    saveNotes();
  }, [dayNotes, isLoaded]);

  // Save monthly goals
  useEffect(() => {
    if (!isLoaded) return;
    const saveMonthlyGoals = async () => {
      try {
        await window.storage.set('monthly-goals', JSON.stringify(monthlyGoals));
      } catch (error) {
        console.error('Error saving monthly goals:', error);
      }
    };
    saveMonthlyGoals();
  }, [monthlyGoals, isLoaded]);

  // Save daily hours
  useEffect(() => {
    if (!isLoaded) return;
    const saveDailyHours = async () => {
      try {
        await window.storage.set('daily-hours', JSON.stringify(dailyHours));
      } catch (error) {
        console.error('Error saving daily hours:', error);
      }
    };
    saveDailyHours();
  }, [dailyHours, isLoaded]);

  // Save habits
  useEffect(() => {
    if (!isLoaded) return;
    const saveHabits = async () => {
      try {
        await window.storage.set('habits', JSON.stringify(habits));
      } catch (error) {
        console.error('Error saving habits:', error);
      }
    };
    saveHabits();
  }, [habits, isLoaded]);

  // Save habit tracking
  useEffect(() => {
    if (!isLoaded) return;
    const saveHabitTracking = async () => {
      try {
        await window.storage.set('habit-tracking', JSON.stringify(habitTracking));
      } catch (error) {
        console.error('Error saving habit tracking:', error);
      }
    };
    saveHabitTracking();
  }, [habitTracking, isLoaded]);

  // Save day schedule
  useEffect(() => {
    if (!isLoaded) return;
    const saveDaySchedule = async () => {
      try {
        await window.storage.set('day-schedule', JSON.stringify(daySchedule));
      } catch (error) {
        console.error('Error saving day schedule:', error);
      }
    };
    saveDaySchedule();
  }, [daySchedule, isLoaded]);

  // Save day tasks
  useEffect(() => {
    if (!isLoaded) return;
    const saveDayTasks = async () => {
      try {
        await window.storage.set('day-tasks', JSON.stringify(dayTasks));
      } catch (error) {
        console.error('Error saving day tasks:', error);
      }
    };
    saveDayTasks();
  }, [dayTasks, isLoaded]);

  // Sync energy task status changes back to linked schedule entries
  useEffect(() => {
    if (!isLoaded) return;
    setDaySchedule(prevSchedule => {
      let changed = false;
      const newSchedule = { ...prevSchedule };
      for (const [dayKey, hourMap] of Object.entries(newSchedule)) {
        const tasksForDay = dayTasks[dayKey] || { high: [], medium: [], low: [] };
        let dayChanged = false;
        const updatedHourMap = { ...hourMap };
        for (const [hour, entry] of Object.entries(updatedHourMap)) {
          if (typeof entry !== 'object' || entry.type !== 'task' || !entry.taskId) continue;
          const energyTasks = tasksForDay[entry.energy] || [];
          const linkedTask = energyTasks.find(t => t.id === entry.taskId);
          if (!linkedTask) {
            updatedHourMap[hour] = entry.text;
            dayChanged = true;
          } else if (linkedTask.completed && entry.status !== 'done') {
            updatedHourMap[hour] = { ...entry, status: 'done' };
            dayChanged = true;
          } else if (!linkedTask.completed && !linkedTask.skipped && entry.status === 'done') {
            updatedHourMap[hour] = { ...entry, status: 'pending' };
            dayChanged = true;
          } else if (linkedTask.skipped && entry.status !== 'skipped') {
            updatedHourMap[hour] = { ...entry, status: 'skipped' };
            dayChanged = true;
          }
        }
        if (dayChanged) {
          newSchedule[dayKey] = updatedHourMap;
          changed = true;
        }
      }
      return changed ? newSchedule : prevSchedule;
    });
  }, [dayTasks, isLoaded]);

  // Save brain dump
  useEffect(() => {
    if (!isLoaded) return;
    const saveBrainDump = async () => {
      try {
        await window.storage.set('brain-dump', JSON.stringify(brainDump));
      } catch (error) {
        console.error('Error saving brain dump:', error);
      }
    };
    saveBrainDump();
  }, [brainDump, isLoaded]);

  // Save todo list
  useEffect(() => {
    if (!isLoaded) return;
    const saveTodoList = async () => {
      try {
        await window.storage.set('todo-list', JSON.stringify(todoList));
      } catch (error) {
        console.error('Error saving todo list:', error);
      }
    };
    saveTodoList();
  }, [todoList, isLoaded]);

  // Save productivity log
  useEffect(() => {
    if (!isLoaded) return;
    const saveProductivityLog = async () => {
      try {
        await window.storage.set('productivity-log', JSON.stringify(productivityLog));
      } catch (error) {
        console.error('Error saving productivity log:', error);
      }
    };
    saveProductivityLog();
  }, [productivityLog, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('recurring-schedule', JSON.stringify(recurringSchedule));
  }, [recurringSchedule, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('user-mantra', JSON.stringify(userMantra));
  }, [userMantra, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('life-wheel', JSON.stringify(lifeWheel));
  }, [lifeWheel, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('daily-health', JSON.stringify(dailyHealth));
  }, [dailyHealth, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    window.storage.set('gratitude-log', JSON.stringify(gratitudeLog));
  }, [gratitudeLog, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    const saveExpenses = async () => {
      try {
        await window.storage.set('expense-manager', JSON.stringify(expenseData));
      } catch (error) {
        console.error('Error saving expense data:', error);
      }
    };
    saveExpenses();
  }, [expenseData, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    const saveEmi = async () => {
      try {
        await window.storage.set('emi-tracker', JSON.stringify(emiData));
      } catch (error) {
        console.error('Error saving EMI data:', error);
      }
    };
    saveEmi();
  }, [emiData, isLoaded]);

  // Save: Life Lab & new features
  useEffect(() => { if (!isLoaded) return; window.storage.set('future-letters', JSON.stringify(futureLetters)); }, [futureLetters, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('dream-board', JSON.stringify(dreamBoard)); }, [dreamBoard, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('idea-incubator', JSON.stringify(ideaIncubator)); }, [ideaIncubator, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('decision-journal', JSON.stringify(decisionJournal)); }, [decisionJournal, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('one-percent-better', JSON.stringify(onePercentBetter)); }, [onePercentBetter, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('anti-todo', JSON.stringify(antiTodo)); }, [antiTodo, isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('micro-adventures', JSON.stringify(microAdventures)); }, [microAdventures, isLoaded]);

  // One-time bulk import: Feb 2026 bank statement
  useEffect(() => {
    if (!isLoaded) return;
    const importKey = 'feb2026-bank-import-done';
    if (localStorage.getItem(importKey)) return;

    const incomeEntries = [
      { id: 1738300001, amount: 92021, source: 'TCS Salary', category: 'salary', date: '2026-02-01', recurring: true, recurringFrequency: 'monthly', nextDueDate: '2026-03-01', notes: 'adjusted from 30/01', smartLogSource: 'statement' },
      { id: 1738300002, amount: 1, source: 'UPI verification', category: 'other', date: '2026-02-13', recurring: false, notes: '', smartLogSource: 'statement' },
      { id: 1738300003, amount: 5000, source: 'MedJobs Application (KANI)', category: 'freelance', date: '2026-02-16', recurring: false, notes: '', smartLogSource: 'statement' },
      { id: 1738300004, amount: 2000, source: 'Mangalam Pay Web Dev', category: 'freelance', date: '2026-02-16', recurring: false, notes: '', smartLogSource: 'statement' },
      { id: 1738300005, amount: 500, source: 'Transfer In (Jagan)', category: 'other', date: '2026-02-21', recurring: false, notes: '', smartLogSource: 'statement' },
    ];

    const expenseEntries = [
      { id: 1738400001, amount: 50000, description: 'Self Transfer', category: 'custom_transfer', type: 'variable', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Self', notes: '', smartLogSource: 'statement' },
      { id: 1738400002, amount: 5000, description: 'Self Transfer', category: 'custom_transfer', type: 'variable', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Self', notes: '', smartLogSource: 'statement' },
      { id: 1738400003, amount: 2000, description: 'Self Transfer', category: 'custom_transfer', type: 'variable', date: '2026-02-09', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Self', notes: '', smartLogSource: 'statement' },
      { id: 1738400004, amount: 3000, description: 'Self Transfer', category: 'custom_transfer', type: 'variable', date: '2026-02-16', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Self', notes: '', smartLogSource: 'statement' },
      { id: 1738400005, amount: 15964, description: 'Loan EMI (NACH DR)', category: 'emi', type: 'fixed', date: '2026-02-05', tags: [], paymentMethod: 'netbanking', creditCardId: null, recurring: true, recurringFrequency: 'monthly', merchant: 'NACH DR', notes: '', smartLogSource: 'statement' },
      { id: 1738400006, amount: 107.67, description: 'DSP Finance', category: 'emi', type: 'fixed', date: '2026-02-07', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'DSP Finance', notes: '', smartLogSource: 'statement' },
      { id: 1738400007, amount: 1000, description: 'Tata Capital', category: 'emi', type: 'fixed', date: '2026-02-16', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: true, recurringFrequency: 'monthly', merchant: 'Tata Capital', notes: '', smartLogSource: 'statement' },
      { id: 1738400008, amount: 1178.82, description: 'Hostinger', category: 'subscriptions', type: 'fixed', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: true, recurringFrequency: 'yearly', merchant: 'Hostinger', notes: '', smartLogSource: 'statement' },
      { id: 1738400009, amount: 499, description: 'Cosmofeed', category: 'subscriptions', type: 'fixed', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: true, recurringFrequency: 'monthly', merchant: 'Cosmofeed', notes: '', smartLogSource: 'statement' },
      { id: 1738400010, amount: 797.68, description: 'Adobe Systems', category: 'subscriptions', type: 'fixed', date: '2026-02-16', tags: [], paymentMethod: 'debit', creditCardId: null, recurring: true, recurringFrequency: 'monthly', merchant: 'Adobe Systems', notes: '', smartLogSource: 'statement' },
      { id: 1738400011, amount: 75, description: 'UIDAI (Aadhaar)', category: 'utilities', type: 'variable', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'UIDAI', notes: '', smartLogSource: 'statement' },
      { id: 1738400012, amount: 3800, description: 'HP Gas (Gas Connection)', category: 'utilities', type: 'variable', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'KCVEE HP Gas Agency', notes: '', smartLogSource: 'statement' },
      { id: 1738400013, amount: 1500, description: 'SBIMOPS (Insurance)', category: 'utilities', type: 'fixed', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'SBIMOPS', notes: '', smartLogSource: 'statement' },
      { id: 1738400014, amount: 349, description: 'MyJio (Phone 2nd Number)', category: 'utilities', type: 'fixed', date: '2026-02-11', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: true, recurringFrequency: 'monthly', merchant: 'MyJio', notes: '', smartLogSource: 'statement' },
      { id: 1738400015, amount: 112, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400016, amount: 112, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-05', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400017, amount: 63.5, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-09', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400018, amount: 82.2, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-10', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400019, amount: 129.1, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-11', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400020, amount: 55, description: 'Hungerbox', category: 'dining', type: 'variable', date: '2026-02-13', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Hungerbox', notes: '', smartLogSource: 'statement' },
      { id: 1738400021, amount: 500, description: 'Sendur Murugan Agency', category: 'groceries', type: 'variable', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sendur Murugan Agency', notes: '', smartLogSource: 'statement' },
      { id: 1738400022, amount: 605, description: 'Blinkit', category: 'groceries', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Blinkit', notes: '', smartLogSource: 'statement' },
      { id: 1738400023, amount: 209, description: 'Zepto', category: 'groceries', type: 'variable', date: '2026-02-02', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Zepto', notes: '', smartLogSource: 'statement' },
      { id: 1738400024, amount: 200, description: 'Sendur Murugan Agency', category: 'groceries', type: 'variable', date: '2026-02-18', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sendur Murugan Agency', notes: '', smartLogSource: 'statement' },
      { id: 1738400025, amount: 500, description: "Sri Sai Children's Hospital", category: 'health', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: "Sri Sai Children's Hospital", notes: '', smartLogSource: 'statement' },
      { id: 1738400026, amount: 209, description: "Sri Sai Children's Hospital", category: 'health', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: "Sri Sai Children's Hospital", notes: '', smartLogSource: 'statement' },
      { id: 1738400027, amount: 209.44, description: 'Apollo Pharmacy', category: 'health', type: 'variable', date: '2026-02-26', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Apollo Pharmacy', notes: '', smartLogSource: 'statement' },
      { id: 1738400028, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: true, recurringFrequency: 'weekly', merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400029, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-04', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400030, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-10', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400031, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-13', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400032, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-19', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400033, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-21', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400034, amount: 70, description: 'Water Can (Devaraj T)', category: 'utilities', type: 'variable', date: '2026-02-26', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Devaraj T', notes: '', smartLogSource: 'statement' },
      { id: 1738400035, amount: 1000, description: 'Car Wash (KANI)', category: 'transport', type: 'variable', date: '2026-02-05', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'KANI', notes: '', smartLogSource: 'statement' },
      { id: 1738400036, amount: 300, description: 'Sahdar', category: 'personal', type: 'variable', date: '2026-02-01', tags: ['adjusted from 30/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sahdar', notes: '', smartLogSource: 'statement' },
      { id: 1738400037, amount: 920, description: 'Chandru', category: 'personal', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Chandru', notes: '', smartLogSource: 'statement' },
      { id: 1738400038, amount: 4000, description: 'Maria Micheal S', category: 'personal', type: 'variable', date: '2026-02-01', tags: ['adjusted from 31/01'], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Maria Micheal S', notes: '', smartLogSource: 'statement' },
      { id: 1738400039, amount: 150, description: 'Karthikeyan', category: 'personal', type: 'variable', date: '2026-02-02', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Karthikeyan', notes: '', smartLogSource: 'statement' },
      { id: 1738400040, amount: 26, description: 'Mohanasundram Rameshkumar', category: 'personal', type: 'variable', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Mohanasundram Rameshkumar', notes: '', smartLogSource: 'statement' },
      { id: 1738400041, amount: 275, description: 'Parthiban Deepak', category: 'personal', type: 'variable', date: '2026-02-03', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Parthiban Deepak', notes: '', smartLogSource: 'statement' },
      { id: 1738400042, amount: 587, description: 'Sahdar', category: 'personal', type: 'variable', date: '2026-02-04', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sahdar', notes: '', smartLogSource: 'statement' },
      { id: 1738400043, amount: 30, description: 'Manoj Kumar', category: 'personal', type: 'variable', date: '2026-02-04', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Manoj Kumar', notes: '', smartLogSource: 'statement' },
      { id: 1738400044, amount: 1000, description: 'Sivanithish K', category: 'personal', type: 'variable', date: '2026-02-16', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sivanithish K', notes: '', smartLogSource: 'statement' },
      { id: 1738400045, amount: 1200, description: 'Prashanth Ramakrishnan', category: 'personal', type: 'variable', date: '2026-02-17', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Prashanth Ramakrishnan', notes: '', smartLogSource: 'statement' },
      { id: 1738400046, amount: 100, description: 'Suresh R', category: 'personal', type: 'variable', date: '2026-02-18', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Suresh R', notes: '', smartLogSource: 'statement' },
      { id: 1738400047, amount: 135, description: 'Shanoj P', category: 'personal', type: 'variable', date: '2026-02-18', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Shanoj P', notes: '', smartLogSource: 'statement' },
      { id: 1738400048, amount: 105, description: 'Sri Bramma Sakthi Store', category: 'personal', type: 'variable', date: '2026-02-18', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sri Bramma Sakthi Store', notes: '', smartLogSource: 'statement' },
      { id: 1738400049, amount: 390, description: 'Sahdar', category: 'personal', type: 'variable', date: '2026-02-18', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Sahdar', notes: '', smartLogSource: 'statement' },
      { id: 1738400050, amount: 500, description: 'Jagan Dilli', category: 'personal', type: 'variable', date: '2026-02-19', tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, merchant: 'Jagan Dilli', notes: '', smartLogSource: 'statement' },
    ];

    // Add Transfer custom category + all entries
    setExpenseData(prev => ({
      ...prev,
      customExpenseCategories: [...(prev.customExpenseCategories || []).filter(c => c.key !== 'custom_transfer'), { key: 'custom_transfer', label: 'Transfer', emoji: '🔄', color: 'bg-violet-100 text-violet-700' }],
      income: [...incomeEntries.map(e => ({ ...e, createdAt: new Date().toISOString() })), ...(prev.income || [])],
      expenses: [...expenseEntries.map(e => ({ ...e, createdAt: new Date().toISOString() })), ...(prev.expenses || [])],
    }));

    // Sync to week data
    setWeekData(prev => {
      const updated = { ...prev };
      incomeEntries.forEach(entry => {
        const weekNum = getWeekNumForDate(new Date(entry.date));
        const current = getWeekDataFromPrev(updated, weekNum);
        const newEntry = { id: entry.id, amount: entry.amount, description: entry.source, date: entry.date, timestamp: new Date().toISOString(), source: 'expense-manager' };
        const newEntries = [newEntry, ...current.revenueEntries];
        updated[weekNum] = { ...current, revenueEntries: newEntries, revenue: newEntries.reduce((s, e) => s + e.amount, 0) };
      });
      expenseEntries.forEach(entry => {
        const weekNum = getWeekNumForDate(new Date(entry.date));
        const current = getWeekDataFromPrev(updated, weekNum);
        const newEntry = { id: entry.id, amount: entry.amount, description: entry.description, date: entry.date, timestamp: new Date().toISOString(), source: 'expense-manager' };
        const newEntries = [newEntry, ...current.expenseEntries];
        updated[weekNum] = { ...current, expenseEntries: newEntries, expenses: newEntries.reduce((s, e) => s + e.amount, 0) };
      });
      return updated;
    });

    localStorage.setItem(importKey, 'true');
    console.log('Feb 2026 bank statement imported: 5 income + 50 expense entries');
  }, [isLoaded]);
  useEffect(() => { if (!isLoaded) return; window.storage.set('life-weeks-birth', JSON.stringify(lifeWeeksBirthDate)); }, [lifeWeeksBirthDate, isLoaded]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen(prev => !prev);
        setCmdInput('');
      }
      if (e.key === 'Escape') setCmdPaletteOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const getWeekData = (week) => {
    const data = weekData[week] || {
      goals: [],
      revenue: 0,
      expenses: 0,
      milestones: [],
      notes: '',
      noteEntries: [],
      revenueEntries: [],
      expenseEntries: []
    };
    // Backward compatibility: ensure arrays exist for old data
    if (!data.noteEntries) data.noteEntries = [];
    if (!data.revenueEntries) data.revenueEntries = [];
    if (!data.expenseEntries) data.expenseEntries = [];
    return data;
  };

  // --- Note Entries (timestamped) ---
  const addNoteEntry = (week, text, customTimestamp = null) => {
    if (!text.trim()) return;
    const currentData = getWeekData(week);
    const entry = {
      id: Date.now(),
      text: text.trim(),
      timestamp: customTimestamp || new Date().toISOString()
    };
    updateWeekData(week, 'noteEntries', [...currentData.noteEntries, entry]);
  };

  const deleteNoteEntry = (week, entryId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'noteEntries', currentData.noteEntries.filter(e => e.id !== entryId));
  };

  const updateNoteEntry = (week, entryId, newText) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'noteEntries', currentData.noteEntries.map(e =>
      e.id === entryId ? { ...e, text: newText } : e
    ));
  };

  // --- Revenue & Expense Entries (with date and description) ---
  const addFinancialEntry = (week, type, amount, description, date) => {
    if (!amount || amount <= 0) return;
    const entry = {
      id: Date.now(),
      amount: parseFloat(amount),
      description: description?.trim() || '',
      date: date || new Date().toISOString().split('T')[0],
      timestamp: new Date().toISOString()
    };
    const field = type === 'revenue' ? 'revenueEntries' : 'expenseEntries';
    // Single atomic update: update entries array + cumulative total together
    setWeekData(prev => {
      const current = getWeekDataFromPrev(prev, week);
      const newEntries = [...current[field], entry];
      const total = newEntries.reduce((sum, e) => sum + e.amount, 0);
      return {
        ...prev,
        [week]: {
          ...current,
          [field]: newEntries,
          [type]: total
        }
      };
    });
    // Sync to Expense Manager
    if (type === 'revenue') {
      setExpenseData(prev => ({ ...prev, income: [{ id: entry.id, amount: entry.amount, source: entry.description || 'Weekly Revenue', category: 'salary', date: entry.date, recurring: false, recurringFrequency: null, nextDueDate: null, notes: '', smartLogSource: 'weekly', createdAt: entry.timestamp }, ...(prev.income || [])] }));
    } else {
      setExpenseData(prev => ({ ...prev, expenses: [{ id: entry.id, amount: entry.amount, description: entry.description || 'Weekly Expense', category: 'other', type: 'variable', date: entry.date, tags: [], paymentMethod: 'upi', creditCardId: null, recurring: false, recurringFrequency: null, nextDueDate: null, merchant: '', notes: '', smartLogSource: 'weekly', createdAt: entry.timestamp }, ...(prev.expenses || [])] }));
    }
  };

  const deleteFinancialEntry = (week, type, entryId) => {
    const field = type === 'revenue' ? 'revenueEntries' : 'expenseEntries';
    // Single atomic update: remove entry + recalculate total together
    setWeekData(prev => {
      const current = getWeekDataFromPrev(prev, week);
      const newEntries = current[field].filter(e => e.id !== entryId);
      const total = newEntries.reduce((sum, e) => sum + e.amount, 0);
      return {
        ...prev,
        [week]: {
          ...current,
          [field]: newEntries,
          [type]: total
        }
      };
    });
    // Sync delete to Expense Manager
    if (type === 'revenue') {
      setExpenseData(prev => ({ ...prev, income: (prev.income || []).filter(i => i.id !== entryId) }));
    } else {
      setExpenseData(prev => ({ ...prev, expenses: (prev.expenses || []).filter(e => e.id !== entryId) }));
    }
  };

  const getTotalRevenue = (week) => {
    const data = getWeekData(week);
    if (data.revenueEntries.length > 0) {
      return data.revenueEntries.reduce((sum, e) => sum + e.amount, 0);
    }
    return data.revenue || 0;
  };

  const getTotalExpenses = (week) => {
    const data = getWeekData(week);
    if (data.expenseEntries.length > 0) {
      return data.expenseEntries.reduce((sum, e) => sum + e.amount, 0);
    }
    return data.expenses || 0;
  };

  // Get monthly cumulative totals
  const getMonthlyCumulativeFinancials = (week) => {
    // Find the month from the current week's start date
    const startDate = new Date(2026, 0, 1);
    const startDay = startDate.getDay();
    const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
    const firstMonday = new Date(2026, 0, daysToMonday);
    const weekStart = new Date(firstMonday);
    weekStart.setDate(firstMonday.getDate() + (week - 1) * 7);
    const currentMonth = weekStart.getMonth();

    let monthlyRevenue = 0;
    let monthlyExpenses = 0;

    // Sum all weeks that fall in the same month
    for (let w = 1; w <= 52; w++) {
      const wStart = new Date(firstMonday);
      wStart.setDate(firstMonday.getDate() + (w - 1) * 7);
      if (wStart.getMonth() === currentMonth) {
        monthlyRevenue += getTotalRevenue(w);
        monthlyExpenses += getTotalExpenses(w);
      }
    }

    return {
      month: monthNames[currentMonth],
      revenue: monthlyRevenue,
      expenses: monthlyExpenses,
      profit: monthlyRevenue - monthlyExpenses
    };
  };

  const getWeekDataFromPrev = (prev, week) => {
    const data = prev[week] || {
      goals: [],
      revenue: 0,
      expenses: 0,
      milestones: [],
      notes: '',
      noteEntries: [],
      revenueEntries: [],
      expenseEntries: []
    };
    if (!data.noteEntries) data.noteEntries = [];
    if (!data.revenueEntries) data.revenueEntries = [];
    if (!data.expenseEntries) data.expenseEntries = [];
    return data;
  };

  const updateWeekData = (week, field, value) => {
    setWeekData(prev => ({
      ...prev,
      [week]: {
        ...getWeekDataFromPrev(prev, week),
        [field]: value
      }
    }));
  };

  const addGoal = (week, goalText) => {
    if (!goalText.trim()) return;
    const currentData = getWeekData(week);
    updateWeekData(week, 'goals', [
      ...currentData.goals,
      { id: Date.now(), text: goalText, completed: false }
    ]);
  };

  const toggleGoal = (week, goalId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'goals', currentData.goals.map(g =>
      g.id === goalId ? { ...g, completed: !g.completed, notDone: false } : g
    ));
  };

  const deleteGoal = (week, goalId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'goals', currentData.goals.filter(g => g.id !== goalId));
  };

  const markGoalNotDone = (week, goalId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'goals', currentData.goals.map(g =>
      g.id === goalId ? { ...g, completed: false, notDone: true } : g
    ));
  };

  const moveGoalToNextWeek = (week, goalId) => {
    const currentData = getWeekData(week);
    const goal = currentData.goals.find(g => g.id === goalId);
    if (!goal) return;
    const nextWeek = week < 52 ? week + 1 : 1;
    addGoal(nextWeek, goal.text);
    deleteGoal(week, goalId);
  };

  const updateGoal = (week, goalId, newText) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'goals', currentData.goals.map(g =>
      g.id === goalId ? { ...g, text: newText } : g
    ));
  };

  const addMilestone = (week, milestoneText, date) => {
    if (!milestoneText.trim()) return;
    const currentData = getWeekData(week);
    updateWeekData(week, 'milestones', [
      ...currentData.milestones,
      { id: Date.now(), text: milestoneText, date: date || '', completed: false }
    ]);
  };

  const toggleMilestone = (week, milestoneId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'milestones', currentData.milestones.map(m =>
      m.id === milestoneId ? { ...m, completed: !m.completed, notDone: false } : m
    ));
  };

  const deleteMilestone = (week, milestoneId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'milestones', currentData.milestones.filter(m => m.id !== milestoneId));
  };

  const markMilestoneNotDone = (week, milestoneId) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'milestones', currentData.milestones.map(m =>
      m.id === milestoneId ? { ...m, completed: false, notDone: true } : m
    ));
  };

  const moveMilestoneToNextWeek = (week, milestoneId) => {
    const currentData = getWeekData(week);
    const milestone = currentData.milestones.find(m => m.id === milestoneId);
    if (!milestone) return;
    const nextWeek = week < 52 ? week + 1 : 1;
    addMilestone(nextWeek, milestone.text, milestone.date);
    deleteMilestone(week, milestoneId);
  };

  const updateMilestone = (week, milestoneId, newText, newDate) => {
    const currentData = getWeekData(week);
    updateWeekData(week, 'milestones', currentData.milestones.map(m =>
      m.id === milestoneId ? { ...m, text: newText, date: newDate } : m
    ));
  };

  const getWeekDateRange = (weekNum) => {
    const startDate = new Date(2026, 0, 1);
    const startDay = startDate.getDay();
    const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
    const firstMonday = new Date(2026, 0, daysToMonday);

    const weekStart = new Date(firstMonday);
    weekStart.setDate(firstMonday.getDate() + (weekNum - 1) * 7);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const formatDate = (date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
  };

  const getWeekStatus = (weekNum) => {
    const now = new Date();
    const startDate = new Date(2026, 0, 1);
    const startDay = startDate.getDay();
    const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
    const firstMonday = new Date(2026, 0, daysToMonday);

    if (now < firstMonday) {
      // Before first week of 2026
      if (weekNum === 1) return 'current';
      return 'future';
    }

    const diffTime = now - firstMonday;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const currentWeekNum = Math.floor(diffDays / 7) + 1;

    if (currentWeekNum > 52) {
      // After week 52
      if (weekNum === 52) return 'current';
      if (weekNum < 52) return 'past';
      return 'future';
    }

    if (weekNum < currentWeekNum) return 'past';
    if (weekNum === currentWeekNum) return 'current';
    return 'future';
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    // Monday-based: 0=Mon, 1=Tue, ..., 6=Sun
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  // ── Hijri Calendar Data (2026) ──────────────────────────────────────
  const HIJRI_MONTH_NAMES = [
    'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani',
    'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', 'Sha\'ban',
    'Ramadan', 'Shawwal', 'Dhul Qa\'dah', 'Dhul Hijjah'
  ];

  // Anchor: 1st of each Hijri month → Gregorian date
  const HIJRI_MONTH_STARTS = [
    { greg: new Date(2025, 11, 21), hijriMonth: 7,  hijriYear: 1447 }, // 1 Rajab 1447
    { greg: new Date(2026, 0, 20),  hijriMonth: 8,  hijriYear: 1447 }, // 1 Sha'ban 1447
    { greg: new Date(2026, 1, 18),  hijriMonth: 9,  hijriYear: 1447 }, // 1 Ramadan 1447
    { greg: new Date(2026, 2, 20),  hijriMonth: 10, hijriYear: 1447 }, // 1 Shawwal 1447
    { greg: new Date(2026, 3, 18),  hijriMonth: 11, hijriYear: 1447 }, // 1 Dhul Qa'dah 1447
    { greg: new Date(2026, 4, 18),  hijriMonth: 12, hijriYear: 1447 }, // 1 Dhul Hijjah 1447
    { greg: new Date(2026, 5, 16),  hijriMonth: 1,  hijriYear: 1448 }, // 1 Muharram 1448
    { greg: new Date(2026, 6, 15),  hijriMonth: 2,  hijriYear: 1448 }, // 1 Safar 1448
    { greg: new Date(2026, 7, 14),  hijriMonth: 3,  hijriYear: 1448 }, // 1 Rabi al-Awwal 1448
    { greg: new Date(2026, 8, 12),  hijriMonth: 4,  hijriYear: 1448 }, // 1 Rabi al-Thani 1448
    { greg: new Date(2026, 9, 12),  hijriMonth: 5,  hijriYear: 1448 }, // 1 Jumada al-Awwal 1448
    { greg: new Date(2026, 10, 11), hijriMonth: 6,  hijriYear: 1448 }, // 1 Jumada al-Thani 1448
    { greg: new Date(2026, 11, 10), hijriMonth: 7,  hijriYear: 1448 }, // 1 Rajab 1448
  ];

  const getHijriDate = (year, month, day) => {
    const date = new Date(year, month, day);
    let anchor = HIJRI_MONTH_STARTS[0];
    for (let i = HIJRI_MONTH_STARTS.length - 1; i >= 0; i--) {
      if (date >= HIJRI_MONTH_STARTS[i].greg) {
        anchor = HIJRI_MONTH_STARTS[i];
        break;
      }
    }
    const diffDays = Math.floor((date - anchor.greg) / (1000 * 60 * 60 * 24));
    const hijriDay = diffDays + 1;
    return {
      day: hijriDay,
      month: anchor.hijriMonth,
      monthName: HIJRI_MONTH_NAMES[anchor.hijriMonth - 1],
      year: anchor.hijriYear
    };
  };

  // ── India Holidays 2026 ───────────────────────────────────────────
  const INDIA_HOLIDAYS_2026 = {
    '2026-01-01': { name: 'New Year', type: 'other' },
    '2026-01-14': { name: 'Makar Sankranti', type: 'hindu' },
    '2026-01-16': { name: 'Shab-e-Meraj', type: 'islamic' },
    '2026-01-26': { name: 'Republic Day', type: 'national' },
    '2026-02-03': { name: 'Shab-e-Barat', type: 'islamic' },
    '2026-02-15': { name: 'Maha Shivaratri', type: 'hindu' },
    '2026-02-18': { name: 'Ramadan Begins', type: 'islamic' },
    '2026-03-03': { name: 'Holika Dahan', type: 'hindu' },
    '2026-03-04': { name: 'Holi', type: 'hindu' },
    '2026-03-16': { name: 'Laylat al-Qadr', type: 'islamic' },
    '2026-03-19': { name: 'Ugadi / Gudi Padwa', type: 'hindu' },
    '2026-03-20': { name: 'Eid ul-Fitr', type: 'islamic' },
    '2026-03-26': { name: 'Ram Navami', type: 'hindu' },
    '2026-03-31': { name: 'Mahavir Jayanti', type: 'jain' },
    '2026-04-03': { name: 'Good Friday', type: 'christian' },
    '2026-04-14': { name: 'Baisakhi', type: 'sikh' },
    '2026-05-01': { name: 'Buddha Purnima', type: 'buddhist' },
    '2026-05-26': { name: 'Day of Arafah', type: 'islamic' },
    '2026-05-27': { name: 'Eid ul-Adha', type: 'islamic' },
    '2026-06-16': { name: 'Islamic New Year', type: 'islamic' },
    '2026-06-25': { name: 'Ashura', type: 'islamic' },
    '2026-07-16': { name: 'Rath Yatra', type: 'hindu' },
    '2026-08-15': { name: 'Independence Day', type: 'national' },
    '2026-08-25': { name: 'Milad-un-Nabi', type: 'islamic' },
    '2026-08-28': { name: 'Raksha Bandhan', type: 'hindu' },
    '2026-09-04': { name: 'Janmashtami', type: 'hindu' },
    '2026-09-14': { name: 'Ganesh Chaturthi', type: 'hindu' },
    '2026-10-02': { name: 'Gandhi Jayanti', type: 'national' },
    '2026-10-11': { name: 'Navratri Begins', type: 'hindu' },
    '2026-10-20': { name: 'Dussehra', type: 'hindu' },
    '2026-10-29': { name: 'Karva Chauth', type: 'hindu' },
    '2026-11-06': { name: 'Dhanteras', type: 'hindu' },
    '2026-11-08': { name: 'Diwali', type: 'hindu' },
    '2026-11-09': { name: 'Govardhan Puja', type: 'hindu' },
    '2026-11-10': { name: 'Bhai Dooj', type: 'hindu' },
    '2026-11-15': { name: 'Chhath Puja', type: 'hindu' },
    '2026-11-24': { name: 'Guru Nanak Jayanti', type: 'sikh' },
    '2026-12-25': { name: 'Christmas', type: 'christian' },
  };

  const HOLIDAY_BADGE_STYLES = {
    national: 'bg-orange-100 text-orange-700 border-orange-200',
    islamic:  'bg-green-100 text-green-700 border-green-200',
    hindu:    'bg-amber-100 text-amber-700 border-amber-200',
    christian:'bg-blue-100 text-blue-700 border-blue-200',
    sikh:     'bg-purple-100 text-purple-700 border-purple-200',
    jain:     'bg-purple-100 text-purple-700 border-purple-200',
    buddhist: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    other:    'bg-gray-100 text-gray-600 border-gray-200',
  };

  const getHoliday = (year, month, day) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return INDIA_HOLIDAYS_2026[key] || null;
  };

  const getHijriMonthRange = (gregMonth, gregYear) => {
    const firstDay = getHijriDate(gregYear, gregMonth, 1);
    const lastDayNum = getDaysInMonth(gregMonth, gregYear);
    const lastDay = getHijriDate(gregYear, gregMonth, lastDayNum);
    if (firstDay.monthName === lastDay.monthName) {
      return `${firstDay.monthName} ${firstDay.year} AH`;
    }
    if (firstDay.year === lastDay.year) {
      return `${firstDay.monthName} – ${lastDay.monthName} ${firstDay.year} AH`;
    }
    return `${firstDay.monthName} ${firstDay.year} – ${lastDay.monthName} ${lastDay.year} AH`;
  };

  const getDayKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const toggleDayHighlight = (year, month, day, color) => {
    const key = getDayKey(year, month, day);
    setDayHighlights(prev => {
      const current = prev[key];
      if (current === color) {
        const newHighlights = { ...prev };
        delete newHighlights[key];
        return newHighlights;
      }
      return { ...prev, [key]: color };
    });
  };

  const getDayHighlight = (year, month, day) => {
    const key = getDayKey(year, month, day);
    return dayHighlights[key] || null;
  };

  const getDayNote = (year, month, day) => {
    const key = getDayKey(year, month, day);
    return dayNotes[key] || '';
  };

  const updateDayNote = (year, month, day, note) => {
    const key = getDayKey(year, month, day);
    if (!note.trim()) {
      setDayNotes(prev => {
        const newNotes = { ...prev };
        delete newNotes[key];
        return newNotes;
      });
    } else {
      setDayNotes(prev => ({
        ...prev,
        [key]: note
      }));
    }
  };

  const getMonthKey = (year, month) => {
    return `${year}-${String(month + 1).padStart(2, '0')}`;
  };

  const getMonthlyGoals = (year, month) => {
    const key = getMonthKey(year, month);
    return monthlyGoals[key] || [];
  };

  const addMonthlyGoal = (year, month, goalText) => {
    if (!goalText.trim()) return;
    const key = getMonthKey(year, month);
    const currentGoals = monthlyGoals[key] || [];
    setMonthlyGoals(prev => ({
      ...prev,
      [key]: [...currentGoals, { id: Date.now(), text: goalText, completed: false }]
    }));
  };

  const toggleMonthlyGoal = (year, month, goalId) => {
    const key = getMonthKey(year, month);
    const currentGoals = monthlyGoals[key] || [];
    setMonthlyGoals(prev => ({
      ...prev,
      [key]: currentGoals.map(g => g.id === goalId ? { ...g, completed: !g.completed, notDone: false } : g)
    }));
  };

  const deleteMonthlyGoal = (year, month, goalId) => {
    const key = getMonthKey(year, month);
    const currentGoals = monthlyGoals[key] || [];
    setMonthlyGoals(prev => ({
      ...prev,
      [key]: currentGoals.filter(g => g.id !== goalId)
    }));
  };

  const markMonthlyGoalNotDone = (year, month, goalId) => {
    const key = getMonthKey(year, month);
    const currentGoals = monthlyGoals[key] || [];
    setMonthlyGoals(prev => ({
      ...prev,
      [key]: currentGoals.map(g => g.id === goalId ? { ...g, completed: false, notDone: true } : g)
    }));
  };

  const moveMonthlyGoalToNextMonth = (year, month, goalId) => {
    const key = getMonthKey(year, month);
    const currentGoals = monthlyGoals[key] || [];
    const goal = currentGoals.find(g => g.id === goalId);
    if (!goal) return;
    const nextMonth = month < 11 ? month + 1 : 0;
    const nextYear = month < 11 ? year : year + 1;
    addMonthlyGoal(nextYear, nextMonth, goal.text);
    deleteMonthlyGoal(year, month, goalId);
  };

  const getDailyHours = (year, month, day) => {
    const key = getDayKey(year, month, day);
    return dailyHours[key] || '';
  };

  // eslint-disable-next-line no-unused-vars
  const updateDailyHours = (year, month, day, hours) => {
    const key = getDayKey(year, month, day);
    if (!hours.trim()) {
      setDailyHours(prev => {
        const newHours = { ...prev };
        delete newHours[key];
        return newHours;
      });
    } else {
      setDailyHours(prev => ({
        ...prev,
        [key]: hours
      }));
    }
  };

  // Habit functions
  const addHabit = (habitName, color) => {
    if (!habitName.trim()) return;
    const newHabit = {
      id: Date.now(),
      name: habitName,
      color: color || 'bg-blue-500',
      createdAt: new Date().toISOString()
    };
    setHabits(prev => [...prev, newHabit]);
  };

  const deleteHabit = (habitId) => {
    setHabits(prev => prev.filter(h => h.id !== habitId));
    // Also clean up tracking data for this habit
    setHabitTracking(prev => {
      const newTracking = { ...prev };
      Object.keys(newTracking).forEach(key => {
        if (newTracking[key][habitId]) {
          delete newTracking[key][habitId];
        }
      });
      return newTracking;
    });
  };

  const toggleHabitForDay = (habitId, year, month, day) => {
    const dayKey = getDayKey(year, month, day);
    setHabitTracking(prev => ({
      ...prev,
      [dayKey]: {
        ...(prev[dayKey] || {}),
        [habitId]: !(prev[dayKey]?.[habitId])
      }
    }));
  };

  const isHabitCompletedForDay = (habitId, year, month, day) => {
    const dayKey = getDayKey(year, month, day);
    return habitTracking[dayKey]?.[habitId] || false;
  };

  const getHabitStreak = (habitId) => {
    let streak = 0;
    const today = new Date();
    let currentDate = new Date(today);

    // If today isn't completed yet, start counting from yesterday
    if (!isHabitCompletedForDay(habitId, currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())) {
      currentDate.setDate(currentDate.getDate() - 1);
    }

    while (true) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const day = currentDate.getDate();

      if (isHabitCompletedForDay(habitId, year, month, day)) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  };

  const getHabitCompletionRate = (habitId, days = 30) => {
    let completed = 0;
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      if (isHabitCompletedForDay(habitId, date.getFullYear(), date.getMonth(), date.getDate())) {
        completed++;
      }
    }

    return Math.round((completed / days) * 100);
  };

  const isToday = (year, month, day) => {
    const today = new Date();
    return today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day;
  };

  // --- Productivity Log helpers ---
  const addProductivityLogEntry = (text, category = 'other') => {
    if (!text.trim()) return;
    setProductivityLog(prev => [
      { id: Date.now(), text: text.trim(), completedAt: new Date().toISOString(), category },
      ...prev
    ]);
  };

  const deleteProductivityLogEntry = (id) => {
    setProductivityLog(prev => prev.filter(e => e.id !== id));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const currentData = getWeekData(currentWeek);
  // Revenue/expense totals are computed inside FinancialSection component

  const HabitsView = () => {
    const [newHabitName, setNewHabitName] = useState('');
    const [selectedColor, setSelectedColor] = useState('bg-blue-500');
    const [habitsTab, setHabitsTab] = useState('tracker');

    const habitColors = [
      { name: 'Blue', value: 'bg-blue-500' },
      { name: 'Green', value: 'bg-green-500' },
      { name: 'Purple', value: 'bg-purple-500' },
      { name: 'Pink', value: 'bg-pink-500' },
      { name: 'Orange', value: 'bg-orange-500' },
      { name: 'Teal', value: 'bg-teal-500' },
      { name: 'Red', value: 'bg-red-500' },
      { name: 'Indigo', value: 'bg-blue-500' },
    ];

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        {/* Habit Stack Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-100 pb-4">
          {[['tracker','📊 Tracker'],['stack','🌅 Morning Stack'],['onepercent','🚀 1% Better']].map(([key,label]) => (
            <button key={key} onClick={() => setHabitsTab(key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${habitsTab === key ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        {habitsTab === 'tracker' && <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Habit Tracker</h2>
          <p className="text-gray-600">Build consistent habits by tracking them daily</p>
        </div>

        {/* Add New Habit */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Add New Habit</h3>

          <div className="flex gap-3 mb-3">
            <input
              type="text"
              value={newHabitName}
              onChange={(e) => setNewHabitName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addHabit(newHabitName, selectedColor);
                  setNewHabitName('');
                }
              }}
              placeholder="e.g., Exercise, Meditation, Read 30 mins..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              onClick={() => {
                addHabit(newHabitName, selectedColor);
                setNewHabitName('');
              }}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Add Habit
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Color:</span>
            {habitColors.map(color => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                className={`w-8 h-8 rounded-full ${color.value} border-2 transition-all ${selectedColor === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                  }`}
                title={color.name}
              />
            ))}
          </div>
        </div>

        {/* Habits List */}
        {habits.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg mb-2">No habits yet</p>
            <p className="text-gray-500 text-sm">Add your first habit above to start tracking!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {habits.map(habit => {
              const streak = getHabitStreak(habit.id);
              const completionRate = getHabitCompletionRate(habit.id, 30);

              return (
                <div key={habit.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${habit.color}`}></div>
                      <div>
                        <h4 className="font-bold text-gray-800">{habit.name}</h4>
                        <div className="flex gap-4 mt-1">
                          <span className="text-sm text-gray-600">
                            {streak} day streak
                          </span>
                          <span className="text-sm text-gray-600">
                            {completionRate}% (30 days)
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete habit "${habit.name}"?`)) {
                          deleteHabit(habit.id);
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Last 14 Days */}
                  <div className="mt-3">
                    <div className="text-xs font-medium text-gray-600 mb-2">Last 14 Days</div>
                    <div className="flex gap-1">
                      {Array.from({ length: 14 }, (_, i) => {
                        const date = new Date();
                        date.setDate(date.getDate() - (13 - i));
                        const isCompleted = isHabitCompletedForDay(
                          habit.id,
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate()
                        );
                        const isTodayDate = date.toDateString() === new Date().toDateString();

                        return (
                          <button
                            key={i}
                            onClick={() => toggleHabitForDay(
                              habit.id,
                              date.getFullYear(),
                              date.getMonth(),
                              date.getDate()
                            )}
                            className={`w-8 h-8 rounded flex items-center justify-center transition-all ${isCompleted
                              ? `${habit.color} text-white`
                              : 'bg-gray-200 hover:bg-gray-300'
                              } ${isTodayDate ? 'ring-2 ring-blue-500' : ''}`}
                            title={`${date.toLocaleDateString()} - ${date.toLocaleDateString('en-US', { weekday: 'short' })}`}
                          >
                            {isCompleted ? <Check size={12} /> : (
                              <span className="text-xs text-gray-500">{date.getDate()}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>}
        {/* Morning Stack View */}
        {habitsTab === 'stack' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🌅</span>
              <div>
                <h3 className="font-bold text-gray-800">Morning Routine Stack</h3>
                <p className="text-xs text-gray-500">{habits.length} habits · drag to reorder</p>
              </div>
            </div>
            {habits.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-3">🌱</div>
                <p className="text-sm">Add habits in the Tracker tab first</p>
              </div>
            ) : (
              <div className="space-y-3">
                {habits.map((habit, idx) => {
                  const streak = getHabitStreak(habit.id);
                  const rate = getHabitCompletionRate(habit.id, 30);
                  return (
                    <div key={habit.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button disabled={idx === 0} onClick={() => {
                          setHabits(prev => { const a = [...prev]; const [h] = a.splice(idx, 1); a.splice(idx - 1, 0, h); return a; });
                        }} className="text-gray-300 hover:text-gray-600 disabled:opacity-20 text-xs leading-none">▲</button>
                        <button disabled={idx === habits.length - 1} onClick={() => {
                          setHabits(prev => { const a = [...prev]; const [h] = a.splice(idx, 1); a.splice(idx + 1, 0, h); return a; });
                        }} className="text-gray-300 hover:text-gray-600 disabled:opacity-20 text-xs leading-none">▼</button>
                      </div>
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${habit.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 text-sm">{habit.name}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Step {idx + 1} in your morning routine
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[11px] font-bold ${streak > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                          {streak > 0 ? `🔥 ${streak}d` : '—'}
                        </span>
                        <span className="text-[10px] text-gray-400">{rate}% / 30d</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {habitsTab === 'onepercent' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🚀</span>
              <div>
                <h3 className="font-bold text-gray-800">1% Better Every Day</h3>
                <p className="text-xs text-gray-500">Small daily improvements → massive results. 1.01³⁶⁵ = 37.7×</p>
              </div>
            </div>

            {/* Today's focus */}
            {(() => {
              const todayKey = new Date().toISOString().split('T')[0];
              const todayEntry = onePercentBetter.find(e => e.date === todayKey);
              const streak = (() => {
                let count = 0;
                const d = new Date(); d.setDate(d.getDate() - (todayEntry?.done ? 0 : 1));
                while (true) {
                  const dk = d.toISOString().split('T')[0];
                  const entry = onePercentBetter.find(e => e.date === dk && e.done);
                  if (!entry) break;
                  count++; d.setDate(d.getDate() - 1);
                }
                return count;
              })();
              const compoundValue = Math.pow(1.01, streak).toFixed(1);

              return (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-indigo-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-black text-indigo-600">{streak}</div>
                      <div className="text-[10px] text-gray-500 font-medium">Day Streak</div>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-black text-emerald-600">{compoundValue}×</div>
                      <div className="text-[10px] text-gray-500 font-medium">Compound</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-black text-amber-600">{onePercentBetter.filter(e => e.done).length}</div>
                      <div className="text-[10px] text-gray-500 font-medium">Total Days</div>
                    </div>
                  </div>

                  {/* Compound growth curve */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-4">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Growth Curve (last 30 days)</div>
                    <div className="flex items-end gap-0.5 h-16">
                      {Array.from({length: 30}, (_, i) => {
                        const d = new Date(); d.setDate(d.getDate() - 29 + i);
                        const dk = d.toISOString().split('T')[0];
                        const entry = onePercentBetter.find(e => e.date === dk);
                        const done = entry?.done;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                            <div className={`w-full rounded-t-sm ${done ? 'bg-indigo-400' : 'bg-gray-200'}`} style={{ height: `${Math.max(Math.pow(1.01, i+1) * 2, 4)}px`, maxHeight: '100%' }} title={dk} />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Today's input */}
                  <div className="bg-white border-2 border-dashed border-indigo-200 rounded-xl p-4 mb-4">
                    <div className="text-xs font-bold text-indigo-500 mb-2">TODAY: What will you improve 1%?</div>
                    {todayEntry ? (
                      <div className="flex items-center gap-3">
                        <button onClick={() => setOnePercentBetter(prev => prev.map(e => e.date === todayKey ? { ...e, done: !e.done } : e))} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${todayEntry.done ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`}>
                          {todayEntry.done && <Check size={14} />}
                        </button>
                        <span className={`text-sm flex-1 ${todayEntry.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>{todayEntry.focus}</span>
                      </div>
                    ) : (
                      <input
                        type="text"
                        defaultValue=""
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            const focusText = e.target.value.trim();
                            setOnePercentBetter(prev => [{ id: Date.now(), date: todayKey, focus: focusText, done: false, note: '' }, ...prev]);
                            e.target.value = '';
                          }
                        }}
                        placeholder="e.g., Read 10 pages, exercise 5 more minutes..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      />
                    )}
                  </div>

                  {/* History */}
                  <div className="space-y-1">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Recent History</div>
                    {onePercentBetter.slice(0, 14).map(entry => (
                      <div key={entry.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50">
                        <span className={`w-2 h-2 rounded-full ${entry.done ? 'bg-green-400' : 'bg-gray-300'}`} />
                        <span className="text-[10px] text-gray-400 w-16 shrink-0">{new Date(entry.date+'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        <span className="text-xs text-gray-600 flex-1">{entry.focus}</span>
                        <button onClick={() => setOnePercentBetter(prev => prev.filter(e => e.id !== entry.id))} className="text-gray-300 hover:text-red-400"><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };
  const JournalView = () => {
    const allEntries = [];

    // Utility to get week number for any entry
    const getWeekNum = (date) => getWeekNumForDate(date);

    // 1. Collect Day Notes
    Object.entries(dayNotes).forEach(([dateKey, text]) => {
      if (text && text.trim()) {
        const date = new Date(dateKey);
        allEntries.push({
          id: `day-${dateKey}`,
          date,
          weekNum: getWeekNum(date),
          dateStr: dateKey,
          displayDate: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          type: 'Daily Note',
          text: text.trim(),
          icon: <FileText size={16} className="text-blue-500" />
        });
      }
    });

    // 2. Collect Daily Schedule & Tasks as journal entries
    Object.entries(daySchedule).forEach(([dateKey, scheduleObj]) => {
      const events = Object.entries(scheduleObj)
        .filter(([, val]) => { const t = getScheduleText(val); return t && t.trim(); })
        .sort(([a], [b]) => {
          const pa = parseScheduleKey(a); const pb = parseScheduleKey(b);
          return pa.hour !== pb.hour ? pa.hour - pb.hour : pa.minute - pb.minute;
        });
      if (events.length === 0) return;
      const date = new Date(dateKey);
      const tasks = dayTasks[dateKey] || { high: [], medium: [], low: [] };
      const highDone = tasks.high?.filter(t => t.completed).length || 0;
      const highTotal = tasks.high?.length || 0;
      const medDone = tasks.medium?.filter(t => t.completed).length || 0;
      const medTotal = tasks.medium?.length || 0;
      const lowDone = tasks.low?.filter(t => t.completed).length || 0;
      const lowTotal = tasks.low?.length || 0;
      const totalTasks = highTotal + medTotal + lowTotal;
      const totalDone = highDone + medDone + lowDone;

      const scheduleLines = events.map(([hr, val]) => {
        const h = parseInt(hr);
        const timeStr = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
        const evtText = getScheduleText(val);
        const entryMeta = normalizeScheduleEntry(val);
        const dur = entryMeta.duration || 60;
        const durationTag = dur !== 60 ? ` (${dur}min)` : '';
        const statusTag = entryMeta.status === 'done' ? ' [DONE]' : entryMeta.status === 'skipped' ? ' [SKIPPED]' : '';
        return `${timeStr} — ${evtText}${durationTag}${statusTag}`;
      }).join('\n');

      let taskSummary = '';
      if (totalTasks > 0) {
        const parts = [];
        if (highTotal > 0) parts.push(`⚡ ${highDone}/${highTotal}`);
        if (medTotal > 0) parts.push(`🔋 ${medDone}/${medTotal}`);
        if (lowTotal > 0) parts.push(`🪫 ${lowDone}/${lowTotal}`);
        taskSummary = `\nTasks: ${parts.join('  ')} (${totalDone}/${totalTasks} done)`;
      }

      allEntries.push({
        id: `schedule-${dateKey}`,
        date,
        weekNum: getWeekNum(date),
        dateStr: dateKey,
        displayDate: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        type: 'Daily Schedule',
        text: scheduleLines + taskSummary,
        icon: <Clock size={16} className="text-green-500" />,
        isSchedule: true,
        scheduleEvents: events,
        taskStats: totalTasks > 0 ? { highDone, highTotal, medDone, medTotal, lowDone, lowTotal, totalDone, totalTasks } : null,
      });
    });

    // 3. Collect "Didn't Do" items (skipped schedule entries) grouped by day
    Object.entries(daySchedule).forEach(([dateKey, scheduleObj]) => {
      const skippedItems = Object.entries(scheduleObj)
        .filter(([, val]) => {
          const entry = normalizeScheduleEntry(val);
          return entry && entry.status === 'skipped' && entry.text && entry.text.trim();
        })
        .sort(([a], [b]) => {
          const pa = parseScheduleKey(a); const pb = parseScheduleKey(b);
          return pa.hour !== pb.hour ? pa.hour - pb.hour : pa.minute - pb.minute;
        });
      if (skippedItems.length === 0) return;
      const date = new Date(dateKey);
      allEntries.push({
        id: `skipped-${dateKey}`,
        date,
        weekNum: getWeekNum(date),
        dateStr: dateKey,
        displayDate: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        type: "Didn't Do",
        text: skippedItems.map(([, val]) => normalizeScheduleEntry(val).text.trim()).join(', '),
        icon: <SkipForward size={16} className="text-orange-500" />,
        isSkippedList: true,
        skippedItems,
      });
    });

    // 4. Collect Gratitude Log entries
    Object.entries(gratitudeLog).forEach(([dateKey, entries]) => {
      const nonEmpty = (entries || []).filter(e => e && e.trim());
      if (nonEmpty.length === 0) return;
      const date = new Date(dateKey);
      allEntries.push({
        id: `gratitude-${dateKey}`,
        date,
        weekNum: getWeekNum(date),
        dateStr: dateKey,
        displayDate: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        type: 'Gratitude',
        text: nonEmpty.join(' · '),
        icon: <span style={{fontSize:'16px'}}>🙏</span>,
        isGratitude: true,
        gratitudeItems: nonEmpty,
      });
    });

    // 5. Collect Weekly Note Entries (Reflections)
    Object.entries(weekData).forEach(([weekNum, data]) => {
      const wNum = parseInt(weekNum);
      if (data.noteEntries) {
        data.noteEntries.forEach(entry => {
          const date = new Date(entry.timestamp);
          const dateStr = entry.timestamp.split('T')[0];
          allEntries.push({
            id: `entry-${entry.id}`,
            date,
            weekNum: wNum,
            dateStr,
            displayDate: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
            type: `Week ${weekNum} Reflection`,
            text: entry.text.trim(),
            icon: <Zap size={16} className="text-amber-500" />,
            timestamp: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          });
        });
      }
      if (data.notes && data.notes.trim()) {
        const dateRangeStr = getWeekDateRange(wNum);
        allEntries.push({
          id: `week-sum-${weekNum}`,
          date: new Date(2026, 0, 1 + (wNum * 7)),
          weekNum: wNum,
          dateStr: `Week ${weekNum}`,
          displayDate: `Week ${weekNum} Overview (${dateRangeStr})`,
          type: 'Weekly Notes',
          text: data.notes.trim(),
          icon: <Target size={16} className="text-purple-500" />
        });
      }
    });

    // Sort by date descending (newest first)
    allEntries.sort((a, b) => b.date - a.date);

    // Group entries by WEEK
    const entriesByWeek = allEntries.reduce((groups, entry) => {
      const wNum = entry.weekNum;
      if (!groups[wNum]) groups[wNum] = [];
      groups[wNum].push(entry);
      return groups;
    }, {});

    const sortedWeeks = Object.keys(entriesByWeek)
      .map(Number)
      .sort((a, b) => b - a);

    const printJournalReport = () => {
      const typeEmoji = { 'Daily Note': '📄', 'Daily Schedule': '🕐', "Didn't Do": '⏭️', 'Gratitude': '🙏', 'Weekly Notes': '🎯' };
      const getEntryEmoji = (entry) => {
        if (entry.type.includes('Reflection')) return '⚡';
        return typeEmoji[entry.type] || '📝';
      };

      // Build entries HTML grouped by week → day
      let entriesHtml = '';
      sortedWeeks.forEach(wNum => {
        const weekEntries = entriesByWeek[wNum];
        const dateRange = getWeekDateRange(wNum);
        entriesHtml += `<div class="week-block"><h2>📅 Week ${wNum} <span class="range">${dateRange}</span> <span class="badge">${weekEntries.length} entries</span></h2>`;

        const groupedInWeek = weekEntries.reduce((g, e) => { const d = e.displayDate; if (!g[d]) g[d] = []; g[d].push(e); return g; }, {});

        Object.entries(groupedInWeek).forEach(([date, entries]) => {
          entriesHtml += `<div class="day-block"><h3>${date}</h3>`;
          entries.forEach(entry => {
            const emoji = getEntryEmoji(entry);
            entriesHtml += `<div class="entry"><div class="entry-type">${emoji} ${entry.type}${entry.timestamp ? ` · ${entry.timestamp}` : ''}</div>`;

            if (entry.isGratitude && entry.gratitudeItems) {
              entry.gratitudeItems.forEach((item, i) => {
                entriesHtml += `<div class="gratitude-item">🙏 Grateful ${i + 1}: ${item}</div>`;
              });
            } else if (entry.isSkippedList && entry.skippedItems) {
              entry.skippedItems.forEach(([hr, val]) => {
                const sk = normalizeScheduleEntry(val);
                const { hour: sh, minute: sm } = parseScheduleKey(hr);
                const base = sh === 0 ? '12 AM' : sh < 12 ? `${sh} AM` : sh === 12 ? '12 PM' : `${sh - 12} PM`;
                const ts = sm ? base.replace(/( AM| PM)/, `:${String(sm).padStart(2,'0')}$1`) : base;
                entriesHtml += `<div class="skipped-item">⏭️ ${ts} — ${sk.text}</div>`;
              });
            } else if (entry.isSchedule && entry.scheduleEvents) {
              entry.scheduleEvents.forEach(([hr, evt]) => {
                const { hour: eh, minute: em } = parseScheduleKey(hr);
                const base = eh === 0 ? '12 AM' : eh < 12 ? `${eh} AM` : eh === 12 ? '12 PM' : `${eh - 12} PM`;
                const ts = em ? base.replace(/( AM| PM)/, `:${String(em).padStart(2,'0')}$1`) : base;
                entriesHtml += `<div class="schedule-item"><span class="time">${ts}</span> ${getScheduleText(evt)}</div>`;
              });
              if (entry.taskStats) {
                const s = entry.taskStats;
                let parts = [];
                if (s.highTotal > 0) parts.push(`⚡ ${s.highDone}/${s.highTotal}`);
                if (s.medTotal > 0) parts.push(`🔋 ${s.medDone}/${s.medTotal}`);
                if (s.lowTotal > 0) parts.push(`🪫 ${s.lowDone}/${s.lowTotal}`);
                entriesHtml += `<div class="task-stats">Tasks: ${parts.join('  ')} (${s.totalDone}/${s.totalTasks} done)</div>`;
              }
            } else {
              entriesHtml += `<div class="entry-text">${entry.text.replace(/\n/g, '<br>')}</div>`;
            }
            entriesHtml += `</div>`;
          });
          entriesHtml += `</div>`;
        });
        entriesHtml += `</div>`;
      });

      // Build decisions HTML
      let decisionsHtml = '';
      if (decisionJournal.length > 0) {
        decisionsHtml = `<div class="section-break"></div><h1>⚖️ Decision Journal</h1><div class="stats-row"><div class="stat"><div class="stat-num">${decisionJournal.length}</div><div class="stat-label">Total</div></div><div class="stat amber"><div class="stat-num">${decisionJournal.filter(d => !d.actualOutcome && new Date(d.reviewDate) <= new Date()).length}</div><div class="stat-label">Needs Review</div></div><div class="stat green"><div class="stat-num">${decisionJournal.filter(d => d.rating).length}</div><div class="stat-label">Reviewed</div></div></div>`;
        decisionJournal.forEach(d => {
          const needsReview = !d.actualOutcome && d.reviewDate && new Date(d.reviewDate) <= new Date();
          const dateStr = new Date(d.date+'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          const reviewStr = d.reviewDate ? new Date(d.reviewDate+'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          decisionsHtml += `<div class="decision ${needsReview ? 'review-due' : d.rating ? 'reviewed' : ''}">`;
          decisionsHtml += `<div class="decision-title">${d.title}${needsReview ? ' <span class="badge-amber">Review Due</span>' : ''}${d.rating ? ' <span class="badge-green">Reviewed</span>' : ''}</div>`;
          decisionsHtml += `<div class="decision-date">${dateStr}${reviewStr ? ` · Review by ${reviewStr}` : ''}</div>`;
          if (d.context) decisionsHtml += `<div class="decision-context">${d.context}</div>`;
          if (d.chosen) decisionsHtml += `<div class="decision-detail"><strong>Chose:</strong> ${d.chosen}</div>`;
          if (d.options) decisionsHtml += `<div class="decision-detail"><strong>Options:</strong> ${d.options}</div>`;
          if (d.expectedOutcome) decisionsHtml += `<div class="decision-detail"><strong>Expected:</strong> ${d.expectedOutcome}</div>`;
          if (d.actualOutcome) decisionsHtml += `<div class="decision-outcome"><strong>Outcome:</strong> ${d.actualOutcome}${d.rating ? ` · ${'⭐'.repeat(d.rating)}` : ''}</div>`;
          decisionsHtml += `</div>`;
        });
      }

      // --- Compute Key Highlights ---
      const uniqueDays = [...new Set(allEntries.map(e => e.dateStr))].sort();
      const totalGratitudeItems = allEntries.filter(e => e.isGratitude).reduce((s, e) => s + (e.gratitudeItems?.length || 0), 0);

      // Task completion aggregate
      const scheduleEntries = allEntries.filter(e => e.taskStats);
      const aggTasks = scheduleEntries.reduce((a, e) => ({ done: a.done + e.taskStats.totalDone, total: a.total + e.taskStats.totalTasks }), { done: 0, total: 0 });
      const taskRate = aggTasks.total > 0 ? Math.round((aggTasks.done / aggTasks.total) * 100) : 0;

      // Total schedule events
      const totalScheduleEvents = allEntries.filter(e => e.isSchedule).reduce((s, e) => s + (e.scheduleEvents?.length || 0), 0);

      // Most active week
      const mostActiveWeek = sortedWeeks.length > 0 ? sortedWeeks.reduce((best, w) => entriesByWeek[w].length > entriesByWeek[best].length ? w : best, sortedWeeks[0]) : null;

      // Journaling streak (consecutive days)
      let streak = 0;
      if (uniqueDays.length > 0) {
        const sorted = uniqueDays.slice().sort((a, b) => b.localeCompare(a)); // newest first
        streak = 1;
        for (let i = 1; i < sorted.length; i++) {
          const curr = new Date(sorted[i - 1] + 'T12:00');
          const prev = new Date(sorted[i] + 'T12:00');
          const diff = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
          if (diff === 1) streak++;
          else break;
        }
      }

      // Longest entry
      const longestEntry = allEntries.reduce((best, e) => (e.text || '').length > (best?.text || '').length ? e : best, null);

      // Entry type breakdown for chart
      const typeCounts = {};
      allEntries.forEach(e => {
        const label = e.isGratitude ? 'Gratitude' : e.isSchedule ? 'Schedule' : e.isSkippedList ? "Didn't Do" : e.type.includes('Reflection') ? 'Reflections' : e.type === 'Weekly Notes' ? 'Weekly Notes' : 'Daily Notes';
        typeCounts[label] = (typeCounts[label] || 0) + 1;
      });
      const typeColors = { 'Daily Notes': '#3b82f6', 'Schedule': '#22c55e', "Didn't Do": '#f97316', 'Gratitude': '#ec4899', 'Reflections': '#f59e0b', 'Weekly Notes': '#8b5cf6' };
      const maxTypeCount = Math.max(...Object.values(typeCounts), 1);

      let highlightsHtml = `<div class="highlights">
        <h2 class="highlights-title">✨ Key Highlights</h2>
        <div class="hl-grid">
          <div class="hl-card blue"><div class="hl-num">${allEntries.length}</div><div class="hl-label">Total Entries</div></div>
          <div class="hl-card green"><div class="hl-num">${uniqueDays.length}</div><div class="hl-label">Days Journaled</div></div>
          <div class="hl-card purple"><div class="hl-num">${sortedWeeks.length}</div><div class="hl-label">Active Weeks</div></div>
          <div class="hl-card amber"><div class="hl-num">${streak}</div><div class="hl-label">Day Streak</div></div>
        </div>`;

      // Activity breakdown
      highlightsHtml += `<div class="hl-section"><h3>📊 Activity Breakdown</h3><div class="hl-bars">`;
      Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).forEach(([label, count]) => {
        const pct = Math.round((count / maxTypeCount) * 100);
        const color = typeColors[label] || '#6b7280';
        highlightsHtml += `<div class="hl-bar-row"><span class="hl-bar-label">${label}</span><div class="hl-bar-track"><div class="hl-bar-fill" style="width:${pct}%;background:${color}"></div></div><span class="hl-bar-count">${count}</span></div>`;
      });
      highlightsHtml += `</div></div>`;

      // Insights row
      highlightsHtml += `<div class="hl-insights">`;
      if (totalScheduleEvents > 0) highlightsHtml += `<div class="hl-insight">🕐 <strong>${totalScheduleEvents}</strong> schedule events logged</div>`;
      if (aggTasks.total > 0) highlightsHtml += `<div class="hl-insight">✅ <strong>${aggTasks.done}/${aggTasks.total}</strong> tasks completed (${taskRate}%)</div>`;
      if (totalGratitudeItems > 0) highlightsHtml += `<div class="hl-insight">🙏 <strong>${totalGratitudeItems}</strong> things you're grateful for</div>`;
      if (mostActiveWeek) highlightsHtml += `<div class="hl-insight">🔥 Most active: <strong>Week ${mostActiveWeek}</strong> (${entriesByWeek[mostActiveWeek].length} entries)</div>`;
      if (decisionJournal.length > 0) highlightsHtml += `<div class="hl-insight">⚖️ <strong>${decisionJournal.length}</strong> decisions tracked${decisionJournal.filter(d => d.rating).length > 0 ? `, ${decisionJournal.filter(d => d.rating).length} reviewed` : ''}</div>`;
      if (longestEntry) highlightsHtml += `<div class="hl-insight">📝 Longest entry: <strong>${(longestEntry.text || '').length} chars</strong> on ${longestEntry.displayDate?.split(',')[0] || 'unknown'}</div>`;
      highlightsHtml += `</div></div>`;

      const html = `<!DOCTYPE html><html><head><title>Journal & Reflections — 52 Week Planner</title><style>
        body{font-family:system-ui,-apple-system,sans-serif;max-width:800px;margin:0 auto;padding:32px;color:#1f2937;line-height:1.6}
        h1{color:#1e40af;border-bottom:2px solid #e5e7eb;padding-bottom:8px;margin-top:24px}
        .subtitle{color:#6b7280;font-size:13px;margin-top:-4px}
        .highlights{background:linear-gradient(135deg,#eff6ff,#f5f3ff,#fdf2f8);border:1px solid #e0e7ff;border-radius:12px;padding:20px;margin:20px 0 28px;page-break-inside:avoid}
        .highlights-title{color:#4338ca;font-size:16px;margin:0 0 12px;border:none;padding:0}
        .hl-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px}
        .hl-card{border-radius:10px;padding:12px;text-align:center}
        .hl-card.blue{background:#dbeafe}.hl-card.green{background:#dcfce7}.hl-card.purple{background:#ede9fe}.hl-card.amber{background:#fef3c7}
        .hl-num{font-size:28px;font-weight:900;color:#1e293b}
        .hl-label{font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;font-weight:600}
        .hl-section h3{font-size:12px;color:#374151;margin:0 0 8px;text-transform:uppercase;letter-spacing:0.5px}
        .hl-bars{display:flex;flex-direction:column;gap:6px}
        .hl-bar-row{display:flex;align-items:center;gap:8px}
        .hl-bar-label{font-size:11px;color:#4b5563;min-width:80px;text-align:right;font-weight:600}
        .hl-bar-track{flex:1;height:16px;background:#f1f5f9;border-radius:8px;overflow:hidden}
        .hl-bar-fill{height:100%;border-radius:8px;transition:width 0.3s}
        .hl-bar-count{font-size:11px;font-weight:800;color:#374151;min-width:24px}
        .hl-insights{display:grid;grid-template-columns:1fr 1fr;gap:6px 16px;margin-top:14px;padding-top:14px;border-top:1px solid #c7d2fe}
        .hl-insight{font-size:11px;color:#4b5563;padding:3px 0}
        .week-block{margin:24px 0;page-break-inside:avoid}
        .week-block h2{color:#1e40af;font-size:16px;margin:16px 0 8px;border-left:4px solid #3b82f6;padding-left:12px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .range{color:#6b7280;font-weight:400;font-size:12px}
        .badge{background:#eff6ff;color:#3b82f6;font-size:10px;padding:2px 8px;border-radius:10px;font-weight:700}
        .day-block{margin:12px 0 16px 16px;padding-left:12px;border-left:2px solid #e5e7eb}
        .day-block h3{font-size:13px;color:#374151;margin:12px 0 6px;text-transform:uppercase;letter-spacing:0.5px}
        .entry{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px 14px;margin:6px 0;page-break-inside:avoid}
        .entry-type{font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px}
        .entry-text{font-size:13px;color:#1f2937;white-space:pre-wrap}
        .schedule-item{font-size:13px;margin:2px 0;display:flex;gap:8px;align-items:center}
        .schedule-item .time{font-family:monospace;color:#3b82f6;font-size:11px;min-width:60px;text-align:right}
        .task-stats{font-size:11px;color:#6b7280;margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb;font-weight:600}
        .gratitude-item{font-size:13px;background:#fff1f2;border:1px solid #fecdd3;border-radius:6px;padding:4px 10px;margin:3px 0;color:#9f1239}
        .skipped-item{font-size:13px;background:#fff7ed;border:1px solid #fed7aa;border-radius:6px;padding:4px 10px;margin:3px 0;color:#9a3412}
        .section-break{border-top:3px solid #e5e7eb;margin:32px 0}
        .stats-row{display:flex;gap:16px;margin:16px 0}
        .stat{background:#f3f4f6;border-radius:10px;padding:12px 20px;text-align:center;flex:1}
        .stat-num{font-size:24px;font-weight:900;color:#4b5563}
        .stat-label{font-size:10px;color:#9ca3af;text-transform:uppercase}
        .stat.amber{background:#fffbeb}.stat.amber .stat-num{color:#d97706}
        .stat.green{background:#f0fdf4}.stat.green .stat-num{color:#16a34a}
        .decision{border:1px solid #e5e7eb;border-radius:10px;padding:14px;margin:10px 0;page-break-inside:avoid}
        .decision.review-due{border-color:#fbbf24;background:#fffbeb}
        .decision.reviewed{border-color:#86efac;background:#f0fdf4}
        .decision-title{font-weight:700;font-size:14px;color:#1f2937;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .decision-date{font-size:10px;color:#9ca3af;margin-top:2px}
        .decision-context{font-size:12px;color:#4b5563;background:#f9fafb;border-radius:6px;padding:8px;margin:8px 0}
        .decision-detail{font-size:11px;color:#6b7280;margin:2px 0}
        .decision-outcome{font-size:12px;color:#16a34a;margin-top:6px;font-weight:600}
        .badge-amber{background:#fef3c7;color:#92400e;font-size:9px;padding:2px 6px;border-radius:8px;font-weight:700}
        .badge-green{background:#dcfce7;color:#166534;font-size:9px;padding:2px 6px;border-radius:8px;font-weight:700}
        .footer{margin-top:32px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #e5e7eb;padding-top:12px}
        @media print{body{padding:16px}.week-block,.entry,.decision{page-break-inside:avoid}}
      </style></head><body>
        <h1>📖 Journal & Reflections</h1>
        <p class="subtitle">${allEntries.length} entries across ${sortedWeeks.length} weeks · Generated ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        ${highlightsHtml}
        ${entriesHtml}
        ${decisionsHtml}
        <div class="footer">Generated by 52 Week Planner · ${new Date().toLocaleDateString()}</div>
      </body></html>`;

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 500);
    };

    const toggleWeek = (wNum) => {
      setExpandedJournalWeeks(prev =>
        prev.includes(wNum)
          ? prev.filter(w => w !== wNum)
          : [...prev, wNum]
      );
    };

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 min-h-[600px]">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BookOpen className="text-blue-500" />
              Journal & Reflections
            </h2>
            <p className="text-gray-500">Your year-long journey, organized by week</p>
          </div>
          <div className="flex items-center gap-3">
            {journalTab === 'entries' && <button
              onClick={() => {
                const today = new Date();
                const dayKey = getDayKey(today.getFullYear(), today.getMonth(), today.getDate());
                setJournalInputDay(dayKey);
                setJournalInputText('');
                const wNum = getWeekNumForDate(today);
                if (!expandedJournalWeeks.includes(wNum)) {
                  setExpandedJournalWeeks(prev => [...prev, wNum]);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors shadow-md text-sm font-bold"
            >
              <Plus size={18} />
              Add Entry
            </button>}
            <button
              onClick={printJournalReport}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-xl hover:from-indigo-600 hover:to-blue-600 transition-all shadow-md text-sm font-bold"
              title="Export journal as PDF"
            >
              <FileText size={16} />
              PDF
            </button>
            <div className="bg-blue-50 text-blue-600 px-4 py-2 rounded-xl text-sm font-bold">
              {allEntries.length} {allEntries.length === 1 ? 'Entry' : 'Entries'}
            </div>
          </div>
        </div>

        {/* Journal Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-100 pb-3">
          {[['entries', '📖 Entries'], ['decisions', '⚖️ Decisions']].map(([key, label]) => (
            <button key={key} onClick={() => setJournalTab(key)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${journalTab === key ? 'bg-blue-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>{label}</button>
          ))}
        </div>

        {/* Decision Journal Tab */}
        {journalTab === 'decisions' && (
          <div className="space-y-4">
            {/* Add Decision */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100 p-4 space-y-3">
              <p className="text-xs font-bold text-purple-600 flex items-center gap-1"><Scale size={14} /> Log a Decision</p>
              <input type="text" defaultValue="" placeholder="Decision title..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" id="dj-title" />
              <textarea defaultValue="" placeholder="Context & reasoning..." rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none" id="dj-context" />
              <div className="grid grid-cols-2 gap-2">
                <input type="text" defaultValue="" placeholder="Options considered..." className="px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-200" id="dj-options" />
                <input type="text" defaultValue="" placeholder="Chosen option..." className="px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-200" id="dj-chosen" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="text" defaultValue="" placeholder="Expected outcome..." className="px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-200" id="dj-expected" />
                <input type="date" defaultValue={new Date(Date.now()+30*24*60*60*1000).toISOString().split('T')[0]} className="px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-200" id="dj-review" />
              </div>
              <button onClick={() => {
                const t = document.getElementById('dj-title')?.value?.trim();
                if (!t) return;
                setDecisionJournal(prev => [{
                  id: Date.now(), title: t,
                  context: document.getElementById('dj-context')?.value?.trim() || '',
                  options: document.getElementById('dj-options')?.value?.trim() || '',
                  chosen: document.getElementById('dj-chosen')?.value?.trim() || '',
                  expectedOutcome: document.getElementById('dj-expected')?.value?.trim() || '',
                  actualOutcome: '', rating: null,
                  date: new Date().toISOString().split('T')[0],
                  reviewDate: document.getElementById('dj-review')?.value || ''
                }, ...prev]);
                ['dj-title','dj-context','dj-options','dj-chosen','dj-expected'].forEach(id => { const el = document.getElementById(id); if (el) el.value = ''; });
              }} className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600">Log Decision</button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100"><div className="text-xl font-black text-purple-600">{decisionJournal.length}</div><div className="text-[10px] text-gray-500">Total</div></div>
              <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100"><div className="text-xl font-black text-amber-600">{decisionJournal.filter(d => !d.actualOutcome && new Date(d.reviewDate) <= new Date()).length}</div><div className="text-[10px] text-gray-500">Needs Review</div></div>
              <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100"><div className="text-xl font-black text-green-600">{decisionJournal.filter(d => d.rating).length}</div><div className="text-[10px] text-gray-500">Reviewed</div></div>
            </div>

            {/* Decisions List */}
            {decisionJournal.map(d => {
              const needsReview = !d.actualOutcome && d.reviewDate && new Date(d.reviewDate) <= new Date();
              return (
                <div key={d.id} className={`bg-white rounded-xl border ${needsReview ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'} p-4 shadow-sm`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800">{d.title}</span>
                        {needsReview && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">Review Due</span>}
                        {d.rating && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">Reviewed</span>}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{new Date(d.date+'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{d.reviewDate && ` · Review by ${new Date(d.reviewDate+'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}</div>
                    </div>
                    <button onClick={() => setDecisionJournal(prev => prev.filter(x => x.id !== d.id))} className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                  </div>
                  {d.context && <p className="text-xs text-gray-600 mt-2 bg-gray-50 rounded-lg p-2">{d.context}</p>}
                  <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
                    {d.chosen && <span><strong>Chose:</strong> {d.chosen}</span>}
                    {d.expectedOutcome && <span><strong>Expected:</strong> {d.expectedOutcome}</span>}
                  </div>
                  {/* Review section */}
                  {needsReview && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                      <p className="text-[10px] font-bold text-amber-600">Time to review this decision!</p>
                      <input type="text" defaultValue="" placeholder="What actually happened?" className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs" id={`dj-actual-${d.id}`} />
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-500">Rate:</span>
                        {[1,2,3,4,5].map(r => (
                          <button key={r} onClick={() => {
                            const actual = document.getElementById(`dj-actual-${d.id}`)?.value?.trim() || '';
                            setDecisionJournal(prev => prev.map(x => x.id === d.id ? { ...x, actualOutcome: actual || 'Reviewed', rating: r } : x));
                          }} className="text-gray-300 hover:text-amber-400"><Star size={14} fill={d.rating >= r ? '#f59e0b' : 'none'} /></button>
                        ))}
                      </div>
                    </div>
                  )}
                  {d.actualOutcome && <div className="mt-2 text-[10px] text-green-600"><strong>Outcome:</strong> {d.actualOutcome} {d.rating && `· ${d.rating}/5 ⭐`}</div>}
                </div>
              );
            })}

            {decisionJournal.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Scale size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No decisions logged</p>
                <p className="text-xs mt-1">Start logging important decisions to track outcomes over time</p>
              </div>
            )}
          </div>
        )}

        {/* Journal Entries Tab */}
        {journalTab === 'entries' && (allEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
              <BookOpen size={40} className="text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-700">Your journal is empty</h3>
            <p className="text-gray-500 max-w-xs mx-auto mt-2">
              Start adding notes in the Day Planner or Weekly views to see them appear here!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedWeeks.map(wNum => {
              const isExpanded = expandedJournalWeeks.includes(wNum);
              const weekEntries = entriesByWeek[wNum];
              const dateRange = getWeekDateRange(wNum);

              // Group entries by date within the week
              const groupedInWeek = weekEntries.reduce((groups, entry) => {
                const date = entry.displayDate;
                if (!groups[date]) groups[date] = [];
                groups[date].push(entry);
                return groups;
              }, {});

              return (
                <div key={wNum} className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => toggleWeek(wNum)}
                    className={`w-full flex items-center justify-between p-4 transition-colors ${isExpanded ? 'bg-blue-50/50' : 'bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
                        }`}>
                        <span className="font-bold text-sm">{wNum}</span>
                      </div>
                      <div className="text-left">
                        <h3 className={`font-bold ${isExpanded ? 'text-blue-700' : 'text-gray-800'}`}>Week {wNum}</h3>
                        <p className="text-xs text-gray-500 font-medium">{dateRange}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-blue-400 bg-blue-50 px-2 py-1 rounded-full">
                        {weekEntries.length} entries
                      </span>
                      {isExpanded ? <ChevronDown size={20} className="text-blue-400" /> : <ChevronRight size={20} className="text-gray-300" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-6 bg-white border-t border-blue-100/50">
                      <div className="space-y-10 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-blue-100">
                        {Object.entries(groupedInWeek).map(([date, entries]) => {
                          const firstDate = entries[0].date;
                          const startOfYear = new Date(2026, 0, 1);
                          const diffTime = Math.abs(firstDate - startOfYear);
                          const dayNum = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const dayKey = getDayKey(firstDate.getFullYear(), firstDate.getMonth(), firstDate.getDate());
                          const isDayExpanded = expandedJournalDays.includes(dayKey);

                          return (
                            <div key={date} className="relative pl-12">
                              <div className="absolute left-0 top-1 w-10 h-10 flex items-center justify-center z-10">
                                <div className={`w-4 h-4 rounded-full ring-4 ring-white shadow-md ${isDayExpanded ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                              </div>
                              <div
                                className="flex items-center justify-between mb-2 cursor-pointer select-none"
                                onClick={() => setExpandedJournalDays(prev => prev.includes(dayKey) ? prev.filter(k => k !== dayKey) : [...prev, dayKey])}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`text-xs font-black px-2 py-0.5 rounded tracking-tighter ${isDayExpanded ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    DAY {dayNum}
                                  </span>
                                  <h3 className={`text-sm font-bold uppercase tracking-widest ${isDayExpanded ? 'text-gray-700' : 'text-gray-400'}`}>{date}</h3>
                                  <span className="text-[10px] text-gray-400">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setJournalInputDay(dayKey);
                                      setJournalInputText('');
                                      setExpandedJournalDays(prev => prev.includes(dayKey) ? prev : [...prev, dayKey]);
                                    }}
                                    className="p-1.5 text-blue-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                                    title="Add to this day"
                                  >
                                    <Plus size={18} />
                                  </button>
                                  {isDayExpanded ? <ChevronDown size={16} className="text-blue-400" /> : <ChevronRight size={16} className="text-gray-300" />}
                                </div>
                              </div>

                              {isDayExpanded && journalInputDay === dayKey && (
                                <div className="mb-6 bg-blue-50/50 p-4 rounded-xl border border-blue-100 animate-in fade-in slide-in-from-top-2">
                                  <textarea
                                    autoFocus
                                    value={journalInputText}
                                    onChange={(e) => setJournalInputText(e.target.value)}
                                    placeholder="What's on your mind? Capture a thought or reflection..."
                                    className="w-full h-24 p-3 bg-white border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm resize-none mb-3"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => setJournalInputDay(null)}
                                      className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 uppercase"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => {
                                        const weekNum = getWeekNumForDate(firstDate);
                                        const timestamp = new Date(firstDate);
                                        timestamp.setHours(12, 0, 0, 0);
                                        addNoteEntry(weekNum, journalInputText, timestamp.toISOString());
                                        setJournalInputDay(null);
                                        setJournalInputText('');
                                      }}
                                      disabled={!journalInputText.trim()}
                                      className="px-4 py-1.5 bg-blue-500 text-white text-xs font-bold rounded-lg hover:bg-blue-600 disabled:opacity-50 uppercase tracking-wide"
                                    >
                                      Save Entry
                                    </button>
                                  </div>
                                </div>
                              )}

                              {isDayExpanded && (
                              <div className="space-y-4">
                                {entries.map(entry => (
                                  <div
                                    key={entry.id}
                                    className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-blue-200 transition-all hover:bg-white hover:shadow-md group"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        {entry.icon}
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-tight">{entry.type}</span>
                                      </div>
                                      {entry.timestamp && (
                                        <span className="text-xs text-gray-400 font-mono">{entry.timestamp}</span>
                                      )}
                                    </div>
                                    {entry.isGratitude ? (
                                      <div className="space-y-1.5">
                                        {entry.gratitudeItems.map((item, i) => (
                                          <div key={i} className="flex items-center gap-2 text-sm bg-rose-50 border border-rose-100 rounded-lg px-3 py-1.5">
                                            <span className="text-[10px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">grateful {i + 1}</span>
                                            <span className="text-gray-700">{item}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : entry.isSkippedList ? (
                                      <div className="space-y-1.5">
                                        {entry.skippedItems.map(([hr, val]) => {
                                          const skippedEntry = normalizeScheduleEntry(val);
                                          const { hour: sh, minute: sm } = parseScheduleKey(hr);
                                          const baseStr = sh === 0 ? '12 AM' : sh < 12 ? `${sh} AM` : sh === 12 ? '12 PM' : `${sh - 12} PM`;
                                          const timeStr = sm ? baseStr.replace(/( AM| PM)/, `:${String(sm).padStart(2,'0')}$1`) : baseStr;
                                          return (
                                            <div key={String(hr)} className="flex items-center gap-2 text-sm bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5">
                                              <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">didn't do</span>
                                              <span className="text-xs font-mono text-orange-400 flex-shrink-0">{timeStr}</span>
                                              <span className="text-gray-700 flex-1">{skippedEntry.text}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : entry.isSchedule ? (
                                      <div>
                                        <div className="space-y-1 mb-2">
                                          {entry.scheduleEvents.map(([hr, evt]) => {
                                            const { hour: evtHour, minute: evtMin } = parseScheduleKey(hr);
                                            const h = evtHour;
                                            const baseStr = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
                                            const timeStr = evtMin ? baseStr.replace(/( AM| PM)/, `:${String(evtMin).padStart(2,'0')}$1`) : baseStr;
                                            return (
                                              <div key={String(hr)} className="flex items-center gap-2 text-sm">
                                                <span className="text-xs font-mono text-blue-500 w-16 text-right flex-shrink-0">{timeStr}</span>
                                                <div className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0"></div>
                                                <span className="text-gray-700">{getScheduleText(evt)}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {entry.taskStats && (
                                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                                            <span className="text-xs font-bold text-gray-500">Tasks:</span>
                                            {entry.taskStats.highTotal > 0 && (
                                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${entry.taskStats.highDone === entry.taskStats.highTotal ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                                ⚡ {entry.taskStats.highDone}/{entry.taskStats.highTotal}
                                              </span>
                                            )}
                                            {entry.taskStats.medTotal > 0 && (
                                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${entry.taskStats.medDone === entry.taskStats.medTotal ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-600'}`}>
                                                🔋 {entry.taskStats.medDone}/{entry.taskStats.medTotal}
                                              </span>
                                            )}
                                            {entry.taskStats.lowTotal > 0 && (
                                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${entry.taskStats.lowDone === entry.taskStats.lowTotal ? 'bg-green-100 text-green-700' : 'bg-emerald-50 text-emerald-600'}`}>
                                                🪫 {entry.taskStats.lowDone}/{entry.taskStats.lowTotal}
                                              </span>
                                            )}
                                            <span className={`text-xs font-bold ml-auto ${entry.taskStats.totalDone === entry.taskStats.totalTasks ? 'text-green-600' : 'text-gray-400'}`}>
                                              {entry.taskStats.totalDone === entry.taskStats.totalTasks ? '✓ All done' : `${entry.taskStats.totalDone}/${entry.taskStats.totalTasks}`}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-gray-800 whitespace-pre-wrap leading-relaxed text-base">
                                        {entry.text}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };


  const SummaryView = () => {
    const weeks = [];

    for (let weekNum = 1; weekNum <= 52; weekNum++) {
      const data = getWeekData(weekNum);
      const status = getWeekStatus(weekNum);
      const hasData = data.goals.length > 0 || data.milestones.length > 0 ||
        data.revenue > 0 || data.expenses > 0 || data.notes.trim() !== '';

      weeks.push({
        number: weekNum,
        data,
        status,
        hasData,
        dateRange: getWeekDateRange(weekNum)
      });
    }

    const completedGoals = (week) => week.data.goals.filter(g => g.completed).length;
    const totalGoals = (week) => week.data.goals.length;
    const completedMilestones = (week) => week.data.milestones.filter(m => m.completed).length;
    const totalMilestones = (week) => week.data.milestones.length;

    // Aggregate stats
    const activeWeeks = weeks.filter(w => w.hasData);
    const totalGoalsAll = weeks.reduce((s, w) => s + totalGoals(w), 0);
    const totalCompletedAll = weeks.reduce((s, w) => s + completedGoals(w), 0);
    const totalMilestonesAll = weeks.reduce((s, w) => s + totalMilestones(w), 0);
    const totalMilestonesCompleted = weeks.reduce((s, w) => s + completedMilestones(w), 0);
    const goalRate = totalGoalsAll > 0 ? Math.round((totalCompletedAll / totalGoalsAll) * 100) : 0;

    // Best week by goals completed
    const bestGoalWeek = activeWeeks.reduce((best, w) => completedGoals(w) > completedGoals(best) ? w : best, activeWeeks[0] || weeks[0]);
    // Current streak (consecutive weeks with data, counting back from current)
    const currentWeekNum = weeks.findIndex(w => w.status === 'current');
    let streak = 0;
    for (let i = currentWeekNum; i >= 0; i--) {
      if (weeks[i].hasData) streak++;
      else break;
    }

    // Sorted descending (newest first), skip empty future weeks
    const sortedWeeks = [...weeks]
      .reverse()
      .filter(week => week.hasData || week.status !== 'future');

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-3xl font-bold mb-1 text-gray-800">Year in Review — 2026</h2>
          <p className="text-gray-400 text-sm">Your progress at a glance, newest weeks first</p>
        </div>

        {/* Top-level Insights Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
            <div className="text-3xl font-black text-blue-700">{activeWeeks.length}<span className="text-lg font-medium text-blue-400">/52</span></div>
            <div className="text-xs font-semibold text-blue-600 mt-1">Active Weeks</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200 text-center">
            <div className="text-3xl font-black text-green-700">{totalCompletedAll}<span className="text-lg font-medium text-green-400">/{totalGoalsAll}</span></div>
            <div className="text-xs font-semibold text-green-600 mt-1">Goals Completed</div>
          </div>
        </div>

        {/* Insights Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Goal Completion Rate */}
          <div className="bg-white rounded-xl shadow p-5 border">
            <div className="flex items-center gap-2 mb-3">
              <Target size={20} className="text-blue-500" />
              <span className="font-bold text-gray-800">Goal Completion Rate</span>
            </div>
            <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all" style={{ width: `${goalRate}%` }} />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{totalCompletedAll} done</span>
              <span className="font-bold text-blue-500">{goalRate}%</span>
            </div>
          </div>

          {/* Streak & Consistency */}
          <div className="bg-white rounded-xl shadow p-5 border">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={20} className="text-amber-500" />
              <span className="font-bold text-gray-800">Consistency</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-black text-amber-600">{streak}</span>
              <span className="text-sm text-gray-500">week streak</span>
            </div>
            <p className="text-xs text-gray-500">
              {streak >= 4 ? 'Incredible momentum — keep it going!' : streak >= 2 ? 'Building a nice rhythm!' : streak === 1 ? 'Great start, keep the streak alive!' : 'Start tracking to build your streak'}
            </p>
          </div>

          {/* Milestones */}
          <div className="bg-white rounded-xl shadow p-5 border">
            <div className="flex items-center gap-2 mb-3">
              <Target size={20} className="text-purple-500" />
              <span className="font-bold text-gray-800">Milestones</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-black text-purple-600">{totalMilestonesCompleted}</span>
              <span className="text-sm text-gray-500">of {totalMilestonesAll} achieved</span>
            </div>
            <p className="text-xs text-gray-500">
              {totalMilestonesAll === 0 ? 'Set milestones to track big wins' : totalMilestonesCompleted === totalMilestonesAll ? 'All milestones crushed!' : `${totalMilestonesAll - totalMilestonesCompleted} remaining — you've got this!`}
            </p>
          </div>
        </div>

        {/* Highlights */}
        {activeWeeks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bestGoalWeek && completedGoals(bestGoalWeek) > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 p-4 flex items-center gap-4">
                <div className="text-3xl">🏆</div>
                <div>
                  <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Most Productive Week</div>
                  <div className="font-bold text-gray-800">Week {bestGoalWeek.number} — {completedGoals(bestGoalWeek)} goals completed</div>
                  <div className="text-xs text-gray-500">{bestGoalWeek.dateRange}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Did vs Didn't Do — Year Trend */}
        {(() => {
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const monthStats = Array.from({ length: 12 }, (_, mIdx) => {
            const daysInMonth = new Date(2026, mIdx + 1, 0).getDate();
            let planned = 0, done = 0, skipped = 0;
            for (let d = 1; d <= daysInMonth; d++) {
              const sched = getDaySchedule(2026, mIdx, d);
              // eslint-disable-next-line no-loop-func
              Object.values(sched).forEach(val => {
                const entry = normalizeScheduleEntry(val);
                if (entry && entry.text && entry.text.trim()) {
                  planned++;
                  if (entry.status === 'done') done++;
                  else if (entry.status === 'skipped') skipped++;
                }
              });
            }
            const attempted = done + skipped;
            const ratio = attempted > 0 ? Math.round((done / attempted) * 100) : null;
            return { month: mIdx, planned, done, skipped, attempted, ratio };
          });

          const activeMonths = monthStats.filter(m => m.attempted > 0);
          if (activeMonths.length === 0) return null;

          // Trend: compare first half vs second half of active months
          const firstHalf = activeMonths.slice(0, Math.ceil(activeMonths.length / 2));
          const secondHalf = activeMonths.slice(Math.ceil(activeMonths.length / 2));
          const avgFirst = firstHalf.length > 0 ? Math.round(firstHalf.reduce((s, m) => s + m.ratio, 0) / firstHalf.length) : 0;
          const avgSecond = secondHalf.length > 0 ? Math.round(secondHalf.reduce((s, m) => s + m.ratio, 0) / secondHalf.length) : 0;
          const trendUp = secondHalf.length > 0 && avgSecond > avgFirst;
          const trendFlat = secondHalf.length > 0 && Math.abs(avgSecond - avgFirst) <= 5;
          const bestMonth = activeMonths.reduce((b, m) => m.ratio > b.ratio ? m : b, activeMonths[0]);
          const worstMonth = activeMonths.reduce((b, m) => m.ratio < b.ratio ? m : b, activeMonths[0]);

          return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <SkipForward size={20} className="text-orange-500" />
                  <h3 className="font-bold text-gray-800 text-lg">Did vs Didn't Do — Year Trend</h3>
                </div>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${trendFlat ? 'bg-gray-100 text-gray-500' : trendUp ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  {trendFlat ? '→ Steady' : trendUp ? '↑ Improving' : '↓ Declining'}
                </span>
              </div>

              {/* Month bars */}
              <div className="grid grid-cols-12 gap-1 items-end mb-2" style={{ height: 100 }}>
                {monthStats.map(m => {
                  const now = new Date();
                  const isFuture = m.month > now.getMonth();
                  const barH = m.ratio !== null ? Math.max(8, m.ratio) : 0;
                  const barColor = m.ratio === null ? 'bg-gray-100' : m.ratio >= 75 ? 'bg-green-400' : m.ratio >= 50 ? 'bg-amber-400' : 'bg-orange-400';
                  return (
                    <div key={m.month} className="flex flex-col items-center gap-1" style={{ height: 100 }}>
                      <div className="flex-1 w-full flex items-end">
                        <div
                          className={`w-full rounded-t transition-all ${isFuture ? 'bg-gray-100' : barColor}`}
                          style={{ height: isFuture ? '8px' : `${barH}%` }}
                          title={m.ratio !== null ? `${monthNames[m.month]}: ${m.ratio}% (${m.done} done, ${m.skipped} skipped)` : `${monthNames[m.month]}: no data`}
                        />
                      </div>
                      <span className="text-[9px] text-gray-400 font-medium">{monthNames[m.month]}</span>
                      {m.ratio !== null && (
                        <span className="text-[9px] font-bold text-gray-500">{m.ratio}%</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-[10px] text-gray-400 mt-1 mb-4">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-400 inline-block" /> 75–100% done</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> 50–74%</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400 inline-block" /> &lt;50%</span>
              </div>

              {/* Best / Worst callouts */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-100 rounded-xl p-3">
                  <div className="text-[10px] font-bold text-green-600 uppercase tracking-wide mb-0.5">Best Month</div>
                  <div className="font-bold text-gray-800">{monthNames[bestMonth.month]} — {bestMonth.ratio}%</div>
                  <div className="text-[10px] text-gray-400">{bestMonth.done} done · {bestMonth.skipped} skipped</div>
                </div>
                {activeMonths.length > 1 && (
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                    <div className="text-[10px] font-bold text-orange-600 uppercase tracking-wide mb-0.5">Needs Work</div>
                    <div className="font-bold text-gray-800">{monthNames[worstMonth.month]} — {worstMonth.ratio}%</div>
                    <div className="text-[10px] text-gray-400">{worstMonth.done} done · {worstMonth.skipped} skipped</div>
                  </div>
                )}
              </div>

              {/* Year motivation */}
              <p className={`text-sm font-medium mt-4 ${trendFlat ? 'text-gray-500' : trendUp ? 'text-green-600' : 'text-orange-600'}`}>
                {trendFlat
                  ? '→ Steady execution — great consistency across the year.'
                  : trendUp
                  ? `↑ You're following through better now than when you started — keep that momentum!`
                  : `↓ Completion rate has dipped. Review what's getting in the way and recalibrate.`}
              </p>
            </div>
          );
        })()}

        {/* Habit Consistency Insights */}
        {habits.length > 0 && (() => {
          const today = new Date();
          const last30 = [];
          for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            last30.push(d);
          }
          // Per-habit stats
          const habitStats = habits.map(habit => {
            const streak = getHabitStreak(habit.id);
            const rate30 = getHabitCompletionRate(habit.id, 30);
            const rate7 = getHabitCompletionRate(habit.id, 7);
            const daily = last30.map(d => isHabitCompletedForDay(habit.id, d.getFullYear(), d.getMonth(), d.getDate()));
            return { ...habit, streak, rate30, rate7, daily };
          });

          // Overall daily completion (how many habits done each day)
          const dailyOverall = last30.map((d, i) => {
            const done = habits.filter(h => isHabitCompletedForDay(h.id, d.getFullYear(), d.getMonth(), d.getDate())).length;
            return { date: d, done, total: habits.length, pct: habits.length > 0 ? Math.round((done / habits.length) * 100) : 0 };
          });

          // Longest streak
          const longestStreak = habitStats.reduce((a, b) => a.streak > b.streak ? a : b, habitStats[0]);
          // Best habit (highest 30-day rate)
          const bestHabit = habitStats.reduce((a, b) => a.rate30 > b.rate30 ? a : b, habitStats[0]);
          // Worst habit (lowest 30-day rate, but > 0 tracked days)
          const needsAttention = habitStats.reduce((a, b) => a.rate30 < b.rate30 ? a : b, habitStats[0]);
          // Overall avg completion
          const overallAvg = habits.length > 0 ? Math.round(habitStats.reduce((s, h) => s + h.rate30, 0) / habits.length) : 0;

          return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              {/* Header + inline metrics */}
              <div className="flex items-center gap-2 mb-3">
                <Check size={18} className="text-blue-500" />
                <h3 className="text-sm font-bold text-gray-800">Habit Insights</h3>
                <div className="ml-auto flex items-center gap-4 text-[11px]">
                  <span className="text-gray-500">Avg <span className="font-black text-blue-500">{overallAvg}%</span></span>
                  <span className="text-gray-500">Streak <span className="font-black text-amber-600">{longestStreak.streak}d</span></span>
                  <span className="text-gray-500">Check-ins <span className="font-black text-purple-600">{habitStats.reduce((s, h) => s + h.daily.filter(Boolean).length, 0)}</span></span>
                </div>
              </div>

              {/* Compact bar chart (last 30 days) */}
              <div className="mb-3">
                <div className="flex items-end gap-[2px] h-10">
                  {dailyOverall.map((day, i) => (
                    <div key={i} className="flex-1 group relative">
                      <div className="w-full rounded-t-sm" style={{
                        height: `${Math.max(day.pct * 0.38, 1)}px`,
                        backgroundColor: day.pct === 100 ? '#22c55e' : day.pct >= 70 ? '#3B82F6' : day.pct >= 40 ? '#f59e0b' : day.pct > 0 ? '#f87171' : '#e5e7eb'
                      }} />
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[8px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {day.done}/{day.total}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[8px] text-gray-400 mt-0.5">
                  <span>30d ago</span>
                  <span>Today</span>
                </div>
              </div>

              {/* Compact per-habit rows */}
              <div className="space-y-1.5 mb-3">
                {habitStats.map(habit => {
                  const color = habit.color || '#3B82F6';
                  const doneCount = habit.daily.filter(Boolean).length;
                  const missedCount = 30 - doneCount;
                  return (
                    <div key={habit.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                      {/* Name + color dot */}
                      <div className="flex items-center gap-1.5 w-32 shrink-0">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-xs font-semibold text-gray-800 truncate">{habit.name}</span>
                      </div>

                      {/* Mini 30-day bars — colored for done, soft red for missed */}
                      <div className="flex gap-[2px] flex-1">
                        {habit.daily.map((done, i) => (
                          <div key={i} className="w-[3.5px] h-3.5 rounded-[1px]" style={{
                            backgroundColor: done ? '#22c55e' : '#fecaca'
                          }} />
                        ))}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
                        {habit.streak > 0 && (
                          <span className="font-bold text-amber-600">{habit.streak}d</span>
                        )}
                        <span className="font-semibold text-green-700">{doneCount}</span>
                        <span className="text-gray-300">/</span>
                        <span className="font-semibold text-red-400">{missedCount}</span>
                        <span className={`font-bold px-1.5 py-0.5 rounded-full ${
                          habit.rate30 >= 80 ? 'bg-green-100 text-green-700' :
                          habit.rate30 >= 50 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>{habit.rate30}%</span>
                        {habit.rate7 > habit.rate30 ? (
                          <span className="text-green-600 font-bold">↑</span>
                        ) : habit.rate7 < habit.rate30 ? (
                          <span className="text-red-500 font-bold">↓</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Best & Needs Attention callouts — compact row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {bestHabit && bestHabit.rate30 > 0 && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                    <span className="text-lg">🏆</span>
                    <div className="min-w-0">
                      <div className="text-[9px] font-bold text-green-600 uppercase">Best Streak</div>
                      <div className="text-xs font-bold text-gray-800 truncate">{bestHabit.name} — {bestHabit.rate30}% · {bestHabit.streak}d streak</div>
                    </div>
                  </div>
                )}
                {needsAttention && needsAttention.rate30 < 80 && habits.length > 1 && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-200">
                    <span className="text-lg">⚠️</span>
                    <div className="min-w-0">
                      <div className="text-[9px] font-bold text-red-600 uppercase">Needs Attention</div>
                      <div className="text-xs font-bold text-gray-800 truncate">{needsAttention.name} — {needsAttention.rate30}% completion</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Week by Week — Descending */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Week by Week Breakdown</h3>
          <div className="space-y-3">
            {sortedWeeks.map((week) => {
              const cGoals = completedGoals(week);
              const tGoals = totalGoals(week);
              const tMilestones = totalMilestones(week);
              const goalPct = tGoals > 0 ? Math.round((cGoals / tGoals) * 100) : 0;

              let ringColor = 'border-gray-200';
              let statusLabel = '';
              let statusClass = '';
              if (week.status === 'current') {
                ringColor = 'border-green-400 ring-2 ring-green-200';
                statusLabel = 'This Week';
                statusClass = 'bg-green-500 text-white';
              } else if (week.status === 'past') {
                ringColor = week.hasData ? 'border-blue-200' : 'border-gray-200';
              } else {
                ringColor = 'border-orange-200';
                statusLabel = 'Upcoming';
                statusClass = 'bg-orange-100 text-orange-700';
              }

              return (
                <div
                  key={week.number}
                  className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${ringColor} ${!week.hasData ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black ${
                        week.status === 'current' ? 'bg-green-500 text-white' :
                        week.hasData ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {week.number}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800">Week {week.number}</span>
                          {statusLabel && (
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${statusClass}`}>{statusLabel}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{week.dateRange}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setCurrentWeek(week.number); setViewMode('week'); }}
                      className="px-3 py-1.5 bg-blue-500 text-white text-xs font-semibold rounded-lg hover:bg-blue-500 transition-colors"
                    >
                      Open
                    </button>
                  </div>

                  {week.hasData ? (
                    <div className="mt-3 space-y-3">
                      {/* Goal progress bar */}
                      {tGoals > 0 && (
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-semibold text-gray-600">Goals</span>
                            <span className="text-xs font-bold text-blue-500">{cGoals}/{tGoals} ({goalPct}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${goalPct === 100 ? 'bg-green-500' : goalPct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`} style={{ width: `${goalPct}%` }} />
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {week.data.goals.map(goal => (
                              <span key={goal.id} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full ${goal.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {goal.completed ? '\u2713' : '\u25CB'} {goal.text.length > 25 ? goal.text.substring(0, 25) + '...' : goal.text}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Milestones */}
                      {tMilestones > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-xs font-semibold text-purple-600 mr-1">Milestones:</span>
                          {week.data.milestones.map(m => (
                            <span key={m.id} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full ${m.completed ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                              {m.completed ? '\u2713' : '\u25CB'} {m.text.length > 30 ? m.text.substring(0, 30) + '...' : m.text}
                            </span>
                          ))}
                        </div>
                      )}


                      {/* Notes snippet */}
                      {week.data.notes && week.data.notes.trim() !== '' && (
                        <div className="text-xs text-gray-500 italic border-l-2 border-gray-200 pl-3 line-clamp-2">
                          {week.data.notes}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic mt-1">No entries yet</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // --- Schedule entry utilities (backward-compatible: string or object) ---
  const normalizeScheduleEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') return { text: entry, type: null, energy: null, taskId: null, status: null };
    return entry;
  };

  const getScheduleText = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    return entry.text || '';
  };

  const parseDateFromText = (str, refYear) => {
    const monthMap = { jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11 };
    const s = str.trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ');
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
    const sl = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
    if (sl) { const yr = sl[3] ? (+sl[3] < 100 ? 2000 + +sl[3] : +sl[3]) : refYear; return new Date(yr, +sl[1] - 1, +sl[2]); }
    const words = s.replace(/(\d)(st|nd|rd|th)/g, '$1').split(' ');
    for (let i = 0; i < words.length; i++) {
      const mIdx = monthMap[words[i]];
      if (mIdx !== undefined) {
        const dayStr = words[i + 1] || words[i - 1];
        const day = dayStr ? parseInt(dayStr) : NaN;
        if (!day || day < 1 || day > 31) continue;
        const yrStr = words[i + 2];
        const yr = yrStr && /^\d{4}$/.test(yrStr) ? +yrStr : refYear;
        return new Date(yr, mIdx, day);
      }
    }
    return null;
  };

  const tryParseEvent = (input) => {
    const separators = [' - ', ' – ', ' → ', ' to ', ' till ', ' until ', ' through ', ' thru '];
    const lower = input.toLowerCase();
    let sepIdx = -1, sepLen = 0;
    for (const sep of separators) {
      const idx = lower.indexOf(sep);
      if (idx > 0) { sepIdx = idx; sepLen = sep.length; break; }
    }
    if (sepIdx < 0) return null;
    const leftPart = input.substring(0, sepIdx).trim();
    const rightPart = input.substring(sepIdx + sepLen).trim();
    const pad = (n) => String(n).padStart(2, '0');
    const toISO = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    const refYear = currentYear;

    // Try right part: may be just a date, or "date title"
    const rightWords = rightPart.split(/\s+/);
    let endDate = null;
    let titleFromRight = '';
    // Try parsing 1-3 words from start of right as end date
    for (let take = Math.min(3, rightWords.length); take >= 1; take--) {
      const datePart = rightWords.slice(0, take).join(' ');
      const parsed = parseDateFromText(datePart, refYear);
      if (parsed) {
        endDate = parsed;
        titleFromRight = rightWords.slice(take).join(' ').replace(/^[\s:–\-,]+/, '').trim();
        break;
      }
    }
    if (!endDate) return null;

    // Try left part: may be "title date" or just "date"
    // Loop from take=1 UP so we find the smallest date match (= maximum title words)
    const leftWords = leftPart.split(/\s+/);
    for (let take = 1; take <= Math.min(4, leftWords.length); take++) {
      const datePart = leftWords.slice(leftWords.length - take).join(' ');
      const startDate = parseDateFromText(datePart, refYear);
      if (startDate && endDate >= startDate) {
        const titleFromLeft = leftWords.slice(0, leftWords.length - take).join(' ').replace(/[\s:–\-,]+$/, '').trim();
        const title = titleFromLeft || titleFromRight || 'Event';
        return { title, startDate: toISO(startDate), endDate: toISO(endDate) };
      }
    }
    return null;
  };

  const parseCommandInput = (input) => {
    const s = input.trim();
    const sl = s.toLowerCase();
    if (/^goal[:\s]/i.test(s))
      return { type: 'goal', text: s.replace(/^goal[:\s]/i, '').trim(), preview: 'Add as weekly goal' };
    if (/^(todo|task)[:\s]/i.test(s))
      return { type: 'todo', text: s.replace(/^(todo|task)[:\s]/i, '').trim(), preview: 'Add to To-Do' };
    // Explicit "event:" prefix
    if (/^event[:\s]/i.test(s)) {
      const evText = s.replace(/^event[:\s]/i, '').trim();
      const ev = tryParseEvent(evText);
      if (ev) return { type: 'event', title: ev.title, startDate: ev.startDate, endDate: ev.endDate, text: ev.title, preview: `Event banner: ${ev.startDate} → ${ev.endDate}` };
      return { type: 'event_hint', text: evText, preview: 'Add dates: e.g. "event: vacation mar 8 to apr 3"' };
    }
    // Try auto-detecting event banner (date range)
    const ev = tryParseEvent(s);
    if (ev) return { type: 'event', title: ev.title, startDate: ev.startDate, endDate: ev.endDate, text: ev.title, preview: `Event banner: ${ev.startDate} → ${ev.endDate}` };
    const timeMatch = sl.match(/(\d{1,2})(:\d{2})?\s*(am|pm)/);
    if (timeMatch) {
      let hour = parseInt(timeMatch[1]);
      const minute = timeMatch[2] ? parseInt(timeMatch[2].slice(1)) : 0;
      const ampm = timeMatch[3];
      if (ampm === 'pm' && hour !== 12) hour += 12;
      if (ampm === 'am' && hour === 12) hour = 0;
      const isTomorrow = /tomorrow/i.test(sl);
      const d = new Date(); if (isTomorrow) d.setDate(d.getDate() + 1);
      const textPart = s.replace(/tomorrow/i, '').replace(/(\d{1,2})(:\d{2})?\s*(am|pm)/i, '').trim();
      const h12 = hour % 12 || 12;
      return { type: 'schedule', hour, minute,
        year: d.getFullYear(), month: d.getMonth(), date: d.getDate(),
        text: textPart || s,
        preview: `Schedule ${isTomorrow ? 'tomorrow' : 'today'} at ${h12}:${String(minute).padStart(2,'0')} ${ampm.toUpperCase()}` };
    }
    return { type: 'todo', text: s, preview: 'Add to To-Do list' };
  };

  // --- Schedule key helpers (supports both integer hour keys and "H:MM" sub-hour keys) ---
  const parseScheduleKey = (key) => {
    const str = String(key);
    if (str.includes(':')) {
      const [h, m] = str.split(':').map(Number);
      return { hour: h, minute: m };
    }
    return { hour: parseInt(str), minute: 0 };
  };

  const makeScheduleKey = (h, m) =>
    m ? `${h}:${String(m).padStart(2, '0')}` : h;

  const getDayHealth = (y, m, d) => dailyHealth[getDayKey(y, m, d)] || { water: 0, steps: 0, calories: 0 };
  const updateDayHealth = (y, m, d, field, value) => {
    const key = getDayKey(y, m, d);
    setDailyHealth(prev => ({ ...prev, [key]: { ...(prev[key] || { water: 0, steps: 0, calories: 0 }), [field]: Math.max(0, value) } }));
  };
  const getDayGratitude = (y, m, d) => gratitudeLog[getDayKey(y, m, d)] || ['', '', ''];
  const updateGratitudeEntry = (y, m, d, idx, val) => {
    const key = getDayKey(y, m, d);
    setGratitudeLog(prev => {
      const arr = [...(prev[key] || ['', '', ''])];
      arr[idx] = val;
      return { ...prev, [key]: arr };
    });
  };
  const getGratitudeStreak = () => {
    let streak = 0;
    const today = new Date();
    let cur = new Date(today);
    while (true) {
      const entries = getDayGratitude(cur.getFullYear(), cur.getMonth(), cur.getDate());
      if (entries.some(e => e && e.trim())) { streak++; cur.setDate(cur.getDate() - 1); }
      else break;
    }
    return streak;
  };
  const getSeasonalStyle = () => {
    const m = new Date().getMonth();
    if (m === 11 || m <= 1) return { background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 40%, #f5f0ff 100%)' };
    if (m <= 4) return { background: 'linear-gradient(135deg, #f0fdf4 0%, #f7fde8 40%, #fffbeb 100%)' };
    if (m <= 7) return { background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)' };
    return { background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 40%, #fef2f2 100%)' };
  };
  const printWeekReport = (week) => {
    const data = getWeekData(week);
    const range = getWeekDateRange(week);
    const habitRows = habits.map(h => {
      const streak = getHabitStreak(h.id);
      const rate = getHabitCompletionRate(h.id, 7);
      return `<tr><td style="padding:6px 12px">${h.name}</td><td style="padding:6px 12px;text-align:center">${streak}d streak</td><td style="padding:6px 12px;text-align:center">${rate}%</td></tr>`;
    }).join('');
    const goalRows = (data.goals || []).map(g =>
      `<li style="margin:4px 0;${g.completed ? 'color:#16a34a;text-decoration:line-through' : ''}">${g.text}${g.completed ? ' ✓' : ''}</li>`
    ).join('');
    const milestoneRows = (data.milestones || []).map(m =>
      `<li style="margin:4px 0;${m.completed ? 'color:#16a34a' : ''}">${m.text}${m.date ? ` (${m.date})` : ''}${m.completed ? ' ✓' : ''}</li>`
    ).join('');
    const noteRows = (data.noteEntries || []).slice().reverse().map(n =>
      `<div style="margin:6px 0;padding:8px;background:#f9fafb;border-radius:6px;font-size:13px">${n.text}</div>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>Week ${week} Report — ${range}</title><style>body{font-family:system-ui,sans-serif;max-width:800px;margin:0 auto;padding:32px;color:#1f2937}h1{color:#1e40af;border-bottom:2px solid #e5e7eb;padding-bottom:8px}h2{color:#374151;font-size:16px;margin-top:24px;margin-bottom:8px}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#f3f4f6;padding:8px 12px;text-align:left}td{border-top:1px solid #e5e7eb}.fin{display:flex;gap:32px;margin:8px 0}.fin-item{background:#f0fdf4;padding:12px 20px;border-radius:8px}@media print{body{padding:16px}}</style></head><body><h1>📅 Week ${week} / 52 — ${range}</h1>${data.cover?.title ? `<p style="color:#6366f1;font-weight:600;font-size:18px">${data.cover.emoji || ''} ${data.cover.title}</p>` : ''}<h2>🎯 Goals</h2><ul>${goalRows || '<li style="color:#9ca3af">No goals set</li>'}</ul><h2>🏆 Milestones</h2><ul>${milestoneRows || '<li style="color:#9ca3af">No milestones</li>'}</ul><h2>🔄 Habits This Week</h2><table><tr><th>Habit</th><th>Streak</th><th>7-day Rate</th></tr>${habitRows || '<tr><td colspan="3" style="padding:8px;color:#9ca3af">No habits tracked</td></tr>'}</table><h2>📝 Notes & Reflections</h2>${noteRows || '<p style="color:#9ca3af">No notes this week</p>'}<p style="margin-top:32px;font-size:11px;color:#9ca3af">Generated by 52 Week Planner · ${new Date().toLocaleDateString()}</p></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  // --- Day Schedule helpers ---
  const getDaySchedule = (year, month, day) => {
    const key = getDayKey(year, month, day);
    const explicit = daySchedule[key] || {};
    const merged = {};
    const date = new Date(year, month, day);
    const dow = date.getDay();
    const isWeekday = dow >= 1 && dow <= 5;
    recurringSchedule.forEach(r => {
      let applies = false;
      if (r.repeat === 'daily') applies = true;
      else if (r.repeat === 'weekdays') applies = isWeekday;
      else if (r.repeat === 'weekly') applies = r.dayOfWeek === dow;
      else if (r.repeat === 'biweekly' && r.dayOfWeek === dow) {
        const start = r.startDate ? new Date(r.startDate) : new Date(2026, 0, 5);
        const diffDays = Math.round((date - start) / (1000 * 60 * 60 * 24));
        applies = diffDays >= 0 && Math.round(diffDays / 7) % 2 === 0;
      } else if (r.repeat === 'monthly') {
        applies = date.getDate() === (r.dayOfMonth || 1);
      } else if (r.repeat === 'yearly') {
        applies = date.getMonth() === (r.monthOfYear ?? 0) && date.getDate() === (r.dayOfMonth || 1);
      }
      if (!applies) return;
      const rKey = makeScheduleKey(r.hour, r.minute);
      if (explicit[rKey] === undefined) {
        merged[rKey] = { text: r.text, duration: r.duration || 60, recurringId: r.id, isRecurring: true, status: null };
      }
    });
    return { ...merged, ...explicit };
  };

  const updateScheduleBlock = (year, month, day, hour, value) => {
    const key = getDayKey(year, month, day);
    setDaySchedule(prev => {
      const existing = prev[key] || {};
      const updated = { ...existing };
      const textValue = typeof value === 'string' ? value : (value?.text || '');
      if (!textValue.trim()) {
        delete updated[hour];
      } else {
        updated[hour] = value;
      }
      return { ...prev, [key]: updated };
    });
  };

  const updateScheduleEntryMeta = (year, month, day, hour, metaUpdates) => {
    const key = getDayKey(year, month, day);
    setDaySchedule(prev => {
      const existing = prev[key] || {};
      const currentEntry = existing[hour];
      if (!currentEntry) return prev;
      const normalized = typeof currentEntry === 'string'
        ? { text: currentEntry, type: null, energy: null, taskId: null, status: null }
        : { ...currentEntry };
      const merged = { ...normalized, ...metaUpdates };
      return { ...prev, [key]: { ...existing, [hour]: merged } };
    });
  };

  // --- .docx text extraction (ZIP + DecompressionStream, no dependencies) ---
  const extractTextFromDocx = async (arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer);
    const entries = [];
    let offset = 0;
    while (offset < bytes.length - 30) {
      if (bytes[offset] === 0x50 && bytes[offset+1] === 0x4B && bytes[offset+2] === 0x03 && bytes[offset+3] === 0x04) {
        const view = new DataView(arrayBuffer, offset);
        const compression = view.getUint16(8, true);
        const compressedSize = view.getUint32(18, true);
        const filenameLen = view.getUint16(26, true);
        const extraLen = view.getUint16(28, true);
        const filename = new TextDecoder().decode(bytes.slice(offset + 30, offset + 30 + filenameLen));
        const dataStart = offset + 30 + filenameLen + extraLen;
        entries.push({ filename, compression, dataStart, compressedSize });
        offset = dataStart + compressedSize;
      } else {
        offset++;
      }
    }
    const docEntry = entries.find(e => e.filename === 'word/document.xml');
    if (!docEntry) throw new Error('Could not find document content. Try Paste Text mode instead.');
    const compressedData = bytes.slice(docEntry.dataStart, docEntry.dataStart + docEntry.compressedSize);
    let xmlText;
    if (docEntry.compression === 0) {
      xmlText = new TextDecoder().decode(compressedData);
    } else if (docEntry.compression === 8 && typeof DecompressionStream !== 'undefined') {
      const ds = new DecompressionStream('deflate-raw');
      const writer = ds.writable.getWriter();
      writer.write(compressedData);
      writer.close();
      const reader = ds.readable.getReader();
      const chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      const totalLen = chunks.reduce((a, c) => a + c.length, 0);
      const result = new Uint8Array(totalLen);
      let pos = 0;
      for (const chunk of chunks) { result.set(chunk, pos); pos += chunk.length; }
      xmlText = new TextDecoder().decode(result);
    } else {
      throw new Error('Browser does not support decompression. Try Paste Text mode instead.');
    }
    const textParts = [];
    const regex = /<w:t[^>]*>([^<]*)<\/w:t>/g;
    let match;
    while ((match = regex.exec(xmlText)) !== null) textParts.push(match[1]);
    const text = textParts.join(' ').replace(/\s+/g, ' ').trim();
    if (!text) throw new Error('No text found in document. Try Paste Text mode instead.');
    return text;
  };

  // --- AI Schedule Import helpers ---
  const AI_SCHEDULE_PROMPT = `Extract ALL schedule/timetable entries from this image. Return ONLY a JSON array where each entry has:
- "time": start time in "HH:MM" 24-hour format
- "text": event/task description
- "duration": estimated duration in minutes (default 60)
- "day": day of week if visible ("monday","tuesday",etc.) or null
- "recurring": "daily"|"weekdays"|"weekly"|null if the schedule appears to repeat

Example: [{"time":"09:00","text":"Math class","duration":60,"day":"monday","recurring":"weekly"}]
Return ONLY the JSON array, no markdown, no explanation.`;

  const parseScheduleFromImage = async (base64Data, mimeType) => {
    if (!openaiApiKey) throw new Error('OpenAI API key not set');
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${openaiApiKey}` },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [{
          role: 'user',
          content: [
            { type: 'image_url', image_url: { url: `data:${mimeType};base64,${base64Data}` } },
            { type: 'text', text: AI_SCHEDULE_PROMPT }
          ]
        }],
        max_tokens: 2000,
        temperature: 0.1
      })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    const content = data.choices[0].message.content.trim();
    const jsonStr = content.replace(/^```json?\n?/, '').replace(/\n?```$/, '');
    return JSON.parse(jsonStr);
  };

  const parseScheduleFromText = async (text) => {
    if (!openaiApiKey) throw new Error('OpenAI API key not set');
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${openaiApiKey}` },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'user',
          content: `Extract schedule entries from this text. Return ONLY a JSON array where each entry has:
- "time": start time in "HH:MM" 24-hour format
- "text": event/task description
- "duration": estimated duration in minutes (default 60)
- "day": day of week if mentioned ("monday","tuesday",etc.) or null
- "recurring": "daily"|"weekdays"|"weekly"|null

Text:\n${text}\n\nReturn ONLY the JSON array, no markdown, no explanation.`
        }],
        max_tokens: 2000,
        temperature: 0.1
      })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    const content = data.choices[0].message.content.trim();
    const jsonStr = content.replace(/^```json?\n?/, '').replace(/\n?```$/, '');
    return JSON.parse(jsonStr);
  };

  const applyImportedEntries = (entries, targetYear, targetMonth, targetDay, asRecurring) => {
    const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    entries.forEach(entry => {
      const [h, m] = (entry.time || '09:00').split(':').map(Number);
      if (asRecurring && entry.recurring) {
        const dow = entry.day ? dayMap[entry.day.toLowerCase()] : null;
        const targetDate = new Date(targetYear, targetMonth, targetDay);
        setRecurringSchedule(prev => [...prev, {
          id: Date.now() + Math.random(),
          text: entry.text,
          hour: h, minute: m || 0,
          duration: entry.duration || 60,
          repeat: entry.recurring,
          dayOfWeek: dow ?? targetDate.getDay(),
          dayOfMonth: targetDate.getDate(),
          monthOfYear: targetDate.getMonth(),
          startDate: getDayKey(targetYear, targetMonth, targetDay)
        }]);
      } else {
        let applyYear = targetYear, applyMonth = targetMonth, applyDay = targetDay;
        if (entry.day) {
          const targetDow = dayMap[entry.day.toLowerCase()];
          if (targetDow !== undefined) {
            const base = new Date(targetYear, targetMonth, targetDay);
            const baseDow = base.getDay();
            const diff = targetDow - baseDow;
            const d = new Date(base);
            d.setDate(base.getDate() + diff);
            applyYear = d.getFullYear();
            applyMonth = d.getMonth();
            applyDay = d.getDate();
          }
        }
        const key = makeScheduleKey(h, m || 0);
        updateScheduleBlock(applyYear, applyMonth, applyDay, key, {
          text: entry.text,
          duration: entry.duration || 60,
          status: null, type: null, energy: null
        });
      }
    });
  };

  // --- Day Tasks helpers (energy categorized) ---
  const getDayTasks = (year, month, day) => {
    const key = getDayKey(year, month, day);
    return dayTasks[key] || { high: [], medium: [], low: [] };
  };

  const addDayTask = (year, month, day, energy, text) => {
    if (!text.trim()) return;
    const key = getDayKey(year, month, day);
    setDayTasks(prev => {
      const existing = prev[key] || { high: [], medium: [], low: [] };
      return {
        ...prev,
        [key]: {
          ...existing,
          [energy]: [...(existing[energy] || []), { id: Date.now(), text: text.trim(), completed: false }]
        }
      };
    });
  };

  const toggleDayTask = (year, month, day, energy, taskId) => {
    const key = getDayKey(year, month, day);
    setDayTasks(prev => {
      const existing = prev[key] || { high: [], medium: [], low: [] };
      return {
        ...prev,
        [key]: {
          ...existing,
          [energy]: (existing[energy] || []).map(t =>
            t.id === taskId ? { ...t, completed: !t.completed, skipped: false } : t
          )
        }
      };
    });
  };

  const deleteDayTask = (year, month, day, energy, taskId) => {
    const key = getDayKey(year, month, day);
    setDayTasks(prev => {
      const existing = prev[key] || { high: [], medium: [], low: [] };
      return {
        ...prev,
        [key]: {
          ...existing,
          [energy]: (existing[energy] || []).filter(t => t.id !== taskId)
        }
      };
    });
  };

  // --- Schedule-Task linking helpers ---
  const promoteScheduleToTask = (year, month, day, hour, energy) => {
    const key = getDayKey(year, month, day);
    const schedule = getDaySchedule(year, month, day);
    const entry = normalizeScheduleEntry(schedule[hour]);
    if (!entry) return;
    const taskId = Date.now();
    setDayTasks(prev => {
      const existing = prev[key] || { high: [], medium: [], low: [] };
      return {
        ...prev,
        [key]: {
          ...existing,
          [energy]: [...(existing[energy] || []), { id: taskId, text: entry.text.trim(), completed: false, linkedHour: hour }]
        }
      };
    });
    updateScheduleEntryMeta(year, month, day, hour, { type: 'task', energy, taskId, status: 'pending' });
  };

  const markScheduleAsFocus = (year, month, day, hour) => {
    updateScheduleEntryMeta(year, month, day, hour, { type: 'focus', energy: null, taskId: null, status: 'active' });
  };

  const demoteScheduleEntry = (year, month, day, hour) => {
    const key = getDayKey(year, month, day);
    const schedule = getDaySchedule(year, month, day);
    const entry = normalizeScheduleEntry(schedule[hour]);
    if (!entry) return;
    setDaySchedule(prev => {
      const existing = prev[key] || {};
      return { ...prev, [key]: { ...existing, [hour]: entry.text } };
    });
  };

  const toggleScheduleTaskStatus = (year, month, day, hour) => {
    const schedule = getDaySchedule(year, month, day);
    const entry = normalizeScheduleEntry(schedule[hour]);
    if (!entry) return;
    if (entry.type === 'task' && entry.taskId && entry.energy) {
      const newStatus = entry.status === 'done' ? 'pending' : 'done';
      toggleDayTask(year, month, day, entry.energy, entry.taskId);
      updateScheduleEntryMeta(year, month, day, hour, { status: newStatus });
    } else if (entry.type === 'focus') {
      const newStatus = entry.status === 'done' ? 'active' : 'done';
      updateScheduleEntryMeta(year, month, day, hour, { status: newStatus });
    }
  };

  const skipScheduleTask = (year, month, day, hour) => {
    const key = getDayKey(year, month, day);
    const schedule = getDaySchedule(year, month, day);
    const entry = normalizeScheduleEntry(schedule[hour]);
    if (!entry) return;
    updateScheduleEntryMeta(year, month, day, hour, { status: 'skipped' });
    if (entry.type === 'task' && entry.taskId && entry.energy) {
      setDayTasks(prev => {
        const existing = prev[key] || { high: [], medium: [], low: [] };
        return {
          ...prev,
          [key]: {
            ...existing,
            [entry.energy]: (existing[entry.energy] || []).map(t =>
              t.id === entry.taskId ? { ...t, completed: false, skipped: true } : t
            )
          }
        };
      });
    }
    // Move the task to the To-Do list as "Didn't do"
    if (entry.text && entry.text.trim()) {
      setTodoList(prev => [
        { id: Date.now(), text: entry.text.trim(), completed: false, createdAt: new Date().toISOString(), tag: "didn't do" },
        ...prev
      ]);
    }
  };

  const updateLinkedTaskText = (year, month, day, energy, taskId, newText) => {
    const key = getDayKey(year, month, day);
    setDayTasks(prev => {
      const existing = prev[key] || { high: [], medium: [], low: [] };
      return {
        ...prev,
        [key]: {
          ...existing,
          [energy]: (existing[energy] || []).map(t =>
            t.id === taskId ? { ...t, text: newText.trim() } : t
          )
        }
      };
    });
  };

  // --- Day Planner View (Google Calendar-style) ---
  const DayPlannerView = ({ year, month, day, onBack }) => {
    const [newTaskText, setNewTaskText] = useState('');
    const [newTaskEnergy, setNewTaskEnergy] = useState('high');
    const schedule = getDaySchedule(year, month, day);
    const tasks = getDayTasks(year, month, day);
    const note = getDayNote(year, month, day);
    // Drag-and-drop state — supports both schedule-to-schedule and task-to-schedule
    const [dragSourceHour, setDragSourceHour] = useState(null);
    const [dragOverHour, setDragOverHour] = useState(null);
    const [dragTaskData, setDragTaskData] = useState(null); // { energy, taskId, text }
    const [dragOverEnergy, setDragOverEnergy] = useState(null); // energy category being hovered
    // Resize state for drag-to-extend/shrink duration
    const [resizingHour, setResizingHour] = useState(null);
    const [resizeTargetHour, setResizeTargetHour] = useState(null);
    // State for inline "add entry" within an hour slot
    const [addingEntryHour, setAddingEntryHour] = useState(null);
    const [addingEntryMinute, setAddingEntryMinute] = useState(0);
    const [editingStartTime, setEditingStartTime] = useState(null);
    const [weekPanelCollapsed, setWeekPanelCollapsed] = useState(false);
    // AI Smart Import state
    const [showSmartImport, setShowSmartImport] = useState(false);
    const [importParsing, setImportParsing] = useState(false);
    const [importedEntries, setImportedEntries] = useState([]);
    const [importError, setImportError] = useState('');
    const [importMode, setImportMode] = useState('upload'); // 'upload' | 'text'
    const [importAsRecurring, setImportAsRecurring] = useState(false);
    const [floatingTodoOpen, setFloatingTodoOpen] = useState(false);
    const [todoPanelPos, setTodoPanelPos] = useState({ x: null, y: null });
    const [todoPanelSize, setTodoPanelSize] = useState({ w: 288, h: 320 });
    const todoDragRef = React.useRef(null);
    const todoResizeRef = React.useRef(null);

    const changeEntryStartMinute = (yr, mo, dy, oldKey, newMinute) => {
      const dk = getDayKey(yr, mo, dy);
      const { hour: h } = parseScheduleKey(oldKey);
      const newKey = makeScheduleKey(h, newMinute);
      if (String(oldKey) === String(newKey)) { setEditingStartTime(null); return; }
      setDaySchedule(prev => {
        const dayData = { ...(prev[dk] || {}) };
        const ent = dayData[oldKey];
        if (!ent) return prev;
        delete dayData[oldKey];
        dayData[newKey] = ent;
        return { ...prev, [dk]: dayData };
      });
      setEditingStartTime(null);
    };

    const dayOfWeek = new Date(year, month, day).toLocaleDateString('en-US', { weekday: 'long' });

    // Navigation to prev/next day
    const navigateDay = (offset) => {
      const date = new Date(year, month, day + offset);
      setActiveDayView({
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate()
      });
      window.scrollTo(0, 0);
    };

    // Drag-and-drop handlers for schedule events (schedule-to-schedule)
    const handleDragStart = (e, hour) => {
      setDragSourceHour(hour);
      setDragTaskData(null);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(hour));
      setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
    };

    // Drag-and-drop handlers for task cards (task-to-schedule)
    const handleTaskDragStart = (e, energy, task) => {
      setDragTaskData({ energy, taskId: task.id, text: task.text });
      setDragSourceHour(null);
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', task.text);
      setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
    };

    const handleDragOver = (e, hour) => {
      // Don't allow drops onto continuation rows
      if (spanMap[hour]) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = dragTaskData ? 'copy' : 'move';
      if (dragOverHour !== hour) setDragOverHour(hour);
    };

    const handleDrop = (e, targetHour) => {
      e.preventDefault();
      // Don't allow drops onto continuation rows
      if (spanMap[targetHour]) { setDragSourceHour(null); setDragOverHour(null); setDragTaskData(null); return; }
      // Task card dropped onto schedule
      if (dragTaskData) {
        if (dragTaskData.energy) {
          // Energy task dragged to schedule
          const existingText = getScheduleText(schedule[targetHour]);
          if (existingText) {
            // Slot occupied — append text (no link)
            updateScheduleBlock(year, month, day, targetHour, `${existingText}, ${dragTaskData.text}`);
          } else {
            // Empty slot — create linked entry
            updateScheduleBlock(year, month, day, targetHour, {
              text: dragTaskData.text, type: 'task', energy: dragTaskData.energy,
              taskId: dragTaskData.taskId, status: 'pending'
            });
            const key = getDayKey(year, month, day);
            setDayTasks(prev => {
              const existing = prev[key] || { high: [], medium: [], low: [] };
              return { ...prev, [key]: { ...existing,
                [dragTaskData.energy]: (existing[dragTaskData.energy] || []).map(t =>
                  t.id === dragTaskData.taskId ? { ...t, linkedHour: targetHour } : t
                )
              }};
            });
          }
        } else {
          // Week Context item — plain text
          const existingText = getScheduleText(schedule[targetHour]);
          const newText = existingText ? `${existingText}, ${dragTaskData.text}` : dragTaskData.text;
          updateScheduleBlock(year, month, day, targetHour, newText);
        }
        setDragTaskData(null);
        setDragOverHour(null);
        return;
      }
      // Schedule-to-schedule swap (preserve full entries)
      const sourceHour = dragSourceHour;
      if (sourceHour === null || sourceHour === targetHour) {
        setDragSourceHour(null);
        setDragOverHour(null);
        return;
      }
      const sourceEntry = schedule[sourceHour];
      const targetEntry = schedule[targetHour];
      const key = getDayKey(year, month, day);
      setDaySchedule(prev => {
        const existing = prev[key] || {};
        const updated = { ...existing };
        if (sourceEntry) { updated[targetHour] = sourceEntry; } else { delete updated[targetHour]; }
        if (targetEntry) { updated[sourceHour] = targetEntry; } else { delete updated[sourceHour]; }
        return { ...prev, [key]: updated };
      });
      setDragSourceHour(null);
      setDragOverHour(null);
    };

    const handleDragEnd = (e) => {
      e.target.style.opacity = '1';
      setDragSourceHour(null);
      setDragOverHour(null);
      setDragTaskData(null);
      setDragOverEnergy(null);
    };

    // Drop handler for energy category cards (Week Context items → energy tasks)
    const handleEnergyDragOver = (e, energyKey) => {
      if (!dragTaskData || dragTaskData.energy) return; // only accept Week Context items (energy === null)
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (dragOverEnergy !== energyKey) setDragOverEnergy(energyKey);
    };

    const handleEnergyDrop = (e, energyKey) => {
      e.preventDefault();
      if (dragTaskData && !dragTaskData.energy) {
        // Week Context item dropped onto energy category — create a new task
        addDayTask(year, month, day, energyKey, dragTaskData.text);
        setDragTaskData(null);
        setDragOverEnergy(null);
      }
    };

    // Full 24-hour schedule (12 AM to 11 PM)
    const timeSlots = [];
    for (let h = 0; h <= 23; h++) {
      timeSlots.push(h);
    }

    const formatHour = (h) => {
      if (h === 0) return '12 AM';
      if (h < 12) return `${h} AM`;
      if (h === 12) return '12 PM';
      return `${h - 12} PM`;
    };

    const formatTimeRange = (startHour, startMinute, durationMin) => {
      const sm = startMinute || 0;
      const startTotalMin = startHour * 60 + sm;
      const endTotalMin = startTotalMin + durationMin;
      const startH = startHour % 12 || 12;
      const startAmPm = startHour >= 12 ? 'PM' : 'AM';
      const startMinStr = sm ? `:${String(sm).padStart(2, '0')}` : ':00';
      const endH24 = Math.floor(endTotalMin / 60);
      const endMin = endTotalMin % 60;
      const endH = endH24 % 12 || 12;
      const endAmPm = endH24 >= 12 ? 'PM' : 'AM';
      if (startAmPm === endAmPm) {
        return `${startH}${startMinStr} - ${endH}:${String(endMin).padStart(2, '0')} ${endAmPm}`;
      }
      return `${startH}${startMinStr} ${startAmPm} - ${endH}:${String(endMin).padStart(2, '0')} ${endAmPm}`;
    };

    const durationPresets = [
      { label: '5m', minutes: 5 },
      { label: '10m', minutes: 10 },
      { label: '15m', minutes: 15 },
      { label: '20m', minutes: 20 },
      { label: '30m', minutes: 30 },
      { label: '45m', minutes: 45 },
      { label: '1h', minutes: 60 },
      { label: '1.5h', minutes: 90 },
      { label: '2h', minutes: 120 },
      { label: '3h', minutes: 180 },
      { label: '4h', minutes: 240 },
      { label: '5h', minutes: 300 },
      { label: '6h', minutes: 360 },
      { label: '8h', minutes: 480 },
    ];

    // Compute the next available start minute within an hour (for "+" add button)
    const computeNextStartMinute = (hour) => {
      const hourEntries = Object.entries(schedule)
        .filter(([k]) => parseScheduleKey(k).hour === hour)
        .sort((a, b) => parseScheduleKey(a[0]).minute - parseScheduleKey(b[0]).minute);
      if (hourEntries.length === 0) return 0;
      const last = hourEntries[hourEntries.length - 1];
      const { minute: lastMin } = parseScheduleKey(last[0]);
      const lastDur = normalizeScheduleEntry(last[1])?.duration || 60;
      return lastMin + lastDur;
    };

    // Compute which hours are covered by multi-hour entries
    const spanMap = {};
    Object.entries(schedule).forEach(([hr, val]) => {
      if (String(hr).includes(':')) return; // sub-hour entries don't span whole hours
      const entry = normalizeScheduleEntry(val);
      const dur = entry?.duration || 60;
      if (dur > 60) {
        const startHour = parseInt(hr);
        const hoursSpanned = Math.ceil(dur / 60);
        for (let i = 1; i < hoursSpanned; i++) {
          const coveredHour = startHour + i;
          if (coveredHour <= 23 && !schedule[coveredHour]) {
            spanMap[coveredHour] = { parentHour: startHour, parentEntry: entry };
          }
        }
      }
    });

    // Resize handlers (mouse events for drag-to-extend duration)
    const scheduleRef = React.useRef(null);
    const resizeTargetRef = React.useRef(null);
    const resizeSourceRef = React.useRef(null);

    const handleResizeStart = (e, schedKey) => {
      e.preventDefault();
      e.stopPropagation();
      const { hour: srcHour } = parseScheduleKey(schedKey);
      setResizingHour(srcHour);
      setResizeTargetHour(srcHour);
      resizeTargetRef.current = srcHour;
      resizeSourceRef.current = srcHour;

      const onMouseMove = (ev) => {
        if (!scheduleRef.current) return;
        const rows = scheduleRef.current.querySelectorAll('[data-hour]');
        for (const row of rows) {
          const rect = row.getBoundingClientRect();
          if (ev.clientY >= rect.top && ev.clientY <= rect.bottom) {
            const rowHour = parseInt(row.getAttribute('data-hour'));
            resizeTargetRef.current = rowHour;
            setResizeTargetHour(rowHour);
            break;
          }
        }
      };

      const onMouseUp = (ev) => {
        window.removeEventListener('mousemove', onMouseMove);
        const src = resizeSourceRef.current;
        const tgt = resizeTargetRef.current;

        if (src !== null && tgt !== null) {
          let newDuration;
          if (tgt > src) {
            // Extended downward — whole-hour granularity
            newDuration = (tgt - src + 1) * 60;
          } else {
            // Same row or above — minute-level precision from Y position within source row
            let srcRowRect = null;
            if (scheduleRef.current) {
              const rows = scheduleRef.current.querySelectorAll('[data-hour]');
              for (const row of rows) {
                if (parseInt(row.getAttribute('data-hour')) === src) {
                  srcRowRect = row.getBoundingClientRect();
                  break;
                }
              }
            }
            if (srcRowRect) {
              const fraction = Math.max(0, Math.min(1, (ev.clientY - srcRowRect.top) / srcRowRect.height));
              const rawMin = Math.round(fraction * 60 / 15) * 15;
              newDuration = Math.max(15, rawMin);
            } else {
              newDuration = 15;
            }
          }
          updateScheduleEntryMeta(year, month, day, schedKey, { duration: newDuration });
        }

        setResizingHour(null);
        setResizeTargetHour(null);
        resizeSourceRef.current = null;
        resizeTargetRef.current = null;
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp, { once: true });
    };

    const energyCategories = [
      {
        key: 'high',
        label: 'High Energy',
        icon: <Zap size={18} />,
        iconSm: <Zap size={14} />,
        color: 'red',
        gradient: 'from-red-500 to-orange-500',
        bg: 'bg-red-50',
        border: 'border-red-200',
        badge: 'bg-red-100 text-red-700',
        borderLeft: 'border-l-red-500',
        desc: 'Deep work, creative tasks, important decisions'
      },
      {
        key: 'medium',
        label: 'Medium Energy',
        icon: <Battery size={18} />,
        iconSm: <Battery size={14} />,
        color: 'amber',
        gradient: 'from-amber-500 to-yellow-500',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        badge: 'bg-amber-100 text-amber-700',
        borderLeft: 'border-l-amber-500',
        desc: 'Meetings, emails, planning, collaboration'
      },
      {
        key: 'low',
        label: 'Low Energy',
        icon: <BatteryLow size={18} />,
        iconSm: <BatteryLow size={14} />,
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bg: 'bg-green-50',
        border: 'border-green-200',
        badge: 'bg-green-100 text-green-700',
        borderLeft: 'border-l-green-500',
        desc: 'Admin, routine tasks, simple follow-ups'
      }
    ];

    const totalTasks = (tasks.high?.length || 0) + (tasks.medium?.length || 0) + (tasks.low?.length || 0);
    const completedTasks = (tasks.high?.filter(t => t.completed).length || 0) +
      (tasks.medium?.filter(t => t.completed).length || 0) +
      (tasks.low?.filter(t => t.completed).length || 0);
    const skippedTasks = (tasks.high?.filter(t => t.skipped).length || 0) +
      (tasks.medium?.filter(t => t.skipped).length || 0) +
      (tasks.low?.filter(t => t.skipped).length || 0);
    const scheduledHours = Object.keys(schedule).length;
    const focusHours = Object.values(schedule).filter(v => typeof v === 'object' && v.type === 'focus').length;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                title="Back to Calendar"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onClick={() => navigateDay(-1)}
                className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                title="Previous Day"
              >
                <ChevronLeft size={24} />
              </button>
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-800">
                  {monthNames[month]} {day}, {year}
                </h1>
                <p className="text-gray-400 text-lg">{dayOfWeek}</p>
              </div>
              <button
                onClick={() => navigateDay(1)}
                className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                title="Next Day"
              >
                <ChevronRight size={24} />
              </button>
            </div>
            <div className="flex gap-3 text-sm flex-wrap">
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold text-blue-600">{scheduledHours}</div>
                <div className="text-gray-500">Scheduled</div>
              </div>
              {focusHours > 0 && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 text-center">
                  <div className="text-2xl font-bold text-indigo-600">{focusHours}</div>
                  <div className="text-gray-500">Focus</div>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold text-blue-600">{completedTasks}/{totalTasks}</div>
                <div className="text-gray-500">Done</div>
              </div>
              {skippedTasks > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-center">
                  <div className="text-2xl font-bold text-gray-500">{skippedTasks}</div>
                  <div className="text-gray-500">Skipped</div>
                </div>
              )}
              <button
                onClick={() => setShowSmartImport(!showSmartImport)}
                className={`rounded-lg px-4 py-2 text-center transition-colors border ${showSmartImport ? 'bg-purple-500 text-white border-purple-500' : 'bg-purple-50 border-purple-100 hover:bg-purple-100'}`}
              >
                <div className={`text-lg font-bold ${showSmartImport ? 'text-white' : 'text-purple-600'}`}><Sparkles size={18} className="inline" /></div>
                <div className={`text-xs ${showSmartImport ? 'text-purple-100' : 'text-gray-500'}`}>AI Import</div>
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {totalTasks > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Task Progress</span>
                <span>{Math.round((completedTasks / totalTasks) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${(completedTasks / totalTasks) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* AI Smart Import Panel */}
        {showSmartImport && (
          <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Sparkles size={18} className="text-purple-500" />
                AI Schedule Import
              </h3>
              <button onClick={() => { setShowSmartImport(false); setImportedEntries([]); setImportError(''); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
            </div>

            {/* API Key Section */}
            {!openaiApiKey && (
              <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
                  <AlertCircle size={12} /> OpenAI API Key Required
                </p>
                <div className="flex gap-2">
                  <input id="ai-api-key-input" type="password" placeholder="sk-..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 font-mono" />
                  <button onClick={() => {
                    const k = document.getElementById('ai-api-key-input')?.value?.trim();
                    if (k) { setOpenaiApiKey(k); localStorage.setItem('openai-api-key', k); }
                  }} className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-bold hover:bg-purple-600">Save</button>
                </div>
                <p className="text-[10px] text-amber-600 mt-1.5">Stored locally in your browser only. Never sent to our servers.</p>
              </div>
            )}

            {openaiApiKey && (
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[10px] text-green-600 font-medium flex items-center gap-1">
                  <Check size={10} /> API key configured
                </span>
                <button onClick={() => { setOpenaiApiKey(''); localStorage.removeItem('openai-api-key'); }}
                  className="text-[10px] text-red-500 hover:text-red-700">Remove key</button>
              </div>
            )}

            {/* Mode Toggle: Upload / Text */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 flex-1">
                <button onClick={() => setImportMode('upload')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${importMode === 'upload' ? 'bg-white shadow text-purple-600' : 'text-gray-500'}`}>
                  <Upload size={13} /> Upload Image/PDF
                </button>
                <button onClick={() => setImportMode('text')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${importMode === 'text' ? 'bg-white shadow text-purple-600' : 'text-gray-500'}`}>
                  <FileText size={13} /> Paste Text
                </button>
              </div>
            </div>

            {/* Upload Mode */}
            {importMode === 'upload' && !importParsing && importedEntries.length === 0 && (() => {
              const handleFile = async (file) => {
                if (!file || !openaiApiKey) return;
                setImportParsing(true);
                setImportError('');
                setImportedEntries([]);
                try {
                  const isDocx = file.name?.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                  const isDoc = file.name?.endsWith('.doc') || file.type === 'application/msword';
                  if (isDocx) {
                    const arrayBuf = await file.arrayBuffer();
                    const text = await extractTextFromDocx(arrayBuf);
                    const entries = await parseScheduleFromText(text);
                    setImportedEntries(entries.map((en, i) => ({ ...en, selected: true, id: Date.now() + i })));
                  } else if (isDoc) {
                    setImportError('Legacy .doc format not supported. Please save as .docx or use Paste Text mode.');
                  } else {
                    const reader = new FileReader();
                    const dataUrl = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
                    const base64Data = dataUrl.split(',')[1];
                    const mimeType = file.type || 'image/png';
                    const entries = await parseScheduleFromImage(base64Data, mimeType);
                    setImportedEntries(entries.map((en, i) => ({ ...en, selected: true, id: Date.now() + i })));
                  }
                } catch (err) {
                  setImportError(err.message || 'Failed to parse schedule');
                } finally {
                  setImportParsing(false);
                }
              };

              return (
                <div
                  className="border-2 border-dashed border-purple-200 rounded-xl p-6 text-center hover:border-purple-400 transition-colors cursor-pointer"
                  tabIndex={0}
                  onPaste={async (e) => {
                    const items = e.clipboardData?.items;
                    if (!items) return;
                    for (const item of items) {
                      if (item.type.startsWith('image/')) {
                        e.preventDefault();
                        const file = item.getAsFile();
                        if (file) await handleFile(file);
                        return;
                      }
                    }
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-500', 'bg-purple-50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50'); }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                    const file = e.dataTransfer?.files?.[0];
                    if (file) await handleFile(file);
                  }}
                >
                  <Upload size={32} className="mx-auto text-purple-300 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Drop, paste, or choose a file</p>
                  <p className="text-[10px] text-gray-400 mb-3">Images, PDFs, Word docs (.docx) — or <strong>Cmd+V</strong> to paste screenshot</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-bold hover:bg-purple-600 cursor-pointer transition-colors">
                    <Upload size={14} /> Choose File
                    <input type="file" accept="image/*,.pdf,.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await handleFile(file);
                      e.target.value = '';
                    }} />
                  </label>
                </div>
              );
            })()}

            {/* Text Mode */}
            {importMode === 'text' && !importParsing && importedEntries.length === 0 && (
              <div>
                <textarea id="ai-text-input" rows={5}
                  placeholder={"Paste your schedule text here, e.g.:\n9:00 AM - Stand-up meeting (30 min)\n10:00 AM - Deep work: Project Alpha\n12:00 PM - Lunch break\n1:30 PM - Client call\n3:00 PM - Code review"}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none" />
                <button onClick={async () => {
                  const txt = document.getElementById('ai-text-input')?.value?.trim();
                  if (!txt || !openaiApiKey) return;
                  setImportParsing(true);
                  setImportError('');
                  try {
                    const entries = await parseScheduleFromText(txt);
                    setImportedEntries(entries.map((en, i) => ({ ...en, selected: true, id: Date.now() + i })));
                  } catch (err) {
                    setImportError(err.message || 'Failed to parse schedule');
                  } finally {
                    setImportParsing(false);
                  }
                }} className="mt-2 w-full px-4 py-2 bg-purple-500 text-white rounded-xl text-sm font-bold hover:bg-purple-600 transition-colors flex items-center justify-center gap-1.5">
                  <Zap size={14} /> Parse with AI
                </button>
              </div>
            )}

            {/* Loading */}
            {importParsing && (
              <div className="py-8 text-center">
                <div className="animate-spin w-8 h-8 border-3 border-purple-200 border-t-purple-500 rounded-full mx-auto mb-3"></div>
                <p className="text-sm text-gray-600 font-medium">Analyzing your schedule...</p>
                <p className="text-[10px] text-gray-400 mt-1">GPT-4o is extracting time blocks</p>
              </div>
            )}

            {/* Error */}
            {importError && (
              <div className="p-3 bg-red-50 rounded-xl border border-red-200 mb-3">
                <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                  <AlertCircle size={14} /> {importError}
                </p>
                <button onClick={() => { setImportError(''); setImportedEntries([]); }}
                  className="text-xs text-red-500 hover:text-red-700 mt-1">Try again</button>
              </div>
            )}

            {/* Preview Table */}
            {importedEntries.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-gray-600">{importedEntries.filter(e => e.selected).length} of {importedEntries.length} entries selected</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setImportedEntries(prev => prev.map(e => ({ ...e, selected: true })))}
                      className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold">Select All</button>
                    <button onClick={() => setImportedEntries(prev => prev.map(e => ({ ...e, selected: false })))}
                      className="text-[10px] text-gray-500 hover:text-gray-700 font-bold">Deselect All</button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left text-gray-500 font-bold">Time</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-bold">Event</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-bold">Duration</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-bold">Day</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-bold">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importedEntries.map((entry, idx) => {
                        const [h] = (entry.time || '09:00').split(':').map(Number);
                        const ampm = h < 12 ? 'AM' : 'PM';
                        const h12 = h % 12 || 12;
                        return (
                          <tr key={entry.id} className={`border-t border-gray-100 ${entry.selected ? 'bg-white' : 'bg-gray-50 opacity-50'}`}>
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={entry.selected}
                                onChange={() => setImportedEntries(prev => prev.map((e, i) => i === idx ? { ...e, selected: !e.selected } : e))}
                                className="rounded border-gray-300 text-purple-500 focus:ring-purple-500" />
                            </td>
                            <td className="px-3 py-2 font-mono text-indigo-600 font-bold">{h12}:{(entry.time || '09:00').split(':')[1]} {ampm}</td>
                            <td className="px-3 py-2 text-gray-800 font-medium">{entry.text}</td>
                            <td className="px-3 py-2 text-gray-500">{entry.duration || 60}m</td>
                            <td className="px-3 py-2 text-gray-500 capitalize">{entry.day || '—'}</td>
                            <td className="px-3 py-2">
                              {entry.recurring ? (
                                <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold">{entry.recurring}</span>
                              ) : (
                                <span className="text-[9px] text-gray-400">once</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Options & Apply */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={importAsRecurring}
                      onChange={(e) => setImportAsRecurring(e.target.checked)}
                      className="rounded border-gray-300 text-purple-500 focus:ring-purple-500" />
                    Add recurring entries to recurring schedule
                  </label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setImportedEntries([]); setImportError(''); }}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 text-xs font-bold">Cancel</button>
                    <button onClick={() => {
                      const selected = importedEntries.filter(e => e.selected);
                      if (selected.length === 0) return;
                      applyImportedEntries(selected, year, month, day, importAsRecurring);
                      setImportedEntries([]);
                      setShowSmartImport(false);
                    }} className="px-5 py-2 bg-purple-500 text-white rounded-xl text-sm font-bold hover:bg-purple-600 transition-colors flex items-center gap-1.5">
                      <Check size={14} /> Apply {importedEntries.filter(e => e.selected).length} to Schedule
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Task Inbox — full width above the grid */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <Plus size={18} className="text-blue-500" />
              Task Inbox
            </h3>
            <p className="text-xs text-gray-400">Add tasks, assign energy, drag onto schedule</p>
          </div>
          <div className="flex gap-3 items-start">
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTaskText.trim()) {
                    addDayTask(year, month, day, newTaskEnergy, newTaskText);
                    setNewTaskText('');
                  }
                }}
                placeholder="What do you need to do today?"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <div className="flex gap-1">
                {energyCategories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setNewTaskEnergy(cat.key)}
                    title={cat.label}
                    className={`flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${newTaskEnergy === cat.key
                      ? `bg-gradient-to-r ${cat.gradient} text-white shadow-md`
                      : `${cat.bg} ${cat.border} border text-gray-600 hover:scale-105`
                      }`}
                  >
                    {cat.icon}
                    <span className="hidden sm:inline">{cat.label.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => {
                if (newTaskText.trim()) {
                  addDayTask(year, month, day, newTaskEnergy, newTaskText);
                  setNewTaskText('');
                }
              }}
              disabled={!newTaskText.trim()}
              className="px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Add
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Hourly Schedule */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
                  <Clock size={20} className="text-blue-500" />
                  Hourly Schedule
                </h2>
                <p className="text-sm text-gray-400">Click to add events, drag tasks here to schedule them</p>
              </div>

              <div className="divide-y divide-gray-100" ref={scheduleRef}>
                {timeSlots.map(hour => {
                  // Check if this hour is covered by a multi-hour block
                  const spanInfo = spanMap[hour];
                  const isResizeHighlight = resizingHour !== null && hour > resizingHour && hour <= resizeTargetHour;

                  if (spanInfo && !schedule[hour]) {
                    const parentEntry = spanInfo.parentEntry;
                    const pIsTask = parentEntry.type === 'task';
                    const pIsFocus = parentEntry.type === 'focus';
                    const pEnergyBorderMap = { high: 'border-red-500', medium: 'border-amber-500', low: 'border-green-500' };
                    const pBorderColor = pIsFocus ? 'border-indigo-500'
                      : pIsTask && parentEntry.energy ? (pEnergyBorderMap[parentEntry.energy] || 'border-blue-500')
                      : 'border-blue-500';
                    const pBgColor = pIsFocus ? 'bg-indigo-50/50'
                      : pIsTask ? 'bg-blue-50/30'
                      : 'bg-blue-50/30';
                    return (
                      <div key={hour} data-hour={hour} className="flex transition-colors">
                        <div className="w-20 flex-shrink-0 px-3 py-1.5 text-right border-r border-gray-200 text-gray-400 text-sm">
                          {formatHour(hour)}
                        </div>
                        <div className="flex-1 min-h-[28px] px-3 py-0.5">
                          <div className={`h-full ${pBgColor} border-l-4 ${pBorderColor} rounded-r flex items-center px-3 opacity-50`}>
                            <span className="text-[10px] text-gray-400 italic">↑ {getScheduleText(schedule[spanInfo.parentHour])} continues</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const now = new Date();
                  const isCurrentHour = isToday(year, month, day) && now.getHours() === hour;
                  const isPastHour = isToday(year, month, day) && now.getHours() > hour;
                  const isDragTarget = dragOverHour === hour && (dragSourceHour !== null || dragTaskData !== null) && dragSourceHour !== hour;

                  return (
                    <div
                      key={hour}
                      data-hour={hour}
                      onDragOver={(e) => handleDragOver(e, hour)}
                      onDrop={(e) => handleDrop(e, hour)}
                      onDragLeave={() => { if (dragOverHour === hour) setDragOverHour(null); }}
                      className={`flex group transition-colors ${
                        isResizeHighlight
                          ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset'
                          : isDragTarget
                          ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset'
                          : isCurrentHour ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset'
                          : isPastHour ? 'bg-gray-50/50'
                          : 'hover:bg-gray-50'
                      }${dragSourceHour === hour ? ' opacity-40' : ''}`}
                    >
                      {/* Time Label */}
                      <div className={`w-20 flex-shrink-0 px-3 py-3 text-right border-r border-gray-200 ${isCurrentHour ? 'text-blue-600 font-bold' : 'text-gray-500'
                        } text-sm`}>
                        {formatHour(hour)}
                        {isCurrentHour && (
                          <div className="text-xs text-blue-500 font-normal">Now</div>
                        )}
                      </div>

                      {/* Event Block */}
                      <div className="flex-1 min-h-[48px] px-3 py-2">
                        {/* Multi-entry rendering: collect all entries for this hour sorted by start minute */}
                        {(() => {
                          const hourEntries = Object.entries(schedule)
                            .filter(([k]) => parseScheduleKey(k).hour === hour)
                            .sort((a, b) => parseScheduleKey(a[0]).minute - parseScheduleKey(b[0]).minute);
                          const energyBorderMap = { high: 'border-red-500', medium: 'border-amber-500', low: 'border-green-500' };
                          const energyTextMap = { high: 'text-red-700', medium: 'text-amber-700', low: 'text-green-700' };

                          return (
                            <>
                              {isDragTarget && hourEntries.length === 0 && (
                                <div className="flex items-center gap-2 text-blue-400 text-xs py-2 animate-pulse">
                                  <Clock size={14} />
                                  Drop here to schedule at {formatHour(hour)}
                                </div>
                              )}

                              {hourEntries.map(([schedKey, val]) => {
                                const { minute: startMin } = parseScheduleKey(schedKey);
                                const isIntegerKey = !String(schedKey).includes(':');
                                const entry = normalizeScheduleEntry(val);
                                const isTask = entry.type === 'task';
                                const isFocus = entry.type === 'focus';
                                const isDone = entry.status === 'done';
                                const isSkipped = entry.status === 'skipped';
                                const energyCat = isTask ? energyCategories.find(c => c.key === entry.energy) : null;
                                const borderColor = isSkipped ? 'border-gray-400'
                                  : isDone ? 'border-green-500'
                                  : isFocus ? 'border-indigo-500'
                                  : isTask && entry.energy ? (energyBorderMap[entry.energy] || 'border-blue-500')
                                  : 'border-blue-500';
                                const bgColor = isSkipped ? 'bg-gray-100'
                                  : isDone ? 'bg-green-50'
                                  : isFocus ? 'bg-indigo-50'
                                  : isTask && energyCat ? energyCat.bg
                                  : 'bg-blue-100';
                                const dur = entry.duration || 60;
                                const showTimeRange = dur !== 60 || startMin > 0;

                                return (
                                  <div key={String(schedKey)} className="flex items-start gap-2 mb-1">
                                    <div
                                      draggable={isIntegerKey ? 'true' : 'false'}
                                      onDragStart={isIntegerKey ? (e) => handleDragStart(e, hour) : undefined}
                                      onDragEnd={isIntegerKey ? handleDragEnd : undefined}
                                      className={`flex-1 ${bgColor} border-l-4 ${borderColor} rounded-r-lg px-3 py-2 ${isIntegerKey ? 'cursor-grab active:cursor-grabbing' : ''} transition-all ${isDone ? 'opacity-75' : ''} ${isSkipped ? 'opacity-50' : ''}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {(isTask || isFocus) && (
                                          <button
                                            onClick={() => toggleScheduleTaskStatus(year, month, day, schedKey)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            draggable="false"
                                            className={`flex-shrink-0 transition-colors ${isDone ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                                            title={isDone ? 'Mark as incomplete' : 'Mark as done'}
                                          >
                                            {isDone ? <CheckSquare size={16} /> : <Square size={16} />}
                                          </button>
                                        )}
                                        {isTask && energyCat && !isDone && !isSkipped && (
                                          <span className="flex-shrink-0" title={energyCat.label}>
                                            {energyCat.icon}
                                          </span>
                                        )}
                                        {isFocus && !isDone && !isSkipped && (
                                          <span className="flex-shrink-0 text-indigo-500" title="Focus Work">
                                            <Activity size={14} />
                                          </span>
                                        )}
                                        <input
                                          type="text"
                                          defaultValue={entry.text}
                                          key={`${schedKey}-${entry.status}-${entry.type}`}
                                          onBlur={(e) => {
                                            if (e.target.value !== entry.text) {
                                              if (typeof schedule[schedKey] === 'object') {
                                                updateScheduleBlock(year, month, day, schedKey, { ...schedule[schedKey], text: e.target.value });
                                                if (entry.taskId && entry.energy) {
                                                  updateLinkedTaskText(year, month, day, entry.energy, entry.taskId, e.target.value);
                                                }
                                              } else {
                                                updateScheduleBlock(year, month, day, schedKey, e.target.value);
                                              }
                                            }
                                          }}
                                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          draggable="false"
                                          className={`w-full bg-transparent text-sm font-medium focus:outline-none ${
                                            isDone ? 'line-through text-green-700'
                                            : isSkipped ? 'line-through text-gray-400'
                                            : isFocus ? 'text-indigo-700'
                                            : isTask && entry.energy ? (energyTextMap[entry.energy] || 'text-blue-700')
                                            : 'text-blue-700'
                                          }`}
                                        />
                                      </div>
                                      {/* Time range label — clickable to edit start time */}
                                      <div className="mt-0.5">
                                        {editingStartTime === String(schedKey) + '-' + hour ? (
                                          <div className="flex flex-wrap gap-0.5 items-center" onMouseDown={(e) => e.stopPropagation()}>
                                            <Clock size={9} className="text-blue-500 mr-0.5" />
                                            {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                                              <button
                                                key={m}
                                                onClick={() => changeEntryStartMinute(year, month, day, schedKey, m)}
                                                className={`px-1 py-0.5 text-[9px] rounded font-medium transition-colors ${startMin === m ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600'}`}
                                              >
                                                :{String(m).padStart(2, '0')}
                                              </button>
                                            ))}
                                            <button onClick={() => setEditingStartTime(null)} className="p-0.5 text-gray-400 hover:text-red-500 ml-1"><X size={10} /></button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => setEditingStartTime(String(schedKey) + '-' + hour)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            draggable="false"
                                            className="text-[10px] text-gray-500 font-medium hover:text-blue-600 hover:bg-blue-50 rounded px-1 py-0.5 -ml-1 transition-colors flex items-center gap-0.5"
                                            title="Click to change start time"
                                          >
                                            <Clock size={9} />
                                            {showTimeRange ? formatTimeRange(hour, startMin, dur) : `${hour % 12 || 12}:00 ${hour >= 12 ? 'PM' : 'AM'}`}
                                          </button>
                                        )}
                                      </div>
                                      {/* Action buttons — visible on hover */}
                                      <div className="flex flex-wrap items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {!isTask && !isFocus && !isDone && !isSkipped && (
                                          <div className="flex items-center gap-0.5">
                                            <span className="text-[10px] text-gray-400 mr-0.5">Task:</span>
                                            {energyCategories.map(cat => (
                                              <button
                                                key={cat.key}
                                                onClick={() => promoteScheduleToTask(year, month, day, schedKey, cat.key)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                draggable="false"
                                                title={`${cat.label} task`}
                                                className={`p-1 rounded ${cat.bg} hover:scale-110 transition-transform`}
                                              >
                                                {React.cloneElement(cat.icon, { size: 12 })}
                                              </button>
                                            ))}
                                            <div className="w-px h-3 bg-gray-300 mx-1" />
                                            <button
                                              onClick={() => markScheduleAsFocus(year, month, day, schedKey)}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              draggable="false"
                                              title="Focus Work"
                                              className="p-1 rounded bg-indigo-50 text-indigo-500 hover:bg-indigo-100 hover:scale-110 transition-all"
                                            >
                                              <Activity size={12} />
                                            </button>
                                          </div>
                                        )}
                                        {(isTask || isFocus) && !isDone && !isSkipped && (
                                          <button
                                            onClick={() => skipScheduleTask(year, month, day, schedKey)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            draggable="false"
                                            title="Didn't Complete"
                                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 text-[10px] transition-all"
                                          >
                                            <SkipForward size={10} /> Skip
                                          </button>
                                        )}
                                        {(isTask || isFocus || isSkipped) && (
                                          <button
                                            onClick={() => demoteScheduleEntry(year, month, day, schedKey)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            draggable="false"
                                            title="Remove designation"
                                            className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
                                          >
                                            <XCircle size={12} />
                                          </button>
                                        )}
                                        {isSkipped && (
                                          <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-medium">
                                            Didn't do
                                          </span>
                                        )}
                                        {isTask && energyCat && !isSkipped && (
                                          <span className={`text-[10px] ${energyCat.badge} px-1.5 py-0.5 rounded-full ml-auto`}>
                                            {energyCat.label}
                                          </span>
                                        )}
                                        {isFocus && !isSkipped && (
                                          <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full ml-auto">
                                            Focus
                                          </span>
                                        )}
                                      </div>
                                      {/* Duration picker — visible on hover, wraps to 2 rows */}
                                      <div className="flex flex-wrap items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-[10px] text-gray-400 mr-0.5">Duration:</span>
                                        {durationPresets.map(dp => {
                                          const currentDur = entry.duration || 60;
                                          const isActive = currentDur === dp.minutes;
                                          return (
                                            <button
                                              key={dp.minutes}
                                              onClick={() => updateScheduleEntryMeta(year, month, day, schedKey, { duration: dp.minutes })}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              draggable="false"
                                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-all ${
                                                isActive
                                                  ? 'bg-blue-500 text-white'
                                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                              }`}
                                            >
                                              {dp.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {/* Recurring badge or set-repeat select */}
                                      <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {entry.isRecurring ? (() => {
                                          const rEntry = recurringSchedule.find(r => r.id === entry.recurringId);
                                          const repeatLabels = { daily: 'Daily', weekdays: 'Weekdays', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', yearly: 'Yearly' };
                                          return (
                                          <div className="flex items-center gap-1">
                                            <span className="text-[10px] bg-indigo-50 text-indigo-500 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                              <RefreshCw size={9} /> {rEntry ? repeatLabels[rEntry.repeat] || 'Recurring' : 'Recurring'}
                                            </span>
                                            <button
                                              onClick={() => {
                                                if (window.confirm('Remove this recurring task from all days?')) {
                                                  setRecurringSchedule(prev => prev.filter(r => r.id !== entry.recurringId));
                                                }
                                              }}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              draggable="false"
                                              className="text-[10px] text-red-400 hover:text-red-600"
                                            >
                                              ×
                                            </button>
                                          </div>
                                          );
                                        })() : (
                                          <select
                                            defaultValue=""
                                            onChange={e => {
                                              if (!e.target.value) return;
                                              const { hour, minute } = parseScheduleKey(schedKey);
                                              const d = new Date(year, month, day);
                                              setRecurringSchedule(prev => [...prev, {
                                                id: Date.now(),
                                                text: entry.text || '',
                                                hour, minute,
                                                duration: entry.duration || 60,
                                                repeat: e.target.value,
                                                dayOfWeek: d.getDay(),
                                                dayOfMonth: d.getDate(),
                                                monthOfYear: d.getMonth(),
                                                startDate: getDayKey(year, month, day)
                                              }]);
                                              e.target.value = '';
                                            }}
                                            className="text-[10px] text-gray-400 bg-transparent border-none cursor-pointer focus:outline-none"
                                          >
                                            <option value="">↻ Repeat...</option>
                                            <option value="daily">Daily</option>
                                            <option value="weekdays">Weekdays</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="biweekly">Every 2 Weeks</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="yearly">Yearly</option>
                                          </select>
                                        )}
                                      </div>
                                      {/* Resize grip — drag down to extend, drag up to shrink */}
                                      <div
                                        onMouseDown={(e) => handleResizeStart(e, schedKey)}
                                        className="h-2 cursor-ns-resize flex items-center justify-center opacity-0 group-hover:opacity-60 transition-opacity mt-1"
                                        title="Drag to adjust duration"
                                      >
                                        <div className="w-10 h-1 bg-gray-300 rounded-full" />
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => updateScheduleBlock(year, month, day, schedKey, '')}
                                      className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity mt-1"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                );
                              })}

                              {/* Add button / inline entry input / empty slot input */}
                              {(() => {
                                const nextMin = computeNextStartMinute(hour);
                                if (addingEntryHour === hour) {
                                  return (
                                    <div className="flex items-center gap-2 mt-1" onMouseDown={(e) => e.stopPropagation()}>
                                      <div className="flex gap-0.5 shrink-0">
                                        {[0, 15, 30, 45].map(m => (
                                          <button
                                            key={m}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setAddingEntryMinute(m)}
                                            className={`px-1.5 py-0.5 text-[10px] rounded font-medium transition-colors ${addingEntryMinute === m ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                          >
                                            :{String(m).padStart(2, '0')}
                                          </button>
                                        ))}
                                      </div>
                                      <input
                                        autoFocus
                                        type="text"
                                        placeholder={`Add at ${hour % 12 || 12}:${String(addingEntryMinute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}...`}
                                        onBlur={(e) => {
                                          if (e.target.value.trim()) {
                                            const key = makeScheduleKey(hour, addingEntryMinute);
                                            const remainingInHour = 60 - addingEntryMinute;
                                            updateScheduleBlock(year, month, day, key, { text: e.target.value.trim(), duration: addingEntryMinute > 0 ? Math.min(remainingInHour, 60) : 60 });
                                          }
                                          setAddingEntryHour(null);
                                          setAddingEntryMinute(0);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.target.value.trim()) e.target.blur();
                                          if (e.key === 'Escape') { setAddingEntryHour(null); setAddingEntryMinute(0); }
                                        }}
                                        className="flex-1 text-sm text-gray-600 focus:outline-none bg-transparent border-b border-dashed border-blue-300 py-0.5"
                                      />
                                    </div>
                                  );
                                }
                                if (hourEntries.length === 0 && !isDragTarget) {
                                  return (
                                    <button
                                      onClick={() => { setAddingEntryHour(hour); setAddingEntryMinute(0); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="w-full h-full text-left text-sm text-gray-400 focus:outline-none opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity py-1"
                                    >
                                      + Add event...
                                    </button>
                                  );
                                }
                                if (hourEntries.length > 0 && nextMin < 60) {
                                  return (
                                    <button
                                      onClick={() => { setAddingEntryHour(hour); setAddingEntryMinute(nextMin < 60 ? nextMin : 0); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="text-[10px] text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 mt-0.5 py-0.5"
                                    >
                                      <Plus size={10} /> add another
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar — Week Context + Tasks + Notes */}
          <div className="lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto space-y-4" style={{ scrollbarWidth: 'thin' }}>

            {/* Week Context Panel — Goals, Milestones, To-Dos */}
            {(() => {
              const weekNum = getWeekNumForDate(new Date(year, month, day));
              const weekInfo = getWeekData(weekNum);
              const activeGoals = weekInfo.goals.filter(g => !g.completed);
              const activeMilestones = weekInfo.milestones.filter(m => !m.completed && !m.notDone);
              const activeTodos = todoList.filter(t => !t.completed);
              const hasItems = activeGoals.length > 0 || activeMilestones.length > 0 || activeTodos.length > 0;

              if (!hasItems) return null;

              const makeDraggable = (item) => ({
                draggable: true,
                onDragStart: (e) => {
                  setDragTaskData({ energy: null, taskId: item.id, text: item.text });
                  setDragSourceHour(null);
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData('text/plain', item.text);
                  setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
                },
                onDragEnd: handleDragEnd,
              });

              return (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2.5 text-white cursor-pointer select-none"
                    onClick={() => setWeekPanelCollapsed(prev => !prev)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-sm flex items-center gap-2">
                        <LayoutGrid size={14} />
                        Week {weekNum}
                        <span className="text-[10px] text-white/60 font-normal">
                          {activeGoals.length + activeMilestones.length + activeTodos.length} items
                        </span>
                      </h3>
                      <div className="flex items-center gap-2">
                        {!weekPanelCollapsed && <span className="text-[10px] text-white/70">drag → schedule</span>}
                        {weekPanelCollapsed ? <ChevronRight size={16} className="text-white/70" /> : <ChevronDown size={16} className="text-white/70" />}
                      </div>
                    </div>
                  </div>

                  {!weekPanelCollapsed && <div className="divide-y divide-gray-100">
                    {/* Goals */}
                    {activeGoals.length > 0 && (
                      <div className="p-3">
                        <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Target size={11} /> Goals <span className="text-indigo-400">({activeGoals.length})</span>
                        </div>
                        <div className="space-y-1">
                          {activeGoals.map(goal => (
                            <div
                              key={goal.id}
                              {...makeDraggable(goal)}
                              className="flex items-center gap-2 px-2 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg cursor-grab active:cursor-grabbing hover:bg-indigo-100 hover:shadow-sm transition-all group text-xs"
                            >
                              <Target size={10} className="text-indigo-400 flex-shrink-0" />
                              <span className="text-gray-700 truncate flex-1">{goal.text}</span>
                              <GripVertical size={10} className="text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Milestones */}
                    {activeMilestones.length > 0 && (
                      <div className="p-3">
                        <div className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Award size={11} /> Milestones <span className="text-purple-400">({activeMilestones.length})</span>
                        </div>
                        <div className="space-y-1">
                          {activeMilestones.map(ms => (
                            <div
                              key={ms.id}
                              {...makeDraggable(ms)}
                              className="flex items-center gap-2 px-2 py-1.5 bg-purple-50 border border-purple-100 rounded-lg cursor-grab active:cursor-grabbing hover:bg-purple-100 hover:shadow-sm transition-all group text-xs"
                            >
                              <Award size={10} className="text-purple-400 flex-shrink-0" />
                              <span className="text-gray-700 truncate flex-1">{ms.text}</span>
                              {ms.date && <span className="text-[9px] text-purple-400 flex-shrink-0">{ms.date}</span>}
                              <GripVertical size={10} className="text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* To-Do items moved to floating panel */}
                  </div>}
                </div>
              );
            })()}

            {/* Energy-Categorized Tasks — draggable onto schedule, droppable from Week Context */}
            {energyCategories.map(cat => {
              const categoryTasks = tasks[cat.key] || [];
              const isDropTarget = dragOverEnergy === cat.key;
              return (
                <div
                  key={cat.key}
                  onDragOver={(e) => handleEnergyDragOver(e, cat.key)}
                  onDragLeave={() => { if (dragOverEnergy === cat.key) setDragOverEnergy(null); }}
                  onDrop={(e) => handleEnergyDrop(e, cat.key)}
                  className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all ${
                    isDropTarget
                      ? 'border-blue-400 ring-2 ring-blue-200 scale-[1.02]'
                      : 'border-gray-100'
                  }`}
                >
                  <div className={`bg-gradient-to-r ${cat.gradient} px-4 py-2.5 text-white flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      {cat.icon}
                      <span className="font-bold text-sm">{cat.label}</span>
                    </div>
                    <span className={`${cat.badge} text-xs font-bold px-2 py-0.5 rounded-full`}>
                      {categoryTasks.filter(t => t.completed).length}/{categoryTasks.length}
                    </span>
                  </div>
                  <div className="p-3">
                    {isDropTarget && (
                      <div className="flex items-center justify-center gap-2 py-2 mb-2 bg-blue-50 border border-dashed border-blue-300 rounded-lg text-blue-500 text-xs font-medium animate-pulse">
                        <Plus size={12} /> Drop here to add as {cat.label.toLowerCase()} task
                      </div>
                    )}
                    {categoryTasks.length === 0 && !isDropTarget ? (
                      <p className="text-gray-400 text-xs text-center py-2">{cat.desc}</p>
                    ) : (
                      <div className="space-y-1.5">
                        {categoryTasks.map(task => (
                          <div
                            key={task.id}
                            draggable={!task.completed && !task.skipped}
                            onDragStart={(e) => handleTaskDragStart(e, cat.key, task)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center gap-2 p-2 rounded-lg group transition-all border-l-3 ${cat.borderLeft} ${
                              task.completed
                                ? 'bg-gray-50 opacity-60'
                                : task.skipped
                                ? 'bg-gray-50 opacity-40'
                                : `${cat.bg} cursor-grab active:cursor-grabbing hover:shadow-md`
                            }`}
                          >
                            <button
                              onClick={() => toggleDayTask(year, month, day, cat.key, task.id)}
                              onMouseDown={(e) => e.stopPropagation()}
                              className={`p-0.5 rounded-md flex-shrink-0 transition-colors ${
                                task.completed ? 'bg-green-500 text-white'
                                : task.skipped ? 'bg-gray-400 text-white'
                                : 'bg-gray-300 hover:bg-gray-400'
                              }`}
                            >
                              {task.skipped ? <SkipForward size={11} /> : <Check size={11} />}
                            </button>
                            <span className={`flex-1 text-xs ${
                              task.completed ? 'line-through text-gray-400'
                              : task.skipped ? 'line-through text-gray-400 italic'
                              : 'text-gray-800'
                            }`}>
                              {task.text}
                            </span>
                            {task.linkedHour !== undefined && task.linkedHour !== null && (
                              <span className="text-[9px] text-gray-400 flex-shrink-0 flex items-center gap-0.5" title={`Scheduled at ${(() => { const { hour: lh, minute: lm } = parseScheduleKey(task.linkedHour); return lm ? `${formatHour(lh)}:${String(lm).padStart(2,'0')}` : formatHour(lh); })()}`}>
                                <Clock size={9} /> {(() => { const { hour: lh, minute: lm } = parseScheduleKey(task.linkedHour); return lm ? `${formatHour(lh)}:${String(lm).padStart(2,'0')}` : formatHour(lh); })()}
                              </span>
                            )}
                            <button
                              onClick={() => deleteDayTask(year, month, day, cat.key, task.id)}
                              onMouseDown={(e) => e.stopPropagation()}
                              className="p-0.5 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Day Notes */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <h3 className="font-bold text-gray-700 mb-2 text-sm flex items-center gap-2">
                <FileText size={14} className="text-orange-500" />
                Day Notes
              </h3>
              <textarea
                defaultValue={note}
                onBlur={(e) => {
                  if (e.target.value !== note) {
                    updateDayNote(year, month, day, e.target.value);
                  }
                }}
                placeholder="Quick notes, reminders, or thoughts for the day..."
                className="w-full h-24 px-3 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none text-sm bg-orange-50/50"
              />
            </div>
          </div>
        </div>

        {/* Floating To-Do Panel — Draggable & Resizable */}
        {(() => {
          const activeTodos = todoList.filter(t => !t.completed);
          if (activeTodos.length === 0) return null;
          const makeTodoDraggable = (item) => ({
            draggable: true,
            onDragStart: (e) => {
              setDragTaskData({ energy: null, taskId: item.id, text: item.text });
              setDragSourceHour(null);
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.setData('text/plain', item.text);
              setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
            },
            onDragEnd: handleDragEnd,
          });

          const startDrag = (e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const startPosX = todoPanelPos.x ?? (window.innerWidth - todoPanelSize.w - 24);
            const startPosY = todoPanelPos.y ?? (window.innerHeight - todoPanelSize.h - 24);
            const onMove = (ev) => {
              const dx = ev.clientX - startX;
              const dy = ev.clientY - startY;
              setTodoPanelPos({
                x: Math.max(0, Math.min(window.innerWidth - 100, startPosX + dx)),
                y: Math.max(0, Math.min(window.innerHeight - 50, startPosY + dy))
              });
            };
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          };

          const startResize = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = todoPanelSize.w;
            const startH = todoPanelSize.h;
            const onMove = (ev) => {
              setTodoPanelSize({
                w: Math.max(220, Math.min(600, startW + (ev.clientX - startX))),
                h: Math.max(200, Math.min(700, startH + (ev.clientY - startY)))
              });
            };
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          };

          const posStyle = todoPanelPos.x !== null
            ? { left: todoPanelPos.x, top: todoPanelPos.y }
            : { right: 24, bottom: 24 };

          return (
            <div className="fixed z-50" style={floatingTodoOpen ? posStyle : { right: 24, bottom: 24 }}>
              {floatingTodoOpen ? (
                <div
                  className="bg-white rounded-2xl shadow-2xl border border-orange-200 overflow-hidden flex flex-col"
                  style={{ width: todoPanelSize.w, height: todoPanelSize.h }}
                >
                  <div
                    ref={todoDragRef}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2.5 text-white cursor-move select-none flex-shrink-0"
                    onMouseDown={startDrag}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-sm flex items-center gap-2">
                        <ListTodo size={14} />
                        To-Do
                        <span className="text-[10px] text-white/70 font-normal">{activeTodos.length} items</span>
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/70">drag → schedule</span>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => { setFloatingTodoOpen(false); setTodoPanelPos({ x: null, y: null }); }}
                          className="p-0.5 hover:bg-white/20 rounded"
                        >
                          <ChevronDown size={14} className="text-white/70" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 p-3 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin' }}>
                    {activeTodos.map(todo => (
                      <div
                        key={todo.id}
                        {...makeTodoDraggable(todo)}
                        className="flex items-center gap-2 px-2.5 py-2 bg-orange-50 border border-orange-100 rounded-lg cursor-grab active:cursor-grabbing hover:bg-orange-100 hover:shadow-sm transition-all group text-xs"
                      >
                        <ListTodo size={10} className="text-orange-400 flex-shrink-0" />
                        <span className="text-gray-700 flex-1">{todo.text}</span>
                        <GripVertical size={10} className="text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                  {/* Resize handle */}
                  <div
                    ref={todoResizeRef}
                    onMouseDown={startResize}
                    className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize flex items-center justify-center"
                    style={{ touchAction: 'none' }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" className="text-orange-300">
                      <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setFloatingTodoOpen(true)}
                  className="w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform relative"
                  title="Open To-Do List"
                >
                  <ListTodo size={22} />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">{activeTodos.length}</span>
                </button>
              )}
            </div>
          );
        })()}
      </div >
    );
  };

  // --- Brain Dump View ---
  const BrainDumpView = () => {
    const handleManualSave = async () => {
      setIsSavingBrainDump(true);
      try {
        const now = new Date();
        const timestamp = `\n\n--- Saved: ${now.toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })} ---\n`;

        const updatedBrainDump = {
          ...brainDump,
          text: timestamp + brainDump.text.trim()
        };

        setBrainDump(updatedBrainDump);
        await window.storage.set('brain-dump', JSON.stringify(updatedBrainDump));

        setBrainDumpSaveSuccess(true);
        setTimeout(() => setBrainDumpSaveSuccess(false), 2000);
      } catch (error) {
        console.error('Manual save failed:', error);
      } finally {
        setIsSavingBrainDump(false);
      }
    };

    const cardColors = [
      { key: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-900', label: 'Yellow' },
      { key: 'blue', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-900', label: 'Blue' },
      { key: 'green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-900', label: 'Green' },
      { key: 'pink', bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-900', label: 'Pink' },
      { key: 'purple', bg: 'bg-purple-100', border: 'border-gray-200', text: 'text-purple-900', label: 'Purple' },
      { key: 'orange', bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-900', label: 'Orange' },
    ];

    const addCard = () => {
      if (!newCardText.trim()) return;
      const cardCount = brainDump.cards.length;
      const col = cardCount % 5;
      const row = Math.floor(cardCount / 5);
      const newZ = cardMaxZ + 1;
      setCardMaxZ(newZ);
      setBrainDump(prev => ({
        ...prev,
        cards: [...prev.cards, { id: Date.now(), text: newCardText.trim(), color: newCardColor, x: 30 + col * 260, y: 30 + row * 230, w: 240, h: 200, z: newZ, expanded: false }]
      }));
      setNewCardText('');
    };

    const deleteCard = (id) => {
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.filter(c => c.id !== id)
      }));
    };

    const updateCardText = (id, text) => {
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.map(c => c.id === id ? { ...c, text } : c)
      }));
    };

    const updateCardPosition = (id, x, y) => {
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.map(c => c.id === id ? { ...c, x, y } : c)
      }));
    };

    const updateCardSize = (id, w, h) => {
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.map(c => c.id === id ? { ...c, w: Math.max(150, w), h: Math.max(100, h) } : c)
      }));
    };

    const bringToFront = (id) => {
      const newZ = cardMaxZ + 1;
      setCardMaxZ(newZ);
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.map(c => c.id === id ? { ...c, z: newZ } : c)
      }));
    };

    const toggleCardExpand = (id) => {
      setBrainDump(prev => ({
        ...prev,
        cards: prev.cards.map(c => c.id === id ? { ...c, expanded: !c.expanded, w: c.expanded ? 240 : 450, h: c.expanded ? 200 : 380 } : c)
      }));
    };

    const handleCardMouseDown = (e, card) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
      e.preventDefault();
      bringToFront(card.id);
      setDraggingCardId(card.id);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const handleCanvasMouseMove = (e) => {
      if (draggingCardId) {
        const canvas = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, e.clientX - canvas.left - dragOffset.x);
        const y = Math.max(0, e.clientY - canvas.top - dragOffset.y);
        updateCardPosition(draggingCardId, x, y);
      }
      if (resizingCardId) {
        const canvas = e.currentTarget.getBoundingClientRect();
        const card = brainDump.cards.find(c => c.id === resizingCardId);
        if (card) {
          const w = Math.max(150, e.clientX - canvas.left - (card.x || 0));
          const h = Math.max(100, e.clientY - canvas.top - (card.y || 0));
          updateCardSize(resizingCardId, w, h);
        }
      }
    };

    const handleCanvasMouseUp = () => {
      setDraggingCardId(null);
      setResizingCardId(null);
    };

    const getColorClasses = (colorKey) => {
      return cardColors.find(c => c.key === colorKey) || cardColors[0];
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <Brain size={32} className="text-blue-500" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Brain Dump</h1>
              <p className="text-gray-400">Dump everything out of your head. No structure needed.</p>
            </div>
          </div>
          <div className="flex gap-4 mt-4 text-sm">
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
              <span className="font-bold text-blue-600">{brainDump.cards.length}</span> <span className="text-gray-500">cards</span>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
              <span className="font-bold text-blue-600">{brainDump.text.length}</span> <span className="text-gray-500">characters</span>
            </div>
          </div>
          {/* Brain Dump Tabs */}
          <div className="flex gap-2 mt-5 border-b border-gray-100 pb-3">
            {[['notes', '📝 Notes'], ['stickies', '📌 Stickies'], ['vision', '🖼 Vision Board'], ['ideas', '💡 Ideas']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setBrainDumpTab(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${brainDumpTab === key ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {brainDumpTab === 'notes' && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-blue-50 px-6 py-4 border-b border-blue-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
                    <FileText size={20} className="text-blue-500" />
                    Free Writing Space
                  </h2>
                  <p className="text-sm text-gray-400">Write anything — ideas, thoughts, plans, random notes</p>
                </div>
                <button
                  onClick={handleManualSave}
                  disabled={isSavingBrainDump}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${brainDumpSaveSuccess
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                >
                  {brainDumpSaveSuccess ? <Check size={18} /> : <Save size={18} />}
                  {brainDumpSaveSuccess ? 'Saved!' : isSavingBrainDump ? 'Saving...' : 'Save Now'}
                </button>
              </div>
              {/* Creative Toolbar */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-1">
                {[
                  { label: '→', title: 'Right Arrow', insert: ' → ' },
                  { label: '←', title: 'Left Arrow', insert: ' ← ' },
                  { label: '↑', title: 'Up Arrow', insert: ' ↑ ' },
                  { label: '↓', title: 'Down Arrow', insert: ' ↓ ' },
                  { label: '↔', title: 'Double Arrow', insert: ' ↔ ' },
                  { label: '⇒', title: 'Bold Arrow', insert: ' ⇒ ' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => setBrainDump(prev => ({ ...prev, text: tool.insert + prev.text }))} title={tool.title}
                    className="px-2 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-blue-50 hover:border-blue-400 transition-colors font-mono">
                    {tool.label}
                  </button>
                ))}
                <div className="w-px bg-gray-300 mx-1" />
                {[
                  { label: '■', title: 'Filled Square', insert: ' ■ ' },
                  { label: '□', title: 'Empty Square', insert: ' □ ' },
                  { label: '●', title: 'Filled Circle', insert: ' ● ' },
                  { label: '○', title: 'Empty Circle', insert: ' ○ ' },
                  { label: '◆', title: 'Diamond', insert: ' ◆ ' },
                  { label: '▲', title: 'Triangle', insert: ' ▲ ' },
                  { label: '★', title: 'Star', insert: ' ★ ' },
                  { label: '♥', title: 'Heart', insert: ' ♥ ' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => setBrainDump(prev => ({ ...prev, text: tool.insert + prev.text }))} title={tool.title}
                    className="px-2 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-pink-50 hover:border-pink-400 transition-colors">
                    {tool.label}
                  </button>
                ))}
                <div className="w-px bg-gray-300 mx-1" />
                {[
                  { label: '━━━', title: 'Divider Line', insert: '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' },
                  { label: '═══', title: 'Double Line', insert: '\n══════════════════════════════\n' },
                  { label: '▬▬▬', title: 'Thick Line', insert: '\n▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬\n' },
                  { label: '• • •', title: 'Dot Separator', insert: '\n• • • • • • • • • •\n' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => setBrainDump(prev => ({ ...prev, text: tool.insert + prev.text }))} title={tool.title}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-yellow-50 hover:border-yellow-400 transition-colors font-mono">
                    {tool.label}
                  </button>
                ))}
                <div className="w-px bg-gray-300 mx-1" />
                {[
                  { label: '📌', title: 'Pin' },
                  { label: '💡', title: 'Idea' },
                  { label: '⚡', title: 'Lightning' },
                  { label: '🎯', title: 'Target' },
                  { label: '🔥', title: 'Fire' },
                  { label: '✅', title: 'Check' },
                  { label: '❌', title: 'Cross' },
                  { label: '⭐', title: 'Star' },
                  { label: '🚀', title: 'Rocket' },
                  { label: '💬', title: 'Chat' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => setBrainDump(prev => ({ ...prev, text: tool.label + ' ' + prev.text }))} title={tool.title}
                    className="px-1.5 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-green-50 hover:border-green-400 transition-colors">
                    {tool.label}
                  </button>
                ))}
                <div className="w-px bg-gray-300 mx-1" />
                {[
                  { label: '[ ] Task', title: 'Checkbox', insert: '\n☐ ' },
                  { label: '# Header', title: 'Header', insert: '\n\n## ' },
                  { label: '• List', title: 'Bullet', insert: '\n• ' },
                  { label: '1. List', title: 'Numbered', insert: '\n1. ' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => setBrainDump(prev => ({ ...prev, text: tool.insert + prev.text }))} title={tool.title}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-purple-50 hover:border-purple-400 transition-colors font-medium">
                    {tool.label}
                  </button>
                ))}
                <div className="w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => {
                    const lines = brainDump.text.split('\n');
                    const buckets = { Tasks: [], Ideas: [], Goals: [], Questions: [], Notes: [] };
                    lines.forEach(line => {
                      const t = line.trim();
                      if (!t || t.startsWith('##') || t.startsWith('#')) { buckets.Notes.push(line); return; }
                      const l = t.toLowerCase();
                      if (t.endsWith('?') || l.startsWith('q:') || l.startsWith('question:')) buckets.Questions.push(t);
                      else if (l.startsWith('idea:') || l.startsWith('idea -') || l.startsWith('thought:') || l.startsWith('💡')) buckets.Ideas.push(t);
                      else if (l.startsWith('do:') || l.startsWith('todo:') || l.startsWith('task:') || l.startsWith('- [ ]') || l.includes('should ') || l.startsWith('need to')) buckets.Tasks.push(t);
                      else if (l.startsWith('goal:') || l.startsWith('want to') || l.startsWith('aim to') || l.startsWith('🎯')) buckets.Goals.push(t);
                      else buckets.Notes.push(t);
                    });
                    const sections = Object.entries(buckets)
                      .filter(([, items]) => items.length > 0)
                      .map(([label, items]) => `## ${label}\n${items.join('\n')}`);
                    setBrainDump(prev => ({ ...prev, text: sections.join('\n\n') }));
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  ✦ Smart Sort
                </button>
              </div>
              <textarea
                ref={(node) => { if (node) { node.scrollTop = 0; } }}
                value={brainDump.text}
                onChange={(e) => setBrainDump(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Start typing... anything goes here.&#10;&#10;💡 Ideas, thoughts, reminders, goals, random musings...&#10;📝 No structure needed — just dump it all here.&#10;🧠 Get it out of your head and onto the page.&#10;&#10;Use the toolbar above to insert arrows, shapes, and formatting!"
                className="w-full px-6 py-4 border-0 focus:outline-none focus:ring-0 resize-none text-gray-800 text-base leading-relaxed"
                style={{ minHeight: 'calc(100vh - 280px)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}
              />
            </div>
          </div>
          )}

          {brainDumpTab === 'stickies' && (
          <div className="lg:col-span-3 space-y-4">
            {/* Add Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <Palette size={18} className="text-purple-500" />
                <input
                  type="text"
                  value={newCardText}
                  onChange={(e) => setNewCardText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newCardText.trim()) addCard(); }}
                  placeholder="Quick thought or idea..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 text-sm"
                />
                <div className="flex gap-1">
                  {cardColors.map(color => (
                    <button
                      key={color.key}
                      onClick={() => setNewCardColor(color.key)}
                      className={`w-6 h-6 rounded-full ${color.bg} border-2 transition-all ${newCardColor === color.key ? `${color.border} scale-110 ring-2 ring-offset-1 ring-purple-400` : 'border-transparent'}`}
                      title={color.label}
                    />
                  ))}
                </div>
                <button
                  onClick={addCard}
                  disabled={!newCardText.trim()}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm font-medium disabled:opacity-40 flex items-center gap-1"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>

            {/* Free-placement Canvas */}
            <div
              className="relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-auto"
              style={{ height: 'calc(100vh - 180px)', background: 'repeating-linear-gradient(0deg, transparent, transparent 19px, #f3f4f6 19px, #f3f4f6 20px), repeating-linear-gradient(90deg, transparent, transparent 19px, #f3f4f6 19px, #f3f4f6 20px)' }}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            >
              {brainDump.cards.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300 pointer-events-none">
                  <Brain size={48} className="mb-2 opacity-30" />
                  <p className="text-sm font-medium">No sticky notes yet</p>
                  <p className="text-xs mt-1">Add your first note above — drag to move, corner to resize</p>
                </div>
              )}

              {brainDump.cards.map(card => {
                const color = getColorClasses(card.color);
                const cx = card.x ?? 20;
                const cy = card.y ?? 20;
                const cw = card.w ?? 200;
                const ch = card.h ?? 150;
                const cz = card.z ?? 1;
                return (
                  <div
                    key={card.id}
                    className={`absolute group ${color.bg} ${color.border} border-2 rounded-xl shadow-lg hover:shadow-xl`}
                    style={{ left: cx, top: cy, width: cw, height: ch, zIndex: cz, cursor: draggingCardId === card.id ? 'grabbing' : 'default', transition: draggingCardId === card.id || resizingCardId === card.id ? 'none' : 'box-shadow 0.2s' }}
                    onMouseDown={(e) => handleCardMouseDown(e, card)}
                  >
                    {/* Drag handle header */}
                    <div className={`flex items-center justify-between px-3 py-1.5 ${color.border} border-b cursor-grab rounded-t-xl`} style={{ cursor: draggingCardId === card.id ? 'grabbing' : 'grab' }}>
                      <div className="flex items-center gap-1.5">
                        <GripVertical size={12} className="text-gray-400 opacity-50" />
                        <span className={`text-[9px] font-bold uppercase tracking-widest ${color.text} opacity-50`}>{card.color}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCardExpand(card.id); }}
                          className="p-1 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          title={card.expanded ? 'Collapse' : 'Expand'}
                        >
                          {card.expanded ? <Square size={11} /> : <LayoutGrid size={11} />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteCard(card.id); }}
                          className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                    {/* Card body */}
                    <textarea
                      value={card.text}
                      onChange={(e) => updateCardText(card.id, e.target.value)}
                      onMouseDown={(e) => { e.stopPropagation(); bringToFront(card.id); }}
                      className={`w-full bg-transparent ${color.text} text-sm resize-none focus:outline-none p-3`}
                      style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", height: `calc(100% - 34px)` }}
                    />
                    {/* Resize handle */}
                    <div
                      className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-60 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%)' , borderRadius: '0 0 10px 0' }}
                      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); bringToFront(card.id); setResizingCardId(card.id); }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {brainDumpTab === 'vision' && (
          <div className="space-y-4">
            {/* Add Vision Item form */}
            {(() => {
              let urlRef = null;
              let labelRef = null;
              let quoteRef = null;
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3">
                  <p className="text-xs font-semibold text-gray-600">Add to Vision Board</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      ref={node => { urlRef = node; }}
                      placeholder="Image URL (https://...)"
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-300"
                    />
                    <input
                      type="text"
                      ref={node => { labelRef = node; }}
                      placeholder="Label (optional)"
                      className="w-28 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-300"
                    />
                    <button
                      onClick={() => {
                        const url = urlRef?.value?.trim();
                        if (!url) return;
                        const label = labelRef?.value?.trim() || '';
                        setBrainDump(prev => ({
                          ...prev,
                          visionItems: [...(prev.visionItems || []), { id: Date.now(), type: 'image', content: url, label }]
                        }));
                        if (urlRef) urlRef.value = '';
                        if (labelRef) labelRef.value = '';
                      }}
                      className="px-3 py-2 bg-indigo-500 text-white text-xs font-medium rounded-lg hover:bg-indigo-600 flex-shrink-0"
                    >
                      Add Image
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      ref={node => { quoteRef = node; }}
                      placeholder="Add an inspiring quote or text..."
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-300"
                    />
                    <button
                      onClick={() => {
                        const text = quoteRef?.value?.trim();
                        if (!text) return;
                        setBrainDump(prev => ({
                          ...prev,
                          visionItems: [...(prev.visionItems || []), { id: Date.now(), type: 'text', content: text, label: '' }]
                        }));
                        if (quoteRef) quoteRef.value = '';
                      }}
                      className="px-3 py-2 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 flex-shrink-0"
                    >
                      Add Quote
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Vision items grid */}
            {(!brainDump.visionItems || brainDump.visionItems.length === 0) ? (
              <div className="text-center py-16 text-gray-400">
                <div className="text-4xl mb-3">🖼</div>
                <p className="text-sm font-medium">Your vision board is empty</p>
                <p className="text-xs mt-1">Add images or quotes that inspire you</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {(brainDump.visionItems || []).map(item => (
                  <div key={item.id} className="relative group rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    {item.type === 'image' ? (
                      <>
                        <img
                          src={item.content}
                          alt={item.label || 'Vision'}
                          className="w-full h-40 object-cover"
                          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div style={{ display: 'none' }} className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                          Image unavailable
                        </div>
                        {item.label && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[11px] px-2 py-1 truncate">
                            {item.label}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center p-4">
                        <p className="text-center text-sm font-medium text-indigo-700 italic leading-snug line-clamp-5">
                          "{item.content}"
                        </p>
                      </div>
                    )}
                    <button
                      onClick={() => setBrainDump(prev => ({
                        ...prev,
                        visionItems: (prev.visionItems || []).filter(v => v.id !== item.id)
                      }))}
                      className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/40 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:bg-red-500"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {brainDumpTab === 'ideas' && (
          <div className="space-y-4">
            {/* Add Idea */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    defaultValue=""
                    id="idea-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const newText = e.target.value.trim();
                        const tagsInput = document.getElementById('idea-tags');
                        const tags = tagsInput?.value ? tagsInput.value.split(',').map(t => t.trim()).filter(Boolean) : [];
                        setIdeaIncubator(prev => [{ id: Date.now(), text: newText, tags, status: 'fresh', createdAt: new Date().toISOString(), lastTouchedAt: new Date().toISOString(), notes: '' }, ...prev]);
                        e.target.value = '';
                        if (tagsInput) tagsInput.value = '';
                      }
                    }}
                    placeholder="Drop an idea here... 💡"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                  />
                  <button
                    onClick={() => {
                      const inp = document.getElementById('idea-input');
                      const tagsInput = document.getElementById('idea-tags');
                      if (!inp || !inp.value.trim()) return;
                      const newText = inp.value.trim();
                      const tags = tagsInput?.value ? tagsInput.value.split(',').map(t => t.trim()).filter(Boolean) : [];
                      setIdeaIncubator(prev => [{ id: Date.now(), text: newText, tags, status: 'fresh', createdAt: new Date().toISOString(), lastTouchedAt: new Date().toISOString(), notes: '' }, ...prev]);
                      inp.value = '';
                      if (tagsInput) tagsInput.value = '';
                    }}
                    className="px-5 py-3 bg-yellow-500 text-white rounded-xl text-sm font-bold hover:bg-yellow-600 transition-colors flex-shrink-0"
                  >Add</button>
                </div>
                <input
                  type="text"
                  defaultValue=""
                  id="idea-tags"
                  placeholder="Tags (comma separated)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-yellow-200"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-yellow-50 rounded-xl p-3 text-center border border-yellow-100">
                <div className="text-xl font-black text-yellow-600">{ideaIncubator.filter(i => i.status === 'fresh').length}</div>
                <div className="text-[10px] text-gray-500 font-medium">🌱 Fresh</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                <div className="text-xl font-black text-blue-600">{ideaIncubator.filter(i => i.status === 'developing').length}</div>
                <div className="text-[10px] text-gray-500 font-medium">🔧 Developing</div>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                <div className="text-xl font-black text-green-600">{ideaIncubator.filter(i => i.status === 'ready').length}</div>
                <div className="text-[10px] text-gray-500 font-medium">✅ Ready</div>
              </div>
            </div>

            {/* Ideas List */}
            {ideaIncubator.map(idea => {
              const age = Math.floor((new Date() - new Date(idea.createdAt)) / (1000*60*60*24));
              const untouched = Math.floor((new Date() - new Date(idea.lastTouchedAt)) / (1000*60*60*24));
              const isGem = untouched >= 30;
              const statusColors = { fresh: 'bg-yellow-100 text-yellow-700', developing: 'bg-blue-100 text-blue-700', ready: 'bg-green-100 text-green-700' };
              const statusLabels = { fresh: '🌱 Fresh', developing: '🔧 Developing', ready: '✅ Ready' };
              return (
                <div key={idea.id} className={`bg-white rounded-2xl shadow-sm border ${isGem ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'} overflow-hidden`}>
                  {/* Idea Header — icon, badge, age on top row; title on its own line below */}
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 px-4 py-3 border-b border-yellow-100">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Lightbulb size={16} className="text-yellow-500" />
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${statusColors[idea.status] || statusColors.fresh}`}>{statusLabels[idea.status] || idea.status}</span>
                        {isGem && <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">💎 Gem</span>}
                      </div>
                      <span className="text-[10px] text-gray-400">{age}d ago</span>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 leading-snug">{idea.text || '(Untitled idea)'}</h4>
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {/* Tags */}
                    {idea.tags && idea.tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {idea.tags.map(tag => (
                        <span key={tag} className="text-[9px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">#{tag}</span>
                      ))}
                    </div>
                    )}
                    {/* Notes with save button */}
                    <div>
                      <div className="flex items-center gap-2">
                        <textarea
                          defaultValue={idea.notes || ''}
                          id={`idea-note-${idea.id}`}
                          placeholder="Add notes about this idea..."
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-yellow-200 resize-none bg-gray-50/50"
                          rows={2}
                        />
                        <button
                          onClick={() => {
                            const val = document.getElementById(`idea-note-${idea.id}`)?.value?.trim() || '';
                            setIdeaIncubator(prev => prev.map(i => i.id === idea.id ? { ...i, notes: val, lastTouchedAt: new Date().toISOString() } : i));
                          }}
                          className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-[10px] font-bold hover:bg-yellow-200 transition-colors flex-shrink-0 self-end"
                        >Save</button>
                      </div>
                      {idea.notes && <p className="text-xs text-gray-600 bg-yellow-50 rounded-lg px-3 py-2 mt-2 border border-yellow-100"><strong className="text-yellow-700">Notes:</strong> {idea.notes}</p>}
                    </div>
                    {/* Status + Delete */}
                    <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        {['fresh','developing','ready'].map(s => (
                          <button key={s} onClick={() => setIdeaIncubator(prev => prev.map(i => i.id === idea.id ? { ...i, status: s, lastTouchedAt: new Date().toISOString() } : i))}
                            className={`px-2.5 py-1 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${idea.status === s ? statusColors[s] + ' shadow-sm' : 'text-gray-400 hover:bg-gray-100'}`}
                          >{s === 'fresh' ? '🌱 Fresh' : s === 'developing' ? '🔧 Dev' : '✅ Ready'}</button>
                        ))}
                      </div>
                      <button onClick={() => setIdeaIncubator(prev => prev.filter(i => i.id !== idea.id))} className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}

            {ideaIncubator.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Lightbulb size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No ideas yet</p>
                <p className="text-xs mt-1">Start dropping ideas above — they'll incubate here</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // --- Life Lab View (JSX component — can use hooks) ---
  const LifeLabView = () => {
    const [showLetterForm, setShowLetterForm] = useState(false);
    const [showDreamForm, setShowDreamForm] = useState(false);
    const [showAdventureForm, setShowAdventureForm] = useState(false);
    const [editingDreamId, setEditingDreamId] = useState(null);
    const [editingDreamFull, setEditingDreamFull] = useState(null);

    const todayStr = new Date().toISOString().split('T')[0];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-center gap-3">
            <Sparkles size={32} />
            <div>
              <h1 className="text-3xl font-bold">Life Lab</h1>
              <p className="text-white/70">Letters to future self · Dream board · Adventures · Life perspective</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            {[['letters','✉️ Letters'],['dreams','🎯 Dreams'],['adventures','🧭 Adventures'],['lifegrid','⬛ Life Grid']].map(([key,label]) => (
              <button key={key} onClick={() => setLifeLabTab(key)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${lifeLabTab === key ? 'bg-white/25 text-white shadow-sm' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>{label}</button>
            ))}
          </div>
        </div>

        {/* ===== FUTURE LETTERS ===== */}
        {lifeLabTab === 'letters' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Mail size={20} className="text-purple-500" /> Letters to Future Self</h2>
              <button onClick={() => setShowLetterForm(!showLetterForm)} className="px-4 py-2 bg-purple-500 text-white rounded-xl text-sm font-medium hover:bg-purple-600 flex items-center gap-1"><Plus size={14} /> Write Letter</button>
            </div>

            {showLetterForm && (
              <div className="bg-white rounded-2xl border-2 border-dashed border-purple-200 p-5 space-y-3">
                <textarea defaultValue="" id="fl-text" rows={6} placeholder="Dear Future Me,&#10;&#10;Write your heart out... This will be sealed until the date you choose." className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }} />
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-gray-500">Open on:</label>
                  <input type="date" id="fl-date" defaultValue={new Date(Date.now()+365*24*60*60*1000).toISOString().split('T')[0]} min={new Date(Date.now()+24*60*60*1000).toISOString().split('T')[0]} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                  <button onClick={() => {
                    const text = document.getElementById('fl-text')?.value?.trim();
                    const unlockDate = document.getElementById('fl-date')?.value;
                    if (!text || !unlockDate) return;
                    setFutureLetters(prev => [{ id: Date.now(), text, writtenAt: new Date().toISOString(), unlockDate, isOpened: false, openedAt: null }, ...prev]);
                    setShowLetterForm(false);
                  }} className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 flex items-center gap-1"><Lock size={14} /> Seal Letter</button>
                </div>
              </div>
            )}

            {/* Letters */}
            {futureLetters.map(letter => {
              const daysLeft = Math.ceil((new Date(letter.unlockDate) - new Date()) / (1000*60*60*24));
              const canOpen = daysLeft <= 0;
              return (
                <div key={letter.id} className={`bg-white rounded-2xl shadow-sm border ${letter.isOpened ? 'border-green-200' : canOpen ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'} p-5`}>
                  {letter.isOpened ? (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Unlock size={16} className="text-green-500" />
                        <span className="text-xs font-bold text-green-600">Opened {letter.openedAt ? new Date(letter.openedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
                        <span className="text-[10px] text-gray-400 ml-auto">Written {new Date(letter.writtenAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-purple-50/50 rounded-xl p-4 border border-purple-100" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{letter.text}</p>
                      <button onClick={() => setFutureLetters(prev => prev.filter(l => l.id !== letter.id))} className="text-[10px] text-gray-400 hover:text-red-400 mt-2">Delete</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                        {canOpen ? <Unlock size={24} className="text-amber-500" /> : <Lock size={24} className="text-purple-400" />}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-gray-700">{canOpen ? '🎉 Ready to open!' : `Sealed letter`}</div>
                        <div className="text-xs text-gray-400">Written {new Date(letter.writtenAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                        <div className="text-xs mt-1">{canOpen ? <span className="text-amber-600 font-bold">Tap to reveal your letter!</span> : <span className="text-purple-500 font-medium">{daysLeft} day{daysLeft !== 1 ? 's' : ''} until unlock · {letter.unlockDate}</span>}</div>
                      </div>
                      {canOpen ? (
                        <button onClick={() => setFutureLetters(prev => prev.map(l => l.id === letter.id ? { ...l, isOpened: true, openedAt: new Date().toISOString() } : l))} className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 animate-pulse">Open</button>
                      ) : (
                        <button onClick={() => setFutureLetters(prev => prev.filter(l => l.id !== letter.id))} className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {futureLetters.length === 0 && !showLetterForm && (
              <div className="text-center py-16 text-gray-400">
                <Mail size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No letters yet</p>
                <p className="text-xs mt-1">Write a letter to your future self — it stays sealed until the date you choose</p>
              </div>
            )}
          </div>
        )}

        {/* ===== DREAM BOARD ===== */}
        {lifeLabTab === 'dreams' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Heart size={20} className="text-rose-500" /> Dream Board</h2>
              <button onClick={() => setShowDreamForm(!showDreamForm)} className="px-4 py-2 bg-rose-500 text-white rounded-xl text-sm font-medium hover:bg-rose-600 flex items-center gap-1"><Plus size={14} /> Add Dream</button>
            </div>

            {showDreamForm && (
              <div className="bg-white rounded-2xl border border-rose-200 p-4 space-y-3">
                <input type="text" defaultValue="" id="db-title" placeholder="Dream title (e.g., 'New Car', 'Bali Trip')" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
                <input type="url" defaultValue="" id="db-img" placeholder="Image URL (optional)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-rose-200" />
                <div className="grid grid-cols-3 gap-2">
                  <input type="date" id="db-date" defaultValue="" className="px-3 py-2 border border-gray-200 rounded-lg text-xs" />
                  <input type="number" id="db-goal" defaultValue="" placeholder="Savings goal (₹)" className="px-3 py-2 border border-gray-200 rounded-lg text-xs" />
                  <select id="db-cat" defaultValue="experience" className="px-3 py-2 border border-gray-200 rounded-lg text-xs">
                    <option value="career">💼 Career</option><option value="travel">✈️ Travel</option><option value="material">🏠 Material</option><option value="experience">🌟 Experience</option><option value="financial">💰 Financial</option>
                  </select>
                </div>
                <button onClick={() => {
                  const t = document.getElementById('db-title')?.value?.trim();
                  if (!t) return;
                  setDreamBoard(prev => [{ id: Date.now(), title: t, imageUrl: document.getElementById('db-img')?.value?.trim() || '', targetDate: document.getElementById('db-date')?.value || '', savingsGoal: parseFloat(document.getElementById('db-goal')?.value) || 0, currentSaved: 0, category: document.getElementById('db-cat')?.value || 'experience', createdAt: new Date().toISOString() }, ...prev]);
                  setShowDreamForm(false);
                }} className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600">Add Dream</button>
              </div>
            )}

            {/* Dream Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dreamBoard.map(dream => {
                const daysLeft = dream.targetDate ? Math.max(0, Math.ceil((new Date(dream.targetDate) - new Date()) / (1000*60*60*24))) : null;
                const savingsPercent = dream.savingsGoal > 0 ? Math.min(100, Math.round((dream.currentSaved / dream.savingsGoal) * 100)) : 0;
                const catEmoji = { career: '💼', travel: '✈️', material: '🏠', experience: '🌟', financial: '💰' }[dream.category] || '🎯';
                return (
                  <div key={dream.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group">
                    {dream.imageUrl ? (
                      <div className="h-32 bg-gray-100 relative overflow-hidden">
                        <img src={dream.imageUrl} alt={dream.title} className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-3 text-white font-bold text-sm">{dream.title}</div>
                        {daysLeft !== null && <div className="absolute top-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{daysLeft}d left</div>}
                      </div>
                    ) : (
                      <div className="h-20 bg-gradient-to-r from-rose-100 to-pink-100 flex items-center justify-center relative">
                        <span className="text-3xl">{catEmoji}</span>
                        {daysLeft !== null && <div className="absolute top-2 right-2 bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{daysLeft}d</div>}
                      </div>
                    )}
                    <div className="p-3">
                      {!dream.imageUrl && <div className="font-bold text-gray-800 text-sm">{dream.title}</div>}
                      {dream.savingsGoal > 0 && (
                        <div className="mt-2">
                          <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                            <span>{'\u20B9'}{dream.currentSaved.toLocaleString('en-IN')}</span>
                            <span>{'\u20B9'}{dream.savingsGoal.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className="bg-rose-500 rounded-full h-2 transition-all" style={{ width: `${savingsPercent}%` }} />
                          </div>
                          <div className="text-[10px] text-rose-500 font-bold mt-1">{savingsPercent}% saved</div>
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-2">
                        {dream.savingsGoal > 0 && editingDreamId !== dream.id && (
                          <button onClick={() => setEditingDreamId(dream.id)} className="text-[10px] text-blue-500 hover:text-blue-600">+ Update savings</button>
                        )}
                        {editingDreamId === dream.id && (
                          <div className="flex gap-1 items-center">
                            <input type="number" defaultValue={dream.currentSaved} id={`ds-${dream.id}`} className="w-20 px-2 py-1 border border-gray-200 rounded text-xs" />
                            <button onClick={() => {
                              const val = parseFloat(document.getElementById(`ds-${dream.id}`)?.value) || 0;
                              setDreamBoard(prev => prev.map(d => d.id === dream.id ? { ...d, currentSaved: val } : d));
                              setEditingDreamId(null);
                            }} className="text-green-500 hover:text-green-600"><Check size={12} /></button>
                          </div>
                        )}
                        <button onClick={() => setEditingDreamFull(dream.id === editingDreamFull ? null : dream.id)} className="text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit dream"><Edit2 size={12} /></button>
                        <button onClick={() => setDreamBoard(prev => prev.filter(d => d.id !== dream.id))} className="text-gray-300 hover:text-red-400 ml-auto opacity-0 group-hover:opacity-100"><X size={12} /></button>
                      </div>
                      {editingDreamFull === dream.id && (
                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                          <input type="text" defaultValue={dream.title} id={`de-title-${dream.id}`} placeholder="Title" className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-rose-200" />
                          <input type="url" defaultValue={dream.imageUrl || ''} id={`de-img-${dream.id}`} placeholder="Image URL" className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-rose-200" />
                          <div className="grid grid-cols-3 gap-2">
                            <input type="date" defaultValue={dream.targetDate || ''} id={`de-date-${dream.id}`} className="px-2 py-1.5 border border-gray-200 rounded-lg text-[10px]" />
                            <input type="number" defaultValue={dream.savingsGoal || ''} id={`de-goal-${dream.id}`} placeholder="Goal (₹)" className="px-2 py-1.5 border border-gray-200 rounded-lg text-[10px]" />
                            <select defaultValue={dream.category || 'experience'} id={`de-cat-${dream.id}`} className="px-2 py-1.5 border border-gray-200 rounded-lg text-[10px]">
                              <option value="career">💼 Career</option><option value="travel">✈️ Travel</option><option value="material">🏠 Material</option><option value="experience">🌟 Experience</option><option value="financial">💰 Financial</option>
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => {
                              const t = document.getElementById(`de-title-${dream.id}`)?.value?.trim();
                              if (!t) return;
                              setDreamBoard(prev => prev.map(d => d.id === dream.id ? {
                                ...d,
                                title: t,
                                imageUrl: document.getElementById(`de-img-${dream.id}`)?.value?.trim() || '',
                                targetDate: document.getElementById(`de-date-${dream.id}`)?.value || '',
                                savingsGoal: parseFloat(document.getElementById(`de-goal-${dream.id}`)?.value) || 0,
                                category: document.getElementById(`de-cat-${dream.id}`)?.value || 'experience'
                              } : d));
                              setEditingDreamFull(null);
                            }} className="px-3 py-1.5 bg-rose-500 text-white rounded-lg text-[10px] font-bold hover:bg-rose-600">Save</button>
                            <button onClick={() => setEditingDreamFull(null)} className="px-3 py-1.5 text-gray-500 text-[10px] font-bold hover:text-gray-700">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {dreamBoard.length === 0 && !showDreamForm && (
              <div className="text-center py-16 text-gray-400">
                <Heart size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No dreams yet</p>
                <p className="text-xs mt-1">Add your goals and dreams with images, deadlines, and savings targets</p>
              </div>
            )}
          </div>
        )}

        {/* ===== MICRO-ADVENTURE LOG ===== */}
        {lifeLabTab === 'adventures' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Compass size={20} className="text-teal-500" /> Micro-Adventure Log</h2>
              <button onClick={() => setShowAdventureForm(!showAdventureForm)} className="px-4 py-2 bg-teal-500 text-white rounded-xl text-sm font-medium hover:bg-teal-600 flex items-center gap-1"><Plus size={14} /> Log Adventure</button>
            </div>

            {showAdventureForm && (
              <div className="bg-white rounded-2xl border border-teal-200 p-4 space-y-3">
                <input type="text" defaultValue="" id="ma-text" placeholder="What novel thing did you do? (e.g., 'Tried Korean BBQ')" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
                <div className="flex gap-2 flex-wrap">
                  {[['🍔','food'],['✈️','travel'],['👥','social'],['📚','learning'],['🎨','creative'],['🌟','other']].map(([emoji, cat]) => (
                    <button key={cat} id={`ma-cat-${cat}`} onClick={(e) => { document.querySelectorAll('[id^="ma-cat-"]').forEach(b => b.classList.remove('ring-2','ring-teal-400')); e.target.classList.add('ring-2','ring-teal-400'); e.target.dataset.selected = cat; }} className="px-3 py-1.5 bg-gray-50 rounded-lg text-xs hover:bg-teal-50">{emoji} {cat}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Rating:</span>
                  {[1,2,3,4,5].map(r => (
                    <button key={r} id={`ma-r-${r}`} onClick={() => { for(let i=1;i<=5;i++) document.getElementById(`ma-r-${i}`).dataset.selected = i <= r ? 'true' : 'false'; }} data-selected="false" className="text-gray-300 hover:text-amber-400"><Star size={16} /></button>
                  ))}
                  <button onClick={() => {
                    const text = document.getElementById('ma-text')?.value?.trim();
                    if (!text) return;
                    const catBtn = document.querySelector('[id^="ma-cat-"].ring-2');
                    const cat = catBtn?.dataset?.selected || 'other';
                    let rating = 3;
                    for(let i=5;i>=1;i--) { if (document.getElementById(`ma-r-${i}`)?.dataset?.selected === 'true') { rating = i; break; } }
                    setMicroAdventures(prev => [{ id: Date.now(), text, category: cat, date: todayStr, rating }, ...prev]);
                    setShowAdventureForm(false);
                  }} className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 ml-auto">Log It</button>
                </div>
              </div>
            )}

            {/* Stats */}
            {(() => {
              const thisWeekStart = new Date(); thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay() + 1);
              const thisWeekKey = thisWeekStart.toISOString().split('T')[0];
              const thisWeekAdv = microAdventures.filter(a => a.date >= thisWeekKey);
              const categories = {};
              microAdventures.forEach(a => { categories[a.category] = (categories[a.category] || 0) + 1; });
              const topCat = Object.entries(categories).sort(([,a],[,b]) => b - a)[0];
              return (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-teal-50 rounded-xl p-3 text-center border border-teal-100"><div className="text-xl font-black text-teal-600">{microAdventures.length}</div><div className="text-[10px] text-gray-500">Total Adventures</div></div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100"><div className="text-xl font-black text-amber-600">{thisWeekAdv.length}</div><div className="text-[10px] text-gray-500">This Week</div></div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100"><div className="text-xl font-black text-purple-600">{topCat ? topCat[0] : '-'}</div><div className="text-[10px] text-gray-500">Top Category</div></div>
                </div>
              );
            })()}

            {/* Adventure Timeline */}
            {(() => {
              const grouped = {};
              microAdventures.forEach(a => { if (!grouped[a.date]) grouped[a.date] = []; grouped[a.date].push(a); });
              return Object.entries(grouped).sort(([a],[b]) => b.localeCompare(a)).map(([date, adventures]) => (
                <div key={date} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{new Date(date+'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                  {adventures.map(a => {
                    const catEmoji = { food: '🍔', travel: '✈️', social: '👥', learning: '📚', creative: '🎨', other: '🌟' }[a.category] || '🌟';
                    return (
                      <div key={a.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-sm">{catEmoji}</span>
                        <span className="text-sm text-gray-700 flex-1">{a.text}</span>
                        <span className="text-[10px] text-amber-500">{'★'.repeat(a.rating)}{'☆'.repeat(5-a.rating)}</span>
                        <button onClick={() => setMicroAdventures(prev => prev.filter(x => x.id !== a.id))} className="text-gray-300 hover:text-red-400"><X size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}

            {microAdventures.length === 0 && !showAdventureForm && (
              <div className="text-center py-16 text-gray-400">
                <Compass size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No adventures logged</p>
                <p className="text-xs mt-1">Track novel experiences to make your life richer</p>
              </div>
            )}
          </div>
        )}

        {/* ===== LIFE IN WEEKS ===== */}
        {lifeLabTab === 'lifegrid' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><LayoutGrid size={20} className="text-gray-600" /> Your Life in Weeks</h2>

            {/* How it works */}
            <div className="bg-gradient-to-r from-gray-50 to-indigo-50 rounded-2xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">How it works</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-gray-600">
                <div className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">●</span> Each tiny box represents <strong>1 week</strong> of your life</div>
                <div className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">●</span> <strong>80 rows</strong> (years) × <strong>52 columns</strong> (weeks) = <strong>4,160 boxes</strong></div>
                <div className="flex items-start gap-2"><span className="w-2.5 h-2.5 bg-blue-400 rounded-sm shrink-0 mt-0.5" /> <strong>Blue</strong> = weeks already lived (darker shade as you age)</div>
                <div className="flex items-start gap-2"><span className="w-2.5 h-2.5 bg-red-500 rounded-sm shrink-0 mt-0.5 animate-pulse" /> <strong>Red pulse</strong> = your current week — YOU ARE HERE</div>
                <div className="flex items-start gap-2"><span className="w-2.5 h-2.5 bg-gray-100 rounded-sm shrink-0 mt-0.5 border border-gray-200" /> <strong>Gray</strong> = your future weeks — make them count</div>
                <div className="flex items-start gap-2"><span className="text-indigo-400 mt-0.5">●</span> Hover any box to see <strong>Age &amp; Week number</strong></div>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 italic">Inspired by Tim Urban's "Your Life in Weeks" — seeing life as a finite grid makes every week feel more valuable.</p>
            </div>

            {!lifeWeeksBirthDate ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="text-4xl mb-4">📅</div>
                <h3 className="text-lg font-bold text-gray-700 mb-2">Enter your birth date to begin</h3>
                <p className="text-sm text-gray-500 mb-4">Each box = 1 week of your life. 80 years × 52 weeks = 4,160 weeks.</p>
                <input type="date" id="lw-birth" className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 mb-3" />
                <br />
                <button onClick={() => {
                  const val = document.getElementById('lw-birth')?.value;
                  if (val) setLifeWeeksBirthDate(val);
                }} className="px-6 py-2 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-600">Generate My Life Grid</button>
              </div>
            ) : (() => {
              const birth = new Date(lifeWeeksBirthDate);
              const now = new Date();
              const weeksLived = Math.floor((now - birth) / (1000*60*60*24*7));
              const totalWeeks = 80 * 52;
              const weeksLeft = Math.max(0, totalWeeks - weeksLived);
              const percentLived = Math.round((weeksLived / totalWeeks) * 100);
              const currentAge = Math.floor(weeksLived / 52);

              return (
                <div>
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100"><div className="text-lg font-black text-blue-600">{weeksLived.toLocaleString()}</div><div className="text-[10px] text-gray-500">Weeks Lived</div></div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200"><div className="text-lg font-black text-gray-600">{weeksLeft.toLocaleString()}</div><div className="text-[10px] text-gray-500">Weeks Left</div></div>
                    <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100"><div className="text-lg font-black text-indigo-600">{percentLived}%</div><div className="text-[10px] text-gray-500">Through</div></div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100"><div className="text-lg font-black text-amber-600">{currentAge}</div><div className="text-[10px] text-gray-500">Current Age</div></div>
                  </div>

                  {/* Grid */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 overflow-x-auto">
                    <div className="flex items-start gap-0.5 min-w-[700px]">
                      {/* Age labels */}
                      <div className="flex flex-col gap-[1px] shrink-0 mr-1">
                        <div className="h-[6px]" /> {/* spacer for header */}
                        {Array.from({length: 80}, (_, age) => (
                          <div key={age} className="h-[6px] flex items-center">
                            {age % 10 === 0 && <span className="text-[7px] text-gray-400 w-4 text-right leading-none">{age}</span>}
                          </div>
                        ))}
                      </div>
                      {/* Weeks grid */}
                      <div>
                        <div className="flex gap-[1px] mb-0.5">
                          {Array.from({length: 52}, (_, w) => (
                            <div key={w} className="w-[6px] text-center">
                              {w % 13 === 0 && <span className="text-[5px] text-gray-300">{w}</span>}
                            </div>
                          ))}
                        </div>
                        {Array.from({length: 80}, (_, age) => (
                          <div key={age} className="flex gap-[1px] mb-[1px]">
                            {Array.from({length: 52}, (_, week) => {
                              const weekIndex = age * 52 + week;
                              const isCurrent = weekIndex === weeksLived;
                              const isPast = weekIndex < weeksLived;
                              const isDecade = age % 10 === 0 && week === 0;
                              return (
                                <div
                                  key={week}
                                  className={`w-[6px] h-[6px] rounded-[1px] transition-colors ${
                                    isCurrent ? 'bg-red-500 ring-1 ring-red-300 animate-pulse' :
                                    isPast ? (age < 18 ? 'bg-blue-300' : age < 30 ? 'bg-blue-400' : age < 50 ? 'bg-blue-500' : 'bg-blue-600') :
                                    isDecade ? 'bg-gray-200' : 'bg-gray-100'
                                  }`}
                                  title={`Age ${age}, Week ${week + 1} ${isCurrent ? '← YOU ARE HERE' : ''}`}
                                />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-[9px] text-gray-400 justify-center">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-sm" /> Lived</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm" /> Now</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-100 rounded-sm" /> Future</span>
                    </div>
                  </div>
                  <button onClick={() => setLifeWeeksBirthDate(null)} className="text-[10px] text-gray-400 hover:text-red-400 mt-2">Reset birth date</button>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // --- To-Do List View ---
  const TodoListView = () => {

    const addTodo = (text) => {
      if (!text.trim()) return;
      setTodoList(prev => [
        { id: Date.now(), text: text.trim(), completed: false, createdAt: new Date().toISOString() },
        ...prev
      ]);
      setNewTodoText('');
    };

    const toggleTodo = (id) => {
      setTodoList(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
    };

    const deleteTodo = (id) => {
      setTodoList(prev => prev.filter(t => t.id !== id));
    };

    const clearCompleted = () => {
      setTodoList(prev => prev.filter(t => !t.completed));
    };

    const filteredTodos = todoList.filter(t => {
      if (todoFilter === 'active') return !t.completed;
      if (todoFilter === 'completed') return t.completed;
      return true;
    });

    const totalCount = todoList.length;
    const completedCount = todoList.filter(t => t.completed).length;
    const activeCount = totalCount - completedCount;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ListTodo size={32} className="text-blue-500" />
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Master To-Do List</h1>
                <p className="text-gray-400">Everything you need to get done, in one place</p>
              </div>
            </div>
            <div className="flex gap-3 text-sm">
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold text-blue-600">{activeCount}</div>
                <div className="text-gray-500">Active</div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold text-blue-600">{completedCount}</div>
                <div className="text-gray-500">Done</div>
              </div>
            </div>
          </div>

          {/* Progress */}
          {totalCount > 0 && todoViewTab === 'tasks' && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Overall Progress</span>
                <span>{Math.round((completedCount / totalCount) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${(completedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Tabs: Tasks | Wins */}
          <div className="flex gap-2 mt-4 border-b border-gray-100 pb-2">
            {[['tasks', '📋 Tasks'], ['wins', '🏆 Wins']].map(([key, label]) => (
              <button key={key} onClick={() => setTodoViewTab(key)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${todoViewTab === key ? 'bg-blue-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>{label}</button>
            ))}
          </div>
        </div>

        {/* Anti-Todo (Wins) Tab */}
        {todoViewTab === 'wins' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  defaultValue=""
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const winText = e.target.value.trim();
                      setAntiTodo(prev => [{ id: Date.now(), text: winText, date: new Date().toISOString().split('T')[0], category: 'personal' }, ...prev]);
                      e.target.value = '';
                    }
                  }}
                  placeholder="What did you accomplish today? 🎉"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {[['💼','work'],['👤','personal'],['💪','health'],['📚','learning'],['🎨','creative'],['👥','social']].map(([emoji, cat]) => (
                  <button key={cat} onClick={() => {
                    const inp = document.querySelector('[placeholder*="accomplish"]');
                    if (inp && inp.value.trim()) { const winText = inp.value.trim(); setAntiTodo(prev => [{ id: Date.now(), text: winText, date: new Date().toISOString().split('T')[0], category: cat }, ...prev]); inp.value = ''; }
                  }} className="px-2 py-1 text-[10px] bg-gray-50 rounded-lg text-gray-500 hover:bg-amber-50 hover:text-amber-600">{emoji} {cat}</button>
                ))}
              </div>
            </div>

            {/* All wins grouped by date */}
            {(() => {
              const today = new Date();
              const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
              const dow = today.getDay();
              const weekStart = new Date(today);
              weekStart.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
              const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth()+1).padStart(2,'0')}-${String(weekStart.getDate()).padStart(2,'0')}`;
              const thisWeekWins = antiTodo.filter(w => w.date >= weekKey);
              const todayWins = antiTodo.filter(w => w.date === todayKey);

              const allGrouped = {};
              antiTodo.forEach(w => { if (!allGrouped[w.date]) allGrouped[w.date] = []; allGrouped[w.date].push(w); });
              const catEmoji = { work: '💼', personal: '👤', health: '💪', learning: '📚', creative: '🎨', social: '👥' };

              return (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-100 p-4 text-center">
                      <div className="text-2xl font-black text-amber-500">{todayWins.length}</div>
                      <div className="text-[10px] text-gray-500 font-medium">Today</div>
                    </div>
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-100 p-4 text-center">
                      <div className="text-2xl font-black text-amber-500">{thisWeekWins.length}</div>
                      <div className="text-[10px] text-gray-500 font-medium">This Week</div>
                    </div>
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-100 p-4 text-center">
                      <div className="text-2xl font-black text-amber-500">{antiTodo.length}</div>
                      <div className="text-[10px] text-gray-500 font-medium">All Time</div>
                    </div>
                  </div>

                  {/* Wins list grouped by date */}
                  {Object.entries(allGrouped).sort(([a],[b]) => b.localeCompare(a)).map(([date, wins]) => {
                    const isToday = date === todayKey;
                    return (
                      <div key={date} className={`bg-white rounded-2xl shadow-sm border ${isToday ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'} p-4`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {isToday ? '🔥 Today' : new Date(date+'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </div>
                          <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-bold">{wins.length} win{wins.length > 1 ? 's' : ''}</span>
                        </div>
                        {wins.map(w => (
                          <div key={w.id} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
                            <span className="text-green-500 flex-shrink-0"><Check size={14} /></span>
                            <span className="text-sm text-gray-700 flex-1">{w.text}</span>
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{catEmoji[w.category] || '🎯'} {w.category}</span>
                            <button onClick={() => setAntiTodo(prev => prev.filter(t => t.id !== w.id))} className="text-gray-300 hover:text-red-400 flex-shrink-0"><X size={12} /></button>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {antiTodo.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <Trophy size={48} className="mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No wins yet</p>
                      <p className="text-xs mt-1">Log something you accomplished today!</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {todoViewTab === 'tasks' && (
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Add Todo Input */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={newTodoText}
                onChange={(e) => setNewTodoText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTodoText.trim()) {
                    addTodo(newTodoText);
                  }
                }}
                placeholder="What needs to be done?"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
              />
              <button
                onClick={() => addTodo(newTodoText)}
                disabled={!newTodoText.trim()}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center justify-between bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
            <div className="flex gap-1">
              {[
                { key: 'all', label: `All (${totalCount})` },
                { key: 'active', label: `Active (${activeCount})` },
                { key: 'completed', label: `Done (${completedCount})` },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setTodoFilter(f.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${todoFilter === f.key
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {completedCount > 0 && (
              <button
                onClick={clearCompleted}
                className="text-sm text-red-500 hover:text-red-700 font-medium px-3 py-1 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear completed
              </button>
            )}
          </div>

          {/* Todo Items */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {filteredTodos.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <ListTodo size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">
                  {todoFilter === 'completed' ? 'No completed tasks yet' :
                    todoFilter === 'active' ? 'All tasks completed! 🎉' :
                      'Your to-do list is empty'}
                </p>
                <p className="text-sm mt-1">
                  {todoFilter === 'all' ? 'Add your first task above' : ''}
                </p>
              </div>
            ) : (
              filteredTodos.map(todo => (
                <div
                  key={todo.id}
                  className={`flex items-center gap-3 px-5 py-4 group transition-colors ${todo.completed ? 'bg-gray-50/50' : 'hover:bg-emerald-50/30'
                    }`}
                >
                  <button
                    onClick={() => toggleTodo(todo.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${todo.completed
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'border-gray-300 hover:border-emerald-400'
                      }`}
                  >
                    {todo.completed && <Check size={14} />}
                  </button>
                  <span className={`flex-1 text-base ${todo.completed ? 'line-through text-gray-400' : 'text-gray-800'
                    }`}>
                    {todo.text}
                  </span>
                  <button
                    onClick={() => deleteTodo(todo.id)}
                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </div>
    );
  };

  const highlightColors = [
    { name: 'Deadline', value: 'bg-red-400', border: 'border-red-600', text: 'text-red-700', bg: 'bg-red-50' },
    { name: 'Meeting', value: 'bg-orange-400', border: 'border-orange-600', text: 'text-orange-700', bg: 'bg-orange-50' },
    { name: 'Reminder', value: 'bg-yellow-400', border: 'border-yellow-600', text: 'text-yellow-700', bg: 'bg-yellow-50' },
    { name: 'Personal', value: 'bg-green-400', border: 'border-green-600', text: 'text-green-700', bg: 'bg-green-50' },
    { name: 'Work', value: 'bg-blue-400', border: 'border-blue-600', text: 'text-blue-700', bg: 'bg-blue-50' },
    { name: 'Event', value: 'bg-purple-400', border: 'border-purple-600', text: 'text-purple-700', bg: 'bg-purple-50' },
    { name: 'Special', value: 'bg-pink-400', border: 'border-pink-600', text: 'text-pink-700', bg: 'bg-pink-50' },
  ];

  const ProductivityView = () => {
    const [newLogText, setNewLogText] = useState('');
    const [logCategory, setLogCategory] = useState('task');
    const [dashboardTab, setDashboardTab] = useState('today');
    const [selectedPill, setSelectedPill] = useState(null); // 'planned' | 'done' | 'skipped' | null
    const [editingMantra, setEditingMantra] = useState(false);
    const [lifeWheelOpen, setLifeWheelOpen] = useState(false);

    const today = new Date();
    const todayKey = getDayKey(today.getFullYear(), today.getMonth(), today.getDate());
    const currentWeekNum = getCurrentWeekNumber();
    const currentMonthIndex = today.getMonth();
    const dayOfYear = Math.floor((today - new Date(2026, 0, 1)) / (1000 * 60 * 60 * 24)) + 1;
    const yearProgress = Math.min(100, Math.max(0, Math.round((dayOfYear / 365) * 100)));

    // --- Compute week start date for iterating days ---
    const getWeekStartDate = (weekNum) => {
      const startDate = new Date(2026, 0, 1);
      const startDay = startDate.getDay();
      const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
      const firstMonday = new Date(2026, 0, daysToMonday);
      const weekStart = new Date(firstMonday);
      weekStart.setDate(firstMonday.getDate() + (weekNum - 1) * 7);
      return weekStart;
    };

    // --- Today's data ---
    const todaySchedule = getDaySchedule(today.getFullYear(), today.getMonth(), today.getDate());
    const scheduledCount = Object.values(todaySchedule).filter(v => { const t = getScheduleText(v); return t && t.trim(); }).length;
    const currentHour = today.getHours();
    const nextEvent = Object.entries(todaySchedule)
      .filter(([h, v]) => parseInt(h) >= currentHour && getScheduleText(v).trim())
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))[0];

    const todayTasks = getDayTasks(today.getFullYear(), today.getMonth(), today.getDate());
    const allTodayTasks = [...(todayTasks.high || []), ...(todayTasks.medium || []), ...(todayTasks.low || [])];
    const todayTasksDone = allTodayTasks.filter(t => t.completed).length;
    const todayTasksTotal = allTodayTasks.length;

    const todayHabitsDone = habits.filter(h =>
      isHabitCompletedForDay(h.id, today.getFullYear(), today.getMonth(), today.getDate())
    ).length;

    // Day Health Score
    const schedDoneCount = Object.values(todaySchedule).filter(v => normalizeScheduleEntry(v)?.status === 'done').length;
    const schedTotalCount = Object.values(todaySchedule).filter(v => getScheduleText(v).trim()).length;
    const habitsScore = habits.length > 0 ? (todayHabitsDone / habits.length) * 30 : 0;
    const schedScore = schedTotalCount > 0 ? (schedDoneCount / schedTotalCount) * 40 : 0;
    const tasksScore = todayTasksTotal > 0 ? (todayTasksDone / todayTasksTotal) * 30 : 0;
    const healthScore = Math.round(habitsScore + schedScore + tasksScore);
    const healthScoreColor = healthScore >= 90 ? '#10b981' : healthScore >= 70 ? '#22c55e' : healthScore >= 40 ? '#f59e0b' : '#ef4444';
    const healthCirc = 2 * Math.PI * 44;
    const healthDash = (healthScore / 100) * healthCirc;

    const todayLogEntries = productivityLog.filter(e => {
      const d = new Date(e.completedAt);
      return getDayKey(d.getFullYear(), d.getMonth(), d.getDate()) === todayKey;
    });

    const activeTodos = todoList.filter(t => !t.completed).length;

    // --- Weekly data ---
    const weekGoals = getWeekData(currentWeekNum).goals;
    const weekGoalsDone = weekGoals.filter(g => g.completed).length;
    const weekGoalsTotal = weekGoals.length;
    const weekGoalRate = weekGoalsTotal > 0 ? Math.round((weekGoalsDone / weekGoalsTotal) * 100) : 0;

    const getWeekDayTasks = () => {
      const ws = getWeekStartDate(currentWeekNum);
      let totalTasks = 0, completedTasks = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(ws);
        d.setDate(ws.getDate() + i);
        const dt = getDayTasks(d.getFullYear(), d.getMonth(), d.getDate());
        const all = [...(dt.high || []), ...(dt.medium || []), ...(dt.low || [])];
        totalTasks += all.length;
        completedTasks += all.filter(t => t.completed).length;
      }
      return { totalTasks, completedTasks };
    };
    const weekTaskStats = getWeekDayTasks();

    const getWeekHabitStats = () => {
      const ws = getWeekStartDate(currentWeekNum);
      const totalChecks = habits.length * 7;
      let doneChecks = 0;
      Array.from({ length: 7 }).forEach((_, i) => {
        const d = new Date(ws);
        d.setDate(ws.getDate() + i);
        habits.forEach(h => {
          if (isHabitCompletedForDay(h.id, d.getFullYear(), d.getMonth(), d.getDate())) doneChecks++;
        });
      });
      return { totalChecks, doneChecks, rate: totalChecks > 0 ? Math.round((doneChecks / totalChecks) * 100) : 0 };
    };
    const weekHabitStats = getWeekHabitStats();

    // Previous week comparison
    const prevWeekGoals = currentWeekNum > 1 ? getWeekData(currentWeekNum - 1).goals : [];
    const prevWeekGoalRate = prevWeekGoals.length > 0
      ? Math.round((prevWeekGoals.filter(g => g.completed).length / prevWeekGoals.length) * 100) : 0;


    // --- Monthly data ---
    const mGoals = getMonthlyGoals(today.getFullYear(), currentMonthIndex);
    const mGoalsDone = mGoals.filter(g => g.completed).length;

    // Weekly trends for this month (which weeks fall in this month)
    const getMonthWeeklyTrends = () => {
      const trends = [];
      for (let w = 1; w <= 52; w++) {
        const ws = getWeekStartDate(w);
        if (ws.getMonth() === currentMonthIndex && ws.getFullYear() === 2026) {
          const wg = getWeekData(w).goals;
          const total = wg.length;
          const done = wg.filter(g => g.completed).length;
          trends.push({ week: w, total, done, rate: total > 0 ? Math.round((done / total) * 100) : 0 });
        }
      }
      return trends;
    };
    const monthWeeklyTrends = getMonthWeeklyTrends();

    // Monthly habit heatmap
    const getMonthHabitHeatmap = () => {
      const daysInMonth = new Date(2026, currentMonthIndex + 1, 0).getDate();
      const cells = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const total = habits.length;
        const done = habits.filter(h => isHabitCompletedForDay(h.id, 2026, currentMonthIndex, d)).length;
        cells.push({ day: d, done, total, rate: total > 0 ? Math.round((done / total) * 100) : 0 });
      }
      return cells;
    };
    const monthHabitHeatmap = getMonthHabitHeatmap();

    // --- Month schedule performance (did vs didn't do) ---
    const getMonthScheduleStats = () => {
      const daysInMonth = new Date(2026, currentMonthIndex + 1, 0).getDate();
      let planned = 0, done = 0, skipped = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const sched = getDaySchedule(2026, currentMonthIndex, d);
        // eslint-disable-next-line no-loop-func
        Object.values(sched).forEach(val => {
          const entry = normalizeScheduleEntry(val);
          if (entry && entry.text && entry.text.trim()) {
            planned++;
            if (entry.status === 'done') done++;
            else if (entry.status === 'skipped') skipped++;
          }
        });
      }
      const attempted = done + skipped;
      const ratio = attempted > 0 ? Math.round((done / attempted) * 100) : null;
      return { planned, done, skipped, attempted, ratio };
    };
    const monthSchedStats = getMonthScheduleStats();

    const getMonthScheduleItems = () => {
      const daysInMonth = new Date(2026, currentMonthIndex + 1, 0).getDate();
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const items = { planned: [], done: [], skipped: [] };
      for (let d = 1; d <= daysInMonth; d++) {
        const sched = getDaySchedule(2026, currentMonthIndex, d);
        Object.entries(sched).forEach(([key, val]) => {
          const entry = normalizeScheduleEntry(val);
          if (entry && entry.text && entry.text.trim()) {
            const { hour, minute } = parseScheduleKey(key);
            const h12 = hour % 12 || 12;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const minStr = minute ? `:${String(minute).padStart(2, '0')}` : ':00';
            const timeLabel = `${h12}${minStr} ${ampm}`;
            const dateLabel = `${monthNames[currentMonthIndex]} ${d}`;
            const item = { text: entry.text.trim(), time: timeLabel, date: dateLabel, day: d };
            items.planned.push(item);
            if (entry.status === 'done') items.done.push(item);
            else if (entry.status === 'skipped') items.skipped.push({ ...item, isSkipped: true });
          }
        });
      }
      return items;
    };

    // --- Motivational stats ---
    const getActiveWeeks = () => {
      let count = 0;
      for (let w = 1; w <= 52; w++) {
        const wd = getWeekData(w);
        if (wd.goals.length > 0 || wd.milestones.length > 0 || wd.noteEntries.length > 0) count++;
      }
      return count;
    };
    const activeWeeks = getActiveWeeks();

    const getAllGoalRate = () => {
      let total = 0, done = 0;
      for (let w = 1; w <= 52; w++) {
        const g = getWeekData(w).goals;
        total += g.length;
        done += g.filter(x => x.completed).length;
      }
      return total > 0 ? Math.round((done / total) * 100) : 0;
    };
    const overallGoalRate = getAllGoalRate();

    const longestHabitStreak = habits.length > 0
      ? Math.max(...habits.map(h => getHabitStreak(h.id)))
      : 0;

    const getMotivationalMessage = () => {
      if (overallGoalRate >= 80) return { text: "You're crushing it! Keep the momentum going.", icon: Flame, color: 'text-orange-500' };
      if (overallGoalRate >= 50) return { text: "Great progress! You're on track to hit your goals.", icon: TrendingUp, color: 'text-blue-500' };
      if (overallGoalRate >= 25) return { text: "Building momentum. Every completed task counts!", icon: Activity, color: 'text-green-500' };
      if (activeWeeks > 0) return { text: "Keep going! Consistency beats perfection.", icon: Target, color: 'text-blue-500' };
      return { text: "Start small, stay consistent. You've got this!", icon: Award, color: 'text-purple-500' };
    };
    const motivation = getMotivationalMessage();

    // Achievement badges
    const badges = [];
    if (weekGoals.some(g => g.completed)) badges.push({ label: 'Goal Getter', desc: 'Completed a weekly goal', icon: Target, color: 'bg-green-100 text-green-700' });
    if (weekGoalsDone >= 5) badges.push({ label: 'Week Warrior', desc: '5+ goals completed this week', icon: Award, color: 'bg-yellow-100 text-yellow-700' });
    if (longestHabitStreak >= 7) badges.push({ label: 'Streak Master', desc: '7+ day habit streak', icon: Flame, color: 'bg-orange-100 text-orange-700' });
    if (activeWeeks >= 4) badges.push({ label: 'Consistent', desc: '4+ active weeks', icon: BarChart3, color: 'bg-blue-100 text-blue-700' });
    if (todayTasksDone >= 3) badges.push({ label: 'Productive Day', desc: '3+ tasks done today', icon: Zap, color: 'bg-purple-100 text-purple-700' });

    // Group log entries by date
    const groupLogByDate = () => {
      const groups = {};
      productivityLog.forEach(entry => {
        const d = new Date(entry.completedAt);
        const key = getDayKey(d.getFullYear(), d.getMonth(), d.getDate());
        if (!groups[key]) groups[key] = { date: key, entries: [] };
        groups[key].entries.push(entry);
      });
      return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
    };

    const categoryColors = {
      task: 'bg-blue-100 text-blue-700',
      commit: 'bg-purple-100 text-purple-700',
      milestone: 'bg-amber-100 text-amber-700',
      other: 'bg-gray-100 text-gray-600'
    };

    const categoryLabels = {
      task: 'Task',
      commit: 'Code',
      milestone: 'Milestone',
      other: 'Other'
    };

    const formatTime = (iso) => {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateLabel = (dateKey) => {
      if (dateKey === todayKey) return 'Today';
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const yKey = getDayKey(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      if (dateKey === yKey) return 'Yesterday';
      const parts = dateKey.split('-');
      const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const getPatterns = () => {
      const hourBuckets = {};
      const dowBuckets = {};
      Object.entries(daySchedule).forEach(([dk, sched]) => {
        const parts = dk.split('-');
        if (parts.length !== 3) return;
        const [y, m, d] = parts.map(Number);
        const dow = new Date(y, m - 1, d).getDay();
        Object.entries(sched).forEach(([key, val]) => {
          const entry = normalizeScheduleEntry(val);
          if (!entry?.text?.trim()) return;
          const { hour } = parseScheduleKey(key);
          if (!hourBuckets[hour]) hourBuckets[hour] = { planned: 0, skipped: 0 };
          hourBuckets[hour].planned++;
          if (entry.status === 'skipped') hourBuckets[hour].skipped++;
          if (!dowBuckets[dow]) dowBuckets[dow] = { planned: 0, skipped: 0 };
          dowBuckets[dow].planned++;
          if (entry.status === 'skipped') dowBuckets[dow].skipped++;
        });
      });
      const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const worstHour = Object.entries(hourBuckets)
        .filter(([,s]) => s.planned >= 3)
        .sort(([,a],[,b]) => (b.skipped / b.planned) - (a.skipped / a.planned))[0];
      const bestHour = Object.entries(hourBuckets)
        .filter(([,s]) => s.planned >= 3)
        .sort(([,a],[,b]) => (a.skipped / a.planned) - (b.skipped / b.planned))[0];
      const worstDay = Object.entries(dowBuckets)
        .filter(([,s]) => s.planned >= 3)
        .sort(([,a],[,b]) => (b.skipped / b.planned) - (a.skipped / a.planned))[0];
      return { worstHour, bestHour, worstDay, dayNames };
    };

    return (
      <div className="space-y-4">
        {/* Header with year progress */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800">
                <TrendingUp size={22} className="text-blue-500" /> Productivity Dashboard
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Day {dayOfYear} of 365 — {yearProgress}% through the year
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-blue-500">{yearProgress}%</div>
              <div className="text-xs text-gray-400">Year Progress</div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-500 rounded-full h-2.5 transition-all duration-700"
              style={{ width: `${yearProgress}%` }}
            />
          </div>
          <div className="flex gap-4 mt-3">
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 text-xs text-blue-700">
              <span className="font-bold">{todayTasksDone}/{todayTasksTotal}</span> tasks today
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 text-xs text-blue-700">
              <span className="font-bold">{todayHabitsDone}/{habits.length}</span> habits today
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 text-xs text-blue-700">
              <span className="font-bold">{weekGoalRate}%</span> weekly goals
            </div>
            {longestHabitStreak > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 text-xs text-blue-700">
                <Flame size={12} className="inline mr-1" /><span className="font-bold">{longestHabitStreak}</span> day streak
              </div>
            )}
          </div>
        </div>

        {/* Sub-tab navigation */}
        <div className="flex gap-2">
          {[
            { key: 'today', label: 'Today', icon: Clock },
            { key: 'week', label: 'This Week', icon: CalendarRange },
            { key: 'month', label: 'This Month', icon: Calendar },
            { key: 'summary', label: 'Summary', icon: FileText },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setDashboardTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                dashboardTab === tab.key
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
              }`}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {dashboardTab === 'today' && (
          <div className="space-y-4">
            {/* Personal Mantra */}
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-indigo-100 rounded-xl px-5 py-4 flex items-center gap-3">
              <span className="text-xl flex-shrink-0">✨</span>
              {editingMantra ? (
                <input
                  autoFocus
                  type="text"
                  defaultValue={userMantra}
                  onBlur={e => { setUserMantra(e.target.value.trim()); setEditingMantra(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingMantra(false); }}
                  placeholder="Your mantra, quote, or intention..."
                  className="flex-1 text-sm font-medium italic text-indigo-700 bg-transparent border-b border-indigo-300 focus:outline-none py-0.5"
                />
              ) : (
                <p
                  onClick={() => setEditingMantra(true)}
                  className={`flex-1 text-sm font-medium italic cursor-text select-none ${userMantra ? 'text-indigo-700' : 'text-indigo-300 hover:text-indigo-400'}`}
                >
                  {userMantra || 'Click to add your personal mantra...'}
                </p>
              )}
              {!editingMantra && (
                <button onClick={() => setEditingMantra(true)} className="text-indigo-300 hover:text-indigo-500 flex-shrink-0">
                  <Edit2 size={13} />
                </button>
              )}
            </div>
            {/* Day Health Score */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm flex items-center gap-6">
              <div className="relative flex-shrink-0">
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="44" fill="none"
                    stroke={healthScoreColor}
                    strokeWidth="8"
                    strokeDasharray={`${healthDash} ${healthCirc}`}
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dasharray 0.6s ease', filter: healthScore >= 90 ? `drop-shadow(0 0 6px ${healthScoreColor})` : 'none' }}
                  />
                  <text x="50" y="45" textAnchor="middle" dominantBaseline="middle" fontSize="20" fontWeight="900" fill={healthScoreColor}>{healthScore}</text>
                  <text x="50" y="62" textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#9ca3af">/ 100</text>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-800 mb-2">Day Health Score</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-20 text-[11px] text-gray-500">Habits</div>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-400 rounded-full" style={{ width: `${habits.length > 0 ? (todayHabitsDone / habits.length) * 100 : 0}%` }} />
                    </div>
                    <div className="text-[11px] text-gray-500 w-8 text-right">{habits.length > 0 ? Math.round((todayHabitsDone / habits.length) * 100) : 0}%</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 text-[11px] text-gray-500">Schedule</div>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${schedTotalCount > 0 ? (schedDoneCount / schedTotalCount) * 100 : 0}%` }} />
                    </div>
                    <div className="text-[11px] text-gray-500 w-8 text-right">{schedTotalCount > 0 ? Math.round((schedDoneCount / schedTotalCount) * 100) : 0}%</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 text-[11px] text-gray-500">Tasks</div>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full" style={{ width: `${todayTasksTotal > 0 ? (todayTasksDone / todayTasksTotal) * 100 : 0}%` }} />
                    </div>
                    <div className="text-[11px] text-gray-500 w-8 text-right">{todayTasksTotal > 0 ? Math.round((todayTasksDone / todayTasksTotal) * 100) : 0}%</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Quick Health Log */}
            {(() => {
              const todayHealth = getDayHealth(today.getFullYear(), today.getMonth(), today.getDate());
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">💪</span>
                    <span className="font-bold text-gray-800 text-sm">Quick Health Log</span>
                    <span className="text-[10px] text-gray-400 ml-auto">Today's snapshot</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { key: 'water', icon: '💧', label: 'Water', unit: 'glasses', color: 'text-blue-600', bg: 'bg-blue-50' },
                      { key: 'steps', icon: '👟', label: 'Steps', unit: 'steps', color: 'text-green-600', bg: 'bg-green-50' },
                      { key: 'calories', icon: '🔥', label: 'Calories', unit: 'kcal', color: 'text-orange-600', bg: 'bg-orange-50' },
                    ].map(({ key, icon, label, unit, color, bg }) => (
                      <div key={key} className={`${bg} rounded-xl p-3 text-center`}>
                        <div className="text-xl mb-1">{icon}</div>
                        <div className={`text-xl font-black ${color}`}>{todayHealth[key]}</div>
                        <div className="text-[10px] text-gray-500 mb-2">{unit}</div>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => updateDayHealth(today.getFullYear(), today.getMonth(), today.getDate(), key, todayHealth[key] - (key === 'steps' ? 1000 : key === 'calories' ? 100 : 1))}
                            className="w-6 h-6 rounded-full bg-white shadow-sm text-gray-500 hover:text-gray-800 text-sm font-bold flex items-center justify-center"
                          >−</button>
                          <button
                            onClick={() => updateDayHealth(today.getFullYear(), today.getMonth(), today.getDate(), key, todayHealth[key] + (key === 'steps' ? 1000 : key === 'calories' ? 100 : 1))}
                            className="w-6 h-6 rounded-full bg-white shadow-sm text-gray-500 hover:text-gray-800 text-sm font-bold flex items-center justify-center"
                          >+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {/* Gratitude Log */}
            {(() => {
              const grStreak = getGratitudeStreak();
              const todayGr = getDayGratitude(today.getFullYear(), today.getMonth(), today.getDate());
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">🙏</span>
                    <span className="font-bold text-gray-800 text-sm flex-1">Gratitude Log</span>
                    {grStreak > 0 && (
                      <span className="text-[10px] bg-rose-50 text-rose-600 px-2 py-1 rounded-full font-semibold">🔥 {grStreak}d streak</span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-rose-400 text-sm flex-shrink-0 w-4">{i + 1}.</span>
                        <input
                          type="text"
                          defaultValue={todayGr[i] || ''}
                          placeholder={i === 0 ? 'I am grateful for...' : i === 1 ? 'Something that made me smile...' : 'A person I appreciate...'}
                          onBlur={e => updateGratitudeEntry(today.getFullYear(), today.getMonth(), today.getDate(), i, e.target.value.trim())}
                          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                          className="flex-1 text-sm text-gray-700 border-b border-gray-200 focus:border-rose-300 focus:outline-none py-1.5 bg-transparent placeholder-gray-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {/* Today's overview cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Schedule card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Clock size={16} className="text-blue-500" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">Schedule</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">{scheduledCount}</div>
                <div className="text-xs text-gray-400">events planned</div>
                {nextEvent && (
                  <div className="mt-2 text-xs bg-blue-50 text-blue-500 px-2 py-1 rounded-md truncate">
                    Next: {getScheduleText(nextEvent[1])} at {parseInt(nextEvent[0]) > 12 ? `${parseInt(nextEvent[0]) - 12}PM` : `${parseInt(nextEvent[0])}AM`}
                  </div>
                )}
              </div>

              {/* Tasks card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                    <Check size={16} className="text-green-500" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">Tasks</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">{todayTasksDone}<span className="text-sm text-gray-400 font-normal">/{todayTasksTotal}</span></div>
                <div className="text-xs text-gray-400">completed</div>
                {todayTasksTotal > 0 && (
                  <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-green-500 rounded-full h-1.5 transition-all" style={{ width: `${Math.round((todayTasksDone / todayTasksTotal) * 100)}%` }} />
                  </div>
                )}
              </div>

              {/* Habits card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                    <Flame size={16} className="text-orange-500" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">Habits</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">{todayHabitsDone}<span className="text-sm text-gray-400 font-normal">/{habits.length}</span></div>
                <div className="text-xs text-gray-400">tracked</div>
                {habits.length > 0 && (
                  <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-orange-500 rounded-full h-1.5 transition-all" style={{ width: `${Math.round((todayHabitsDone / habits.length) * 100)}%` }} />
                  </div>
                )}
              </div>

              {/* To-Do card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                    <ListTodo size={16} className="text-purple-500" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">To-Do</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">{activeTodos}</div>
                <div className="text-xs text-gray-400">pending items</div>
                {todayLogEntries.length > 0 && (
                  <div className="mt-2 text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-md">
                    {todayLogEntries.length} logged today
                  </div>
                )}
              </div>
            </div>

            {/* Today's habits detail */}
            {habits.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Flame size={15} className="text-orange-500" /> Today's Habits
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {habits.map(h => {
                    const done = isHabitCompletedForDay(h.id, today.getFullYear(), today.getMonth(), today.getDate());
                    return (
                      <div key={h.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${done ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                        <div className={`w-3 h-3 rounded-full ${done ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={done ? 'line-through' : ''}>{h.name}</span>
                        {done && <Check size={14} className="ml-auto text-green-500" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Today's task breakdown */}
            {todayTasksTotal > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Zap size={15} className="text-yellow-500" /> Today's Task Breakdown
                </h3>
                <div className="space-y-2">
                  {[
                    { label: 'High Energy', tasks: todayTasks.high || [], color: 'bg-red-500', bg: 'bg-red-50' },
                    { label: 'Medium Energy', tasks: todayTasks.medium || [], color: 'bg-yellow-500', bg: 'bg-yellow-50' },
                    { label: 'Low Energy', tasks: todayTasks.low || [], color: 'bg-green-500', bg: 'bg-green-50' },
                  ].filter(g => g.tasks.length > 0).map(group => {
                    const done = group.tasks.filter(t => t.completed).length;
                    return (
                      <div key={group.label} className="flex items-center gap-3">
                        <div className={`w-2 h-8 rounded-full ${group.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-gray-600">{group.label}</span>
                            <span className="text-gray-400">{done}/{group.tasks.length}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                            <div className={`${group.color} rounded-full h-1.5 transition-all`} style={{ width: `${group.tasks.length > 0 ? Math.round((done / group.tasks.length) * 100) : 0}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {dashboardTab === 'week' && (
          <div className="space-y-4">
            {/* Weekly goals progress */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Target size={15} className="text-blue-500" /> Week {currentWeekNum} Goals
                </h3>
                <span className="text-xs text-gray-400">{getWeekDateRange(currentWeekNum)}</span>
              </div>
              {weekGoalsTotal > 0 ? (
                <>
                  <div className="flex items-center gap-4 mb-3">
                    {/* Ring chart */}
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#3B82F6" strokeWidth="3"
                          strokeDasharray={`${weekGoalRate} ${100 - weekGoalRate}`}
                          strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-800">{weekGoalRate}%</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-1">{weekGoalsDone} of {weekGoalsTotal} goals completed</div>
                      {currentWeekNum > 1 && (
                        <div className={`text-xs ${weekGoalRate >= prevWeekGoalRate ? 'text-green-600' : 'text-red-500'}`}>
                          {weekGoalRate >= prevWeekGoalRate ? '↑' : '↓'} {Math.abs(weekGoalRate - prevWeekGoalRate)}% vs last week
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {weekGoals.map(g => (
                      <div key={g.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${g.completed ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'}`}>
                        <div className={`w-2 h-2 rounded-full ${g.completed ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={g.completed ? 'line-through' : ''}>{g.text}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">No goals set for this week. Head to the Week view to add some!</div>
              )}
            </div>

            {/* Weekly stats grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
                <div className="text-2xl font-bold text-gray-800">{weekTaskStats.completedTasks}<span className="text-sm text-gray-400 font-normal">/{weekTaskStats.totalTasks}</span></div>
                <div className="text-xs text-gray-400 mt-1">Tasks Done</div>
                {weekTaskStats.totalTasks > 0 && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                    <div className="bg-blue-500 rounded-full h-1.5" style={{ width: `${Math.round((weekTaskStats.completedTasks / weekTaskStats.totalTasks) * 100)}%` }} />
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
                <div className="text-2xl font-bold text-gray-800">{weekHabitStats.rate}%</div>
                <div className="text-xs text-gray-400 mt-1">Habit Rate</div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                  <div className="bg-orange-500 rounded-full h-1.5" style={{ width: `${weekHabitStats.rate}%` }} />
                </div>
              </div>
            </div>

            {/* Weekly Health Totals */}
            {(() => {
              const ws = getWeekStartDate(currentWeekNum);
              let totalWater = 0, totalSteps = 0, totalCalories = 0, trackedDays = 0;
              for (let i = 0; i < 7; i++) {
                const d = new Date(ws); d.setDate(ws.getDate() + i);
                const h = getDayHealth(d.getFullYear(), d.getMonth(), d.getDate());
                if (h.water > 0 || h.steps > 0 || h.calories > 0) trackedDays++;
                totalWater += h.water;
                totalSteps += h.steps;
                totalCalories += h.calories;
              }
              if (trackedDays === 0) return null;
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-base">💪</span>
                    <span className="font-bold text-gray-800 text-sm">Weekly Health</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{trackedDays}/7 days logged</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-blue-50 rounded-xl p-3">
                      <div className="text-xl font-black text-blue-700">{totalWater}</div>
                      <div className="text-[10px] text-blue-500">💧 glasses</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3">
                      <div className="text-xl font-black text-green-700">{totalSteps.toLocaleString()}</div>
                      <div className="text-[10px] text-green-500">👟 steps</div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-3">
                      <div className="text-xl font-black text-orange-700">{totalCalories.toLocaleString()}</div>
                      <div className="text-[10px] text-orange-500">🔥 kcal</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Week habits per-day breakdown */}
            {habits.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Weekly Habit Tracker</h3>
                <div className="space-y-2">
                  {habits.map(h => {
                    const ws = getWeekStartDate(currentWeekNum);
                    const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
                    return (
                      <div key={h.id} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-24 truncate">{h.name}</span>
                        <div className="flex gap-1">
                          {dayLabels.map((dl, i) => {
                            const d = new Date(ws);
                            d.setDate(ws.getDate() + i);
                            const done = isHabitCompletedForDay(h.id, d.getFullYear(), d.getMonth(), d.getDate());
                            return (
                              <div key={i} className="flex flex-col items-center gap-0.5">
                                <span className="text-[9px] text-gray-400">{dl}</span>
                                <div className={`w-5 h-5 rounded-md ${done ? 'bg-green-500' : 'bg-gray-100'}`} />
                              </div>
                            );
                          })}
                        </div>
                        <span className="text-xs text-gray-400 ml-auto">{getHabitStreak(h.id)}d streak</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {dashboardTab === 'month' && (
          <div className="space-y-4">
            {/* Monthly goals */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Target size={15} className="text-blue-500" /> {monthNames[currentMonthIndex]} Goals
              </h3>
              {mGoals.length > 0 ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                      <div className="bg-blue-500 rounded-full h-2.5 transition-all" style={{ width: `${mGoals.length > 0 ? Math.round((mGoalsDone / mGoals.length) * 100) : 0}%` }} />
                    </div>
                    <span className="text-sm font-medium text-gray-600">{mGoalsDone}/{mGoals.length}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {mGoals.map(g => (
                      <div key={g.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${g.completed ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'}`}>
                        <div className={`w-2 h-2 rounded-full ${g.completed ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={g.completed ? 'line-through' : ''}>{g.text}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-gray-400 text-sm">No monthly goals set. Add them in the Monthly view!</div>
              )}
            </div>

            {/* Weekly trends bar chart */}
            {monthWeeklyTrends.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <BarChart3 size={15} className="text-blue-500" /> Weekly Goal Completion
                </h3>
                <div className="flex items-end gap-2 h-24">
                  {monthWeeklyTrends.map(t => (
                    <div key={t.week} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-500">{t.rate}%</span>
                      <div className="w-full bg-gray-100 rounded-t-md relative" style={{ height: '80px' }}>
                        <div
                          className={`absolute bottom-0 w-full rounded-t-md transition-all ${t.week === currentWeekNum ? 'bg-blue-500' : 'bg-blue-300'}`}
                          style={{ height: `${Math.max(t.rate, 4)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-gray-400">W{t.week}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly habit heatmap */}
            {habits.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Activity size={15} className="text-green-500" /> Habit Consistency — {monthNames[currentMonthIndex]}
                </h3>
                <div className="flex flex-wrap gap-1">
                  {monthHabitHeatmap.map(cell => {
                    const opacity = cell.rate === 0 ? 0 : Math.max(0.2, cell.rate / 100);
                    const isPast = cell.day <= today.getDate();
                    return (
                      <div
                        key={cell.day}
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[10px] font-medium relative group"
                        style={{
                          backgroundColor: isPast
                            ? (cell.rate > 0 ? `rgba(34, 197, 94, ${opacity})` : '#fef2f2')
                            : '#f9fafb',
                          color: isPast ? (cell.rate > 60 ? 'white' : '#374151') : '#d1d5db'
                        }}
                        title={`Day ${cell.day}: ${cell.done}/${cell.total} habits (${cell.rate}%)`}
                      >
                        {cell.day}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#fef2f2' }} /> 0%</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(34,197,94,0.3)' }} /> Low</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(34,197,94,0.6)' }} /> Mid</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(34,197,94,1)' }} /> 100%</span>
                </div>
              </div>
            )}

            {/* Schedule Performance — Did vs Didn't Do */}
            {(() => {
              const s = monthSchedStats;
              const ratio = s.ratio;
              const motivation =
                ratio === null ? { text: "No scheduled tasks tracked yet — start planning your days!", color: 'text-gray-500', emoji: '📋' }
                : ratio >= 90 ? { text: "Outstanding! You're crushing your schedule this month.", color: 'text-green-600', emoji: '🔥' }
                : ratio >= 75 ? { text: "Great momentum! A few more follow-throughs and you'll be unstoppable.", color: 'text-blue-600', emoji: '💪' }
                : ratio >= 60 ? { text: "Solid effort — push through the rough patches and keep building.", color: 'text-blue-500', emoji: '📈' }
                : ratio >= 40 ? { text: "More than half done. Consistency is the key — one day at a time.", color: 'text-amber-600', emoji: '🌱' }
                : { text: "Tough stretch, but showing up matters. Recalibrate and go again.", color: 'text-orange-600', emoji: '💫' };

              const doneWidth = s.attempted > 0 ? (s.done / s.planned) * 100 : 0;
              const skippedWidth = s.attempted > 0 ? (s.skipped / s.planned) * 100 : 0;

              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <SkipForward size={18} className="text-orange-500" />
                    <span className="font-bold text-gray-800">Schedule Performance</span>
                    {ratio !== null && (
                      <span className={`ml-auto text-2xl font-black ${ratio >= 70 ? 'text-green-600' : ratio >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                        {ratio}% <span className="text-sm font-medium text-gray-400">completion</span>
                      </span>
                    )}
                  </div>

                  {/* Stat pills — clickable */}
                  {(() => {
                    const pillConfig = [
                      { key: 'planned', label: 'Planned', count: s.planned, bg: 'bg-blue-50', border: 'border-blue-100', activeBorder: 'border-blue-400', numColor: 'text-blue-700', labelColor: 'text-blue-500' },
                      { key: 'done', label: 'Done', count: s.done, bg: 'bg-green-50', border: 'border-green-100', activeBorder: 'border-green-400', numColor: 'text-green-700', labelColor: 'text-green-500' },
                      { key: 'skipped', label: "Didn't Do", count: s.skipped, bg: 'bg-orange-50', border: 'border-orange-100', activeBorder: 'border-orange-400', numColor: 'text-orange-600', labelColor: 'text-orange-500' },
                    ];
                    const pillItems = selectedPill ? getMonthScheduleItems()[selectedPill] : [];
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          {pillConfig.map(p => (
                            <button
                              key={p.key}
                              onClick={() => setSelectedPill(prev => prev === p.key ? null : p.key)}
                              className={`${p.bg} border-2 ${selectedPill === p.key ? p.activeBorder : p.border} rounded-lg p-3 text-center transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98]`}
                            >
                              <div className={`text-xl font-black ${p.numColor}`}>{p.count}</div>
                              <div className={`text-[10px] font-semibold ${p.labelColor} uppercase tracking-wide`}>{p.label}</div>
                              <div className="text-[9px] text-gray-400 mt-0.5">{selectedPill === p.key ? 'tap to close ▲' : 'tap to see ▼'}</div>
                            </button>
                          ))}
                        </div>
                        {selectedPill && (
                          <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                            <div className={`px-3 py-2 text-xs font-semibold flex items-center justify-between ${
                              selectedPill === 'planned' ? 'bg-blue-50 text-blue-700' :
                              selectedPill === 'done' ? 'bg-green-50 text-green-700' :
                              'bg-orange-50 text-orange-700'
                            }`}>
                              <span>
                                {selectedPill === 'planned' ? '📋 All Planned Items' :
                                 selectedPill === 'done' ? '✅ Completed Items' :
                                 "⏭️ Didn't Do Items"}
                                {' '}({pillItems.length})
                              </span>
                              <button onClick={() => setSelectedPill(null)} className="text-gray-400 hover:text-gray-600 text-base leading-none">×</button>
                            </div>
                            <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                              {pillItems.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-4">No items in this category yet.</p>
                              ) : (
                                pillItems.map((item, idx) => (
                                  <div key={idx} className="flex items-start gap-3 px-3 py-2">
                                    <div className="flex-shrink-0 text-center w-14">
                                      <div className="text-[10px] text-gray-400">{item.date}</div>
                                      <div className={`text-[10px] font-semibold ${
                                        selectedPill === 'done' ? 'text-green-600' :
                                        selectedPill === 'skipped' ? 'text-orange-500' :
                                        'text-blue-500'
                                      }`}>{item.time}</div>
                                    </div>
                                    <div className="flex-1 text-sm text-gray-700 py-0.5">{item.text}</div>
                                    {selectedPill === 'done' && <span className="text-green-500 text-xs flex-shrink-0 mt-0.5">✓</span>}
                                    {selectedPill === 'skipped' && <span className="text-orange-500 text-xs flex-shrink-0 mt-0.5">⏭</span>}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Bar: done (green) + skipped (orange) + untracked (gray) */}
                  {s.planned > 0 && (
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex mb-3">
                      <div className="h-full bg-green-400 rounded-l-full transition-all" style={{ width: `${doneWidth}%` }} />
                      <div className="h-full bg-orange-400 transition-all" style={{ width: `${skippedWidth}%` }} />
                    </div>
                  )}

                  {/* Legend + motivation */}
                  <div className="flex items-center gap-4 text-[10px] text-gray-400 mb-3">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" /> Done</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Didn't do</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-200 inline-block" /> Untracked</span>
                  </div>
                  <p className={`text-sm font-medium ${motivation.color}`}>{motivation.emoji} {motivation.text}</p>
                </div>
              );
            })()}
            {/* Pattern Detection */}
            {(() => {
              const { worstHour, bestHour, worstDay, dayNames } = getPatterns();
              const insights = [];
              if (worstHour) {
                const h = parseInt(worstHour[0]);
                const rate = Math.round((worstHour[1].skipped / worstHour[1].planned) * 100);
                insights.push({ icon: '⏰', text: `Tasks at ${h % 12 || 12}${h >= 12 ? 'PM' : 'AM'} have a ${rate}% skip rate — try scheduling them earlier` });
              }
              if (worstDay) {
                const rate = Math.round((worstDay[1].skipped / worstDay[1].planned) * 100);
                insights.push({ icon: '📅', text: `${dayNames[parseInt(worstDay[0])]} is your toughest day (${rate}% skipped) — plan lighter` });
              }
              if (bestHour) {
                const h = parseInt(bestHour[0]);
                const rate = Math.round(((bestHour[1].planned - bestHour[1].skipped) / bestHour[1].planned) * 100);
                insights.push({ icon: '✅', text: `${h % 12 || 12}${h >= 12 ? 'PM' : 'AM'} is your peak slot — ${rate}% completion rate` });
              }
              if (insights.length === 0) return <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm text-xs text-gray-400 text-center">Complete more schedule blocks to see your patterns.</div>;
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={16} className="text-amber-500" />
                    <span className="font-bold text-gray-800 text-sm">Your Patterns</span>
                  </div>
                  <div className="space-y-2.5">
                    {insights.map((ins, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="flex-shrink-0 text-base">{ins.icon}</span>
                        <span>{ins.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {/* Life Wheel Check-in */}
            {(() => {
              const lifeAreas = ['Career','Health','Finance','Fun','Relationships','Growth','Creativity','Spirituality'];
              const monthKey = `2026-${String(currentMonthIndex + 1).padStart(2, '0')}`;
              const ratings = lifeWheel[monthKey] || {};
              const n = lifeAreas.length;
              const cx = 110, cy = 110, maxR = 80;
              const angle = (i) => (2 * Math.PI * i / n) - Math.PI / 2;
              const ptX = (i, r) => cx + r * Math.cos(angle(i));
              const ptY = (i, r) => cy + r * Math.sin(angle(i));
              const gridPoints = (frac) => lifeAreas.map((_, i) => `${ptX(i, maxR * frac)},${ptY(i, maxR * frac)}`).join(' ');
              const dataPoints = lifeAreas.map((a, i) => `${ptX(i, maxR * ((ratings[a.toLowerCase()] || 0) / 10))},${ptY(i, maxR * ((ratings[a.toLowerCase()] || 0) / 10))}`).join(' ');
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">🎡</span>
                    <span className="font-bold text-gray-800 text-sm flex-1">Life Wheel — {new Date(2026, currentMonthIndex).toLocaleString('default', { month: 'long' })}</span>
                    <button onClick={() => setLifeWheelOpen(v => !v)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium">
                      {lifeWheelOpen ? 'Hide ▲' : 'Rate ▼'}
                    </button>
                  </div>
                  <div className="flex justify-center mb-4">
                    <svg width="220" height="220" viewBox="0 0 220 220">
                      {[0.2, 0.4, 0.6, 0.8, 1].map(frac => (
                        <polygon key={frac} points={gridPoints(frac)} fill="none" stroke="#e5e7eb" strokeWidth="1" />
                      ))}
                      {lifeAreas.map((_, i) => (
                        <line key={i} x1={cx} y1={cy} x2={ptX(i, maxR)} y2={ptY(i, maxR)} stroke="#e5e7eb" strokeWidth="1" />
                      ))}
                      {dataPoints.includes('NaN') ? null : (
                        <polygon points={dataPoints} fill="rgba(99,102,241,0.2)" stroke="#6366f1" strokeWidth="2" />
                      )}
                      {lifeAreas.map((area, i) => {
                        const lx = ptX(i, maxR + 20);
                        const ly = ptY(i, maxR + 20);
                        return (
                          <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#6b7280" fontWeight="600">
                            {area}
                          </text>
                        );
                      })}
                    </svg>
                  </div>
                  {lifeWheelOpen && (
                    <div className="grid grid-cols-2 gap-3">
                      {lifeAreas.map(area => {
                        const key = area.toLowerCase();
                        const val = ratings[key] || 0;
                        return (
                          <div key={area} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600 font-medium">{area}</span>
                              <span className="text-xs font-bold text-indigo-600">{val}/10</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="10"
                              value={val}
                              onChange={e => setLifeWheel(prev => ({
                                ...prev,
                                [monthKey]: { ...(prev[monthKey] || {}), [key]: parseInt(e.target.value) }
                              }))}
                              className="w-full h-1.5 accent-indigo-500 cursor-pointer"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {dashboardTab === 'summary' && (
          <SummaryView />
        )}

        {/* Motivational section (always visible) */}
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center ${motivation.color}`}>
              <motivation.icon size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">{motivation.text}</p>
              <div className="flex gap-3 mt-1 text-xs text-gray-400">
                <span>{activeWeeks} active weeks</span>
                <span>{overallGoalRate}% goal rate</span>
                {longestHabitStreak > 0 && <span>{longestHabitStreak} day best streak</span>}
              </div>
            </div>
          </div>

          {/* Badges */}
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {badges.map((b, i) => (
                <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${b.color}`}>
                  <b.icon size={13} />
                  {b.label}
                </div>
              ))}
            </div>
          )}
          {badges.length === 0 && (
            <div className="text-xs text-gray-400 italic">Complete goals, build streaks, and track habits to earn achievement badges!</div>
          )}
        </div>

        {/* Done Log / Work Completion Tracker */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Check size={15} className="text-green-500" /> Work Log
          </h3>

          {/* Quick-add form */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newLogText}
              onChange={(e) => setNewLogText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newLogText.trim()) {
                  addProductivityLogEntry(newLogText, logCategory);
                  setNewLogText('');
                }
              }}
              placeholder="What did you finish?"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <select
              value={logCategory}
              onChange={(e) => setLogCategory(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="task">Task</option>
              <option value="commit">Code</option>
              <option value="milestone">Milestone</option>
              <option value="other">Other</option>
            </select>
            <button
              onClick={() => {
                if (newLogText.trim()) {
                  addProductivityLogEntry(newLogText, logCategory);
                  setNewLogText('');
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Log entries grouped by date */}
          {productivityLog.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {groupLogByDate().map(group => (
                <div key={group.date}>
                  <div className="text-xs font-medium text-gray-400 mb-1.5">{formatDateLabel(group.date)}</div>
                  <div className="space-y-1">
                    {group.entries.map(entry => (
                      <div key={entry.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${categoryColors[entry.category]}`}>
                          {categoryLabels[entry.category]}
                        </span>
                        <span className="text-sm text-gray-700 flex-1">{entry.text}</span>
                        <span className="text-[10px] text-gray-400">{formatTime(entry.completedAt)}</span>
                        <button
                          onClick={() => deleteProductivityLogEntry(entry.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 text-sm">
              No entries yet. Log your completed work to track progress!
            </div>
          )}
        </div>
      </div>
    );
  };


  // ========== EXPENSE MANAGER VIEW (disabled for client version) ==========
  // eslint-disable-next-line no-unused-vars
  const ExpenseManagerView = () => {
    const activeTab = expenseActiveTab;
    const setActiveTab = setExpenseActiveTab;
    const selectedMonth = expenseSelectedMonth;
    const setSelectedMonth = setExpenseSelectedMonth;
    const chatInputRef = React.useRef(null);
    const [showAddIncome, setShowAddIncome] = useState(false);
    const [showAddExpense, setShowAddExpense] = useState(false);
    const [showAddCard, setShowAddCard] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceTranscript, setVoiceTranscript] = useState('');
    const [voiceError, setVoiceError] = useState('');
    const [smsText, setSmsText] = useState('');
    const [parsedSmartLog, setParsedSmartLog] = useState(null);
    // Statement Import Wizard
    const [stmtWizardStep, setStmtWizardStep] = useState(0); // 0=closed, 1=Upload, 2=Review, 3=Adjust, 4=Import
    const [stmtRawText, setStmtRawText] = useState('');
    const [stmtParsedRows, setStmtParsedRows] = useState([]);
    const [stmtSelected, setStmtSelected] = useState({});
    const [stmtAdjustments, setStmtAdjustments] = useState([]);
    const [stmtAdjustInput, setStmtAdjustInput] = useState('');
    const [stmtDateRange, setStmtDateRange] = useState({ from: null, to: null });
    const [expenseTypeFilter, setExpenseTypeFilter] = useState('all');
    const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('all');
    const [cardDetailView, setCardDetailView] = useState(null);
    const [cardCycleOffset, setCardCycleOffset] = useState({});
    const [payingCardId, setPayingCardId] = useState(null);
    const [payBillAmount, setPayBillAmount] = useState('');
    const [showAddEmi, setShowAddEmi] = useState(null);
    const [chatDuplicateWarning, setChatDuplicateWarning] = useState(null);
    const [selectedHeatmapDay, setSelectedHeatmapDay] = useState(null);
    const [editingCatInline, setEditingCatInline] = useState(null);
    const showTransactionsPage = expenseShowTxnPage;
    const setShowTransactionsPage = setExpenseShowTxnPage;
    const [txnSearchQuery, setTxnSearchQuery] = useState('');
    const [txnPaymentFilter, setTxnPaymentFilter] = useState('all');
    const [txnEditItem, setTxnEditItem] = useState(null);
    const [editingIncome, setEditingIncome] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    // Credit Card Statement Import
    const [ccStmtCard, setCcStmtCard] = useState(null);
    const [ccStmtText, setCcStmtText] = useState('');
    const [ccStmtRows, setCcStmtRows] = useState([]);
    const [ccStmtSelected, setCcStmtSelected] = useState({});
    const [ccStmtStep, setCcStmtStep] = useState(0); // 0=closed, 1=paste, 2=review
    const [ccPdfPassword, setCcPdfPassword] = useState('');
    const [ccPdfPasswordNeeded, setCcPdfPasswordNeeded] = useState(false);
    const [ccPdfLoading, setCcPdfLoading] = useState(false);
    const [ccPdfError, setCcPdfError] = useState('');
    const [ccPdfFile, setCcPdfFile] = useState(null);

    const EXPENSE_CATEGORIES = [
      { key: 'housing', label: 'Housing', emoji: '🏠', color: 'bg-blue-100 text-blue-700' },
      { key: 'groceries', label: 'Groceries', emoji: '🛒', color: 'bg-green-100 text-green-700' },
      { key: 'dining', label: 'Dining', emoji: '🍔', color: 'bg-orange-100 text-orange-700' },
      { key: 'transport', label: 'Transport', emoji: '🚗', color: 'bg-yellow-100 text-yellow-700' },
      { key: 'entertainment', label: 'Entertainment', emoji: '🎬', color: 'bg-purple-100 text-purple-700' },
      { key: 'shopping', label: 'Shopping', emoji: '🛍️', color: 'bg-pink-100 text-pink-700' },
      { key: 'utilities', label: 'Utilities', emoji: '⚡', color: 'bg-amber-100 text-amber-700' },
      { key: 'health', label: 'Health', emoji: '🏥', color: 'bg-red-100 text-red-700' },
      { key: 'education', label: 'Education', emoji: '📚', color: 'bg-indigo-100 text-indigo-700' },
      { key: 'subscriptions', label: 'Subscriptions', emoji: '🔄', color: 'bg-cyan-100 text-cyan-700' },
      { key: 'insurance', label: 'Insurance', emoji: '🛡️', color: 'bg-teal-100 text-teal-700' },
      { key: 'emi', label: 'EMI/Loans', emoji: '🏦', color: 'bg-slate-100 text-slate-700' },
      { key: 'personal', label: 'Personal', emoji: '👤', color: 'bg-rose-100 text-rose-700' },
      { key: 'gifts', label: 'Gifts', emoji: '🎁', color: 'bg-fuchsia-100 text-fuchsia-700' },
      { key: 'other', label: 'Other', emoji: '📌', color: 'bg-gray-100 text-gray-600' },
    ];
    const INCOME_CATEGORIES = [
      { key: 'salary', label: 'Salary', emoji: '💼' },
      { key: 'freelance', label: 'Freelance', emoji: '💻' },
      { key: 'investment', label: 'Investment', emoji: '📈' },
      { key: 'rental', label: 'Rental', emoji: '🏠' },
      { key: 'refund', label: 'Refund', emoji: '↩️' },
      { key: 'other', label: 'Other', emoji: '💰' },
    ];
    const PAYMENT_METHODS = [
      { key: 'cash', label: 'Cash', emoji: '💵' },
      { key: 'upi', label: 'UPI', emoji: '📱' },
      { key: 'debit', label: 'Debit Card', emoji: '💳' },
      { key: 'credit', label: 'Credit Card', emoji: '💳' },
      { key: 'netbanking', label: 'Net Banking', emoji: '🏦' },
      { key: 'wallet', label: 'Wallet', emoji: '👛' },
    ];
    const CARD_COLORS = ['#1e40af','#059669','#d97706','#dc2626','#7c3aed','#db2777','#0891b2','#4f46e5'];

    const allExpenseCategories = [...EXPENSE_CATEGORIES, ...(expenseData.customExpenseCategories || [])];
    const allIncomeCategories = [...INCOME_CATEGORIES, ...(expenseData.customIncomeCategories || [])];
    const getCatInfo = (key) => allExpenseCategories.find(c => c.key === key) || EXPENSE_CATEGORIES[14];
    const getIncomeCatInfo = (key) => allIncomeCategories.find(c => c.key === key) || INCOME_CATEGORIES[5];
    const getPaymentInfo = (key) => PAYMENT_METHODS.find(p => p.key === key) || PAYMENT_METHODS[0];

    const emMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const monthKey = `${selectedMonth.year}-${String(selectedMonth.month + 1).padStart(2, '0')}`;
    const prevMonth = () => { setSelectedMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { ...p, month: p.month - 1 }); setCardCycleOffset({}); };
    const nextMonth = () => { setSelectedMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { ...p, month: p.month + 1 }); setCardCycleOffset({}); };

    const getMonthExpenses = (mk) => (expenseData.expenses || []).filter(e => e.date && e.date.startsWith(mk));
    const getMonthIncome = (mk) => (expenseData.income || []).filter(i => i.date && i.date.startsWith(mk));
    const curExpenses = getMonthExpenses(monthKey);
    const curSpending = curExpenses.filter(e => !e.isRepayment);
    const curIncome = getMonthIncome(monthKey);
    const totalExpenses = curSpending.reduce((s, e) => s + (e.amount || 0), 0);
    const totalIncome = curIncome.reduce((s, i) => s + (i.amount || 0), 0);
    const savings = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? Math.round((savings / totalIncome) * 100) : 0;
    const getCategoryTotals = (exps) => { const t = {}; exps.forEach(e => { t[e.category] = (t[e.category] || 0) + (e.amount || 0); }); return t; };
    const catTotals = getCategoryTotals(curSpending);
    const healthScore = totalIncome > 0 ? Math.max(0, Math.min(100, Math.round((1 - totalExpenses / totalIncome) * 100))) : 0;

    const getUpcomingBills = () => {
      const today = new Date();
      const todayDay = today.getDate();
      return (expenseData.expenses || []).filter(e => e.recurring).map(e => {
        let daysUntil;
        if (e.nextDueDate) {
          const due = new Date(e.nextDueDate);
          daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
          if (daysUntil < 0) daysUntil += 30;
        } else {
          const recurDay = parseInt(e.date?.split('-')[2]) || 1;
          daysUntil = recurDay - todayDay; if (daysUntil < 0) daysUntil += 30;
        }
        return { ...e, daysUntil };
      }).sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 5);
    };
    const getCardBillingCycle = (card, targetMonth = null, offset = 0) => {
      const bd = card.billingDate || 15;
      let endMonth, endYear;
      if (targetMonth && typeof targetMonth === 'object') {
        // Target month mode: cycle ending in targetMonth (statement date falls in targetMonth)
        // endMonth = targetMonth.month - 1 so cycleStart is in prev month, cycleEnd in targetMonth
        endMonth = targetMonth.month - 1 + offset;
        endYear = targetMonth.year;
      } else {
        // Legacy offset mode (from current date)
        const legacyOffset = (typeof targetMonth === 'number') ? targetMonth : 0;
        const now = new Date();
        endMonth = now.getMonth() + legacyOffset;
        endYear = now.getFullYear();
        const refDay = now.getDate();
        if (refDay < bd && legacyOffset === 0) { endMonth -= 1; }
      }
      while (endMonth < 0) { endMonth += 12; endYear -= 1; }
      while (endMonth > 11) { endMonth -= 12; endYear += 1; }
      // Cycle: bd+1 to next bd+1. Filter uses >= start && < end.
      // e.g. HDFC bd=14: cycleStart=Dec 15, cycleEnd=Jan 15 → Dec 15 to Jan 14 inclusive
      // For imported CC txns, ccStatementMonth overrides date-range filtering
      const cycleStart = new Date(endYear, endMonth, bd + 1);
      let nextMonth = endMonth + 1;
      let nextYear = endYear;
      if (nextMonth > 11) { nextMonth = 0; nextYear += 1; }
      const cycleEnd = new Date(nextYear, nextMonth, bd + 1);
      return { cycleStart, cycleEnd };
    };
    // For the Cards tab: given a selectedMonth, return the statement month whose payment is DUE in selectedMonth
    const getCardDueBasedMonth = (card, month) => {
      const bd = card.billingDate || 15;
      const dd = card.dueDate || 5;
      if (dd <= bd) {
        // Due falls in the month AFTER statement, so look back 1 month to find the statement cycle
        let m = month.month - 1, y = month.year;
        if (m < 0) { m = 11; y -= 1; }
        return { month: m, year: y };
      }
      return month;
    };
    // Get statement month key from cycle end date (e.g. cycleEnd in Jan → "2026-01")
    const getCycleStmtKey = (cycleEnd) => `${cycleEnd.getFullYear()}-${String(cycleEnd.getMonth()+1).padStart(2,'0')}`;
    const getCardSpend = (cardId, targetMonth = null) => {
      const card = (expenseData.creditCards || []).find(c => c.id === cardId);
      if (!card) return 0;
      const { cycleStart, cycleEnd } = getCardBillingCycle(card, targetMonth);
      const stmtKey = getCycleStmtKey(cycleEnd);
      return (expenseData.expenses || []).filter(e => {
        if (e.creditCardId !== cardId) return false;
        if (e.isRepayment) return false;
        // Use ccStatementMonth for imported txns (handles billing-date edge cases)
        if (e.ccStatementMonth) return e.ccStatementMonth === stmtKey;
        const d = new Date(e.date);
        return d >= cycleStart && d < cycleEnd;
      }).reduce((s, e) => s + (e.isCredit ? -(e.amount || 0) : (e.amount || 0)), 0);
    };

    // Compute Total Amount Due for a card at a given cycle (cumulative balance)
    const getStatementDue = (cardId, targetMonth = null) => {
      const card = (expenseData.creditCards || []).find(c => c.id === cardId);
      if (!card) return 0;
      const { cycleEnd } = getCardBillingCycle(card, targetMonth);
      const stmtKey = getCycleStmtKey(cycleEnd);
      const prevBal = card.previousBalance || 0;
      const matched = (expenseData.expenses || []).filter(e => {
        if (e.creditCardId !== cardId) return false;
        if (e.ccStatementMonth) return e.ccStatementMonth <= stmtKey;
        const d = new Date(e.date);
        return d < cycleEnd;
      });
      const sum = matched.reduce((s, e) => {
        if (e.isRepayment || e.isCredit) return s - (e.amount || 0);
        return s + (e.amount || 0);
      }, 0);
      const debits = matched.filter(e=>!e.isRepayment&&!e.isCredit).reduce((s,e)=>s+(e.amount||0),0);
      const credits = matched.filter(e=>e.isCredit&&!e.isRepayment).reduce((s,e)=>s+(e.amount||0),0);
      const repay = matched.filter(e=>e.isRepayment).reduce((s,e)=>s+(e.amount||0),0);
      console.log(`[StmtDue] ${card.name} stmtKey=${stmtKey} prevBal=${prevBal} txns=${matched.length} debits=₹${debits.toFixed(2)} credits=₹${credits.toFixed(2)} repay=₹${repay.toFixed(2)} sum=₹${sum.toFixed(2)} due=₹${(prevBal+sum).toFixed(2)}`);
      return prevBal + sum;
    };

    // CRUD — with bidirectional sync to weekly finance
    const syncToWeekRevenue = (id, amount, description, date) => {
      const weekNum = getWeekNumForDate(new Date(date));
      setWeekData(prev => {
        const current = getWeekDataFromPrev(prev, weekNum);
        const newEntry = { id, amount, description: description || '', date, timestamp: new Date().toISOString(), source: 'expense-manager' };
        const newEntries = [newEntry, ...current.revenueEntries];
        return { ...prev, [weekNum]: { ...current, revenueEntries: newEntries, revenue: newEntries.reduce((s, e) => s + e.amount, 0) } };
      });
    };
    const syncToWeekExpense = (id, amount, description, date) => {
      const weekNum = getWeekNumForDate(new Date(date));
      setWeekData(prev => {
        const current = getWeekDataFromPrev(prev, weekNum);
        const newEntry = { id, amount, description: description || '', date, timestamp: new Date().toISOString(), source: 'expense-manager' };
        const newEntries = [newEntry, ...current.expenseEntries];
        return { ...prev, [weekNum]: { ...current, expenseEntries: newEntries, expenses: newEntries.reduce((s, e) => s + e.amount, 0) } };
      });
    };
    const removeFromWeek = (id, date, type) => {
      const weekNum = getWeekNumForDate(new Date(date));
      const field = type === 'revenue' ? 'revenueEntries' : 'expenseEntries';
      const totalField = type === 'revenue' ? 'revenue' : 'expenses';
      setWeekData(prev => {
        const current = getWeekDataFromPrev(prev, weekNum);
        const newEntries = current[field].filter(e => e.id !== id);
        return { ...prev, [weekNum]: { ...current, [field]: newEntries, [totalField]: newEntries.reduce((s, e) => s + e.amount, 0) } };
      });
    };
    const updateInWeek = (id, oldDate, newFields, type) => {
      const field = type === 'revenue' ? 'revenueEntries' : 'expenseEntries';
      const totalField = type === 'revenue' ? 'revenue' : 'expenses';
      const oldWeek = getWeekNumForDate(new Date(oldDate));
      const newDate = newFields.date || oldDate;
      const newWeek = getWeekNumForDate(new Date(newDate));
      if (oldWeek === newWeek) {
        setWeekData(prev => {
          const current = getWeekDataFromPrev(prev, oldWeek);
          const newEntries = current[field].map(e => e.id === id ? { ...e, amount: newFields.amount ?? e.amount, description: newFields.description ?? newFields.source ?? e.description, date: newDate } : e);
          return { ...prev, [oldWeek]: { ...current, [field]: newEntries, [totalField]: newEntries.reduce((s, e) => s + e.amount, 0) } };
        });
      } else {
        removeFromWeek(id, oldDate, type);
        if (type === 'revenue') syncToWeekRevenue(id, newFields.amount, newFields.source || newFields.description, newDate);
        else syncToWeekExpense(id, newFields.amount, newFields.description, newDate);
      }
    };

    const addIncome = (entry) => {
      const entryId = Date.now();
      setExpenseData(prev => ({ ...prev, income: [{ ...entry, id: entryId, createdAt: new Date().toISOString() }, ...(prev.income || [])] }));
      syncToWeekRevenue(entryId, entry.amount, entry.source, entry.date);
      setShowAddIncome(false); setEditingIncome(null);
    };
    const updateIncome = (id, updatedFields) => {
      const existing = (expenseData.income || []).find(i => i.id === id);
      setExpenseData(prev => ({ ...prev, income: (prev.income || []).map(i => i.id === id ? { ...i, ...updatedFields } : i) }));
      if (existing) updateInWeek(id, existing.date, updatedFields, 'revenue');
      setEditingIncome(null); setShowAddIncome(false);
    };
    const deleteIncome = (id) => {
      const existing = (expenseData.income || []).find(i => i.id === id);
      setExpenseData(prev => ({ ...prev, income: (prev.income || []).filter(i => i.id !== id) }));
      if (existing) removeFromWeek(id, existing.date, 'revenue');
    };
    const addExpense = (entry) => {
      const entryId = Date.now();
      setExpenseData(prev => ({ ...prev, expenses: [{ ...entry, id: entryId, createdAt: new Date().toISOString() }, ...(prev.expenses || [])] }));
      syncToWeekExpense(entryId, entry.amount, entry.description, entry.date);
      setShowAddExpense(false); setEditingExpense(null);
    };
    const updateExpenseInline = (id, updatedFields) => {
      const existing = (expenseData.expenses || []).find(e => e.id === id);
      setExpenseData(prev => ({ ...prev, expenses: (prev.expenses || []).map(e => e.id === id ? { ...e, ...updatedFields } : e) }));
      if (existing) updateInWeek(id, existing.date, updatedFields, 'expenses');
    };
    const updateExpense = (id, updatedFields) => {
      updateExpenseInline(id, updatedFields);
      setEditingExpense(null); setShowAddExpense(false);
    };
    const deleteExpense = (id) => {
      const existing = (expenseData.expenses || []).find(e => e.id === id);
      setExpenseData(prev => ({ ...prev, expenses: (prev.expenses || []).filter(e => e.id !== id) }));
      if (existing) removeFromWeek(id, existing.date, 'expenses');
    };
    const addCreditCard = (card) => { setExpenseData(prev => ({ ...prev, creditCards: [...(prev.creditCards || []), { ...card, id: Date.now() }] })); setShowAddCard(false); };
    const deleteCreditCard = (id) => { setExpenseData(prev => ({ ...prev, creditCards: (prev.creditCards || []).filter(c => c.id !== id) })); };
    const addCustomExpenseCategory = (label, emoji) => { const key = 'custom_' + label.toLowerCase().replace(/\s+/g, '_'); setExpenseData(prev => ({ ...prev, customExpenseCategories: [...(prev.customExpenseCategories || []), { key, label, emoji, color: 'bg-violet-100 text-violet-700' }] })); };
    const addCustomIncomeCategory = (label, emoji) => { const key = 'custom_' + label.toLowerCase().replace(/\s+/g, '_'); setExpenseData(prev => ({ ...prev, customIncomeCategories: [...(prev.customIncomeCategories || []), { key, label, emoji }] })); };

    // --- Month Plan helpers ---
    const getOrCreateMonthPlan = (mk) => {
      const plans = expenseData.monthlyPlans || {};
      if (plans[mk]) return plans[mk];
      // Auto-populate from recurring entries
      const recurringIncome = (expenseData.income || []).filter(i => i.recurring);
      let plannedIncome;
      if (recurringIncome.length > 0) {
        plannedIncome = recurringIncome.map(i => ({
          id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category, isRecurring: true
        }));
      } else {
        // Fallback: pull from previous month's actual income as templates
        const [y, m] = mk.split('-').map(Number);
        const prevM = m === 1 ? 12 : m - 1;
        const prevY = m === 1 ? y - 1 : y;
        const prevMk = `${prevY}-${String(prevM).padStart(2, '0')}`;
        const prevIncome = (expenseData.income || []).filter(i => i.date && i.date.startsWith(prevMk));
        if (prevIncome.length > 0) {
          plannedIncome = prevIncome.map(i => ({
            id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category || 'salary', isRecurring: false
          }));
        } else {
          // Last fallback: pull from ALL income entries (unique sources, latest amount)
          const allIncome = expenseData.income || [];
          const sourceMap = {};
          allIncome.forEach(i => {
            if (!sourceMap[i.source] || i.date > sourceMap[i.source].date) sourceMap[i.source] = i;
          });
          plannedIncome = Object.values(sourceMap).map(i => ({
            id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category || 'salary', isRecurring: false
          }));
        }
      }
      const plannedExpenses = (expenseData.expenses || []).filter(e => e.recurring && !e.isRepayment).map(e => ({
        id: Date.now() + Math.random(), description: e.description || e.merchant || '', amount: e.amount, category: e.category, isRecurring: true
      }));
      const plan = { plannedIncome, plannedExpenses };
      setExpenseData(prev => ({ ...prev, monthlyPlans: { ...(prev.monthlyPlans || {}), [mk]: plan } }));
      return plan;
    };
    const updateMonthPlan = (mk, updater) => {
      setExpenseData(prev => {
        const plans = { ...(prev.monthlyPlans || {}) };
        plans[mk] = updater(plans[mk] || { plannedIncome: [], plannedExpenses: [] });
        return { ...prev, monthlyPlans: plans };
      });
    };
    const addPlannedIncome = (mk, item) => updateMonthPlan(mk, p => ({ ...p, plannedIncome: [...p.plannedIncome, { ...item, id: Date.now() + Math.random() }] }));
    const deletePlannedIncome = (mk, id) => updateMonthPlan(mk, p => ({ ...p, plannedIncome: p.plannedIncome.filter(i => i.id !== id) }));
    const addPlannedExpense = (mk, item) => updateMonthPlan(mk, p => ({ ...p, plannedExpenses: [...p.plannedExpenses, { ...item, id: Date.now() + Math.random() }] }));
    const deletePlannedExpense = (mk, id) => updateMonthPlan(mk, p => ({ ...p, plannedExpenses: p.plannedExpenses.filter(e => e.id !== id) }));
    const updatePlannedItem = (mk, type, id, updates) => updateMonthPlan(mk, p => ({
      ...p,
      [type]: p[type].map(item => item.id === id ? { ...item, ...updates } : item)
    }));
    const resetMonthPlan = (mk) => {
      const recurringIncome = (expenseData.income || []).filter(i => i.recurring);
      let plannedIncome;
      if (recurringIncome.length > 0) {
        plannedIncome = recurringIncome.map(i => ({
          id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category, isRecurring: true
        }));
      } else {
        const [y, m] = mk.split('-').map(Number);
        const prevM = m === 1 ? 12 : m - 1;
        const prevY = m === 1 ? y - 1 : y;
        const prevMk = `${prevY}-${String(prevM).padStart(2, '0')}`;
        const prevIncome = (expenseData.income || []).filter(i => i.date && i.date.startsWith(prevMk));
        if (prevIncome.length > 0) {
          plannedIncome = prevIncome.map(i => ({
            id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category || 'salary', isRecurring: false
          }));
        } else {
          const allIncome = expenseData.income || [];
          const sourceMap = {};
          allIncome.forEach(i => { if (!sourceMap[i.source] || i.date > sourceMap[i.source].date) sourceMap[i.source] = i; });
          plannedIncome = Object.values(sourceMap).map(i => ({
            id: Date.now() + Math.random(), source: i.source, amount: i.amount, category: i.category || 'salary', isRecurring: false
          }));
        }
      }
      const plannedExpenses = (expenseData.expenses || []).filter(e => e.recurring && !e.isRepayment).map(e => ({
        id: Date.now() + Math.random(), description: e.description || e.merchant || '', amount: e.amount, category: e.category, isRecurring: true
      }));
      updateMonthPlan(mk, p => ({ plannedIncome, plannedExpenses, coverSteps: p.coverSteps || [] }));
    };
    const addCoverStep = (mk, step) => updateMonthPlan(mk, p => ({ ...p, coverSteps: [...(p.coverSteps || []), { ...step, id: Date.now() + Math.random(), resolved: false }] }));
    const deleteCoverStep = (mk, id) => updateMonthPlan(mk, p => ({ ...p, coverSteps: (p.coverSteps || []).filter(s => s.id !== id) }));
    const toggleCoverStep = (mk, id) => updateMonthPlan(mk, p => ({ ...p, coverSteps: (p.coverSteps || []).map(s => s.id === id ? { ...s, resolved: !s.resolved } : s) }));

    // One-time dedup: remove duplicate credit cards and re-link transactions
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.ccCardsDeduplicated) return;
      const tataNeuId = 1740100000000;
      const hdfcId = 1740200000000;
      const cards = expenseData.creditCards || [];
      if (cards.length <= 2 && cards.every(c => c.id === tataNeuId || c.id === hdfcId)) {
        // Already clean, just set flag and rename
        setExpenseData(prev => ({
          ...prev,
          creditCards: (prev.creditCards || []).map(c => c.id === tataNeuId ? { ...c, name: 'Tata Neu', lastFour: '8621' } : c),
          settings: { ...prev.settings, ccCardsDeduplicated: true },
        }));
        return;
      }
      // Find old duplicate card IDs (not the canonical ones)
      const oldTataNeuCards = cards.filter(c => c.id !== tataNeuId && (c.name?.toLowerCase().includes('tata') || c.lastFour === '8621' || c.lastFour === '8691'));
      const oldHdfcCards = cards.filter(c => c.id !== hdfcId && (c.name?.toLowerCase().includes('hdfc') || c.lastFour === '2274'));
      const oldTataNeuIds = oldTataNeuCards.map(c => c.id);
      const oldHdfcIds = oldHdfcCards.map(c => c.id);
      // Canonical card definitions
      const tataNeuCard = { id: tataNeuId, name: 'Tata Neu', lastFour: '8621', limit: 80000, billingDate: 1, dueDate: 21, color: '#7c3aed' };
      const hdfcCard = { id: hdfcId, name: 'HDFC Money Back', lastFour: '2274', limit: 80000, billingDate: 14, dueDate: 5, color: '#2563eb' };
      setExpenseData(prev => {
        // Re-link transactions from old card IDs to canonical IDs
        const expenses = (prev.expenses || []).map(e => {
          if (oldTataNeuIds.includes(e.creditCardId)) return { ...e, creditCardId: tataNeuId };
          if (oldHdfcIds.includes(e.creditCardId)) return { ...e, creditCardId: hdfcId };
          return e;
        });
        // Keep only canonical cards (remove duplicates)
        let cleanCards = (prev.creditCards || []).filter(c => c.id !== tataNeuId && c.id !== hdfcId && !oldTataNeuIds.includes(c.id) && !oldHdfcIds.includes(c.id));
        cleanCards = [tataNeuCard, hdfcCard, ...cleanCards];
        return { ...prev, creditCards: cleanCards, expenses, settings: { ...prev.settings, ccCardsDeduplicated: true } };
      });
    }, [isLoaded, expenseData?.settings?.ccCardsDeduplicated]); // eslint-disable-line react-hooks/exhaustive-deps

    // One-time: fix Tata Neu due date from 20 to 21
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.ccDueDateFixed) return;
      setExpenseData(prev => ({
        ...prev,
        creditCards: (prev.creditCards || []).map(c => c.id === 1740100000000 ? { ...c, dueDate: 21 } : c),
        settings: { ...prev.settings, ccDueDateFixed: true }
      }));
    }, [isLoaded, expenseData?.settings?.ccDueDateFixed]); // eslint-disable-line react-hooks/exhaustive-deps

    // One-time: clear cached monthly plans with empty income so they re-populate with fallback logic
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.planIncomeFix) return;
      const plans = expenseData.monthlyPlans || {};
      const hasEmptyPlans = Object.values(plans).some(p => !p.plannedIncome || p.plannedIncome.length === 0);
      if (hasEmptyPlans || Object.keys(plans).length > 0) {
        setExpenseData(prev => ({
          ...prev,
          monthlyPlans: {},
          settings: { ...prev.settings, planIncomeFix: true }
        }));
      } else {
        setExpenseData(prev => ({ ...prev, settings: { ...prev.settings, planIncomeFix: true } }));
      }
    }, [isLoaded, expenseData?.settings?.planIncomeFix]); // eslint-disable-line react-hooks/exhaustive-deps

    // One-time: deduplicate CC transactions (same card + date + amount)
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.ccTxnsDeduplicated) return;
      const expenses = expenseData.expenses || [];
      const seen = new Set();
      const deduped = [];
      for (const e of expenses) {
        if (e.creditCardId && e.smartLogSource === 'cc-statement') {
          const key = `${e.creditCardId}|${e.date}|${e.amount}|${(e.description || '').substring(0, 20).toLowerCase()}`;
          if (seen.has(key)) continue;
          seen.add(key);
        }
        deduped.push(e);
      }
      if (deduped.length < expenses.length) {
        setExpenseData(prev => ({ ...prev, expenses: deduped, settings: { ...prev.settings, ccTxnsDeduplicated: true } }));
      } else {
        setExpenseData(prev => ({ ...prev, settings: { ...prev.settings, ccTxnsDeduplicated: true } }));
      }
    }, [isLoaded, expenseData?.settings?.ccTxnsDeduplicated]); // eslint-disable-line react-hooks/exhaustive-deps

    // One-time credit card + transaction import
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.ccCardsImported) return;
      const tataNeuId = 1740100000000;
      const hdfcId = 1740200000000;
      const tataNeuCard = { id: tataNeuId, name: 'Tata Neu', lastFour: '8621', limit: 80000, billingDate: 1, dueDate: 21, color: '#7c3aed' };
      const hdfcCard = { id: hdfcId, name: 'HDFC Money Back', lastFour: '2274', limit: 80000, billingDate: 14, dueDate: 5, color: '#2563eb' };
      const txns = [
        { date: '2026-02-01', description: 'Amazon Pay India Private', amount: 1004, category: 'shopping', merchant: 'Amazon' },
        { date: '2026-02-02', description: 'Google Play Mumbai', amount: 975, category: 'subscriptions', merchant: 'Google Play' },
        { date: '2026-02-02', description: 'Claude.AI Subscription Anthropic', amount: 2172.29, category: 'subscriptions', merchant: 'Claude.AI' },
        { date: '2026-02-03', description: 'CGST', amount: 3.91, category: 'other', merchant: 'CGST' },
        { date: '2026-02-03', description: 'SGST', amount: 3.91, category: 'other', merchant: 'SGST' },
        { date: '2026-02-05', description: 'Amazon Pay India Private', amount: 234, category: 'shopping', merchant: 'Amazon' },
        { date: '2026-02-05', description: 'Amazon Pay India Private', amount: 1303, category: 'shopping', merchant: 'Amazon' },
        { date: '2026-02-05', description: 'Zepto Marketplace Private Bangalore', amount: 1016, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-05', description: 'Blinkit Gurgaon', amount: 596.74, category: 'groceries', merchant: 'Blinkit' },
        { date: '2026-02-08', description: 'House Of Fitness Chennai (EMI)', amount: 3144, category: 'emi', merchant: 'House Of Fitness' },
        { date: '2026-02-08', description: 'Zepto Marketplace Private Bangalore', amount: 688, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-10', description: 'RAZ*Makers Tribe Chennai', amount: 799, category: 'shopping', merchant: 'Makers Tribe' },
        { date: '2026-02-10', description: 'Zepto Marketplace Private Bangalore', amount: 1906, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-11', description: 'Zepto Gurgaon', amount: 965, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-13', description: 'BookMyShow Mumbai', amount: 410.22, category: 'entertainment', merchant: 'BookMyShow' },
        { date: '2026-02-14', description: 'Zepto Gurgaon', amount: 141, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-14', description: 'Zepto Gurgaon', amount: 765, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-14', description: 'Blinkit Gurgaon', amount: 910.17, category: 'groceries', merchant: 'Blinkit' },
        { date: '2026-02-15', description: 'Blinkit Gurgaon', amount: 111, category: 'groceries', merchant: 'Blinkit' },
        { date: '2026-02-15', description: 'Zepto Gurgaon', amount: 810, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-19', description: 'SQSP* Domain Dublin', amount: 1822.80, category: 'subscriptions', merchant: 'Squarespace' },
        { date: '2026-02-20', description: 'CGST', amount: 3.28, category: 'other', merchant: 'CGST' },
        { date: '2026-02-20', description: 'SGST', amount: 3.28, category: 'other', merchant: 'SGST' },
        { date: '2026-02-20', description: 'Blinkit Gurgaon', amount: 524.67, category: 'groceries', merchant: 'Blinkit' },
        { date: '2026-03-01', description: 'Consolidated FCY Markup Fee', amount: 79.91, category: 'other', merchant: 'FCY Markup Fee' },
      ].map((t, i) => ({
        ...t, id: tataNeuId + i + 1, type: 'variable', paymentMethod: 'credit', creditCardId: tataNeuId,
        recurring: false, isRepayment: false, notes: 'CC spend on Tata Neu',
        smartLogSource: 'cc-statement', tags: ['cc-import'], createdAt: new Date().toISOString(),
      }));
      setExpenseData(prev => ({
        ...prev,
        creditCards: [tataNeuCard, hdfcCard, ...(prev.creditCards || []).filter(c => c.id !== tataNeuId && c.id !== hdfcId)],
        expenses: [...txns, ...(prev.expenses || [])],
        settings: { ...prev.settings, ccCardsImported: true },
      }));
      // Sync to weekly finance
      txns.forEach(t => syncToWeekExpense(t.id, t.amount, t.description, t.date));
    }, [isLoaded, expenseData?.settings?.ccCardsImported]); // eslint-disable-line react-hooks/exhaustive-deps

    // One-time HDFC Money Back Jan-Feb 2026 statement import
    // eslint-disable-next-line
    React.useEffect(() => {
      if (!isLoaded || expenseData?.settings?.hdfcJanFeb2026Imported) return;
      const hdfcId = 1740200000000;
      const cardName = 'HDFC Money Back';
      const txns = [
        // Groceries (Zepto)
        { date: '2026-01-14', description: 'Zepto Bangalore', amount: 1449, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-16', description: 'Zepto Marketplace Bangalore', amount: 686, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-17', description: 'Zepto Marketplace Bangalore', amount: 491, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-19', description: 'Zepto Marketplace Bangalore', amount: 112, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-21', description: 'Zepto Marketplace Bangalore', amount: 221, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-26', description: 'Zepto Marketplace Bangalore', amount: 144, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-27', description: 'Zepto Marketplace Bangalore', amount: 288, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-27', description: 'Zepto Marketplace Bangalore', amount: 213, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-31', description: 'Zepto Marketplace Bangalore', amount: 1176, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-01-31', description: 'Zepto Marketplace Bangalore', amount: 1089, category: 'groceries', merchant: 'Zepto' },
        { date: '2026-02-01', description: 'Zepto Marketplace Bangalore', amount: 472, category: 'groceries', merchant: 'Zepto' },
        // Dining (Swiggy)
        { date: '2026-01-14', description: 'Swiggy Food Bangalore', amount: 270, category: 'dining', merchant: 'Swiggy' },
        { date: '2026-01-14', description: 'Swiggy Food Bangalore', amount: 979, category: 'dining', merchant: 'Swiggy' },
        { date: '2026-01-31', description: 'Swiggy Food Bangalore', amount: 823, category: 'dining', merchant: 'Swiggy' },
        // Shopping
        { date: '2026-01-24', description: 'Amazon Pay India Bangalore', amount: 1312, category: 'shopping', merchant: 'Amazon' },
        // Personal (Heads Up For Tails - pet supplies)
        { date: '2026-01-31', description: 'Heads Up For Tails Kanchipuram', amount: 2699, category: 'personal', merchant: 'Heads Up For Tails' },
        // Transport
        { date: '2026-01-31', description: 'Central Petrol Supplies Chennai', amount: 2551.76, category: 'transport', merchant: 'Central Petrol' },
        { date: '2026-02-08', description: 'CRED FASTag Bangalore', amount: 500, category: 'transport', merchant: 'CRED FASTag' },
        // Subscriptions
        { date: '2026-01-29', description: 'OpenAI ChatGPT Subscription', amount: 2176.58, category: 'subscriptions', merchant: 'OpenAI' },
        { date: '2026-02-11', description: 'Claude.AI Subscription', amount: 9404.79, category: 'subscriptions', merchant: 'Claude.AI' },
        // EMI (Amazon EMI installment charges)
        { date: '2026-01-28', description: 'EMI Processing Fee (Amazon)', amount: 299, category: 'emi', merchant: 'Amazon EMI' },
        { date: '2026-02-14', description: 'EMI Principal (Amazon)', amount: 1207, category: 'emi', merchant: 'Amazon EMI' },
        { date: '2026-02-14', description: 'EMI Interest (Amazon)', amount: 134, category: 'emi', merchant: 'Amazon EMI' },
        // Taxes & Fees
        { date: '2026-01-28', description: 'CGST on EMI Processing Fee', amount: 26.91, category: 'other', merchant: 'CGST' },
        { date: '2026-01-28', description: 'SGST on EMI Processing Fee', amount: 26.91, category: 'other', merchant: 'SGST' },
        { date: '2026-01-30', description: 'CGST', amount: 6.86, category: 'other', merchant: 'CGST' },
        { date: '2026-01-30', description: 'SGST', amount: 6.86, category: 'other', merchant: 'SGST' },
        { date: '2026-02-12', description: 'CGST', amount: 29.63, category: 'other', merchant: 'CGST' },
        { date: '2026-02-12', description: 'SGST', amount: 29.63, category: 'other', merchant: 'SGST' },
        { date: '2026-02-14', description: 'Consolidated FCY Markup Fee', amount: 405.35, category: 'other', merchant: 'FCY Markup Fee' },
      ].map((t, i) => ({
        ...t, id: hdfcId + 100 + i, type: 'variable', paymentMethod: 'credit', creditCardId: hdfcId,
        recurring: false, isRepayment: false, notes: `CC spend on ${cardName}`,
        smartLogSource: 'cc-statement', tags: ['cc-import'], createdAt: new Date().toISOString(),
      }));
      // Credits/Repayments
      const credits = [
        { date: '2026-01-31', description: 'Credit Card Payment (Net Banking)', amount: 33300, category: 'other', merchant: 'Card Payment', isRepayment: true },
        { date: '2026-01-31', description: 'Petro Surcharge Waiver', amount: 25.26, category: 'other', merchant: 'Surcharge Waiver', isRepayment: true },
      ].map((t, i) => ({
        ...t, id: hdfcId + 200 + i, type: 'variable', paymentMethod: 'credit', creditCardId: hdfcId,
        recurring: false, notes: `CC refund from ${cardName}`,
        smartLogSource: 'cc-statement', tags: ['cc-import'], createdAt: new Date().toISOString(),
      }));
      const allTxns = [...txns, ...credits];
      setExpenseData(prev => ({
        ...prev,
        expenses: [...allTxns, ...(prev.expenses || [])],
        settings: { ...prev.settings, hdfcJanFeb2026Imported: true },
      }));
      allTxns.filter(t => !t.isRepayment).forEach(t => syncToWeekExpense(t.id, t.amount, t.description, t.date));
    }, [isLoaded, expenseData?.settings?.hdfcJanFeb2026Imported]); // eslint-disable-line react-hooks/exhaustive-deps

    // Voice
    const startVoiceInput = () => {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { setVoiceError('Voice not supported. Use Chrome or Edge.'); return; }
      setIsListening(true); setVoiceError(''); setVoiceTranscript(''); setParsedSmartLog(null);
      const recognition = new SR(); recognition.lang = 'en-IN'; recognition.continuous = false; recognition.interimResults = true;
      recognition.onresult = (event) => { const t = event.results[0][0].transcript; setVoiceTranscript(t); if (event.results[0].isFinal) { setIsListening(false); parseNaturalInput(t, 'voice'); } };
      recognition.onerror = () => { setIsListening(false); setVoiceError('Could not understand. Try again.'); };
      recognition.onend = () => setIsListening(false);
      recognition.start();
    };
    const parseNaturalInput = (text, source) => {
      const lower = text.toLowerCase();
      const amountMatch = lower.match(/(\d[\d,]*\.?\d*)\s*(?:rupees?|rs\.?|inr)?/i) || lower.match(/(?:rupees?|rs\.?|inr)\s*(\d[\d,]*\.?\d*)/i) || lower.match(/(?:spent|paid|bought|cost|received|got|earned)\s+(\d[\d,]*\.?\d*)/i);
      const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : null;
      const isIncome = /(?:received|got|earned|credited|salary|income|refund)/i.test(lower);
      const categoryMap = { groceries: /grocer|vegetable|fruit|supermarket|bigbazaar|dmart|kirana/i, dining: /food|restaurant|eat|dinner|lunch|breakfast|cafe|zomato|swiggy|biryani/i, transport: /uber|ola|auto|cab|taxi|petrol|fuel|bus|train|metro|rapido/i, shopping: /amazon|flipkart|myntra|shop|buy|purchase|cloth|shoe/i, entertainment: /movie|netflix|spotify|hotstar|game|concert/i, health: /doctor|hospital|medicine|pharmacy|medical|gym|apollo/i, utilities: /electric|water|gas|internet|phone|bill|recharge|jio|airtel/i, housing: /rent|maintenance|society|apartment/i, subscriptions: /subscription|membership|annual|renew/i, emi: /emi|loan|installment/i, education: /course|book|tuition|udemy|class/i };
      let detectedCategory = 'other';
      for (const [cat, regex] of Object.entries(categoryMap)) { if (regex.test(lower)) { detectedCategory = cat; break; } }
      const merchantMatch = lower.match(/(?:at|to|from|on)\s+([a-z][a-z\s]+?)(?:\s+(?:for|on|today|yesterday)|$)/i);
      setParsedSmartLog({ type: isIncome ? 'income' : 'expense', amount, category: detectedCategory, description: text, merchant: merchantMatch ? merchantMatch[1].trim() : '', date: new Date().toISOString().split('T')[0], source, raw: text, paymentMethod: 'upi' });
    };
    // SMS
    const parseSMS = (text) => {
      const amountMatch = text.match(/(?:Rs\.?|INR)\s?([\d,]+\.?\d*)/i);
      const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : null;
      const isCredit = /credited|received|deposit|refund/i.test(text);
      const cardMatch = text.match(/(?:card|a\/c)\s*(?:xx|XX|\*{2,4})(\d{4})/i);
      const cardLast4 = cardMatch ? cardMatch[1] : null;
      const merchantMatch = text.match(/(?:at|to|from|Info:\s*(?:UPI\/|IMPS\/)?)\s*([A-Z][A-Za-z0-9\s&.]+)/);
      const merchant = merchantMatch ? merchantMatch[1].trim() : '';
      const dateMatch = text.match(/(\d{1,2})[/-](\w{3}|\d{1,2})[/-](\d{2,4})/);
      let date = new Date().toISOString().split('T')[0];
      if (dateMatch) {
        const monthMap = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
        const day = parseInt(dateMatch[1]); const mStr = dateMatch[2].toLowerCase();
        const month = monthMap[mStr] !== undefined ? monthMap[mStr] : parseInt(dateMatch[2]) - 1;
        const year = dateMatch[3].length === 2 ? 2000 + parseInt(dateMatch[3]) : parseInt(dateMatch[3]);
        const d = new Date(year, month, day);
        if (!isNaN(d)) date = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      }
      let paymentMethod = 'upi'; let creditCardId = null;
      if (/credit\s*card/i.test(text)) paymentMethod = 'credit';
      else if (/debit\s*card/i.test(text)) paymentMethod = 'debit';
      else if (/NEFT|IMPS|RTGS|netbanking/i.test(text)) paymentMethod = 'netbanking';
      if (cardLast4 && expenseData.creditCards) { const m = expenseData.creditCards.find(c => c.lastFour === cardLast4); if (m) { creditCardId = m.id; paymentMethod = 'credit'; } }
      setParsedSmartLog({ type: isCredit ? 'income' : 'expense', amount, merchant, date, source: 'sms', raw: text, category: 'other', description: merchant || text.substring(0, 60), paymentMethod, creditCardId });
    };
    // ===== SMART STATEMENT IMPORT =====
    const normalizeDate = (raw) => {
      const mm = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
      let m = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (m) { const d = new Date(parseInt(m[3]), parseInt(m[2])-1, parseInt(m[1])); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
      m = raw.match(/^(\d{1,2})[/-](\w{3})[/-](\d{2,4})$/i);
      if (m) { const mon = mm[m[2].toLowerCase()]; const yr = m[3].length === 2 ? 2000+parseInt(m[3]) : parseInt(m[3]); if (mon !== undefined) return `${yr}-${String(mon+1).padStart(2,'0')}-${String(parseInt(m[1])).padStart(2,'0')}`; }
      return new Date().toISOString().split('T')[0];
    };

    const extractMerchant = (desc) => {
      const upi = desc.match(/UPI\/\d+\/\s*([^/]+)\/([^/]+)/i);
      if (upi) return { merchant: upi[1].trim(), cleanDesc: upi[1].trim() };
      const neft = desc.match(/NEFT\|[A-Z0-9]+\s+(.+?)(?:\s{2,}|$)/i);
      if (neft) return { merchant: neft[1].trim(), cleanDesc: neft[1].trim() };
      const purch = desc.match(/PURCHASE\s+(.+?)(?:\s+\d{6}.*|$)/i);
      if (purch) return { merchant: purch[1].trim(), cleanDesc: purch[1].trim() };
      const nach = desc.match(/NACH\s*DR[^\s]*\s+(.+?)(?:\s{2,}|$)/i);
      if (nach) return { merchant: nach[1].trim(), cleanDesc: nach[1].trim() };
      if (/NODNHTSYM|NACH\s*DR/i.test(desc)) return { merchant: 'Loan EMI (NACH DR)', cleanDesc: 'Loan EMI (NACH DR)' };
      return { merchant: desc.substring(0, 60).trim(), cleanDesc: desc.substring(0, 60).trim() };
    };

    const MERCHANT_CAT_MAP = {
      'hungerbox': 'dining', 'zomato': 'dining', 'swiggy': 'dining', 'dominos': 'dining', 'mcdonalds': 'dining', 'starbucks': 'dining',
      'blinkit': 'groceries', 'zepto': 'groceries', 'bigbazaar': 'groceries', 'big bazaar': 'groceries', 'dmart': 'groceries', 'jiomart': 'groceries', 'sendur murugan': 'groceries',
      'uber': 'transport', 'ola ': 'transport', 'rapido': 'transport', 'irctc': 'transport', 'central petrol': 'transport', 'cred_fastag': 'transport', 'fastag': 'transport',
      'apollo': 'health', 'pharmacy': 'health', 'hospital': 'health', 'medplus': 'health', 'sri sai children': 'health',
      'netflix': 'subscriptions', 'spotify': 'subscriptions', 'adobe': 'subscriptions', 'hostinger': 'subscriptions', 'cosmofeed': 'subscriptions', 'youtube premium': 'subscriptions',
      'airtel': 'utilities', 'jio': 'utilities', 'myjio': 'utilities', 'hp gas': 'utilities', 'indane': 'utilities', 'bescom': 'utilities', 'tangedco': 'utilities', 'uidai': 'utilities',
      'nach dr': 'emi', 'tata capital': 'emi', 'dsp finance': 'emi', 'bajaj finance': 'emi',
      'amazon': 'shopping', 'flipkart': 'shopping', 'myntra': 'shopping', 'makers tribe': 'shopping',
      'sbimops': 'insurance', 'lic ': 'insurance',
      'bookmyshow': 'entertainment', 'book my show': 'entertainment',
      'claude.ai': 'subscriptions', 'anthropic': 'subscriptions', 'sqsp': 'subscriptions', 'google play': 'subscriptions', 'openai': 'subscriptions', 'chatgpt': 'subscriptions',
      'house of fitness': 'emi', 'offus emi': 'emi',
      'heads up for tails': 'personal',
      'cgst': 'other', 'sgst': 'other', 'fcy markup': 'other', 'markup fee': 'other',
    };

    const autoCategorize = (desc, merchant, type) => {
      const lower = (desc + ' ' + merchant).toLowerCase();
      let pm = 'netbanking';
      if (/UPI/i.test(desc)) pm = 'upi';
      else if (/NEFT|RTGS|IMPS/i.test(desc)) pm = 'netbanking';
      else if (/PURCHASE|POS/i.test(desc)) pm = 'debit';

      if (type === 'income') {
        if (/tata consultancy|tcs|infosys|wipro|cognizant|accenture|hcl|salary/i.test(lower)) return { category: 'salary', confidence: 'high', pm };
        if (/freelance|consulting/i.test(lower)) return { category: 'freelance', confidence: 'medium', pm };
        if (/interest|dividend|mutual.*fund/i.test(lower)) return { category: 'investment', confidence: 'medium', pm };
        if (/refund|reversal|cashback/i.test(lower)) return { category: 'refund', confidence: 'medium', pm };
        return { category: 'other', confidence: 'low', pm };
      }

      for (const [key, cat] of Object.entries(MERCHANT_CAT_MAP)) {
        if (lower.includes(key)) return { category: cat, confidence: 'high', pm };
      }
      const kwMap = { housing:/\b(rent|mortgage|maintenance|society)\b/i, groceries:/\b(grocery|vegetable|fruit|supermarket|kirana)\b/i, dining:/\b(food|restaurant|eat|dinner|lunch|cafe|biryani)\b/i, transport:/\b(cab|taxi|petrol|fuel|bus|train|metro|parking)\b/i, entertainment:/\b(movie|game|concert|theatre)\b/i, shopping:/\b(shop|buy|purchase|cloth|shoe)\b/i, utilities:/\b(electric|water|gas|internet|phone|bill|recharge)\b/i, health:/\b(doctor|medicine|medical|gym)\b/i, education:/\b(school|college|tuition|course|book|fees)\b/i, subscriptions:/\b(subscription|membership|renewal)\b/i, insurance:/\b(insurance|policy|premium)\b/i, emi:/\b(emi|loan|installment)\b/i, personal:/\b(salon|haircut|spa|laundry)\b/i, gifts:/\b(gift|birthday|donation|charity)\b/i };
      for (const [cat, regex] of Object.entries(kwMap)) { if (regex.test(lower)) return { category: cat, confidence: 'medium', pm }; }
      return { category: 'other', confidence: 'low', pm };
    };

    const parseStatementSmart = (text) => {
      const lines = text.split('\n').filter(l => l.trim());
      const transactions = [];
      let lid = 0;
      const headerIdx = lines.findIndex(l => /date/i.test(l) && /deposit|withdrawal|credit|debit|amount|balance/i.test(l));
      const dataLines = headerIdx >= 0 ? lines.slice(headerIdx + 1) : lines;

      for (const line of dataLines) {
        if (/^[-=_\s]+$/.test(line) || /page\s+\d/i.test(line) || /^current balance/i.test(line) || /^available balance/i.test(line)) continue;

        let parsed = null;
        // Citi format: tab-separated with Deposit/Withdrawal columns
        const cols = line.split('\t').map(s => s.trim());
        if (cols.length >= 3) {
          const dateMatch = cols[0].match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (dateMatch && cols[1]) {
            const desc = cols[1];
            // Find deposit/withdrawal amounts in remaining columns
            const numCols = cols.slice(2).filter(c => c && c !== 'INR' && /^[\d,]+\.?\d*$/.test(c.replace(/,/g, '')));
            if (numCols.length >= 2) {
              // Pattern: [currency], [deposit_or_empty], [withdrawal_or_empty], [balance]
              // With tabs, empty columns may collapse — check by position
              const afterDesc = line.substring(line.indexOf(cols[1]) + cols[1].length);
              const amtMatches = [...afterDesc.matchAll(/([\d,]+\.\d{2})/g)].map(m => parseFloat(m[1].replace(/,/g, '')));
              if (amtMatches.length >= 2) {
                if (amtMatches.length === 2) {
                  const amt = amtMatches[0];
                  // Split by tabs to determine deposit vs withdrawal column position
                  const fullTabCols = afterDesc.split('\t');
                  const afterINR = fullTabCols.slice(fullTabCols.findIndex(c => c.trim() === 'INR') + 1);
                  const isDeposit = afterINR[0] && afterINR[0].trim() && /[\d,]+/.test(afterINR[0].trim());
                  parsed = { rawDate: cols[0], description: desc, amount: amt, type: isDeposit ? 'income' : 'expense' };
                } else if (amtMatches.length === 3) {
                  // deposit, withdrawal, balance — one should match the balance change
                  const deposit = amtMatches[0];
                  const withdrawal = amtMatches[1];
                  if (deposit > 0 && withdrawal > 0) {
                    // Both present — unlikely, treat larger as amount
                    parsed = { rawDate: cols[0], description: desc, amount: Math.max(deposit, withdrawal), type: deposit > withdrawal ? 'income' : 'expense' };
                  }
                }
              }
            }
            // Fallback for simpler tab format
            if (!parsed && numCols.length >= 1) {
              const amt = parseFloat(numCols[0].replace(/,/g, ''));
              if (amt > 0) parsed = { rawDate: cols[0], description: desc, amount: amt, type: /Cr|\+|credited|deposit/i.test(line) ? 'income' : 'expense' };
            }
          }
        }

        // Dr/Cr format fallback
        if (!parsed) {
          const drCr = line.match(/(\d{1,2}[/-]\w{2,3}[/-]\d{2,4})\s+(.+?)\s+([\d,]+\.?\d*)\s*(?:Dr|Cr|[-+])?/i);
          if (drCr) parsed = { rawDate: drCr[1], description: drCr[2].trim(), amount: parseFloat(drCr[3].replace(/,/g, '')), type: /Cr|\+/i.test(line) ? 'income' : 'expense' };
        }

        if (parsed && parsed.amount > 0) {
          lid++;
          const nd = normalizeDate(parsed.rawDate);
          const { merchant, cleanDesc } = extractMerchant(parsed.description);
          const { category, confidence, pm } = autoCategorize(parsed.description, merchant, parsed.type);
          transactions.push({ id: `stmt-${lid}-${Date.now()}`, date: nd, rawDate: parsed.rawDate, description: cleanDesc, rawDescription: parsed.description, merchant, amount: parsed.amount, type: parsed.type, category, categoryConfidence: confidence, paymentMethod: pm, selected: true, tags: [], recurring: false, recurringFrequency: null, adjustedFrom: null });
        }
      }

      const dates = transactions.map(t => new Date(t.date)).filter(d => !isNaN(d));
      const from = dates.length ? new Date(Math.min(...dates)).toISOString().split('T')[0] : null;
      const to = dates.length ? new Date(Math.max(...dates)).toISOString().split('T')[0] : null;

      setStmtParsedRows(transactions);
      setStmtDateRange({ from, to });
      const sel = {}; transactions.forEach(t => { sel[t.id] = true; }); setStmtSelected(sel);
      setStmtWizardStep(2);
    };

    const parseCCStatement = (text, cardId) => {
      const lines = text.split('\n').filter(l => l.trim());
      const transactions = [];
      let lid = 0;
      const headerIdx = lines.findIndex(l => /date/i.test(l) && (/amount|debit|credit|transaction/i.test(l)));
      const dataLines = headerIdx >= 0 ? lines.slice(headerIdx + 1) : lines;

      for (const line of dataLines) {
        if (/^[-=_\s]+$/.test(line) || /page\s+\d/i.test(line) || /total|opening|closing|minimum|payment\s+due|reward/i.test(line.toLowerCase()) || line.trim().length < 10) continue;

        let parsed = null;
        // Tab-separated format
        const cols = line.split('\t').map(s => s.trim());
        if (cols.length >= 3) {
          const dateMatch = cols[0].match(/^(\d{1,2})[/-](\d{1,2}|\w{3})[/-](\d{2,4})$/);
          if (dateMatch) {
            const desc = cols[1];
            const amtMatches = [...line.matchAll(/([\d,]+\.\d{2})/g)].map(m => parseFloat(m[1].replace(/,/g, '')));
            if (amtMatches.length > 0) {
              const amt = amtMatches[0];
              const isCredit = /Cr|credit|refund|reversal|cashback/i.test(line);
              parsed = { rawDate: cols[0], description: desc, amount: amt, type: isCredit ? 'income' : 'expense' };
            }
          }
        }
        // Space/comma separated fallback
        if (!parsed) {
          const m = line.match(/(\d{1,2}[/-]\w{2,3}[/-]\d{2,4})\s+(.+?)\s+([\d,]+\.?\d*)\s*(?:Dr|Cr|[-+])?/i);
          if (m) {
            const isCredit = /Cr|\+|credit|refund|reversal|cashback/i.test(line);
            parsed = { rawDate: m[1], description: m[2].trim(), amount: parseFloat(m[3].replace(/,/g, '')), type: isCredit ? 'income' : 'expense' };
          }
        }
        // CSV format
        if (!parsed) {
          const csvCols = line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
          if (csvCols.length >= 3) {
            const dateMatch = csvCols[0].match(/^(\d{1,2})[/-](\d{1,2}|\w{3})[/-](\d{2,4})$/);
            if (dateMatch) {
              const desc = csvCols[1];
              const amtStr = csvCols.slice(2).find(c => /^[\d,]+\.?\d*$/.test(c.replace(/,/g, '')));
              if (amtStr) {
                const isCredit = /Cr|credit|refund|reversal|cashback/i.test(line);
                parsed = { rawDate: csvCols[0], description: desc, amount: parseFloat(amtStr.replace(/,/g, '')), type: isCredit ? 'income' : 'expense' };
              }
            }
          }
        }

        if (parsed && parsed.amount > 0) {
          lid++;
          const nd = normalizeDate(parsed.rawDate);
          const { merchant, cleanDesc } = extractMerchant(parsed.description);
          const { category, confidence } = autoCategorize(parsed.description, merchant, parsed.type);
          transactions.push({ id: `cc-${lid}-${Date.now()}`, date: nd, rawDate: parsed.rawDate, description: cleanDesc, rawDescription: parsed.description, merchant, amount: parsed.amount, type: parsed.type, category, categoryConfidence: confidence, creditCardId: cardId, selected: true });
        }
      }
      return transactions;
    };

    // --- PDF CC Statement Import ---
    const extractTextFromPDF = async (file, password = null) => {
      setCcPdfLoading(true);
      setCcPdfError('');
      try {
        const data = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data, password: password || undefined });
        const pdf = await loadingTask.promise;
        let fullText = '';
        const Y_TOL = 5; // y-coordinate tolerance for same-line grouping
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const items = textContent.items.filter(item => item.str.trim());
          if (items.length === 0) continue;
          // Sort top-to-bottom (y descending in PDF coords), then left-to-right
          items.sort((a, b) => {
            const yDiff = b.transform[5] - a.transform[5];
            if (Math.abs(yDiff) > Y_TOL) return yDiff;
            return a.transform[4] - b.transform[4];
          });
          // Group into lines by y-coordinate clusters
          const lineGroups = [];
          let groupY = items[0].transform[5];
          let group = [];
          for (const item of items) {
            const y = item.transform[5];
            if (Math.abs(y - groupY) > Y_TOL) {
              if (group.length > 0) lineGroups.push(group);
              group = [];
              groupY = y;
            }
            group.push({ text: item.str, x: item.transform[4], width: item.width || (item.str.length * 5) });
          }
          if (group.length > 0) lineGroups.push(group);
          // Convert groups to text lines
          for (const grp of lineGroups) {
            grp.sort((a, b) => a.x - b.x);
            let lineStr = '';
            for (let j = 0; j < grp.length; j++) {
              if (j > 0) {
                const gap = grp[j].x - (grp[j-1].x + grp[j-1].width);
                lineStr += gap > 40 ? '\t' : ' ';
              }
              lineStr += grp[j].text;
            }
            if (lineStr.trim()) fullText += lineStr.trim() + '\n';
          }
          fullText += '\n';
        }
        return fullText;
      } catch (err) {
        if (err.name === 'PasswordException' || err.message?.includes('password')) {
          setCcPdfPasswordNeeded(true);
          setCcPdfError('');
          return null;
        }
        setCcPdfError(`PDF parsing failed: ${err.message}`);
        return null;
      } finally {
        setCcPdfLoading(false);
      }
    };

    // HDFC PDF-specific parser: handles dd/mm/yyyy| HH:mm format, ₹ amounts, +/- credits
    const parseCCPdfText = (text, cardId) => {
      const lines = text.split('\n');
      const transactions = [];
      let lid = 0;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        // Match date anywhere on the line (allow leading whitespace/tabs/bullet)
        const dateMatch = line.match(/^\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*\|?\s*\d{0,2}:?\d{0,2}/);
        if (!dateMatch) continue;
        // Extract all amounts on the line (with or without decimals: 1,449.00 or 221)
        let amountMatches = [...line.matchAll(/(?:₹\s*)?([\d,]+\.\d{2})\b/g)];
        // Fallback: try amounts without decimals (e.g. ₹ 221 or tab-separated 221)
        if (amountMatches.length === 0) {
          amountMatches = [...line.matchAll(/[₹]\s*([\d,]+)\b/g)];
        }
        // If no amount on this line, check the next line (multi-line transaction)
        let combinedLine = line;
        if (amountMatches.length === 0 && i + 1 < lines.length) {
          const nextLine = lines[i + 1];
          // Only merge if next line does NOT start with a date (it's a continuation)
          if (!nextLine.match(/^\s*\d{1,2}\/\d{1,2}\/\d{4}/)) {
            combinedLine = line + ' ' + nextLine;
            amountMatches = [...combinedLine.matchAll(/(?:₹\s*)?([\d,]+\.\d{2})\b/g)];
            if (amountMatches.length === 0) {
              amountMatches = [...combinedLine.matchAll(/[₹]\s*([\d,]+)\b/g)];
            }
            if (amountMatches.length > 0) i++; // skip merged line
          }
        }
        if (amountMatches.length === 0) continue;
        // Use the last amount (INR for international txns that also show USD)
        const amtStr = amountMatches[amountMatches.length - 1][1];
        const amount = parseFloat(amtStr.replace(/,/g, ''));
        if (!amount || amount <= 0) continue;
        // Credit detection: + sign before amount (HDFC uses "+ C amount"), Cr suffix, CREDIT/refund keywords
        const amtIdx = combinedLine.lastIndexOf(amtStr);
        const beforeAmt = combinedLine.substring(0, amtIdx);
        const afterAmt = combinedLine.substring(amtIdx + amtStr.length);
        const isCredit = /\+\s*C?\s*$/.test(beforeAmt) || /\+\s*[₹RC]/.test(beforeAmt.slice(-10)) || /Cr\b/i.test(afterAmt) || /\bCREDIT\b/i.test(combinedLine) || /\brefund\b/i.test(combinedLine) || /\breversal\b/i.test(combinedLine) || /\bcashback\b/i.test(combinedLine) || /\bSURCHARGE WAIVER\b/i.test(combinedLine);
        // Extract description: between date/time and amount area
        let desc = combinedLine.substring(dateMatch[0].length).trim();
        // Remove EMI tags, amount values, ₹ symbols, USD amounts, CREDIT keyword, PI indicators
        desc = desc.replace(/\bEMI\b/gi, '').replace(/\bCREDIT\b/gi, '').replace(/[+]?\s*[₹`]\s*/g, '').replace(/USD\s*[\d.,]+/gi, '');
        desc = desc.replace(/([\d,]+\.\d{2})/g, '').replace(/₹\s*[\d,]+/g, '').replace(/\t+/g, ' ').replace(/\s{2,}/g, ' ').trim();
        // Remove trailing dots/bullets (PI column indicator)
        desc = desc.replace(/[•●·.]+\s*$/, '').trim();
        if (!desc || desc.length < 2) continue;
        lid++;
        const nd = normalizeDate(dateMatch[1]);
        const { merchant, cleanDesc } = extractMerchant(desc);
        const { category, confidence } = autoCategorize(desc, merchant, isCredit ? 'income' : 'expense');
        // Detect card repayments (credit card payment / net banking payment)
        const isRepayment = isCredit && /\bCARD\s*PAYMENT/i.test(combinedLine);
        transactions.push({
          id: `ccpdf-${lid}-${Date.now()}`, date: nd, rawDate: dateMatch[1],
          description: cleanDesc || desc, rawDescription: desc, merchant, amount,
          type: isCredit ? 'income' : 'expense', category: isRepayment ? 'emi' : category,
          categoryConfidence: confidence, creditCardId: cardId, selected: true,
          isRepayment
        });
      }
      return transactions;
    };

    const handleCCPdfUpload = async (file, password = null) => {
      setCcPdfFile(file);
      const text = await extractTextFromPDF(file, password);
      if (text) setCcStmtText(text); // Show extracted text in paste box for debugging
      if (text && ccStmtCard) {
        // Try PDF-specific parser first, then generic
        let rows = parseCCPdfText(text, ccStmtCard);
        if (rows.length < 3) {
          const genericRows = parseCCStatement(text, ccStmtCard);
          if (genericRows.length > rows.length) rows = genericRows;
        }
        if (rows.length > 0) {
          // Filter out transactions that already exist (same card + date + amount)
          const existing = new Set((expenseData.expenses || []).filter(e => e.creditCardId === ccStmtCard).map(e => `${e.date}|${e.amount}`));
          rows = rows.map(r => ({ ...r, selected: !existing.has(`${r.date}|${r.amount}`) }));
          setCcStmtRows(rows);
          const sel = {}; rows.forEach(r => { if (r.selected) sel[r.id] = true; }); setCcStmtSelected(sel);
          setCcStmtStep(2);
          setCcPdfPasswordNeeded(false); setCcPdfPassword(''); setCcPdfFile(null);
        } else {
          setCcPdfError('No transactions found. Check the extracted text in the paste box — you can edit and try "Parse Statement" manually.');
          setCcPdfPasswordNeeded(false); setCcPdfPassword(''); setCcPdfFile(null);
        }
      }
    };

    const handlePdfPasswordSubmit = () => {
      if (ccPdfFile && ccPdfPassword) {
        handleCCPdfUpload(ccPdfFile, ccPdfPassword);
      }
    };

    const importCCStatement = () => {
      const selected = ccStmtRows.filter(r => ccStmtSelected[r.id]);
      const card = (expenseData.creditCards || []).find(c => c.id === ccStmtCard);
      const cardName = card ? card.name : 'Card';
      // Compute statement month from max transaction date in this batch
      const bd = card ? (card.billingDate || 15) : 15;
      const maxTxnDate = new Date(Math.max(...selected.map(r => new Date(r.date).getTime())));
      let stmtM = maxTxnDate.getMonth(), stmtY = maxTxnDate.getFullYear();
      if (maxTxnDate.getDate() > bd) { stmtM++; if (stmtM > 11) { stmtM = 0; stmtY++; } }
      const ccStatementMonth = `${stmtY}-${String(stmtM + 1).padStart(2, '0')}`;
      selected.forEach(r => {
        if (r.type === 'income') {
          // Only mark as repayment if it's an actual card payment, not a merchant refund/credit
          const isActualRepayment = r.isRepayment || /\bCARD\s*PAYMENT/i.test(r.rawDescription || r.description);
          addExpense({ amount: r.amount, description: r.description, category: r.category, type: 'variable', date: r.date, paymentMethod: 'credit', creditCardId: ccStmtCard, isRepayment: isActualRepayment, isCredit: true, merchant: r.merchant, notes: isActualRepayment ? `Card payment for ${cardName}` : `CC credit/refund on ${cardName}`, smartLogSource: 'cc-statement', tags: ['cc-import'], ccStatementMonth });
        } else {
          addExpense({ amount: r.amount, description: r.description, category: r.category, type: 'variable', date: r.date, paymentMethod: 'credit', creditCardId: ccStmtCard, recurring: false, merchant: r.merchant, notes: `CC spend on ${cardName}`, smartLogSource: 'cc-statement', tags: ['cc-import'], ccStatementMonth });
        }
      });
      setCcStmtStep(0); setCcStmtText(''); setCcStmtRows([]); setCcStmtSelected({}); setCcStmtCard(null);
      setCcPdfPasswordNeeded(false); setCcPdfPassword(''); setCcPdfFile(null); setCcPdfError('');
    };

    const processAdjustment = (input) => {
      const lower = input.toLowerCase().trim();
      let appliedCount = 0;
      let systemResponse = '';

      // Pattern: Move Month dates to Month Day
      const moveDateMatch = lower.match(/move\s+(\w+)\s+(?:dates?\s+)?to\s+(\w+)\s+(\d{1,2})/);
      if (moveDateMatch) {
        const mn = { jan:0,january:0,feb:1,february:1,mar:2,march:2,apr:3,april:3,may:4,jun:5,june:5,jul:6,july:6,aug:7,august:7,sep:8,september:8,oct:9,october:9,nov:10,november:10,dec:11,december:11 };
        const srcM = mn[moveDateMatch[1].toLowerCase()];
        const tgtM = mn[moveDateMatch[2].toLowerCase()];
        const tgtD = parseInt(moveDateMatch[3]);
        if (srcM !== undefined && tgtM !== undefined) {
          setStmtParsedRows(prev => prev.map(row => {
            const rd = new Date(row.date);
            if (rd.getMonth() === srcM) {
              appliedCount++;
              const origDate = row.date;
              const yr = tgtM < srcM ? rd.getFullYear() + 1 : rd.getFullYear();
              const newDate = `${yr}-${String(tgtM+1).padStart(2,'0')}-${String(tgtD).padStart(2,'0')}`;
              return { ...row, date: newDate, adjustedFrom: origDate, tags: [...row.tags.filter(t => !t.startsWith('adjusted')), `adjusted from ${origDate}`] };
            }
            return row;
          }));
          systemResponse = `Moved ${appliedCount} entries from ${moveDateMatch[1]} to ${moveDateMatch[2]} ${tgtD}`;
        }
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      // Pattern: Remove/Exclude X
      const removeMatch = lower.match(/(?:remove|exclude|uncheck|deselect|hide)\s+(.+)/);
      if (removeMatch) {
        const search = removeMatch[1].trim();
        setStmtSelected(prev => {
          const ns = { ...prev };
          stmtParsedRows.forEach(row => {
            const l = (row.merchant + ' ' + row.description + ' ' + row.category).toLowerCase();
            if (l.includes(search) || (search === 'self transfers' && row.category === 'custom_transfer')) {
              appliedCount++;
              ns[row.id] = false;
            }
          });
          return ns;
        });
        systemResponse = `Excluded ${appliedCount} entries matching "${search}"`;
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      // Pattern: Include/Select X
      const includeMatch = lower.match(/(?:include|select|check|show)\s+(?:all\s+)?(.+)/);
      if (includeMatch && !lower.match(/(?:all\s+)?(.+?)\s+(?:are|is)\s+/)) {
        const search = includeMatch[1].trim();
        setStmtSelected(prev => {
          const ns = { ...prev };
          stmtParsedRows.forEach(row => {
            if ((row.merchant + ' ' + row.description).toLowerCase().includes(search)) { appliedCount++; ns[row.id] = true; }
          });
          return ns;
        });
        systemResponse = `Included ${appliedCount} entries matching "${search}"`;
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      // Pattern: Mark/Categorize X as Y
      const catMatch = lower.match(/(?:mark|categorize|set|change)\s+(.+?)\s+(?:as|to)\s+(.+)/);
      if (catMatch) {
        const search = catMatch[1].trim();
        const catName = catMatch[2].trim();
        const allCats = [...EXPENSE_CATEGORIES, ...(expenseData.customExpenseCategories || [])];
        const matched = allCats.find(c => c.key === catName || c.label.toLowerCase() === catName.toLowerCase());
        if (matched) {
          setStmtParsedRows(prev => prev.map(row => {
            if ((row.merchant + ' ' + row.description).toLowerCase().includes(search)) { appliedCount++; return { ...row, category: matched.key, categoryConfidence: 'high' }; }
            return row;
          }));
          systemResponse = `Categorized ${appliedCount} "${search}" entries as ${matched.label}`;
        } else {
          systemResponse = `Category "${catName}" not found. Try: ${allCats.map(c => c.label.toLowerCase()).join(', ')}`;
        }
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      // Pattern: X is weekly/monthly/yearly (recurring)
      const recurMatch = lower.match(/(.+?)\s+is\s+(daily|weekly|biweekly|monthly|quarterly|yearly)$/);
      if (recurMatch) {
        const search = recurMatch[1].trim();
        const freq = recurMatch[2];
        setStmtParsedRows(prev => prev.map(row => {
          if ((row.merchant + ' ' + row.description).toLowerCase().includes(search)) { appliedCount++; return { ...row, recurring: true, recurringFrequency: freq }; }
          return row;
        }));
        systemResponse = `Marked ${appliedCount} "${search}" entries as ${freq} recurring`;
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      // Pattern: All X are Y / X is Y [under Z]
      const renameMatch = lower.match(/(?:all\s+)?(.+?)\s+(?:are|is)\s+(.+?)(?:\s+(?:under|as\s+category|category)\s+(.+))?$/);
      if (renameMatch) {
        const search = renameMatch[1].trim();
        const newDesc = renameMatch[2].trim();
        const catHint = renameMatch[3]?.trim();
        let newCat = null;
        if (catHint) {
          const allCats = [...EXPENSE_CATEGORIES, ...(expenseData.customExpenseCategories || [])];
          newCat = allCats.find(c => c.key === catHint || c.label.toLowerCase() === catHint.toLowerCase())?.key;
        }
        setStmtParsedRows(prev => prev.map(row => {
          if ((row.merchant + ' ' + row.description + ' ' + row.rawDescription).toLowerCase().includes(search)) {
            appliedCount++;
            return { ...row, description: newDesc.charAt(0).toUpperCase() + newDesc.slice(1), merchant: newDesc.charAt(0).toUpperCase() + newDesc.slice(1), ...(newCat ? { category: newCat, categoryConfidence: 'high' } : {}) };
          }
          return row;
        }));
        systemResponse = `Renamed ${appliedCount} entries matching "${search}" to "${newDesc}"${newCat ? ` (category: ${newCat})` : ''}`;
        setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount, timestamp: new Date().toISOString() }, ...prev]);
        return;
      }

      systemResponse = `Could not understand. Try:\n• "All X are Y" — rename entries\n• "Move Jan dates to Feb 01" — adjust dates\n• "Remove self transfers" — exclude entries\n• "Mark X as utilities" — change category\n• "X is monthly" — set recurring`;
      setStmtAdjustments(prev => [{ id: Date.now(), userMessage: input, systemResponse, appliedCount: 0, timestamp: new Date().toISOString() }, ...prev]);
    };

    const importStatementWizard = () => {
      const selected = stmtParsedRows.filter(row => stmtSelected[row.id]);
      selected.forEach(row => {
        if (row.type === 'income') {
          addIncome({ amount: row.amount, source: row.description || row.merchant, category: row.category || 'other', date: row.date, recurring: row.recurring || false, recurringFrequency: row.recurringFrequency || undefined, notes: row.adjustedFrom ? `adjusted from ${row.adjustedFrom}` : '', smartLogSource: 'statement' });
        } else {
          addExpense({ amount: row.amount, description: row.description || row.merchant, category: row.category || 'other', type: row.recurring ? 'fixed' : 'variable', date: row.date, tags: row.tags || [], paymentMethod: row.paymentMethod || 'upi', creditCardId: null, recurring: row.recurring || false, recurringFrequency: row.recurringFrequency || undefined, merchant: row.merchant, notes: '', smartLogSource: 'statement' });
        }
      });
      setStmtWizardStep(0); setStmtParsedRows([]); setStmtRawText(''); setStmtSelected({}); setStmtAdjustments([]);
    };
    const confirmSmartLog = () => {
      if (!parsedSmartLog || !parsedSmartLog.amount) return;
      const p = parsedSmartLog;
      if (p.type === 'income') addIncome({ amount: p.amount, source: p.merchant || p.description, category: p.category || 'other', date: p.date, recurring: false, notes: p.raw, smartLogSource: p.source });
      else addExpense({ amount: p.amount, description: p.merchant || p.description, category: p.category || 'other', type: 'variable', date: p.date, tags: [], paymentMethod: p.paymentMethod || 'upi', creditCardId: p.creditCardId || null, recurring: false, merchant: p.merchant, notes: p.raw, smartLogSource: p.source });
      setParsedSmartLog(null); setVoiceTranscript(''); setSmsText('');
    };

    // --- Sub-components ---
    const IncomeForm = ({ editData }) => {
      const [amt, setAmt] = useState(editData ? String(editData.amount) : '');
      const [src, setSrc] = useState(editData ? editData.source : '');
      const [cat, setCat] = useState(editData ? editData.category : 'salary');
      const [date, setDate] = useState(editData ? editData.date : new Date().toISOString().split('T')[0]);
      const [rec, setRec] = useState(editData ? !!editData.recurring : false);
      const [freq, setFreq] = useState(editData ? (editData.recurringFrequency || 'monthly') : 'monthly');
      const [nextDue, setNextDue] = useState(editData ? (editData.nextDueDate || '') : '');
      const [showAddCustomIncCat, setShowAddCustomIncCat] = useState(false);
      const [newCatLabel, setNewCatLabel] = useState('');
      const [newCatEmoji, setNewCatEmoji] = useState('📌');
      const isEdit = !!editData;
      return (
        <div className="bg-white border border-emerald-200 rounded-2xl p-5 space-y-3 mt-3 shadow-sm">
          {isEdit && <div className="text-xs font-bold text-emerald-600 flex items-center gap-1"><Edit2 size={12} /> Editing Income Entry</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Amount (₹)</label>
              <input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="0" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Source</label>
              <input type="text" value={src} onChange={e => setSrc(e.target.value)} placeholder="e.g. Company Salary" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none" /></div>
          </div>
          <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allIncomeCategories.map(c => (
                <button key={c.key} onClick={() => setCat(c.key)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${cat === c.key ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{c.emoji} {c.label}</button>
              ))}
              {!showAddCustomIncCat && <button onClick={() => { setShowAddCustomIncCat(true); setNewCatLabel(''); setNewCatEmoji('📌'); }} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-dashed border border-dashed border-gray-300 text-gray-400 hover:text-emerald-500 hover:border-emerald-300">+ New</button>}
            </div>
            {showAddCustomIncCat && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-white rounded-lg border border-emerald-200">
                <select value={newCatEmoji} onChange={e => setNewCatEmoji(e.target.value)} className="px-1 py-1 border border-gray-200 rounded text-sm">
                  {['💼','💻','📈','🏠','💰','🎯','📊','🔔','💎','🏆','📦','🎨'].map(em => <option key={em} value={em}>{em}</option>)}
                </select>
                <input type="text" value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} placeholder="Category name" className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />
                <button onClick={() => { if (newCatLabel.trim()) { addCustomIncomeCategory(newCatLabel.trim(), newCatEmoji); setCat('custom_' + newCatLabel.trim().toLowerCase().replace(/\s+/g, '_')); setShowAddCustomIncCat(false); }}} className="px-2 py-1 bg-emerald-500 text-white rounded text-xs font-medium">Add</button>
                <button onClick={() => setShowAddCustomIncCat(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 outline-none" /></div>
            <div className="flex flex-col gap-2 pt-4">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={rec} onChange={e => setRec(e.target.checked)} className="rounded border-gray-300 text-emerald-500" /> Recurring
              </label>
              {rec && <div className="flex items-center gap-2">
                <select value={freq} onChange={e => setFreq(e.target.value)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
                  <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
                </select>
              </div>}
            </div>
          </div>
          {rec && (
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Next Due Date</label>
              <input type="date" value={nextDue} onChange={e => setNextDue(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 outline-none" />
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowAddIncome(false); setEditingIncome(null); }} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            {isEdit ? (
              <button onClick={() => { if (amt && src) updateIncome(editData.id, { amount: parseFloat(amt), source: src, category: cat, date, recurring: rec, recurringFrequency: rec ? freq : null, nextDueDate: rec ? nextDue : null }); }}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1.5"><Check size={14} /> Update</button>
            ) : (
              <button onClick={() => { if (amt && src) addIncome({ amount: parseFloat(amt), source: src, category: cat, date, recurring: rec, recurringFrequency: rec ? freq : null, nextDueDate: rec ? nextDue : null, notes: '', smartLogSource: 'manual' }); }}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1.5"><Plus size={14} /> Add Income</button>
            )}
          </div>
        </div>
      );
    };

    const ExpenseForm = ({ editData }) => {
      const [amt, setAmt] = useState(editData ? String(editData.amount) : '');
      const [desc, setDesc] = useState(editData ? (editData.description || '') : '');
      const [cat, setCat] = useState(editData ? editData.category : 'other');
      const [typ, setTyp] = useState(editData ? (editData.type || 'variable') : 'variable');
      const [pay, setPay] = useState(editData ? (editData.paymentMethod || 'upi') : 'upi');
      const [card, setCard] = useState(editData ? (editData.creditCardId || null) : null);
      const [date, setDate] = useState(editData ? editData.date : new Date().toISOString().split('T')[0]);
      const [rec, setRec] = useState(editData ? !!editData.recurring : false);
      const [freq, setFreq] = useState(editData ? (editData.recurringFrequency || 'monthly') : 'monthly');
      const [nextDue, setNextDue] = useState(editData ? (editData.nextDueDate || '') : '');
      const [showAddCustomExpCat, setShowAddCustomExpCat] = useState(false);
      const [newCatLabel, setNewCatLabel] = useState('');
      const [newCatEmoji, setNewCatEmoji] = useState('📌');
      const [tags, setTags] = useState(editData ? (Array.isArray(editData.tags) ? editData.tags.join(', ') : '') : '');
      const [merchant, setMerchant] = useState(editData ? (editData.merchant || '') : '');
      const [isRepayment, setIsRepayment] = useState(editData ? !!editData.isRepayment : false);
      const isEdit = !!editData;
      return (
        <div className="bg-white border border-rose-200 rounded-2xl p-5 space-y-3 mt-3 shadow-sm">
          {isEdit && <div className="text-xs font-bold text-rose-600 flex items-center gap-1"><Edit2 size={12} /> Editing Expense Entry</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Amount (₹)</label>
              <input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="0" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Description</label>
              <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What was this for?" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" /></div>
          </div>
          <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allExpenseCategories.map(c => (
                <button key={c.key} onClick={() => setCat(c.key)} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${cat === c.key ? 'bg-rose-500 text-white shadow-sm scale-105' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>{c.emoji} {c.label}</button>
              ))}
              {!showAddCustomExpCat && <button onClick={() => { setShowAddCustomExpCat(true); setNewCatLabel(''); setNewCatEmoji('📌'); }} className="px-2 py-1 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-400 hover:text-rose-500 hover:border-rose-300">+ New</button>}
            </div>
            {showAddCustomExpCat && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-white rounded-lg border border-rose-200">
                <select value={newCatEmoji} onChange={e => setNewCatEmoji(e.target.value)} className="px-1 py-1 border border-gray-200 rounded text-sm">
                  {['📌','🏠','🛒','🍔','🚗','🎬','🛍️','⚡','🏥','📚','🔄','🛡️','🏦','👤','🎁','🐶','✈️','🎮','☕','📱','🏋️','💊','🧹','👶','🎓'].map(em => <option key={em} value={em}>{em}</option>)}
                </select>
                <input type="text" value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} placeholder="Category name" className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />
                <button onClick={() => { if (newCatLabel.trim()) { addCustomExpenseCategory(newCatLabel.trim(), newCatEmoji); setCat('custom_' + newCatLabel.trim().toLowerCase().replace(/\s+/g, '_')); setShowAddCustomExpCat(false); }}} className="px-2 py-1 bg-rose-500 text-white rounded text-xs font-medium">Add</button>
                <button onClick={() => setShowAddCustomExpCat(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</label>
              <div className="flex gap-1.5 mt-1">{['fixed','variable'].map(t => (
                <button key={t} onClick={() => setTyp(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${typ === t ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{t === 'fixed' ? '🔒 Fixed' : '📊 Variable'}</button>
              ))}</div></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Payment</label>
              <select value={pay} onChange={e => { setPay(e.target.value); if (e.target.value !== 'credit') setCard(null); }} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                {PAYMENT_METHODS.map(p => <option key={p.key} value={p.key}>{p.emoji} {p.label}</option>)}
              </select>
              {pay === 'credit' && (expenseData.creditCards || []).length > 0 && (
                <select value={card || ''} onChange={e => setCard(e.target.value ? parseInt(e.target.value) : null)} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                  <option value="">Select card...</option>
                  {(expenseData.creditCards || []).map(c => <option key={c.id} value={c.id}>{c.name} (xx{c.lastFour})</option>)}
                </select>
              )}
              {pay === 'credit' && card && (
                <div className="flex gap-1 mt-1.5">
                  <button onClick={() => setIsRepayment(false)} className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${!isRepayment ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>💳 Card Spend</button>
                  <button onClick={() => setIsRepayment(true)} className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${isRepayment ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>💸 Repayment</button>
                </div>
              )}</div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-rose-300 outline-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={merchant} onChange={e => setMerchant(e.target.value)} placeholder="Merchant (optional)" className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" />
            <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="Tags (comma separated)" className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={rec} onChange={e => setRec(e.target.checked)} className="rounded border-gray-300 text-rose-500" /> Recurring
            </label>
            {rec && <select value={freq} onChange={e => setFreq(e.target.value)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
            </select>}
          </div>
          {rec && (
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Next Due Date</label>
              <input type="date" value={nextDue} onChange={e => setNextDue(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" />
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowAddExpense(false); setEditingExpense(null); }} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            {isEdit ? (
              <button onClick={() => { if (amt && desc) updateExpense(editData.id, { amount: parseFloat(amt), description: desc, category: cat, type: typ, date, tags: tags ? tags.split(',').map(t => t.trim()) : [], paymentMethod: pay, creditCardId: card, recurring: rec, recurringFrequency: rec ? freq : null, nextDueDate: rec ? nextDue : null, merchant, isRepayment: pay === 'credit' && card ? isRepayment : false }); }}
                className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors flex items-center gap-1.5"><Check size={14} /> Update</button>
            ) : (
              <button onClick={() => { if (amt && desc) addExpense({ amount: parseFloat(amt), description: desc, category: cat, type: typ, date, tags: tags ? tags.split(',').map(t => t.trim()) : [], paymentMethod: pay, creditCardId: card, recurring: rec, recurringFrequency: rec ? freq : null, nextDueDate: rec ? nextDue : null, merchant, notes: '', smartLogSource: 'manual', isRepayment: pay === 'credit' && card ? isRepayment : false }); }}
                className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors flex items-center gap-1.5"><Plus size={14} /> Add Expense</button>
            )}
          </div>
        </div>
      );
    };

    const CardForm = () => {
      const [name, setName] = useState(''); const [last4, setLast4] = useState(''); const [limit, setLimit] = useState('');
      const [billDate, setBillDate] = useState('15'); const [dueDate, setDueDate] = useState('5'); const [color, setColor] = useState(CARD_COLORS[0]);
      return (
        <div className="bg-white border border-indigo-200 rounded-2xl p-5 space-y-3 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Card Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. HDFC Regalia" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Last 4 Digits</label>
              <input type="text" maxLength={4} value={last4} onChange={e => setLast4(e.target.value.replace(/\D/g,''))} placeholder="1234" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Credit Limit (₹)</label>
              <input type="number" value={limit} onChange={e => setLimit(e.target.value)} placeholder="200000" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Statement Date</label>
              <input type="number" min="1" max="28" value={billDate} onChange={e => setBillDate(e.target.value)} placeholder="14" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Payment Due</label>
              <input type="number" min="1" max="28" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300" /></div>
          </div>
          <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Card Color</label>
            <div className="flex gap-2 mt-1">{CARD_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-indigo-400 scale-110' : ''}`} style={{ backgroundColor: c }} />
            ))}</div></div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAddCard(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={() => { if (name && last4 && limit) addCreditCard({ name, lastFour: last4, limit: parseFloat(limit), billingDate: parseInt(billDate), dueDate: parseInt(dueDate), color }); }}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors flex items-center gap-1.5"><Plus size={14} /> Add Card</button>
          </div>
        </div>
      );
    };

    const ReceiptQuickEntry = () => {
      const [rAmt, setRAmt] = useState('');
      const [rMerch, setRMerch] = useState('');
      const [rCat, setRCat] = useState('other');
      const [rDate, setRDate] = useState(new Date().toISOString().split('T')[0]);
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={rAmt} onChange={e => setRAmt(e.target.value)} placeholder="Amount ₹" className="px-3 py-2 border border-green-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-300 outline-none" />
            <input type="text" value={rMerch} onChange={e => setRMerch(e.target.value)} placeholder="Merchant / Store" className="px-3 py-2 border border-green-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-300 outline-none" />
          </div>
          <div className="flex flex-wrap gap-1">{EXPENSE_CATEGORIES.map(c => (
            <button key={c.key} onClick={() => setRCat(c.key)} className={`px-2 py-1 rounded-lg text-[11px] transition-all ${rCat === c.key ? 'bg-green-500 text-white font-bold scale-105' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'}`}>{c.emoji}</button>
          ))}</div>
          <div className="flex items-center gap-3">
            <input type="date" value={rDate} onChange={e => setRDate(e.target.value)} className="px-3 py-2 border border-green-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-300 outline-none" />
            <button onClick={() => {
              if (rAmt && rMerch) {
                addExpense({ amount: parseFloat(rAmt), description: rMerch, category: rCat, type: 'variable', date: rDate, tags: [], paymentMethod: 'cash', creditCardId: null, recurring: false, merchant: rMerch, notes: '', smartLogSource: 'receipt' });
                setRAmt(''); setRMerch(''); setRCat('other');
              }
            }} className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-1.5"><Save size={14} /> Save Receipt</button>
          </div>
        </div>
      );
    };

    const TxnEditModal = ({ item, onClose, onSave, onDelete }) => {
      const [amt, setAmt] = useState(String(item.amount || ''));
      const [desc, setDesc] = useState(item.description || '');
      const [cat, setCat] = useState(item.category || 'other');
      const [typ, setTyp] = useState(item.type || 'variable');
      const [pay, setPay] = useState(item.paymentMethod || 'upi');
      const [card, setCard] = useState(item.creditCardId || null);
      const [date, setDate] = useState(item.date || '');
      const [rec, setRec] = useState(!!item.recurring);
      const [freq, setFreq] = useState(item.recurringFrequency || 'monthly');
      const [merchant, setMerchant] = useState(item.merchant || '');
      const [isRepayment, setIsRepayment] = useState(!!item.isRepayment);
      const [confirmDelete, setConfirmDelete] = useState(false);
      return (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={onClose}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={ev => ev.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 rounded-t-2xl flex items-center justify-between z-10">
              <div>
                <div className="text-sm font-bold text-gray-800 flex items-center gap-2"><Edit2 size={14} className="text-rose-500" /> Edit Transaction</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{item.description || item.merchant} · ₹{(item.amount || 0).toLocaleString('en-IN')}</div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            {/* Form */}
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Amount (₹)</label>
                  <input type="number" value={amt} onChange={e => setAmt(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" /></div>
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Description</label>
                  <input type="text" value={desc} onChange={e => setDesc(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" /></div>
              </div>
              <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {allExpenseCategories.map(c => (
                    <button key={c.key} onClick={() => setCat(c.key)} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${cat === c.key ? 'bg-rose-500 text-white shadow-sm scale-105' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>{c.emoji} {c.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</label>
                  <div className="flex gap-1.5 mt-1">{['fixed','variable'].map(t => (
                    <button key={t} onClick={() => setTyp(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${typ === t ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{t === 'fixed' ? '🔒 Fixed' : '📊 Variable'}</button>
                  ))}</div></div>
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Payment</label>
                  <select value={pay} onChange={e => { setPay(e.target.value); if (e.target.value !== 'credit') setCard(null); }} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                    {PAYMENT_METHODS.map(p => <option key={p.key} value={p.key}>{p.emoji} {p.label}</option>)}
                  </select>
                  {pay === 'credit' && (expenseData.creditCards || []).length > 0 && (
                    <select value={card || ''} onChange={e => setCard(e.target.value ? parseInt(e.target.value) : null)} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                      <option value="">Select card...</option>
                      {(expenseData.creditCards || []).map(c => <option key={c.id} value={c.id}>{c.name} (xx{c.lastFour})</option>)}
                    </select>
                  )}
                  {pay === 'credit' && card && (
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => setIsRepayment(false)} className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-medium ${!isRepayment ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-500'}`}>💳 Spend</button>
                      <button onClick={() => setIsRepayment(true)} className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-medium ${isRepayment ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>💸 Repay</button>
                    </div>
                  )}</div>
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date</label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-rose-300 outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Merchant</label>
                  <input type="text" value={merchant} onChange={e => setMerchant(e.target.value)} placeholder="Optional" className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 outline-none" /></div>
                <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Recurring</label>
                  <div className="flex items-center gap-2 mt-1">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={rec} onChange={e => setRec(e.target.checked)} className="rounded border-gray-300 text-rose-500" /> Yes
                    </label>
                    {rec && <select value={freq} onChange={e => setFreq(e.target.value)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
                      <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
                    </select>}
                  </div></div>
              </div>
            </div>
            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 rounded-b-2xl flex items-center justify-between">
              <div>{confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-500 font-medium">Delete this?</span>
                  <button onClick={() => onDelete(item.id)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium">Yes, Delete</button>
                  <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              )}</div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                <button onClick={() => { if (amt && desc) onSave(item.id, { amount: parseFloat(amt), description: desc, category: cat, type: typ, date, paymentMethod: pay, creditCardId: card, recurring: rec, recurringFrequency: rec ? freq : null, merchant, isRepayment: pay === 'credit' && card ? isRepayment : false }); }}
                  className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors flex items-center gap-1.5"><Check size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // ======= MAIN RENDER =======
    if (showTransactionsPage) {
      const filtered = curExpenses.filter(e =>
        (expenseTypeFilter === 'all' || e.type === expenseTypeFilter) &&
        (expenseCategoryFilter === 'all' || e.category === expenseCategoryFilter) &&
        (txnPaymentFilter === 'all' || (txnPaymentFilter.startsWith('cc-') ? e.creditCardId === parseInt(txnPaymentFilter.replace('cc-','')) : e.paymentMethod === txnPaymentFilter)) &&
        (!txnSearchQuery || (e.description || e.merchant || '').toLowerCase().includes(txnSearchQuery.toLowerCase()) || getCatInfo(e.category).label.toLowerCase().includes(txnSearchQuery.toLowerCase()))
      ).sort((a, b) => new Date(b.date) - new Date(a.date));
      const totalFiltered = filtered.reduce((s, e) => s + (e.amount || 0), 0);
      const grouped = {};
      filtered.forEach(e => { const d = e.date; if (!grouped[d]) grouped[d] = []; grouped[d].push(e); });
      const dateKeys = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
      return (
        <div className="flex flex-col h-full -m-4" style={{minHeight: 'calc(100vh - 80px)'}}>
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-400 text-white px-5 py-4 shadow-lg flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setShowTransactionsPage(false)} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
                <ArrowLeft size={18} /> <span className="text-sm font-medium">Back to Expenses</span>
              </button>
              <div className="text-[10px] text-white/70 uppercase tracking-widest">{emMonthNames[selectedMonth.month]} {selectedMonth.year}</div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-white/70 uppercase tracking-widest">Total Spending</div>
                <div className="text-2xl font-black">₹{totalFiltered.toLocaleString('en-IN')}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black">{filtered.length}</div>
                <div className="text-[10px] text-white/70">transactions</div>
              </div>
            </div>
          </div>
          {/* Search + Filters */}
          <div className="bg-white border-b border-gray-200 px-5 py-3 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={txnSearchQuery} onChange={ev => setTxnSearchQuery(ev.target.value)} placeholder="Search transactions..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-300 outline-none" />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {['all','fixed','variable'].map(t => (<button key={t} onClick={()=>setExpenseTypeFilter(t)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${expenseTypeFilter===t?'bg-rose-500 text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t==='all'?'All':t==='fixed'?'🔒 Fixed':'📊 Variable'}</button>))}
              <select value={expenseCategoryFilter} onChange={ev=>setExpenseCategoryFilter(ev.target.value)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
                <option value="all">All Categories</option>{allExpenseCategories.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
              </select>
              <select value={txnPaymentFilter} onChange={ev=>setTxnPaymentFilter(ev.target.value)} className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
                <option value="all">All Payments</option>
                {PAYMENT_METHODS.map(p => <option key={p.key} value={p.key}>{p.emoji} {p.label}</option>)}
                {(expenseData.creditCards||[]).map(c => <option key={`cc-${c.id}`} value={`cc-${c.id}`}>💳 {c.name}</option>)}
              </select>
            </div>
          </div>
          {/* Scrollable Transactions */}
          <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50">
            {dateKeys.length === 0 && <div className="text-center text-gray-400 text-sm py-12">No transactions match your filters</div>}
            {dateKeys.map(dateKey => {
              const dayTotal = grouped[dateKey].reduce((s, e) => s + (e.amount || 0), 0);
              const dayDate = new Date(dateKey);
              const dayLabel = dayDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
              return (
              <div key={dateKey} className="mb-4">
                <div className="flex items-center justify-between mb-1.5 sticky top-0 bg-gray-50 py-1 z-10">
                  <div className="text-xs font-bold text-gray-500">{dayLabel}</div>
                  <div className="text-xs font-bold text-rose-500">₹{dayTotal.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
                  {grouped[dateKey].map(e => (
                    <div key={e.id} className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 group transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="relative flex-shrink-0">
                          <button onClick={() => setEditingCatInline(editingCatInline === e.id ? null : e.id)} className="w-10 h-10 rounded-xl flex items-center justify-center text-xl hover:ring-2 hover:ring-rose-200 transition-all cursor-pointer bg-gray-50" title="Change category">
                            {getCatInfo(e.category).emoji}
                          </button>
                          {editingCatInline === e.id && (
                            <div className="absolute top-11 left-0 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-2 w-48 max-h-52 overflow-y-auto">
                              <div className="text-[9px] font-bold text-gray-400 uppercase px-2 py-1">Change Category</div>
                              {allExpenseCategories.map(c => (
                                <button key={c.key} onClick={() => { updateExpenseInline(e.id, { category: c.key }); setEditingCatInline(null); }} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs hover:bg-gray-50 transition-colors ${e.category === c.key ? 'bg-rose-50 font-bold' : ''}`}>
                                  <span>{c.emoji}</span><span>{c.label}</span>{e.category === c.key && <Check size={10} className="ml-auto text-rose-500" />}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-800 truncate">{e.description || e.merchant}</div>
                          <div className="text-[10px] text-gray-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${e.type==='fixed'?'bg-gray-100 text-gray-500':'bg-blue-50 text-blue-500'}`}>{e.type}</span>
                            <span className="text-gray-300">{getPaymentInfo(e.paymentMethod).emoji}</span>
                            <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 text-[9px]">{getCatInfo(e.category).label}</span>
                            {e.paymentMethod==='credit'&&e.creditCardId&&!e.isRepayment&&<span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[9px] font-medium border border-indigo-200">💳 {((expenseData.creditCards||[]).find(c=>c.id===e.creditCardId)||{}).name||'Card'}</span>}
                            {e.isRepayment && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[9px] font-medium border border-blue-200">💸 Repayment → {((expenseData.creditCards||[]).find(c=>c.id===e.creditCardId)||{}).name||'Card'}</span>}
                            {e.recurring && <span className="text-amber-500 text-[9px]">🔄 {e.recurringFrequency}</span>}
                            {e.smartLogSource && <span className={`text-[9px] px-1 py-0.5 rounded ${e.smartLogSource==='weekly'?'bg-blue-50 text-blue-500':'text-purple-500'}`}>{e.smartLogSource==='weekly'?`W${getWeekNumForDate(new Date(e.date))}`:`⚡${e.smartLogSource}`}</span>}
                            {Array.isArray(e.tags) && e.tags.filter(t=>t.startsWith('adjusted')).map(t=><span key={t} className="px-1 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-medium border border-amber-200">📅 {t}</span>)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-sm font-bold text-rose-600">-₹{e.amount.toLocaleString('en-IN')}</span>
                        <button onClick={() => setTxnEditItem(e)} className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 transition-all"><Edit2 size={13} /></button>
                        <button onClick={() => deleteExpense(e.id)} className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"><X size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>);
            })}
          </div>
          {/* Edit Transaction Modal */}
          {txnEditItem && <TxnEditModal item={txnEditItem} onClose={() => setTxnEditItem(null)} onSave={(id, fields) => { updateExpenseInline(id, fields); setTxnEditItem(null); }} onDelete={(id) => { deleteExpense(id); setTxnEditItem(null); }} />}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {/* Header with integrated month selector */}
        <div className="bg-gradient-to-br from-emerald-600 via-teal-500 to-cyan-500 rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-20 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-white/5 rounded-full translate-y-14 -translate-x-10"></div>
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewMode('week')} className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/15 hover:bg-white/25 transition-all backdrop-blur-sm">
                <ArrowLeft size={16} />
              </button>
              <h2 className="text-lg font-bold tracking-tight">Expenses</h2>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black tracking-tight">₹{savings.toLocaleString('en-IN')}</div>
              <div className="text-white/60 text-[11px] font-medium">saved this month</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 relative z-10">
            <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/15 hover:bg-white/25 transition-all"><ChevronLeft size={16} /></button>
            <span className="text-sm font-bold min-w-[130px] text-center tracking-wide">{emMonthNames[selectedMonth.month]} {selectedMonth.year}</span>
            <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/15 hover:bg-white/25 transition-all"><ChevronRight size={16} /></button>
          </div>
        </div>
        {/* Sub-tabs with icons */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {[
            {key:'dashboard', label:'Home', icon: <Compass size={13}/>},
            {key:'plan', label:'Plan', icon: <ClipboardList size={13}/>},
            {key:'income', label:'In', icon: <ArrowDownLeft size={13}/>},
            {key:'expenses', label:'Out', icon: <ArrowUpRight size={13}/>},
            {key:'chatlog', label:'Chat', icon: <MessageSquare size={13}/>},
            {key:'smartlog', label:'Tools', icon: <Zap size={13}/>},
            {key:'cards', label:'Cards', icon: <CreditCard size={13}/>},
            {key:'insights', label:'Insights', icon: <BarChart3 size={13}/>},
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW ===== */}
        {activeTab === 'dashboard' && (<div className="space-y-3">
          {/* Quick Add - top for easy access */}
          <div className="flex gap-2">
            <button onClick={() => {setShowAddIncome(!showAddIncome);setShowAddExpense(false);}} className="flex-1 py-2 bg-emerald-500 rounded-xl text-white font-semibold text-xs hover:bg-emerald-600 transition-all flex items-center justify-center gap-1.5 shadow-sm"><Plus size={13} /> Income</button>
            <button onClick={() => {setShowAddExpense(!showAddExpense);setShowAddIncome(false);}} className="flex-1 py-2 bg-rose-500 rounded-xl text-white font-semibold text-xs hover:bg-rose-600 transition-all flex items-center justify-center gap-1.5 shadow-sm"><Plus size={13} /> Expense</button>
          </div>
          {showAddIncome && <IncomeForm />}{showAddExpense && <ExpenseForm />}
          {/* Income / Expenses / Fixed vs Variable row */}
          {(() => {
            const fixedExp = curExpenses.filter(e => e.type === 'fixed' || e.recurring);
            const variableExp = curExpenses.filter(e => e.type !== 'fixed' && !e.recurring);
            const fixedTotal = fixedExp.reduce((s, e) => s + (e.amount || 0), 0);
            const variableTotal = variableExp.reduce((s, e) => s + (e.amount || 0), 0);
            const total = fixedTotal + variableTotal;
            const fixedPct = total > 0 ? Math.round((fixedTotal / total) * 100) : 0;
            const variablePct = total > 0 ? 100 - fixedPct : 0;
            return (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Income</div>
                <div className="text-base font-bold text-emerald-600">₹{totalIncome.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Expenses</div>
                <div className="text-base font-bold text-rose-600">₹{totalExpenses.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Fixed / Variable</div>
                <div className="h-2.5 rounded-full overflow-hidden bg-gray-100 flex mb-1">
                  {fixedTotal > 0 && <div className="bg-amber-400 h-full" style={{width: `${fixedPct}%`}}></div>}
                  {variableTotal > 0 && <div className="bg-blue-400 h-full" style={{width: `${variablePct}%`}}></div>}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold text-amber-600">{fixedPct}% Fixed</span>
                  <span className="text-[9px] font-bold text-blue-600">{variablePct}% Var</span>
                </div>
              </div>
            </div>);
          })()}
          {/* Pending Credit Card Payment */}
          {(() => {
            const ccTransactions = curExpenses.filter(e => e.paymentMethod === 'credit' && !e.isRepayment && !e.isCredit);
            const pendingTotal = ccTransactions.reduce((s, e) => s + (e.amount || 0), 0);
            if (pendingTotal === 0) return null;
            const cards = expenseData.creditCards || [];
            const perCard = {};
            ccTransactions.forEach(e => { if (e.creditCardId) perCard[e.creditCardId] = (perCard[e.creditCardId] || 0) + (e.amount || 0); });
            return (
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl shadow-sm border border-violet-200 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] font-bold text-violet-500 uppercase tracking-wider flex items-center gap-1">💳 Pending CC Payment</div>
                <div className="text-base font-bold text-violet-700">₹{pendingTotal.toLocaleString('en-IN')}</div>
              </div>
              <div className="space-y-1">
                {Object.entries(perCard).map(([cardId, amt]) => {
                  const card = cards.find(c => c.id === parseInt(cardId));
                  return card ? (
                  <div key={cardId} className="flex items-center justify-between">
                    <span className="text-[10px] text-violet-600 font-medium">{card.name} {card.lastFour ? `(xx${card.lastFour})` : ''}</span>
                    <span className="text-[10px] font-bold text-violet-700">₹{amt.toLocaleString('en-IN')}</span>
                  </div>) : null;
                })}
              </div>
            </div>);
          })()}
          {/* Weekly Spending Digest */}
          {(() => {
            const today = new Date();
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 6);
            const weekExpenses = (expenseData.expenses || []).filter(e => {
              if (!e.date || e.isRepayment) return false;
              const d = new Date(e.date);
              return d >= weekAgo && d <= today;
            });
            const weekTotal = weekExpenses.reduce((s, e) => s + (e.amount || 0), 0);
            const txnCount = weekExpenses.length;
            const dailyAvg = Math.round(weekTotal / 7);
            const weekCatTotals = {};
            weekExpenses.forEach(e => { weekCatTotals[e.category] = (weekCatTotals[e.category] || 0) + (e.amount || 0); });
            const topCatEntry = Object.entries(weekCatTotals).sort((a, b) => b[1] - a[1])[0];
            const topCat = topCatEntry ? getCatInfo(topCatEntry[0]) : null;
            const topCatAmt = topCatEntry ? topCatEntry[1] : 0;
            const dayTotals = [];
            for (let i = 6; i >= 0; i--) {
              const d = new Date(today);
              d.setDate(today.getDate() - i);
              const dk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const dayTotal = (expenseData.expenses || []).filter(e => e.date === dk && !e.isRepayment).reduce((s, e) => s + (e.amount || 0), 0);
              dayTotals.push({ day: ['S','M','T','W','T','F','S'][d.getDay()], amount: dayTotal });
            }
            const maxDay = Math.max(...dayTotals.map(d => d.amount), 1);
            return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-700 flex items-center gap-1">📊 This Week</span>
                <span className="text-[10px] text-gray-300">{txnCount} transactions</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div>
                  <div className="text-[10px] text-gray-400 font-medium">Spent</div>
                  <div className="text-base font-black text-gray-800">₹{weekTotal.toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 font-medium">Daily Avg</div>
                  <div className="text-base font-black text-gray-800">₹{dailyAvg.toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 font-medium">Top Category</div>
                  {topCat ? (
                    <div className="text-xs font-bold text-gray-700 flex items-center gap-0.5">{topCat.emoji} {topCat.label}</div>
                  ) : (
                    <div className="text-xs text-gray-300">--</div>
                  )}
                  {topCatAmt > 0 && <div className="text-[10px] text-gray-400">₹{topCatAmt.toLocaleString('en-IN')}</div>}
                </div>
              </div>
              <div className="flex items-end gap-1 h-10">
                {dayTotals.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full rounded-t" style={{ height: `${Math.max(2, (d.amount / maxDay) * 32)}px`, background: d.amount > 0 ? (d.amount === maxDay ? '#f43f5e' : '#93c5fd') : '#f3f4f6' }}></div>
                    <span className="text-[8px] text-gray-400">{d.day}</span>
                  </div>
                ))}
              </div>
            </div>);
          })()}
          {/* Cash Flow Forecast */}
          {(() => {
            const recurringIncome = (expenseData.income || []).filter(i => i.recurring).reduce((s, i) => s + (i.amount || 0), 0);
            const recurringExpenses = (expenseData.expenses || []).filter(e => (e.recurring || e.type === 'fixed') && !e.isRepayment).reduce((s, e) => s + (e.amount || 0), 0);
            const netRecurring = recurringIncome - recurringExpenses;
            const currentBalance = totalIncome - totalExpenses;
            const nextMonthProjected = currentBalance + netRecurring;
            const monthAfterProjected = currentBalance + (netRecurring * 2);
            const trend = netRecurring >= 0;
            const m1 = selectedMonth.month;
            const m2 = (selectedMonth.month + 1) % 12;
            const m3 = (selectedMonth.month + 2) % 12;
            const shortMonths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return (
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-3 border border-cyan-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-700 flex items-center gap-1">🔮 Forecast</span>
                {trend ? <span className="text-[10px] text-emerald-500 font-bold flex items-center gap-0.5"><TrendingUp size={10} /> Positive</span> : <span className="text-[10px] text-rose-500 font-bold flex items-center gap-0.5"><TrendingUp size={10} className="rotate-180" /> Negative</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="bg-white/60 rounded-xl p-2 text-center">
                  <div className="text-[9px] text-gray-400 font-medium">{shortMonths[m1]}</div>
                  <div className={`text-sm font-black ${currentBalance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>₹{Math.abs(Math.round(currentBalance)).toLocaleString('en-IN')}</div>
                  {currentBalance < 0 && <div className="text-[8px] text-rose-400">deficit</div>}
                </div>
                <div className="bg-white/60 rounded-xl p-2 text-center">
                  <div className="text-[9px] text-gray-400 font-medium">{shortMonths[m2]}</div>
                  <div className={`text-sm font-black ${nextMonthProjected >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>₹{Math.abs(Math.round(nextMonthProjected)).toLocaleString('en-IN')}</div>
                  {nextMonthProjected < 0 && <div className="text-[8px] text-rose-400">deficit</div>}
                </div>
                <div className="bg-white/60 rounded-xl p-2 text-center">
                  <div className="text-[9px] text-gray-400 font-medium">{shortMonths[m3]}</div>
                  <div className={`text-sm font-black ${monthAfterProjected >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>₹{Math.abs(Math.round(monthAfterProjected)).toLocaleString('en-IN')}</div>
                  {monthAfterProjected < 0 && <div className="text-[8px] text-rose-400">deficit</div>}
                </div>
              </div>
              <div className="text-[9px] text-gray-400 text-center">Based on ₹{recurringIncome.toLocaleString('en-IN')} recurring income and ₹{recurringExpenses.toLocaleString('en-IN')} fixed expenses</div>
            </div>);
          })()}
          {/* Health Score + Velocity — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2.5">
                <svg width="40" height="40" viewBox="0 0 40 40" className="flex-shrink-0">
                  <circle cx="20" cy="20" r="16" fill="none" stroke="#f3f4f6" strokeWidth="3.5" />
                  <circle cx="20" cy="20" r="16" fill="none" stroke={healthScore > 70 ? '#10b981' : healthScore > 40 ? '#f59e0b' : '#ef4444'} strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${healthScore * 1.005} 100.5`} transform="rotate(-90 20 20)" />
                  <text x="20" y="23" textAnchor="middle" fontSize="11" fontWeight="900" fill="#1f2937">{healthScore}</text>
                </svg>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Health</div>
                  <div className={`text-sm font-black ${savingsRate >= 20 ? 'text-emerald-600' : savingsRate >= 0 ? 'text-amber-600' : 'text-red-600'}`}>{savingsRate}% <span className="text-[10px] font-medium text-gray-400">saved</span></div>
                </div>
              </div>
              {/* Cash flow bar */}
              <div className="h-2 rounded-full overflow-hidden bg-gray-100 flex mt-2.5">
                {totalIncome > 0 && <div className="bg-emerald-400 h-full" style={{width:`${Math.min(100,(totalIncome/(totalIncome+totalExpenses||1))*100)}%`}}></div>}
                {totalExpenses > 0 && <div className="bg-rose-400 h-full" style={{width:`${Math.min(100,(totalExpenses/(totalIncome+totalExpenses||1))*100)}%`}}></div>}
              </div>
              <div className="flex justify-between mt-1 text-[9px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>In</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>Out</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Velocity</div>
              {(() => { const dayOfMonth = new Date().getDate(); const daysInMonth = new Date(selectedMonth.year, selectedMonth.month+1,0).getDate(); const dailyRate = dayOfMonth > 0 ? totalExpenses/dayOfMonth : 0; const projected = Math.round(dailyRate*daysInMonth); const totalBudget = Object.values(expenseData.budgets||{}).reduce((s,v)=>s+v,0);
                return (<div><div className="text-lg font-black text-gray-800">₹{Math.round(dailyRate).toLocaleString('en-IN')}<span className="text-[10px] font-medium text-gray-400">/day</span></div><div className="text-[10px] text-gray-400">→ ₹{projected.toLocaleString('en-IN')} projected</div>
                  {totalBudget > 0 && (<div className="mt-1.5"><div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${totalExpenses>totalBudget?'bg-red-400':totalExpenses>totalBudget*0.8?'bg-amber-400':'bg-emerald-400'}`} style={{width:`${Math.min(100,(totalExpenses/totalBudget)*100)}%`}}></div></div><div className="text-[9px] text-gray-400 mt-0.5">{Math.round((totalExpenses/totalBudget)*100)}% of budget</div></div>)}</div>); })()}
            </div>
          </div>
          {/* Top Spends + Upcoming — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Top Spends</div>
              <div className="space-y-2">{Object.entries(catTotals).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([cat,amt]) => {
                const info = getCatInfo(cat); const pct = totalExpenses > 0 ? Math.round((amt/totalExpenses)*100) : 0;
                return (<div key={cat}><div className="flex items-center justify-between mb-0.5"><span className="text-[11px] text-gray-600 font-medium flex items-center gap-1"><span>{info.emoji}</span>{info.label}</span><span className="text-[10px] text-gray-500 font-bold">₹{amt.toLocaleString('en-IN')}</span></div><div className="h-1.5 bg-gray-100 rounded-full"><div className="h-full bg-rose-400 rounded-full" style={{width:`${pct}%`}}></div></div></div>);
              })}{Object.keys(catTotals).length === 0 && <div className="text-center text-gray-400 text-[10px] py-3">No expenses</div>}</div>
            </div>
            {getUpcomingBills().length > 0 ? (
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Upcoming</div>
              <div className="space-y-1">{getUpcomingBills().slice(0, 4).map(bill => (
                <div key={bill.id} className="flex items-center justify-between py-1 group">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1"><span className="text-sm">{getCatInfo(bill.category).emoji}</span><div className="min-w-0"><div className="text-[11px] font-medium text-gray-700 truncate">{bill.description||bill.merchant}</div><div className={`text-[9px] font-medium ${bill.daysUntil<=3?'text-red-500':bill.daysUntil<=7?'text-amber-500':'text-gray-400'}`}>{bill.daysUntil===0?'Due today':`In ${bill.daysUntil}d`}</div></div></div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                    <span className="text-[11px] font-bold text-rose-600">₹{bill.amount.toLocaleString('en-IN')}</span>
                    <button onClick={()=>{ setActiveTab('expenses'); setEditingExpense(bill); setShowAddExpense(true); }} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-all" title="Edit"><Edit2 size={10} /></button>
                    <button onClick={()=>deleteExpense(bill.id)} className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" title="Remove"><X size={10} /></button>
                  </div>
                </div>))}</div>
            </div>
            ) : (
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-400 text-[10px]">No upcoming bills</div>
            </div>
            )}
          </div>
        </div>)}

        {/* ===== MONTH PLAN ===== */}
        {activeTab === 'plan' && (() => {
          const plan = getOrCreateMonthPlan(monthKey);
          const pIncome = plan.plannedIncome || [];
          const pExpenses = plan.plannedExpenses || [];
          const totalPlanInc = pIncome.reduce((s, i) => s + (i.amount || 0), 0);
          const totalPlanExp = pExpenses.reduce((s, e) => s + (e.amount || 0), 0);
          const netBalance = totalPlanInc - totalPlanExp;
          const planCatTotals = {};
          pExpenses.forEach(e => { planCatTotals[e.category] = (planCatTotals[e.category] || 0) + (e.amount || 0); });
          const planCatSorted = Object.entries(planCatTotals).sort((a, b) => b[1] - a[1]);
          return (
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center"><ClipboardList size={14} className="text-white" /></div>
                Draft Plan
              </h3>
              <button onClick={() => { if (window.confirm('Reset plan and re-populate from recurring entries?')) resetMonthPlan(monthKey); }}
                className="px-3 py-1.5 text-gray-400 hover:text-gray-600 text-[10px] font-medium flex items-center gap-1"><RefreshCw size={11} /> Reset</button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 text-center">
                <div className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Expected In</div>
                <div className="text-base font-black text-emerald-700 mt-1">₹{totalPlanInc.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-rose-50 rounded-xl p-3 border border-rose-100 text-center">
                <div className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Planned Out</div>
                <div className="text-base font-black text-rose-700 mt-1">₹{totalPlanExp.toLocaleString('en-IN')}</div>
              </div>
              <div className={`${netBalance >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'} rounded-xl p-3 border text-center`}>
                <div className={`text-[10px] font-semibold ${netBalance >= 0 ? 'text-blue-500' : 'text-red-500'} uppercase tracking-wider`}>Net Balance</div>
                <div className={`text-base font-black ${netBalance >= 0 ? 'text-blue-700' : 'text-red-700'} mt-1`}>{netBalance < 0 ? '-' : ''}₹{Math.abs(netBalance).toLocaleString('en-IN')}</div>
              </div>
            </div>

            {/* Planned Income */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><ArrowDownLeft size={13} className="text-emerald-500" /> Expected Income</div>
                <button onClick={() => {
                  const amt = prompt('Amount:');
                  if (!amt || isNaN(parseFloat(amt))) return;
                  const src = prompt('Source (e.g. Salary, Freelance):') || 'Income';
                  addPlannedIncome(monthKey, { source: src, amount: parseFloat(amt), category: 'salary', isRecurring: false });
                }} className="px-2.5 py-1 bg-emerald-500 text-white rounded-lg text-[10px] font-semibold hover:bg-emerald-600 flex items-center gap-1"><Plus size={10} /> Add</button>
              </div>
              {pIncome.length === 0 && <div className="text-center text-gray-400 text-xs py-4">No planned income. Add expected earnings for this month.</div>}
              <div className="space-y-1">
                {pIncome.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-emerald-50/50 group">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getIncomeCatInfo(item.category).emoji}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-700">{item.source}{item.isRecurring && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-semibold">recurring</span>}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-emerald-600">+₹{(item.amount || 0).toLocaleString('en-IN')}</span>
                      <button onClick={() => {
                        const newAmt = prompt('Amount:', item.amount);
                        if (newAmt !== null && !isNaN(parseFloat(newAmt))) updatePlannedItem(monthKey, 'plannedIncome', item.id, { amount: parseFloat(newAmt) });
                      }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-emerald-100 rounded"><Edit2 size={11} className="text-gray-400" /></button>
                      <button onClick={() => deletePlannedIncome(monthKey, item.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"><X size={11} className="text-gray-400" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Planned Expenses */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><ArrowUpRight size={13} className="text-rose-500" /> Planned Expenses</div>
                <button onClick={() => {
                  const amt = prompt('Amount:');
                  if (!amt || isNaN(parseFloat(amt))) return;
                  const desc = prompt('Description (e.g. Rent, Groceries):') || 'Expense';
                  addPlannedExpense(monthKey, { description: desc, amount: parseFloat(amt), category: 'other', isRecurring: false });
                }} className="px-2.5 py-1 bg-rose-500 text-white rounded-lg text-[10px] font-semibold hover:bg-rose-600 flex items-center gap-1"><Plus size={10} /> Add</button>
              </div>
              {pExpenses.length === 0 && <div className="text-center text-gray-400 text-xs py-4">No planned expenses. Add expected spending for this month.</div>}
              <div className="space-y-1">
                {pExpenses.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-rose-50/50 group">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCatInfo(item.category).emoji}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-700">{item.description}{item.isRecurring && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-semibold">recurring</span>}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-rose-600">-₹{(item.amount || 0).toLocaleString('en-IN')}</span>
                      <button onClick={() => {
                        const newAmt = prompt('Amount:', item.amount);
                        if (newAmt !== null && !isNaN(parseFloat(newAmt))) updatePlannedItem(monthKey, 'plannedExpenses', item.id, { amount: parseFloat(newAmt) });
                      }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-100 rounded"><Edit2 size={11} className="text-gray-400" /></button>
                      <button onClick={() => deletePlannedExpense(monthKey, item.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"><X size={11} className="text-gray-400" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            {planCatSorted.length > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="text-xs font-semibold text-gray-500 mb-3">Planned by Category</div>
                <div className="space-y-2">
                  {planCatSorted.map(([cat, amt]) => {
                    const info = getCatInfo(cat);
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium text-gray-700">{info.emoji} {info.label}</span>
                          <span className="font-bold text-gray-800">₹{amt.toLocaleString('en-IN')} <span className="text-gray-400 font-normal">({totalPlanExp > 0 ? Math.round((amt / totalPlanExp) * 100) : 0}%)</span></span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-indigo-400" style={{ width: `${totalPlanExp > 0 ? (amt / totalPlanExp) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cover the Gap — shown when deficit exists */}
            {netBalance < 0 && (() => {
              const deficit = Math.abs(netBalance);
              const steps = plan.coverSteps || [];
              const coveredTotal = steps.reduce((s, st) => s + (st.amount || 0), 0);
              const coverPct = Math.min(100, Math.round((coveredTotal / deficit) * 100));
              const remaining = Math.max(0, deficit - coveredTotal);
              return (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center"><Scale size={16} className="text-white" /></div>
                    <div>
                      <div className="text-sm font-bold text-amber-900">Cover the Gap</div>
                      <div className="text-[10px] text-amber-600">Plan how to bridge the ₹{deficit.toLocaleString('en-IN')} deficit</div>
                    </div>
                  </div>
                  <button onClick={() => {
                    const desc = prompt('How will you cover this? (e.g. Transfer from savings, Freelance, Sell items)');
                    if (!desc) return;
                    const amt = prompt('Amount (₹):');
                    if (!amt || isNaN(parseFloat(amt))) return;
                    addCoverStep(monthKey, { description: desc, amount: parseFloat(amt) });
                  }} className="px-2.5 py-1 bg-amber-500 text-white rounded-lg text-[10px] font-semibold hover:bg-amber-600 flex items-center gap-1"><Plus size={10} /> Add Step</button>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="font-semibold text-amber-700">Covered: ₹{coveredTotal.toLocaleString('en-IN')} / ₹{deficit.toLocaleString('en-IN')}</span>
                    <span className={`font-bold ${coverPct >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>{coverPct}%</span>
                  </div>
                  <div className="h-3 bg-amber-200/50 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${coverPct >= 100 ? 'bg-emerald-500' : coverPct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                      style={{ width: `${coverPct}%` }}></div>
                  </div>
                  {remaining > 0 && <div className="text-[10px] text-amber-600 mt-1">₹{remaining.toLocaleString('en-IN')} still needs to be covered</div>}
                  {remaining <= 0 && <div className="text-[10px] text-emerald-600 font-semibold mt-1">Deficit fully covered!</div>}
                </div>

                {/* Steps list */}
                {steps.length === 0 && <div className="text-center text-amber-500 text-xs py-2">Add steps to plan how you'll cover the deficit</div>}
                <div className="space-y-1">
                  {steps.map(step => (
                    <div key={step.id} className={`flex items-center justify-between py-2 px-3 rounded-xl group ${step.resolved ? 'bg-emerald-50/80' : 'bg-white/60'}`}>
                      <div className="flex items-center gap-2.5">
                        <button onClick={() => toggleCoverStep(monthKey, step.id)}
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${step.resolved ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-amber-400'}`}>
                          {step.resolved && <Check size={12} className="text-white" />}
                        </button>
                        <div>
                          <div className={`text-sm font-medium ${step.resolved ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{step.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${step.resolved ? 'text-emerald-500' : 'text-amber-700'}`}>+₹{(step.amount || 0).toLocaleString('en-IN')}</span>
                        <button onClick={() => deleteCoverStep(monthKey, step.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"><X size={11} className="text-gray-400" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })()}

            {/* Net Summary */}
            <div className={`rounded-2xl p-4 border ${netBalance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${netBalance >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}>
                  {netBalance >= 0 ? <TrendingUp size={20} className="text-white" /> : <AlertTriangle size={20} className="text-white" />}
                </div>
                <div>
                  <div className={`text-sm font-bold ${netBalance >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                    {netBalance >= 0 ? 'Looking good!' : 'Over budget!'}
                  </div>
                  <div className={`text-xs ${netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {netBalance >= 0
                      ? `You should have ₹${netBalance.toLocaleString('en-IN')} remaining after planned expenses`
                      : `You're planning to spend ₹${Math.abs(netBalance).toLocaleString('en-IN')} more than expected income`}
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* ===== MONEY IN ===== */}
        {activeTab === 'income' && (<div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center"><ArrowDownLeft size={14} className="text-white" /></div> Income</h3>
            <button onClick={() => { setEditingIncome(null); setShowAddIncome(!showAddIncome); }} className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition-all shadow-sm flex items-center gap-1.5"><Plus size={12} /> Add</button>
          </div>
          {(showAddIncome || editingIncome) && <IncomeForm editData={editingIncome} />}
          {(expenseData.income||[]).filter(i=>i.recurring).length > 0 && (
            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
              <div className="text-xs text-emerald-700 font-bold mb-3">Recurring Income</div>
              <div className="grid grid-cols-2 gap-2">{(expenseData.income||[]).filter(i=>i.recurring).map(i => (
                <div key={i.id} className="bg-white rounded-xl p-3 flex items-center justify-between border border-emerald-50">
                  <div><div className="text-sm font-medium text-gray-700">{getIncomeCatInfo(i.category).emoji} {i.source}</div><div className="text-[11px] text-gray-400">{i.recurringFrequency}{i.nextDueDate && <span className="text-emerald-500 ml-1">· Due: {i.nextDueDate}</span>}</div></div>
                  <div className="text-sm font-bold text-emerald-600">₹{i.amount.toLocaleString('en-IN')}</div>
                </div>))}</div>
              <div className="mt-3 text-right text-xs font-bold text-emerald-700">Expected monthly: ₹{(expenseData.income||[]).filter(i=>i.recurring&&(i.recurringFrequency==='monthly'||!i.recurringFrequency)).reduce((s,i)=>s+i.amount,0).toLocaleString('en-IN')}</div>
            </div>)}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">Income by Category</div>
            {(() => { const incomeByCat = {}; curIncome.forEach(i => { incomeByCat[i.category] = (incomeByCat[i.category]||0)+i.amount; });
              return Object.entries(incomeByCat).length > 0 ? (<div className="space-y-2">{Object.entries(incomeByCat).sort((a,b)=>b[1]-a[1]).map(([cat,amt]) => {
                const info = getIncomeCatInfo(cat); const pct = totalIncome > 0 ? Math.round((amt/totalIncome)*100) : 0;
                return (<div key={cat} className="flex items-center gap-2"><span className="text-lg">{info.emoji}</span><div className="flex-1"><div className="flex justify-between text-xs"><span className="font-medium text-gray-700">{info.label}</span><span className="text-emerald-600 font-bold">₹{amt.toLocaleString('en-IN')} ({pct}%)</span></div><div className="h-2 bg-gray-100 rounded-full mt-1"><div className="h-full bg-emerald-400 rounded-full" style={{width:`${pct}%`}}></div></div></div></div>);
              })}</div>) : <div className="text-center text-gray-400 text-xs py-4">No income this month</div>; })()}
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">All Income · {emMonthNames[selectedMonth.month]}</div>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {curIncome.length > 0 ? curIncome.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(i => (
                <div key={i.id} className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-emerald-50/50 group transition-colors">
                  <div className="flex items-center gap-2"><span className="text-lg">{getIncomeCatInfo(i.category).emoji}</span><div><div className="text-sm font-medium text-gray-700">{i.source}</div><div className="text-[10px] text-gray-400">{i.date}{i.recurring && <span className="text-emerald-500"> · 🔄 {i.recurringFrequency}</span>}{i.recurring && i.nextDueDate && <span className="text-emerald-400"> · Next: {i.nextDueDate}</span>}{i.smartLogSource==='weekly' && <span className="ml-1 px-1 py-0.5 rounded bg-blue-50 text-blue-500 text-[9px] font-medium">W{getWeekNumForDate(new Date(i.date))}</span>}{i.notes&&i.notes.startsWith('adjusted')&&<span className="ml-1 px-1 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-medium border border-amber-200">📅 {i.notes}</span>}</div></div></div>
                  <div className="flex items-center gap-2"><span className="text-sm font-bold text-emerald-600">+₹{i.amount.toLocaleString('en-IN')}</span><button onClick={()=>{ setEditingIncome(i); setShowAddIncome(true); }} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-emerald-500 transition-all"><Edit2 size={12} /></button><button onClick={()=>deleteIncome(i.id)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 transition-all"><X size={12} /></button></div>
                </div>)) : <div className="text-center text-gray-400 text-xs py-4">No income entries this month</div>}
            </div>
          </div>
        </div>)}

        {/* ===== MONEY OUT ===== */}
        {activeTab === 'expenses' && (<div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center"><ArrowUpRight size={14} className="text-white" /></div> Expenses</h3>
            <button onClick={()=>{ setEditingExpense(null); setShowAddExpense(!showAddExpense); }} className="px-4 py-2 bg-rose-500 text-white rounded-xl text-xs font-semibold hover:bg-rose-600 transition-all shadow-sm flex items-center gap-1.5"><Plus size={12} /> Add</button>
          </div>
          {(showAddExpense || editingExpense) && <ExpenseForm editData={editingExpense} />}
          {/* Total Spending */}
          {(() => {
            const totalExp = curSpending.reduce((s, e) => s + (e.amount || 0), 0);
            return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Spending · {emMonthNames[selectedMonth.month]}</div>
              <div className="text-2xl font-bold text-rose-600 tracking-tight">₹{totalExp.toLocaleString('en-IN')}</div>
            </div>);
          })()}
          {/* Pending Credit Card Payment */}
          {(() => {
            const ccTransactions = curExpenses.filter(e => e.paymentMethod === 'credit' && !e.isRepayment && !e.isCredit);
            const pendingTotal = ccTransactions.reduce((s, e) => s + (e.amount || 0), 0);
            if (pendingTotal === 0) return null;
            const cards = expenseData.creditCards || [];
            const perCard = {};
            ccTransactions.forEach(e => {
              if (e.creditCardId) {
                perCard[e.creditCardId] = (perCard[e.creditCardId] || 0) + (e.amount || 0);
              }
            });
            return (
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl shadow-sm border border-violet-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold text-violet-500 uppercase tracking-wider flex items-center gap-1.5">💳 Pending Credit Card Payment</div>
                <div className="text-lg font-bold text-violet-700">₹{pendingTotal.toLocaleString('en-IN')}</div>
              </div>
              <div className="space-y-1.5">
                {Object.entries(perCard).map(([cardId, amt]) => {
                  const card = cards.find(c => c.id === parseInt(cardId));
                  return card ? (
                  <div key={cardId} className="flex items-center justify-between">
                    <span className="text-[11px] text-violet-600 font-medium">{card.name} {card.lastFour ? `(xx${card.lastFour})` : ''}</span>
                    <span className="text-[11px] font-bold text-violet-700">₹{amt.toLocaleString('en-IN')}</span>
                  </div>) : null;
                })}
              </div>
            </div>);
          })()}
          {/* Fixed vs Variable + Category Breakdown */}
          {(() => {
            const allExp = curExpenses;
            const totalExp = allExp.reduce((s, e) => s + (e.amount || 0), 0);
            if (totalExp === 0) return null;
            const fixedExp = allExp.filter(e => e.type === 'fixed' || e.recurring);
            const variableExp = allExp.filter(e => e.type !== 'fixed' && !e.recurring);
            const fixedTotal = fixedExp.reduce((s, e) => s + (e.amount || 0), 0);
            const variableTotal = variableExp.reduce((s, e) => s + (e.amount || 0), 0);
            const fixedPct = totalExp > 0 ? Math.round((fixedTotal / totalExp) * 100) : 0;
            const variablePct = 100 - fixedPct;
            // Category totals
            const catTotals = {};
            allExp.forEach(e => {
              const k = e.category || 'other';
              catTotals[k] = (catTotals[k] || 0) + (e.amount || 0);
            });
            const sortedCats = Object.entries(catTotals).sort((a, b) => b[1] - a[1]);
            const maxCatAmt = sortedCats[0]?.[1] || 1;
            return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              {/* Fixed vs Variable row */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1">
                  <div className="h-3 rounded-full overflow-hidden bg-gray-100 flex">
                    {fixedTotal > 0 && <div className="bg-amber-400 h-full" style={{width: `${fixedPct}%`}}></div>}
                    {variableTotal > 0 && <div className="bg-blue-400 h-full" style={{width: `${variablePct}%`}}></div>}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                  <span className="text-[11px] font-semibold text-gray-600">Fixed ₹{fixedTotal.toLocaleString('en-IN')}</span>
                  <span className="text-[10px] text-gray-400">({fixedPct}%)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-400"></div>
                  <span className="text-[11px] font-semibold text-gray-600">Variable ₹{variableTotal.toLocaleString('en-IN')}</span>
                  <span className="text-[10px] text-gray-400">({variablePct}%)</span>
                </div>
              </div>
              {/* Category Breakdown */}
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">Category Spending · {emMonthNames[selectedMonth.month]}</div>
              <div className="space-y-2">
                {sortedCats.map(([cat, amt]) => {
                  const info = getCatInfo(cat);
                  return (
                  <div key={cat} className="flex items-center gap-2">
                    <span className="text-sm w-6 text-center flex-shrink-0">{info.emoji}</span>
                    <span className="text-[11px] text-gray-600 w-20 truncate flex-shrink-0">{info.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-rose-400 h-full rounded-full transition-all" style={{ width: `${(amt / maxCatAmt) * 100}%` }}></div>
                    </div>
                    <span className="text-[11px] font-bold text-gray-700 w-20 text-right flex-shrink-0">₹{amt.toLocaleString('en-IN')}</span>
                  </div>);
                })}
              </div>
            </div>);
          })()}
          {/* Recent Transactions Preview + View All */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-gray-500">Recent Transactions</div>
              <button onClick={() => { setShowTransactionsPage(true); setTxnSearchQuery(''); }} className="px-3.5 py-1.5 bg-rose-500 text-white rounded-xl text-xs font-semibold hover:bg-rose-600 transition-all shadow-sm flex items-center gap-1.5">View All ({curExpenses.length}) <ChevronRight size={12} /></button>
            </div>
            <div className="space-y-1">{curExpenses.sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-rose-50/50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getCatInfo(e.category).emoji}</span>
                  <div>
                    <div className="text-sm font-medium text-gray-700">{e.description||e.merchant}</div>
                    <div className="text-[10px] text-gray-400">{e.date} · {getCatInfo(e.category).label}{e.paymentMethod==='credit'&&e.creditCardId&&!e.isRepayment?` · 💳 ${((expenseData.creditCards||[]).find(c=>c.id===e.creditCardId)||{}).name||'Card'}`:''}{e.isRepayment ? ` · 💸 → ${((expenseData.creditCards||[]).find(c=>c.id===e.creditCardId)||{}).name||'Card'}` : ''}</div>
                  </div>
                </div>
                <span className="text-sm font-bold text-rose-600">-₹{e.amount.toLocaleString('en-IN')}</span>
              </div>))}
              {curExpenses.length === 0 && <div className="text-center text-gray-400 text-xs py-4">No expenses this month</div>}
              {curExpenses.length > 5 && <div className="text-center pt-2"><button onClick={() => { setShowTransactionsPage(true); setTxnSearchQuery(''); }} className="text-xs text-rose-500 hover:text-rose-700 font-medium">+{curExpenses.length - 5} more transactions →</button></div>}
            </div>
          </div>

        </div>)}

        {/* ===== SMART LOG ===== */}
        {activeTab === 'smartlog' && (<div className="space-y-3">
          <div className="text-center py-1"><div className="text-sm font-bold text-gray-800">Smart Log</div><div className="text-[11px] text-gray-400">Speak, paste, or import</div></div>
          {/* Voice */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-purple-500 flex items-center justify-center"><Mic size={14} className="text-white" /></div><h3 className="text-sm font-semibold text-gray-700">Voice Log</h3><span className="text-[11px] text-gray-400 ml-auto">Say "spent 500 on groceries"</span></div>
            <div className="flex flex-col items-center gap-3">
              <button onClick={startVoiceInput} disabled={isListening} className={`w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg ${isListening?'bg-red-500 text-white animate-pulse scale-110':'bg-purple-500 text-white hover:bg-purple-600 hover:scale-105'}`}>{isListening?<MicOff size={24} />:<Mic size={24} />}</button>
              {isListening && <div className="text-sm text-purple-600 animate-pulse">Listening...</div>}
              {voiceTranscript && <div className="w-full bg-white rounded-lg p-3 text-sm text-gray-700 border border-purple-100">"{voiceTranscript}"</div>}
              {voiceError && <div className="text-xs text-red-500">{voiceError}</div>}
            </div>
          </div>
          {/* SMS */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-blue-500 flex items-center justify-center"><MessageSquare size={14} className="text-white" /></div><h3 className="text-sm font-semibold text-gray-700">SMS / Text Parser</h3></div>
            <textarea value={smsText} onChange={e=>setSmsText(e.target.value)} placeholder={"Paste your bank SMS here...\ne.g. Rs.500.00 debited from a/c **1234 on 14-Feb-26. Info: UPI/AMAZON"} className="w-full h-20 px-3 py-2 border border-blue-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-300 outline-none" />
            <button onClick={()=>{if(smsText.trim())parseSMS(smsText);}} className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors">Parse SMS</button>
          </div>
          {/* Smart Statement Import */}
          {stmtWizardStep === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center"><FileText size={14} className="text-white" /></div><h3 className="text-sm font-semibold text-gray-700">Statement Import</h3></div>
              <button onClick={() => setStmtWizardStep(1)} className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 flex items-center gap-1.5"><Upload size={12} /> Import</button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Auto-categorize your bank statement with smart parsing</p>
          </div>
          ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-amber-200 overflow-hidden">
            {/* Step Indicator */}
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold text-sm">Smart Statement Import</h3>
                <button onClick={() => { setStmtWizardStep(0); setStmtParsedRows([]); setStmtRawText(''); setStmtSelected({}); setStmtAdjustments([]); }} className="text-white/70 hover:text-white"><X size={16} /></button>
              </div>
              <div className="flex items-center justify-center gap-1">
                {[{n:1,l:'Upload'},{n:2,l:'Review'},{n:3,l:'Adjust'},{n:4,l:'Import'}].map((s,i) => (
                  <React.Fragment key={s.n}>
                    {i > 0 && <div className={`w-6 h-0.5 ${stmtWizardStep >= s.n ? 'bg-white' : 'bg-white/30'}`} />}
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition-all ${stmtWizardStep === s.n ? 'bg-white text-amber-600 shadow' : stmtWizardStep > s.n ? 'bg-white/30 text-white' : 'text-white/50'}`}>
                      {stmtWizardStep > s.n ? <Check size={10} /> : <span>{s.n}</span>} <span className="hidden sm:inline">{s.l}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="p-4">
            {/* Step 1: Upload/Paste */}
            {stmtWizardStep === 1 && (<div className="space-y-3">
              <div className="text-xs text-gray-500">Paste your bank statement text or upload a .txt/.csv file</div>
              <textarea value={stmtRawText} onChange={e => setStmtRawText(e.target.value)} placeholder={"Paste bank statement here...\n\nSupported formats:\n• Citi Bank (tab-separated with Deposit/Withdrawal columns)\n• Standard Dr/Cr format\n• CSV exports"} className="w-full h-40 px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none font-mono focus:ring-2 focus:ring-amber-300 outline-none" />
              <div className="flex items-center gap-3">
                <button onClick={() => { if (stmtRawText.trim()) parseStatementSmart(stmtRawText); }} disabled={!stmtRawText.trim()} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"><Activity size={14} /> Parse Statement</button>
                <label className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1.5">
                  <Upload size={14} /> Upload File
                  <input type="file" accept=".txt,.csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = ev => { const text = ev.target?.result; if (text) { setStmtRawText(text); } }; r.readAsText(f); } e.target.value = ''; }} />
                </label>
              </div>
            </div>)}

            {/* Step 2: Review Parsed Results */}
            {stmtWizardStep === 2 && (() => {
              const selRows = stmtParsedRows.filter(r => stmtSelected[r.id]);
              const incomeRows = selRows.filter(r => r.type === 'income');
              const expenseRows = selRows.filter(r => r.type === 'expense');
              const totalInc = incomeRows.reduce((s, r) => s + r.amount, 0);
              const totalExp = expenseRows.reduce((s, r) => s + r.amount, 0);
              return (<div className="space-y-3">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-emerald-50 rounded-xl p-2 text-center"><div className="text-[9px] font-bold text-emerald-500 uppercase">Income</div><div className="text-sm font-black text-emerald-700">₹{totalInc.toLocaleString('en-IN')}</div><div className="text-[9px] text-emerald-400">{incomeRows.length} entries</div></div>
                  <div className="bg-rose-50 rounded-xl p-2 text-center"><div className="text-[9px] font-bold text-rose-500 uppercase">Expenses</div><div className="text-sm font-black text-rose-700">₹{totalExp.toLocaleString('en-IN')}</div><div className="text-[9px] text-rose-400">{expenseRows.length} entries</div></div>
                  <div className="bg-blue-50 rounded-xl p-2 text-center"><div className="text-[9px] font-bold text-blue-500 uppercase">Total</div><div className="text-sm font-black text-blue-700">{stmtParsedRows.length}</div><div className="text-[9px] text-blue-400">{selRows.length} selected</div></div>
                  <div className="bg-purple-50 rounded-xl p-2 text-center"><div className="text-[9px] font-bold text-purple-500 uppercase">Dates</div><div className="text-[10px] font-bold text-purple-700">{stmtDateRange.from?.slice(5) || '—'}</div><div className="text-[9px] text-purple-400">to {stmtDateRange.to?.slice(5) || '—'}</div></div>
                </div>
                {/* Select All toggle */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={stmtParsedRows.every(r => stmtSelected[r.id])} onChange={e => { const v = e.target.checked; const ns = {}; stmtParsedRows.forEach(r => { ns[r.id] = v; }); setStmtSelected(ns); }} className="rounded border-gray-300" /> Select All
                  </label>
                  <div className="text-[10px] text-gray-400">Click category badge to change</div>
                </div>
                {/* Table */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">{stmtParsedRows.map(row => {
                    const catInfo = row.type === 'income' ? (getIncomeCatInfo(row.category)) : (getCatInfo(row.category));
                    return (
                    <div key={row.id} className={`flex items-center gap-2 px-3 py-2 border-b border-gray-50 text-xs transition-colors ${stmtSelected[row.id] ? 'bg-white' : 'bg-gray-50 opacity-50'}`}>
                      <input type="checkbox" checked={!!stmtSelected[row.id]} onChange={e => setStmtSelected(p => ({ ...p, [row.id]: e.target.checked }))} className="rounded border-gray-300 flex-shrink-0" />
                      <span className="text-gray-400 w-[70px] flex-shrink-0">{row.date}</span>
                      <span className="flex-1 text-gray-700 truncate" title={row.rawDescription}>{row.description}</span>
                      <div className="relative group flex-shrink-0">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium cursor-pointer ${row.type === 'income' ? 'bg-emerald-100 text-emerald-700' : (catInfo.color || 'bg-gray-100 text-gray-600')}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${row.categoryConfidence === 'high' ? 'bg-green-400' : row.categoryConfidence === 'medium' ? 'bg-yellow-400' : 'bg-red-400'}`}></span>
                          {catInfo.emoji}
                        </span>
                        <div className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-xl p-1 min-w-[140px]">
                          {(row.type === 'income' ? INCOME_CATEGORIES : [...EXPENSE_CATEGORIES, ...(expenseData.customExpenseCategories || [])]).map(c => (
                            <button key={c.key} onClick={() => setStmtParsedRows(prev => prev.map(r => r.id === row.id ? { ...r, category: c.key, categoryConfidence: 'high' } : r))} className={`w-full text-left px-2 py-1 rounded text-[10px] hover:bg-gray-50 ${row.category === c.key ? 'font-bold bg-amber-50' : ''}`}>{c.emoji} {c.label}</button>
                          ))}
                        </div>
                      </div>
                      <span className={`font-bold flex-shrink-0 w-24 text-right ${row.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{row.type === 'income' ? '+' : '-'}₹{row.amount.toLocaleString('en-IN')}</span>
                    </div>);
                  })}</div>
                </div>
                {/* Navigation */}
                <div className="flex justify-between items-center pt-2">
                  <button onClick={() => setStmtWizardStep(1)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><ChevronLeft size={14} /> Back</button>
                  <button onClick={() => setStmtWizardStep(3)} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 flex items-center gap-1">Adjust <ChevronRight size={14} /></button>
                </div>
              </div>);
            })()}

            {/* Step 3: Conversational Adjustments */}
            {stmtWizardStep === 3 && (() => {
              const selCount = Object.values(stmtSelected).filter(Boolean).length;
              const selInc = stmtParsedRows.filter(r => stmtSelected[r.id] && r.type === 'income').reduce((s, r) => s + r.amount, 0);
              const selExp = stmtParsedRows.filter(r => stmtSelected[r.id] && r.type === 'expense').reduce((s, r) => s + r.amount, 0);
              return (<div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-emerald-50 rounded-lg p-2"><div className="text-[9px] font-bold text-emerald-500">INCOME</div><div className="text-xs font-black text-emerald-700">₹{selInc.toLocaleString('en-IN')}</div></div>
                  <div className="bg-rose-50 rounded-lg p-2"><div className="text-[9px] font-bold text-rose-500">EXPENSES</div><div className="text-xs font-black text-rose-700">₹{selExp.toLocaleString('en-IN')}</div></div>
                  <div className="bg-blue-50 rounded-lg p-2"><div className="text-[9px] font-bold text-blue-500">SELECTED</div><div className="text-xs font-black text-blue-700">{selCount} entries</div></div>
                </div>
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase self-center mr-1">Quick:</span>
                  <button onClick={() => { const ns = {}; stmtParsedRows.forEach(r => { ns[r.id] = true; }); setStmtSelected(ns); }} className="px-2 py-1 rounded-lg text-[10px] bg-gray-100 text-gray-600 hover:bg-gray-200">Select All</button>
                  <button onClick={() => processAdjustment('remove self transfers')} className="px-2 py-1 rounded-lg text-[10px] bg-gray-100 text-gray-600 hover:bg-gray-200">Remove Self Transfers</button>
                </div>
                {/* Chat Adjustments */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200"><div className="text-[10px] font-bold text-gray-500 uppercase">Adjustments</div></div>
                  <div className="max-h-[250px] overflow-y-auto p-3 space-y-2 flex flex-col-reverse">
                    {stmtAdjustments.length === 0 && (
                      <div className="text-center py-4 text-gray-400 text-xs space-y-1.5">
                        <div className="font-medium">Type commands to adjust your data</div>
                        <div className="space-y-0.5 text-[10px]">
                          <div className="text-gray-500">"All Devaraj T are Water Can"</div>
                          <div className="text-gray-500">"Move Jan dates to Feb 01"</div>
                          <div className="text-gray-500">"Airtel is Wifi + Phone under utilities"</div>
                          <div className="text-gray-500">"Remove self transfers"</div>
                          <div className="text-gray-500">"Water Can is weekly"</div>
                        </div>
                      </div>
                    )}
                    {stmtAdjustments.map(adj => (
                      <div key={adj.id} className="space-y-1.5">
                        <div className="flex justify-end"><div className="bg-amber-500 text-white px-3 py-1.5 rounded-2xl rounded-br-sm text-xs max-w-[80%]">{adj.userMessage}</div></div>
                        <div className="flex justify-start"><div className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-2xl rounded-bl-sm text-xs max-w-[80%]">{adj.appliedCount > 0 ? <span className="text-emerald-600 font-medium">{adj.systemResponse}</span> : <span className="text-amber-600">{adj.systemResponse}</span>}</div></div>
                      </div>
                    ))}
                  </div>
                  <div className="p-2 border-t border-gray-200 flex gap-2">
                    <input type="text" value={stmtAdjustInput} onChange={e => setStmtAdjustInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && stmtAdjustInput.trim()) { processAdjustment(stmtAdjustInput.trim()); setStmtAdjustInput(''); } }} placeholder='e.g. "All Devaraj T are Water Can"' className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-300 outline-none" />
                    <button onClick={() => { if (stmtAdjustInput.trim()) { processAdjustment(stmtAdjustInput.trim()); setStmtAdjustInput(''); } }} className="px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600">Apply</button>
                  </div>
                </div>
                {/* Navigation */}
                <div className="flex justify-between items-center pt-2">
                  <button onClick={() => setStmtWizardStep(2)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><ChevronLeft size={14} /> Review</button>
                  <button onClick={() => setStmtWizardStep(4)} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 flex items-center gap-1">Final Review <ChevronRight size={14} /></button>
                </div>
              </div>);
            })()}

            {/* Step 4: Final Review & Import */}
            {stmtWizardStep === 4 && (() => {
              const selRows = stmtParsedRows.filter(r => stmtSelected[r.id]);
              const incRows = selRows.filter(r => r.type === 'income');
              const expRows = selRows.filter(r => r.type === 'expense');
              const totalInc = incRows.reduce((s, r) => s + r.amount, 0);
              const totalExp = expRows.reduce((s, r) => s + r.amount, 0);
              // Group by category
              const catGroups = {};
              selRows.forEach(r => { const k = r.type + '_' + r.category; if (!catGroups[k]) catGroups[k] = { type: r.type, category: r.category, rows: [], total: 0 }; catGroups[k].rows.push(r); catGroups[k].total += r.amount; });
              return (<div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-emerald-50 rounded-xl p-3 text-center"><div className="text-[9px] font-bold text-emerald-500 uppercase">Total Income</div><div className="text-lg font-black text-emerald-700">₹{totalInc.toLocaleString('en-IN')}</div><div className="text-[9px] text-emerald-400">{incRows.length} entries</div></div>
                  <div className="bg-rose-50 rounded-xl p-3 text-center"><div className="text-[9px] font-bold text-rose-500 uppercase">Total Expenses</div><div className="text-lg font-black text-rose-700">₹{totalExp.toLocaleString('en-IN')}</div><div className="text-[9px] text-rose-400">{expRows.length} entries</div></div>
                  <div className={`rounded-xl p-3 text-center ${totalInc - totalExp >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}><div className="text-[9px] font-bold text-gray-500 uppercase">Net</div><div className={`text-lg font-black ${totalInc - totalExp >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>{totalInc - totalExp >= 0 ? '+' : ''}₹{(totalInc - totalExp).toLocaleString('en-IN')}</div></div>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="max-h-[350px] overflow-y-auto">
                    {Object.values(catGroups).sort((a, b) => b.total - a.total).map(group => {
                      const ci = group.type === 'income' ? getIncomeCatInfo(group.category) : getCatInfo(group.category);
                      return (<div key={group.type + group.category}>
                        <div className="bg-gray-50 px-3 py-1.5 flex items-center justify-between border-b border-gray-200 sticky top-0">
                          <span className="text-[10px] font-bold text-gray-600">{ci.emoji} {ci.label} ({group.rows.length})</span>
                          <span className={`text-xs font-bold ${group.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{group.type === 'income' ? '+' : '-'}₹{group.total.toLocaleString('en-IN')}</span>
                        </div>
                        {group.rows.map(row => (
                          <div key={row.id} className="flex items-center justify-between px-3 py-1.5 border-b border-gray-50 text-xs">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-gray-400 w-[70px] flex-shrink-0">{row.date}</span>
                              <span className="text-gray-700 truncate">{row.description}</span>
                              {row.recurring && <span className="text-amber-500 text-[9px] flex-shrink-0">🔄 {row.recurringFrequency}</span>}
                              {row.tags.filter(t => t.startsWith('adjusted')).map(t => <span key={t} className="px-1 py-0.5 rounded bg-amber-50 text-amber-600 text-[8px] font-medium border border-amber-200 flex-shrink-0">📅 {t}</span>)}
                            </div>
                            <span className={`font-bold flex-shrink-0 ml-2 ${group.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{group.type === 'income' ? '+' : '-'}₹{row.amount.toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>);
                    })}
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <button onClick={() => setStmtWizardStep(3)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><ChevronLeft size={14} /> Adjust</button>
                  <button onClick={importStatementWizard} className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl text-sm font-bold hover:from-emerald-600 hover:to-teal-600 shadow-lg flex items-center gap-2"><Check size={16} /> Import {selRows.length} Entries</button>
                </div>
              </div>);
            })()}
            </div>
          </div>
          )}
          {/* Receipt */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-green-500 flex items-center justify-center"><Receipt size={14} className="text-white" /></div><h3 className="text-sm font-bold text-gray-700">Receipt Quick Entry</h3></div>
            <ReceiptQuickEntry />
          </div>
          {/* Parsed Preview */}
          {parsedSmartLog && (<div className="bg-white rounded-2xl shadow-sm border border-emerald-200 p-5">
            <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-3">✨ Parsed Result — Review & Confirm</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-[10px] text-gray-400 font-bold">Type</label><div className="flex gap-2 mt-1">
                <button onClick={()=>setParsedSmartLog(p=>({...p,type:'expense'}))} className={`px-3 py-1 rounded-lg text-xs font-medium ${parsedSmartLog.type==='expense'?'bg-rose-500 text-white':'bg-gray-100 text-gray-600'}`}>Expense</button>
                <button onClick={()=>setParsedSmartLog(p=>({...p,type:'income'}))} className={`px-3 py-1 rounded-lg text-xs font-medium ${parsedSmartLog.type==='income'?'bg-emerald-500 text-white':'bg-gray-100 text-gray-600'}`}>Income</button></div></div>
              <div><label className="text-[10px] text-gray-400 font-bold">Amount (₹)</label><input type="number" value={parsedSmartLog.amount||''} onChange={e=>setParsedSmartLog(p=>({...p,amount:parseFloat(e.target.value)||0}))} className="w-full mt-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm" /></div>
              <div><label className="text-[10px] text-gray-400 font-bold">Description</label><input type="text" value={parsedSmartLog.description||''} onChange={e=>setParsedSmartLog(p=>({...p,description:e.target.value}))} className="w-full mt-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm" /></div>
              <div><label className="text-[10px] text-gray-400 font-bold">Date</label><input type="date" value={parsedSmartLog.date||''} onChange={e=>setParsedSmartLog(p=>({...p,date:e.target.value}))} className="w-full mt-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm" /></div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">{(parsedSmartLog.type==='expense'?EXPENSE_CATEGORIES:INCOME_CATEGORIES).map(c => (
              <button key={c.key} onClick={()=>setParsedSmartLog(p=>({...p,category:c.key}))} className={`px-2 py-1 rounded-lg text-[11px] ${parsedSmartLog.category===c.key?'bg-emerald-500 text-white':'bg-gray-100 text-gray-600'}`}>{c.emoji} {c.label}</button>
            ))}</div>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setParsedSmartLog(null)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Discard</button>
              <button onClick={confirmSmartLog} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1.5"><Check size={14} /> Confirm & Save</button>
            </div>
          </div>)}
        </div>)}

        {/* ===== CREDIT CARDS ===== */}
        {activeTab === 'cards' && (cardDetailView ? (
          /* ===== CARD DETAIL PAGE (single card) ===== */
          <div className="space-y-4">
            {/* Back button + Month navigator */}
            <div className="flex items-center justify-between">
              <button onClick={()=>setCardDetailView(null)} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"><ArrowLeft size={16} /> Back to Cards</button>
              <div className="flex items-center gap-3">
                <button onClick={()=>setSelectedMonth(p=>{let m=p.month-1,y=p.year;if(m<0){m=11;y--;}return{month:m,year:y};})} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><ChevronLeft size={16} className="text-gray-500" /></button>
                <span className="text-sm font-bold text-gray-700 min-w-[100px] text-center">{new Date(selectedMonth.year,selectedMonth.month).toLocaleDateString('en-IN',{month:'long',year:'numeric'})}</span>
                <button onClick={()=>setSelectedMonth(p=>{let m=p.month+1,y=p.year;if(m>11){m=0;y++;}return{month:m,year:y};})} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><ChevronRight size={16} className="text-gray-500" /></button>
              </div>
            </div>

            {/* Selected card detail */}
            {(expenseData.creditCards||[]).filter(c=>c.id===cardDetailView).map(card => {
              const _dueBasedMonth = getCardDueBasedMonth(card, selectedMonth);
              const offset = cardCycleOffset[card.id] || 0;
              const { cycleStart, cycleEnd } = getCardBillingCycle(card, _dueBasedMonth, offset);
              const stmtDue = Math.max(0, getStatementDue(card.id, _dueBasedMonth));
              const util = card.limit > 0 ? Math.round((stmtDue / card.limit) * 100) : 0;
              const _allSpend = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&!e.isRepayment&&!e.isCredit).reduce((a,e)=>a+(e.amount||0),0);
              const _allRepay = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&(e.isRepayment||e.isCredit)).reduce((a,e)=>a+(e.amount||0),0);
              const _outstanding = Math.max(0, _allSpend - _allRepay);
              const _stmtKey = getCycleStmtKey(cycleEnd);
              const cycleExpenses = (expenseData.expenses||[]).filter(e=>{
                if(e.creditCardId!==card.id)return false;
                if(e.ccStatementMonth) return e.ccStatementMonth===_stmtKey;
                const d=new Date(e.date);return d>=cycleStart&&d<cycleEnd;
              }).sort((a,b)=>new Date(b.date)-new Date(a.date));
              const cycleTotal = cycleExpenses.filter(e=>!e.isRepayment&&!e.isCredit).reduce((s,e)=>s+(e.amount||0),0);
              const ct = {};
              cycleExpenses.filter(e=>!e.isRepayment&&!e.isCredit).forEach(e=>{ct[e.category||'other']=(ct[e.category||'other']||0)+(e.amount||0);});
              const bd = card.billingDate||15;
              const stmtDate = new Date(cycleEnd); stmtDate.setDate(stmtDate.getDate()-1);
              const dd = card.dueDate||5;
              const dueMonth = dd <= bd ? new Date(cycleEnd.getFullYear(), cycleEnd.getMonth()+1, dd) : new Date(cycleEnd.getFullYear(), cycleEnd.getMonth(), dd);
              const stmtFmt = stmtDate.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
              const dueFmt = dueMonth.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});

              return (
                <div key={card.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Card Header */}
                  <div className="p-5 text-white relative overflow-hidden" style={{background:`linear-gradient(135deg, ${card.color}, ${card.color}cc)`}}>
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 bg-white -translate-y-12 translate-x-12"></div>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-[10px] font-bold opacity-70 uppercase tracking-widest">{card.name}</div>
                        <div className="text-xl font-mono mt-2 tracking-widest">•••• •••• •••• {card.lastFour}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] opacity-70">Outstanding</div>
                        <div className={`text-lg font-bold ${_outstanding===0?'text-green-300':'text-white'}`}>{_outstanding>0?`₹${_outstanding.toLocaleString('en-IN')}`:'Fully Paid'}</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end mt-4">
                      <div><div className="text-[10px] opacity-70">Amount Due</div><div className="text-lg font-bold">₹{stmtDue.toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div>
                      <div className="text-center"><div className="text-[10px] opacity-70">Limit</div><div className="text-lg font-bold">₹{card.limit.toLocaleString('en-IN')}</div></div>
                      <div className="text-right"><div className="text-[10px] opacity-70">Utilization</div><div className={`text-lg font-bold ${util<30?'text-green-300':util<70?'text-yellow-300':'text-red-300'}`}>{util}%</div></div>
                    </div>
                    <div className="mt-3 h-2 bg-white/30 rounded-full overflow-hidden"><div className={`h-full rounded-full ${util<30?'bg-green-300':util<70?'bg-yellow-300':'bg-red-300'}`} style={{width:`${Math.min(100,util)}%`}}></div></div>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Statement & Due Date + Opening Balance */}
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-400">Statement: <span className="font-bold text-gray-600">{stmtFmt}</span></span>
                        <span className="text-gray-400">Due: <span className="font-bold text-gray-600">{dueFmt}</span></span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">Opening Bal:</span>
                        <input type="number" defaultValue={card.previousBalance||''} placeholder="0" onBlur={e=>{const v=parseFloat(e.target.value)||0;setExpenseData(p=>({...p,creditCards:(p.creditCards||[]).map(c=>c.id===card.id?{...c,previousBalance:v}:c)}));}} className="w-20 px-1.5 py-0.5 border border-gray-200 rounded text-xs text-right font-bold text-gray-700 focus:outline-none focus:border-indigo-400" />
                      </div>
                    </div>

                    {/* Billing Cycle Navigator */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                      <button onClick={()=>setCardCycleOffset(p=>({...p,[card.id]:(p[card.id]||0)-1}))} className="p-1 hover:bg-gray-200 rounded-lg transition-colors"><ChevronLeft size={16} className="text-gray-500" /></button>
                      <div className="text-center">
                        <div className="text-[10px] text-gray-400 uppercase tracking-widest">Billing Cycle</div>
                        <div className="text-sm font-bold text-gray-700">{cycleStart.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} — {new Date(cycleEnd.getTime()-86400000).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                      </div>
                      <button onClick={()=>setCardCycleOffset(p=>({...p,[card.id]:(p[card.id]||0)+1}))} className="p-1 hover:bg-gray-200 rounded-lg transition-colors"><ChevronRight size={16} className="text-gray-500" /></button>
                    </div>

                    {/* Spending by Category */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Spending by Category</div>
                        <div className="text-sm font-bold text-rose-600">₹{cycleTotal.toLocaleString('en-IN')}</div>
                      </div>
                      {Object.entries(ct).length > 0 ? (
                        <div className="space-y-1.5">
                          {Object.entries(ct).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>{
                            const pct = cycleTotal>0?Math.round((amt/cycleTotal)*100):0;
                            return (<div key={cat} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span>{getCatInfo(cat).emoji} {getCatInfo(cat).label}</span>
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-rose-400 rounded-full" style={{width:`${pct}%`}}></div></div>
                              </div>
                              <span className="font-bold text-gray-700 ml-2">₹{amt.toLocaleString('en-IN')}</span>
                            </div>);
                          })}
                        </div>
                      ) : <div className="text-xs text-gray-400 text-center py-2">No spending this cycle</div>}
                    </div>

                    {/* Transactions */}
                    <div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Transactions ({cycleExpenses.length})</div>
                      <div className="space-y-1">
                        {cycleExpenses.map(e=>(
                          <div key={e.id} className="flex items-center text-xs py-2 border-b border-gray-50 group">
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-600">{getCatInfo(e.category).emoji} {e.description}{e.isRepayment&&<span className="ml-1 text-blue-500 text-[9px]">💸 Repayment</span>}{e.isCredit&&!e.isRepayment&&<span className="ml-1 text-emerald-500 text-[9px]">↩ Credit</span>}</span>
                              <div className="text-[9px] text-gray-400">{new Date(e.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}{(e.smartLogSource==='cc-statement'||(e.tags||[]).includes('cc-import'))&&<span className="ml-1 text-indigo-400">• imported</span>}</div>
                            </div>
                            <span className={`font-bold flex-shrink-0 ${e.isRepayment?'text-blue-600':e.isCredit?'text-emerald-600':'text-rose-600'}`}>{(e.isRepayment||e.isCredit)?'+ ':'- '}₹{e.amount.toLocaleString('en-IN')}</span>
                            <button onClick={()=>deleteExpense(e.id)} className="ml-2 opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all flex-shrink-0"><X size={12} /></button>
                          </div>
                        ))}
                        {cycleExpenses.length===0&&<div className="text-xs text-gray-400 text-center py-4">No transactions this cycle</div>}
                      </div>
                    </div>

                    {/* Repayment History */}
                    {(()=>{
                      const cycleRepayments = cycleExpenses.filter(e=>e.isRepayment);
                      const allRepayments = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&e.isRepayment).sort((a,b)=>new Date(b.date)-new Date(a.date));
                      const cycleRepayTotal = cycleRepayments.reduce((s,e)=>s+(e.amount||0),0);
                      return (<div className="bg-blue-50 rounded-xl border border-blue-100 p-4 space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="text-[10px] font-bold text-blue-600 uppercase tracking-widest flex items-center gap-1"><Wallet size={12} /> Payments Made</div>
                          <div className="text-sm font-bold text-blue-700">₹{cycleRepayTotal.toLocaleString('en-IN')}</div>
                        </div>
                        {cycleRepayments.length > 0 ? (
                          <div className="space-y-1">
                            {cycleRepayments.map(e=>(
                              <div key={e.id} className="flex justify-between text-xs py-1.5 border-b border-blue-100/50">
                                <div className="flex-1 min-w-0">
                                  <span className="text-blue-700">{e.description || 'Card Payment'}</span>
                                  <div className="text-[9px] text-blue-400">{new Date(e.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                                </div>
                                <span className="font-bold text-blue-600 flex-shrink-0">₹{e.amount.toLocaleString('en-IN')}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-blue-400 text-center py-1">No payments this cycle</div>
                        )}
                        {allRepayments.length > cycleRepayments.length && (
                          <div className="text-[9px] text-blue-400 text-center">Total all-time payments: ₹{allRepayments.reduce((s,e)=>s+(e.amount||0),0).toLocaleString('en-IN')}</div>
                        )}
                      </div>);
                    })()}

                    {/* EMI Tracker */}
                    {(()=>{
                      const cardEmis = (emiData||[]).filter(e=>e.cardId===card.id);
                      const activeEmis = cardEmis.filter(e=>e.completedInstallments < e.totalInstallments);
                      return (<div className="bg-purple-50 rounded-xl border border-purple-100 p-4 space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="text-[10px] font-bold text-purple-600 uppercase tracking-widest flex items-center gap-1"><Activity size={12} /> EMIs ({activeEmis.length} active)</div>
                          <button onClick={()=>setShowAddEmi(showAddEmi===card.id?null:card.id)} className="px-2 py-0.5 bg-purple-500 text-white rounded-lg text-[10px] font-semibold hover:bg-purple-600 flex items-center gap-1"><Plus size={10} /> Add</button>
                        </div>
                        {cardEmis.length > 0 ? (
                          <div className="space-y-2">
                            {cardEmis.map(emi=>{
                              const progress = emi.totalInstallments > 0 ? Math.round((emi.completedInstallments/emi.totalInstallments)*100) : 0;
                              const remaining = (emi.totalInstallments - emi.completedInstallments) * emi.monthlyAmount;
                              const isComplete = emi.completedInstallments >= emi.totalInstallments;
                              return (
                                <div key={emi.id} className={`bg-white rounded-lg p-3 border ${isComplete?'border-green-200':'border-purple-100'}`}>
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0"><div className="text-xs font-bold text-gray-700 truncate">{emi.description}</div><div className="text-[10px] text-gray-400 mt-0.5">₹{emi.monthlyAmount.toLocaleString('en-IN')}/mo</div></div>
                                    <div className="text-right flex-shrink-0"><div className={`text-xs font-bold ${isComplete?'text-emerald-600':'text-purple-600'}`}>{emi.completedInstallments}/{emi.totalInstallments}</div>{!isComplete && <div className="text-[9px] text-gray-400">₹{remaining.toLocaleString('en-IN')} left</div>}</div>
                                  </div>
                                  <div className="mt-1.5 h-1.5 bg-purple-100 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all ${isComplete?'bg-emerald-500':'bg-purple-500'}`} style={{width:`${Math.min(100,progress)}%`}}></div></div>
                                  <div className="flex justify-between items-center mt-1">
                                    <div className="text-[9px] text-gray-400">{progress}% done</div>
                                    <div className="flex items-center gap-1">
                                      {!isComplete && <button onClick={()=>{setEmiData(prev=>prev.map(e=>e.id===emi.id?{...e,completedInstallments:Math.min(e.totalInstallments,e.completedInstallments+1)}:e));}} className="text-[9px] text-purple-500 hover:text-purple-700 font-medium">+1 paid</button>}
                                      <button onClick={()=>{setEmiData(prev=>prev.filter(e=>e.id!==emi.id));}} className="text-[9px] text-red-400 hover:text-red-600 ml-1">remove</button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-purple-400 text-center py-1">No EMIs on this card</div>
                        )}
                        {showAddEmi===card.id && (
                          <div className="bg-white rounded-lg border border-purple-200 p-3 space-y-2 mt-1">
                            <div className="text-[10px] font-bold text-purple-600 uppercase tracking-widest">New EMI</div>
                            <input id={`emi-desc-${card.id}`} type="text" placeholder="Description (e.g., iPhone 16)" className="w-full px-2 py-1.5 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-300 outline-none" />
                            <div className="grid grid-cols-2 gap-2">
                              <input id={`emi-installments-${card.id}`} type="number" placeholder="Total installments" className="px-2 py-1.5 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-300 outline-none" />
                              <input id={`emi-amount-${card.id}`} type="number" placeholder="Monthly amount" className="px-2 py-1.5 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-300 outline-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className="text-[9px] text-gray-400">Start Date</label><input id={`emi-start-${card.id}`} type="date" className="w-full px-2 py-1.5 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-300 outline-none" /></div>
                              <div><label className="text-[9px] text-gray-400">Completed</label><input id={`emi-completed-${card.id}`} type="number" placeholder="0" min="0" className="w-full px-2 py-1.5 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-300 outline-none" /></div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button onClick={()=>setShowAddEmi(null)} className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                              <button onClick={()=>{
                                const desc = document.getElementById(`emi-desc-${card.id}`)?.value?.trim();
                                const installments = parseInt(document.getElementById(`emi-installments-${card.id}`)?.value)||0;
                                const amount = parseFloat(document.getElementById(`emi-amount-${card.id}`)?.value)||0;
                                const startDate = document.getElementById(`emi-start-${card.id}`)?.value||new Date().toISOString().split('T')[0];
                                const completed = parseInt(document.getElementById(`emi-completed-${card.id}`)?.value)||0;
                                if(!desc||!installments||!amount) return;
                                const startD = new Date(startDate);
                                const endD = new Date(startD);
                                endD.setMonth(endD.getMonth()+installments);
                                setEmiData(prev=>[...prev,{id:Date.now(),cardId:card.id,description:desc,totalInstallments:installments,completedInstallments:Math.min(completed,installments),monthlyAmount:amount,startDate,endDate:endD.toISOString().split('T')[0]}]);
                                setShowAddEmi(null);
                              }} className="px-3 py-1 bg-purple-500 text-white rounded-lg text-xs font-semibold hover:bg-purple-600">Add EMI</button>
                            </div>
                          </div>
                        )}
                      </div>);
                    })()}

                    {/* Pay Bill */}
                    {payingCardId === card.id ? (
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 space-y-2">
                        <div className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">Pay Bill</div>
                        <div className="flex gap-2">
                          <input type="number" value={payBillAmount} onChange={e=>setPayBillAmount(e.target.value)} placeholder="Amount" className="flex-1 px-3 py-2 border border-blue-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" autoFocus />
                          <button onClick={()=>{
                            const amt = parseFloat(payBillAmount);
                            if (amt > 0) {
                              const today = getDayKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                              addExpense({ amount: amt, description: `${card.name} bill payment`, category: 'emi', type: 'variable', date: today, paymentMethod: 'credit', creditCardId: card.id, isRepayment: true, notes: `Card bill payment for ${card.name}` });
                              setPayingCardId(null); setPayBillAmount('');
                            }
                          }} className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600 transition-colors"><Check size={14} /></button>
                          <button onClick={()=>{setPayingCardId(null); setPayBillAmount('');}} className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm hover:bg-gray-200 transition-colors"><X size={14} /></button>
                        </div>
                        <div className="text-[9px] text-blue-400">This records a card payment (not counted as spending)</div>
                      </div>
                    ) : (
                      <button onClick={()=>{setPayingCardId(card.id); setPayBillAmount(String(cycleTotal));}} className="w-full py-2.5 text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors flex items-center justify-center gap-1.5 border border-blue-200">💸 Pay Bill</button>
                    )}

                    {/* Clear Imported Transactions */}
                    {(()=>{
                      const importedCount = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&(e.smartLogSource==='cc-statement'||(e.tags||[]).includes('cc-import'))).length;
                      return importedCount > 0 ? (
                        <button onClick={()=>{if(window.confirm(`Delete all ${importedCount} imported transactions for ${card.name}? You can re-import from the statement afterward.`)){setExpenseData(prev=>({...prev,expenses:(prev.expenses||[]).filter(e=>!(e.creditCardId===card.id&&(e.smartLogSource==='cc-statement'||(e.tags||[]).includes('cc-import'))))}));}}} className="w-full py-2 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors flex items-center justify-center gap-1.5 border border-amber-200">🗑 Clear {importedCount} Imported Transactions</button>
                      ) : null;
                    })()}

                    {/* Delete Card */}
                    <button onClick={()=>deleteCreditCard(card.id)} className="w-full py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors">Remove Card</button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (<div className="space-y-3">
          <div className="flex items-center justify-between"><h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center"><CreditCard size={14} className="text-white" /></div> Cards</h3>
            <button onClick={()=>setShowAddCard(!showAddCard)} className="px-4 py-2 bg-indigo-500 text-white rounded-xl text-xs font-semibold hover:bg-indigo-600 transition-all shadow-sm flex items-center gap-1.5"><Plus size={12} /> Add Card</button></div>
          {showAddCard && <CardForm />}
          {(expenseData.creditCards||[]).length > 0 && (()=>{
            const totalOutstanding = (expenseData.creditCards||[]).reduce((s,c)=>{
              const allSpend = (expenseData.expenses||[]).filter(e=>e.creditCardId===c.id&&!e.isRepayment&&!e.isCredit).reduce((a,e)=>a+(e.amount||0),0);
              const allRepay = (expenseData.expenses||[]).filter(e=>e.creditCardId===c.id&&(e.isRepayment||e.isCredit)).reduce((a,e)=>a+(e.amount||0),0);
              return s + Math.max(0, allSpend - allRepay);
            },0);
            return (<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-4 gap-3 text-center">
              <div><div className="text-[10px] text-gray-400 font-medium">Credit Limit</div><div className="text-base font-black text-gray-700">₹{Math.max(...(expenseData.creditCards||[]).map(c=>c.limit||0)).toLocaleString('en-IN')}</div></div>
              <div><div className="text-[10px] text-gray-400 font-medium">Total Due</div><div className="text-base font-black text-rose-600">₹{Math.max(0,(expenseData.creditCards||[]).reduce((s,c)=>s+getStatementDue(c.id,getCardDueBasedMonth(c,selectedMonth)),0)).toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div>
              <div><div className="text-[10px] text-gray-400 font-medium">Outstanding</div><div className={`text-base font-black ${totalOutstanding>0?'text-amber-600':'text-emerald-600'}`}>{totalOutstanding>0?`₹${totalOutstanding.toLocaleString('en-IN')}`:'Fully Paid'}</div></div>
              <div><div className="text-[10px] text-gray-400 font-medium">Avg Utilization</div>
                {(()=>{const tl=Math.max(...(expenseData.creditCards||[]).map(c=>c.limit||0));const tu=Math.max(0,(expenseData.creditCards||[]).reduce((s,c)=>s+getStatementDue(c.id,getCardDueBasedMonth(c,selectedMonth)),0));const u=tl>0?Math.round((tu/tl)*100):0;return <div className={`text-base font-black ${u<30?'text-emerald-600':u<70?'text-amber-600':'text-red-600'}`}>{u}%</div>;})()}</div>
            </div>
          </div>);})()}
          <div className="grid grid-cols-2 gap-4">{(expenseData.creditCards||[]).map(card => {
            const _dueBasedMonth = getCardDueBasedMonth(card, selectedMonth);
            const spent = Math.max(0, getStatementDue(card.id, _dueBasedMonth)); const util = card.limit>0?Math.round((spent/card.limit)*100):0;
            const _allSpend = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&!e.isRepayment&&!e.isCredit).reduce((a,e)=>a+(e.amount||0),0);
            const _allRepay = (expenseData.expenses||[]).filter(e=>e.creditCardId===card.id&&(e.isRepayment||e.isCredit)).reduce((a,e)=>a+(e.amount||0),0);
            const _outstanding = Math.max(0, _allSpend - _allRepay);
            const _dd = card.dueDate||5;
            const _dueDate = new Date(selectedMonth.year, selectedMonth.month, _dd);
            const _today = new Date(); _today.setHours(0,0,0,0);
            const _daysUntil = Math.ceil((_dueDate - _today) / 86400000);
            const _dueFmt = _dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
            return (<div key={card.id} className="space-y-2">
              <div onClick={()=>setCardDetailView(card.id)} className="rounded-2xl p-4 text-white cursor-pointer hover:scale-[1.02] transition-transform shadow-lg relative overflow-hidden" style={{background:`linear-gradient(135deg, ${card.color}, ${card.color}cc)`}}>
                <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 bg-white -translate-y-8 translate-x-8"></div>
                <div className="text-[10px] font-bold opacity-70 uppercase tracking-widest">{card.name}</div>
                <div className="text-lg font-mono mt-2 tracking-widest">•••• •••• •••• {card.lastFour}</div>
                <div className="flex justify-between items-end mt-3"><div><div className="text-[10px] opacity-70">Amount Due</div><div className="text-sm font-bold">₹{spent.toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div><div className="text-right"><div className="text-[10px] opacity-70">Limit</div><div className="text-sm font-bold">₹{card.limit.toLocaleString('en-IN')}</div></div></div>
                <div className="flex justify-between items-center mt-1"><div className="text-[10px] opacity-70">Outstanding</div><div className={`text-xs font-bold ${_outstanding===0?'text-green-300':'text-white'}`}>{_outstanding>0?`₹${_outstanding.toLocaleString('en-IN')}`:'Fully Paid'}</div></div>
                <div className="mt-2 h-1.5 bg-white/30 rounded-full overflow-hidden"><div className={`h-full rounded-full ${util<30?'bg-green-300':util<70?'bg-yellow-300':'bg-red-300'}`} style={{width:`${Math.min(100,util)}%`}}></div></div>
                <div className="flex justify-between mt-1 text-[9px] opacity-70"><span>{util}% used</span><span>{_daysUntil > 0 ? `Due ${_dueFmt} (${_daysUntil}d)` : _daysUntil === 0 ? 'Due today' : `Due ${_dueFmt}`}</span></div>
              </div>
            </div>);})}</div>
          {(expenseData.creditCards||[]).length===0 && (<div className="text-center py-8 text-gray-400"><CreditCard size={32} className="mx-auto mb-2 opacity-30" /><div className="text-sm">No credit cards added yet</div><div className="text-xs">Add a card to start tracking spending</div></div>)}

          {/* ===== CREDIT CARD STATEMENT IMPORT ===== */}
          {(expenseData.creditCards||[]).length > 0 && (<div className="mt-4">
            {ccStmtStep === 0 ? (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><CreditCard size={18} className="text-indigo-500" /><h3 className="text-sm font-bold text-indigo-700">Import Card Statement</h3></div>
                  <button onClick={() => { setCcStmtStep(1); setCcStmtCard((expenseData.creditCards||[])[0]?.id || null); }} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 flex items-center gap-1.5"><Upload size={14} /> Import</button>
                </div>
                <p className="text-xs text-indigo-500 mt-2">Paste text, upload CSV, or import PDF statements with auto-categorization</p>
              </div>
            ) : ccStmtStep === 1 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-indigo-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4 flex items-center justify-between">
                  <h3 className="text-white font-bold text-sm flex items-center gap-2"><CreditCard size={16} /> Import Card Statement</h3>
                  <button onClick={() => { setCcStmtStep(0); setCcStmtText(''); setCcPdfPasswordNeeded(false); setCcPdfPassword(''); setCcPdfFile(null); setCcPdfError(''); }} className="text-white/70 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                  <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Select Credit Card</label>
                    <select value={ccStmtCard || ''} onChange={e => setCcStmtCard(parseInt(e.target.value))} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none">
                      {(expenseData.creditCards||[]).map(c => <option key={c.id} value={c.id}>💳 {c.name} (••{c.lastFour})</option>)}
                    </select>
                  </div>
                  <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Paste Statement</label>
                    <textarea value={ccStmtText} onChange={e => setCcStmtText(e.target.value)} placeholder={"Paste your credit card statement here...\n\nSupported formats:\n• Tab-separated (Date, Description, Amount)\n• CSV exports\n• Standard Dr/Cr format"} className="w-full mt-1 h-40 px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none font-mono focus:ring-2 focus:ring-indigo-300 outline-none" />
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => { if (ccStmtText.trim() && ccStmtCard) { const rows = parseCCStatement(ccStmtText, ccStmtCard); setCcStmtRows(rows); const sel = {}; rows.forEach(r => { sel[r.id] = true; }); setCcStmtSelected(sel); setCcStmtStep(2); } }} disabled={!ccStmtText.trim() || !ccStmtCard} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"><Activity size={14} /> Parse Statement</button>
                    <label className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1.5">
                      <Upload size={14} /> Upload File
                      <input type="file" accept=".txt,.csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = ev => { const text = ev.target?.result; if (text) setCcStmtText(text); }; r.readAsText(f); } e.target.value = ''; }} />
                    </label>
                    <label className="px-4 py-2 border border-purple-200 bg-purple-50 rounded-lg text-sm text-purple-600 hover:bg-purple-100 cursor-pointer flex items-center gap-1.5">
                      <FileText size={14} /> Upload PDF
                      <input type="file" accept=".pdf" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f && ccStmtCard) { handleCCPdfUpload(f); } else if (!ccStmtCard) { setCcPdfError('Please select a credit card first'); } e.target.value = ''; }} />
                    </label>
                  </div>
                  {ccPdfLoading && (<div className="flex items-center gap-2 text-sm text-purple-600 animate-pulse"><Activity size={14} className="animate-spin" /> Extracting transactions from PDF...</div>)}
                  {ccPdfError && (<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600 flex items-center gap-2"><AlertCircle size={14} /> {ccPdfError}<button onClick={() => setCcPdfError('')} className="ml-auto text-red-400 hover:text-red-600"><X size={12} /></button></div>)}
                  {ccPdfPasswordNeeded && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-amber-700"><Lock size={14} /> PDF is password-protected</div>
                      <div className="text-[10px] text-amber-500">Common passwords: DOB (DDMMYYYY), last 4 digits + DOB, or PAN number</div>
                      <div className="flex gap-2">
                        <input type="password" value={ccPdfPassword} onChange={e => setCcPdfPassword(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handlePdfPasswordSubmit(); }} placeholder="Enter PDF password" className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 outline-none" autoFocus />
                        <button onClick={handlePdfPasswordSubmit} disabled={!ccPdfPassword} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5"><Unlock size={14} /> Unlock</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-indigo-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><CreditCard size={16} /> {((expenseData.creditCards||[]).find(c=>c.id===ccStmtCard)||{}).name || 'Card'} Statement</h3>
                    <button onClick={() => { setCcStmtStep(1); setCcStmtRows([]); setCcStmtSelected({}); }} className="text-white/70 hover:text-white"><X size={16} /></button>
                  </div>
                  {(() => {
                    const sel = ccStmtRows.filter(r => ccStmtSelected[r.id]);
                    const totalSpend = sel.filter(r => r.type === 'expense').reduce((s, r) => s + r.amount, 0);
                    const totalRefund = sel.filter(r => r.type === 'income').reduce((s, r) => s + r.amount, 0);
                    return (
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 text-center"><div className="text-[10px] text-white/70">Transactions</div><div className="text-lg font-bold text-white">{sel.length}/{ccStmtRows.length}</div></div>
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 text-center"><div className="text-[10px] text-white/70">Spending</div><div className="text-lg font-bold text-white">₹{totalSpend.toLocaleString('en-IN')}</div></div>
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 text-center"><div className="text-[10px] text-white/70">Refunds</div><div className="text-lg font-bold text-white">₹{totalRefund.toLocaleString('en-IN')}</div></div>
                    </div>);
                  })()}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => { const sel = {}; ccStmtRows.forEach(r => { sel[r.id] = true; }); setCcStmtSelected(sel); }} className="text-[10px] text-indigo-500 hover:text-indigo-700 font-medium">Select All</button>
                      <button onClick={() => setCcStmtSelected({})} className="text-[10px] text-gray-400 hover:text-gray-600 font-medium">Deselect All</button>
                    </div>
                    <button onClick={() => setCcStmtStep(1)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1"><ArrowLeft size={10} /> Back</button>
                  </div>
                  <div className="space-y-1 max-h-80 overflow-y-auto border border-gray-100 rounded-xl">
                    {ccStmtRows.map(r => (
                      <div key={r.id} className={`flex items-center gap-2 py-2.5 px-3 hover:bg-gray-50 transition-colors ${!ccStmtSelected[r.id] ? 'opacity-40' : ''}`}>
                        <input type="checkbox" checked={!!ccStmtSelected[r.id]} onChange={() => setCcStmtSelected(p => ({ ...p, [r.id]: !p[r.id] }))} className="rounded border-gray-300 text-indigo-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">{getCatInfo(r.category).emoji}</span>
                            <span className="text-sm font-medium text-gray-700 truncate">{r.description}</span>
                          </div>
                          <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                            <span>{r.date}</span>
                            <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 text-[9px]">{getCatInfo(r.category).label}</span>
                            {r.type === 'income' && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[9px] font-medium">Refund/Credit</span>}
                            {r.categoryConfidence === 'low' && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-500 text-[9px]">⚠ uncategorized</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-sm font-bold ${r.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{r.type === 'income' ? '+' : '-'}₹{r.amount.toLocaleString('en-IN')}</span>
                          <select value={r.category} onChange={ev => setCcStmtRows(prev => prev.map(p => p.id === r.id ? { ...p, category: ev.target.value } : p))} className="px-1 py-0.5 border border-gray-200 rounded text-[10px] w-20">
                            {allExpenseCategories.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
                          </select>
                        </div>
                      </div>
                    ))}
                    {ccStmtRows.length === 0 && <div className="text-center text-gray-400 text-xs py-8">No transactions parsed. Check your statement format.</div>}
                  </div>
                  <div className="flex gap-2 justify-end mt-4">
                    <button onClick={() => { setCcStmtStep(1); setCcStmtRows([]); setCcStmtSelected({}); }} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={importCCStatement} disabled={ccStmtRows.filter(r => ccStmtSelected[r.id]).length === 0} className="px-5 py-2 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"><Check size={14} /> Import {ccStmtRows.filter(r => ccStmtSelected[r.id]).length} Transactions</button>
                  </div>
                </div>
              </div>
            )}
          </div>)}
        </div>))}

        {/* ===== CHAT LOG ===== */}
        {activeTab === 'chatlog' && (() => {
          const chatLog = expenseData.chatLog || [];
          const mk = `${selectedMonth.year}-${String(selectedMonth.month + 1).padStart(2, '0')}`;
          const monthEntries = chatLog.filter(e => (e.month || '').startsWith(mk));
          const cs = expenseData.settings?.currencySymbol || '₹';

          const categoryKeywords = {
            housing: /\b(rent|mortgage|house|flat|apartment|maintenance|society)\b/i,
            groceries: /\b(grocery|groceries|vegetable|fruit|supermarket|bigbazaar|dmart|kirana|provisions)\b/i,
            dining: /\b(food|restaurant|eat|dinner|lunch|breakfast|cafe|zomato|swiggy|biryani|pizza|burger|snack|tea|coffee)\b/i,
            transport: /\b(uber|ola|cab|taxi|petrol|diesel|fuel|gas|metro|bus|auto|parking|toll)\b/i,
            entertainment: /\b(movie|netflix|prime|hotstar|spotify|game|concert|show)\b/i,
            shopping: /\b(amazon|flipkart|clothes|shoes|dress|shirt|shopping|buy|bought|purchase)\b/i,
            utilities: /\b(electric|electricity|water|gas|internet|wifi|broadband|phone|mobile|recharge)\b/i,
            health: /\b(doctor|hospital|medicine|pharmacy|medical|gym|fitness|health)\b/i,
            education: /\b(school|college|tuition|course|book|books|fees|class)\b/i,
            subscriptions: /\b(subscription|membership|renewal|plan)\b/i,
            insurance: /\b(insurance|lic|policy|premium)\b/i,
            emi: /\b(emi|loan|installment|instalment)\b/i,
            personal: /\b(salon|haircut|spa|grooming|laundry|dry\s*clean)\b/i,
            gifts: /\b(gift|birthday|anniversary|present|donation|charity)\b/i,
          };

          const incomeKeywords = /\b(salary|income|received|earned|credited|got\s+paid|freelance|bonus|refund|dividend|interest|rental\s*income|commission|profit)\b/i;

          const parseChatMessage = (text) => {
            const lower = text.toLowerCase().trim();
            // Extract amount — find numbers (with optional commas)
            const amountMatch = lower.match(/(?:₹|rs\.?|inr)?\s*([\d,]+(?:\.\d{1,2})?)/);
            const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : 0;
            if (!amount) return null;

            // Detect recurring
            let recurring = null;
            if (/\b(monthly|every\s*month)\b/i.test(lower)) recurring = 'monthly';
            else if (/\b(weekly|every\s*week)\b/i.test(lower)) recurring = 'weekly';
            else if (/\b(yearly|annual|every\s*year)\b/i.test(lower)) recurring = 'yearly';
            else if (/\b(daily|every\s*day)\b/i.test(lower)) recurring = 'daily';

            // Detect income vs expense
            const isIncome = incomeKeywords.test(lower);

            // Detect credit card
            let cardMatch = null;
            const cardNames = (expenseData.creditCards || []);
            for (const card of cardNames) {
              if (lower.includes(card.name.toLowerCase()) || (card.lastFour && lower.includes(card.lastFour))) {
                cardMatch = card;
                break;
              }
            }

            // Detect card bill payment / repayment
            const isCardRepayment = cardMatch && /\b(paid|pay|payment|bill|cleared|settled|repay|repaid|repayment)\b/i.test(lower);

            // Detect category
            let category = 'other';
            for (const [cat, regex] of Object.entries(categoryKeywords)) {
              if (regex.test(lower)) { category = cat; break; }
            }
            if (isIncome) {
              if (/salary/i.test(lower)) category = 'salary';
              else if (/freelance/i.test(lower)) category = 'freelance';
              else if (/invest|dividend|interest/i.test(lower)) category = 'investment';
              else if (/rental/i.test(lower)) category = 'rental';
              else if (/refund/i.test(lower)) category = 'refund';
              else category = 'other';
            }

            // Build label — remove amount, currency, recurring keywords
            let label = text.replace(/₹|rs\.?|inr/gi, '').replace(/[\d,]+(?:\.\d{1,2})?/g, '').replace(/\b(monthly|weekly|yearly|daily|annual|every\s*(?:month|week|year|day))\b/gi, '').replace(/\b(spent|paid|for|on|at|to|from|got|received|earned)\b/gi, '').replace(/\s+/g, ' ').trim();
            if (!label) label = isIncome ? 'Income' : 'Expense';
            label = label.charAt(0).toUpperCase() + label.slice(1);

            const catObj = isIncome
              ? ([...(INCOME_CATEGORIES), ...((expenseData.customIncomeCategories || []))].find(c => c.key === category) || { emoji: '💰', label: 'Other' })
              : ([...EXPENSE_CATEGORIES, ...((expenseData.customExpenseCategories || []))].find(c => c.key === category) || { emoji: '📌', label: 'Other' });

            return {
              id: Date.now() + Math.random(),
              text,
              type: isCardRepayment ? 'repayment' : (isIncome ? 'in' : 'out'),
              amount,
              label: isCardRepayment ? `${cardMatch.name} bill payment` : label,
              category: isCardRepayment ? 'emi' : category,
              categoryEmoji: isCardRepayment ? '💸' : catObj.emoji,
              categoryLabel: isCardRepayment ? 'Card Payment' : catObj.label,
              recurring,
              cardName: cardMatch?.name || null,
              cardId: cardMatch?.id || null,
              month: mk,
              timestamp: new Date().toISOString()
            };
          };

          // --- Expense Query Engine ---
          const answerExpenseQuery = (text) => {
            const lower = text.toLowerCase().trim();
            // Detect if user is asking about a specific month
            const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
            const shortMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
            let queryMk = mk; // default to current selected month
            let queryMonthLabel = emMonthNames[selectedMonth.month];
            let queryYear = selectedMonth.year;
            for (let i = 0; i < 12; i++) {
              if (lower.includes(monthNames[i]) || new RegExp(`\\b${shortMonths[i]}\\b`).test(lower)) {
                queryMk = `${queryYear}-${String(i + 1).padStart(2, '0')}`;
                queryMonthLabel = emMonthNames[i];
                // If month mentioned is in the future of current year, check previous year
                if (i > new Date().getMonth()) {
                  queryYear = queryYear - 1;
                  queryMk = `${queryYear}-${String(i + 1).padStart(2, '0')}`;
                }
                break;
              }
            }
            // Detect credit card filter
            let cardFilter = null;
            const cardNames = (expenseData.creditCards || []);
            for (const card of cardNames) {
              if (lower.includes(card.name.toLowerCase()) || (card.lastFour && lower.includes(card.lastFour))) {
                cardFilter = card; break;
              }
            }
            const cardLabel = cardFilter ? ` on ${cardFilter.name}` : '';

            let qExpenses = (expenseData.expenses || []).filter(e => e.date && e.date.startsWith(queryMk));
            if (cardFilter) qExpenses = qExpenses.filter(e => e.creditCardId === cardFilter.id);
            const qIncome = (expenseData.income || []).filter(i => i.date && i.date.startsWith(queryMk));
            const qTotalExp = qExpenses.reduce((s, e) => s + (e.amount || 0), 0);
            const qTotalInc = qIncome.reduce((s, i) => s + (i.amount || 0), 0);
            const qCatTotals = {};
            qExpenses.forEach(e => { const k = e.category || 'other'; qCatTotals[k] = (qCatTotals[k] || 0) + (e.amount || 0); });
            const allCats = [...EXPENSE_CATEGORIES, ...(expenseData.customExpenseCategories || [])];
            const getCatLabel = (k) => (allCats.find(c => c.key === k) || { label: k, emoji: '📌' });

            // Question: biggest/largest/highest spend
            if (/\b(biggest|largest|highest|most|top|max)\b.*\b(spend|expense|payment|transaction|cost)\b/i.test(lower) || /\b(spend|expense).*\b(biggest|largest|highest|most|top|max)\b/i.test(lower)) {
              if (qExpenses.length === 0) return `No expenses found in ${queryMonthLabel} ${queryYear}${cardLabel}.`;
              const sorted = [...qExpenses].sort((a, b) => (b.amount || 0) - (a.amount || 0));
              const top = sorted[0];
              const cat = getCatLabel(top.category);
              let answer = `💰 Biggest spend in ${queryMonthLabel}${cardLabel}: ${cs}${top.amount.toLocaleString('en-IN')} — ${top.description || cat.label} (${cat.emoji} ${cat.label})`;
              if (sorted.length > 1) {
                answer += `\n\nTop 3 spends:`;
                sorted.slice(0, 3).forEach((e, i) => {
                  const c = getCatLabel(e.category);
                  answer += `\n${i + 1}. ${cs}${e.amount.toLocaleString('en-IN')} — ${e.description || c.label} (${c.emoji})`;
                });
              }
              return answer;
            }

            // Question: smallest/lowest spend
            if (/\b(smallest|lowest|least|min)\b.*\b(spend|expense|payment|transaction|cost)\b/i.test(lower)) {
              if (qExpenses.length === 0) return `No expenses found in ${queryMonthLabel} ${queryYear}${cardLabel}.`;
              const sorted = [...qExpenses].sort((a, b) => (a.amount || 0) - (b.amount || 0));
              const bot = sorted[0];
              const cat = getCatLabel(bot.category);
              return `📉 Smallest expense in ${queryMonthLabel}${cardLabel}: ${cs}${bot.amount.toLocaleString('en-IN')} — ${bot.description || cat.label} (${cat.emoji} ${cat.label})`;
            }

            // Question: specific category query (water, subscription, grocery, etc.)
            const categoryMatch = allCats.find(c => lower.includes(c.key) || lower.includes(c.label.toLowerCase()));
            // Also match common keywords to categories
            const keywordToCat = {
              water: 'utilities', electric: 'utilities', electricity: 'utilities', internet: 'utilities', wifi: 'utilities', phone: 'utilities', mobile: 'utilities', recharge: 'utilities',
              rent: 'housing', mortgage: 'housing',
              grocery: 'groceries', vegetables: 'groceries', supermarket: 'groceries',
              food: 'dining', restaurant: 'dining', zomato: 'dining', swiggy: 'dining',
              uber: 'transport', ola: 'transport', petrol: 'transport', fuel: 'transport',
              netflix: 'subscriptions', spotify: 'subscriptions', subscription: 'subscriptions', membership: 'subscriptions',
              amazon: 'shopping', flipkart: 'shopping', clothes: 'shopping',
              doctor: 'health', hospital: 'health', medicine: 'health', gym: 'health',
              school: 'education', tuition: 'education', fees: 'education', course: 'education',
              insurance: 'insurance', lic: 'insurance', premium: 'insurance',
              emi: 'emi', loan: 'emi', installment: 'emi',
              salon: 'personal', haircut: 'personal', grooming: 'personal',
              gift: 'gifts', birthday: 'gifts', donation: 'gifts',
              movie: 'entertainment', game: 'entertainment',
            };
            let matchedCatKey = categoryMatch?.key || null;
            if (!matchedCatKey) {
              for (const [kw, catKey] of Object.entries(keywordToCat)) {
                if (lower.includes(kw)) { matchedCatKey = catKey; break; }
              }
            }
            // Also try matching against expense descriptions
            const descWords = lower.replace(/[?.,!]/g, '').split(/\s+/).filter(w => w.length > 2 && !['what','how','much','the','for','this','that','total','amount','spend','spent','expense','cost','month','is','are','was','were','did','does','have','has','been','do'].includes(w));

            if (matchedCatKey && (/\b(how\s*much|total|amount|spend|spent|cost|expense)\b/i.test(lower) || /\?/.test(text))) {
              const catTotal = qCatTotals[matchedCatKey] || 0;
              const catObj = getCatLabel(matchedCatKey);
              const catExpenses = qExpenses.filter(e => e.category === matchedCatKey);
              if (catExpenses.length === 0) return `No ${catObj.emoji} ${catObj.label} expenses found in ${queryMonthLabel} ${queryYear}${cardLabel}.`;
              let answer = `${catObj.emoji} ${catObj.label} in ${queryMonthLabel}${cardLabel}: ${cs}${catTotal.toLocaleString('en-IN')} (${catExpenses.length} transaction${catExpenses.length > 1 ? 's' : ''})`;
              if (catExpenses.length > 1) {
                answer += `\n\nBreakdown:`;
                catExpenses.sort((a, b) => (b.amount || 0) - (a.amount || 0)).forEach(e => {
                  answer += `\n• ${cs}${e.amount.toLocaleString('en-IN')} — ${e.description || 'Unnamed'}`;
                  if (e.date) answer += ` (${e.date})`;
                });
              }
              return answer;
            }

            // Check descriptions for keyword match
            if (descWords.length > 0) {
              const matchedByDesc = qExpenses.filter(e => {
                const d = (e.description || '').toLowerCase();
                const m = (e.merchant || '').toLowerCase();
                return descWords.some(w => d.includes(w) || m.includes(w));
              });
              if (matchedByDesc.length > 0) {
                const total = matchedByDesc.reduce((s, e) => s + (e.amount || 0), 0);
                let answer = `🔍 Found ${matchedByDesc.length} matching transaction${matchedByDesc.length > 1 ? 's' : ''} in ${queryMonthLabel}${cardLabel}: ${cs}${total.toLocaleString('en-IN')}`;
                matchedByDesc.sort((a, b) => (b.amount || 0) - (a.amount || 0)).forEach(e => {
                  const c = getCatLabel(e.category);
                  answer += `\n• ${cs}${e.amount.toLocaleString('en-IN')} — ${e.description || 'Unnamed'} (${c.emoji} ${c.label})`;
                });
                return answer;
              }
            }

            // Question: total spending / how much did I spend
            if (/\b(total|overall|all)\b.*\b(spend|spent|expense|cost|out)\b/i.test(lower) || /\bhow\s*much\b.*\b(spend|spent)\b/i.test(lower)) {
              if (qExpenses.length === 0) return `No expenses recorded in ${queryMonthLabel} ${queryYear}${cardLabel}.`;
              let answer = `📊 Total spending in ${queryMonthLabel}${cardLabel}: ${cs}${qTotalExp.toLocaleString('en-IN')} across ${qExpenses.length} transactions`;
              const sortedCats = Object.entries(qCatTotals).sort((a, b) => b[1] - a[1]);
              if (sortedCats.length > 0) {
                answer += `\n\nBy category:`;
                sortedCats.forEach(([cat, amt]) => {
                  const c = getCatLabel(cat);
                  const pct = Math.round((amt / qTotalExp) * 100);
                  answer += `\n${c.emoji} ${c.label}: ${cs}${amt.toLocaleString('en-IN')} (${pct}%)`;
                });
              }
              return answer;
            }

            // Question: total income / how much did I earn
            if (/\b(total|overall|all)\b.*\b(income|earn|earned|salary|received)\b/i.test(lower) || /\bhow\s*much\b.*\b(income|earn|earned|made|received)\b/i.test(lower)) {
              if (qIncome.length === 0) return `No income recorded in ${queryMonthLabel} ${queryYear}.`;
              let answer = `💰 Total income in ${queryMonthLabel}: ${cs}${qTotalInc.toLocaleString('en-IN')} from ${qIncome.length} source${qIncome.length > 1 ? 's' : ''}`;
              qIncome.forEach(i => {
                answer += `\n• ${cs}${i.amount.toLocaleString('en-IN')} — ${i.source || 'Income'}`;
              });
              return answer;
            }

            // Question: savings / balance / net
            if (/\b(saving|savings|balance|net|left|remaining|surplus|deficit)\b/i.test(lower)) {
              const net = qTotalInc - qTotalExp;
              return `${net >= 0 ? '✅' : '⚠️'} ${queryMonthLabel} balance: ${cs}${Math.abs(net).toLocaleString('en-IN')} ${net >= 0 ? 'surplus' : 'deficit'}\n\nIncome: ${cs}${qTotalInc.toLocaleString('en-IN')}\nExpenses: ${cs}${qTotalExp.toLocaleString('en-IN')}\nSavings rate: ${qTotalInc > 0 ? Math.round((net / qTotalInc) * 100) : 0}%`;
            }

            // Question: summary / overview / breakdown
            if (/\b(summary|overview|breakdown|report|insight|analyze|analyse)\b/i.test(lower)) {
              if (qExpenses.length === 0 && qIncome.length === 0) return `No data for ${queryMonthLabel} ${queryYear} yet.`;
              const net = qTotalInc - qTotalExp;
              let answer = `📋 ${queryMonthLabel} ${queryYear} Summary\n\n💰 Income: ${cs}${qTotalInc.toLocaleString('en-IN')} (${qIncome.length} entries)\n💸 Expenses: ${cs}${qTotalExp.toLocaleString('en-IN')} (${qExpenses.length} entries)\n${net >= 0 ? '✅' : '⚠️'} Net: ${cs}${Math.abs(net).toLocaleString('en-IN')} ${net >= 0 ? 'saved' : 'overspent'}`;
              const sortedCats = Object.entries(qCatTotals).sort((a, b) => b[1] - a[1]);
              if (sortedCats.length > 0) {
                answer += `\n\n🏷️ Top categories:`;
                sortedCats.slice(0, 5).forEach(([cat, amt]) => {
                  const c = getCatLabel(cat);
                  answer += `\n${c.emoji} ${c.label}: ${cs}${amt.toLocaleString('en-IN')}`;
                });
              }
              if (qExpenses.length > 0) {
                const biggest = [...qExpenses].sort((a, b) => (b.amount || 0) - (a.amount || 0))[0];
                const bc = getCatLabel(biggest.category);
                answer += `\n\n🔝 Biggest single expense: ${cs}${biggest.amount.toLocaleString('en-IN')} — ${biggest.description || bc.label}`;
              }
              return answer;
            }

            // Question: compare months
            if (/\b(compare|vs|versus|comparison|difference)\b/i.test(lower)) {
              const prevM = selectedMonth.month === 0 ? 11 : selectedMonth.month - 1;
              const prevY = selectedMonth.month === 0 ? selectedMonth.year - 1 : selectedMonth.year;
              const prevMk = `${prevY}-${String(prevM + 1).padStart(2, '0')}`;
              const prevExp = (expenseData.expenses || []).filter(e => e.date && e.date.startsWith(prevMk)).reduce((s, e) => s + (e.amount || 0), 0);
              const prevInc = (expenseData.income || []).filter(i => i.date && i.date.startsWith(prevMk)).reduce((s, i) => s + (i.amount || 0), 0);
              const diff = qTotalExp - prevExp;
              return `📊 ${emMonthNames[prevM]} vs ${queryMonthLabel}\n\nIncome: ${cs}${prevInc.toLocaleString('en-IN')} → ${cs}${qTotalInc.toLocaleString('en-IN')}\nExpenses: ${cs}${prevExp.toLocaleString('en-IN')} → ${cs}${qTotalExp.toLocaleString('en-IN')}\n\nSpending ${diff > 0 ? `increased by ${cs}${diff.toLocaleString('en-IN')} ⬆️` : diff < 0 ? `decreased by ${cs}${Math.abs(diff).toLocaleString('en-IN')} ⬇️` : 'is the same'}`;
            }

            // Question: recurring / fixed
            if (/\b(recurring|fixed|monthly|subscription|regular|auto)\b.*\b(expense|bill|payment|spend|cost|charge)\b/i.test(lower) || /\b(fixed|recurring)\b/i.test(lower)) {
              const recurring = qExpenses.filter(e => e.recurring);
              if (recurring.length === 0) return `No recurring expenses found in ${queryMonthLabel}.`;
              const total = recurring.reduce((s, e) => s + (e.amount || 0), 0);
              let answer = `🔄 Recurring expenses in ${queryMonthLabel}: ${cs}${total.toLocaleString('en-IN')} (${recurring.length} items)`;
              recurring.sort((a, b) => (b.amount || 0) - (a.amount || 0)).forEach(e => {
                const c = getCatLabel(e.category);
                answer += `\n• ${cs}${e.amount.toLocaleString('en-IN')} — ${e.description || c.label} (${c.emoji})`;
              });
              return answer;
            }

            // Card-specific catch-all: if user mentions a card name, show card summary
            if (cardFilter) {
              if (qExpenses.length === 0) return `No transactions found for ${cardFilter.name} in ${queryMonthLabel} ${queryYear}.`;
              const sortedCats = Object.entries(qCatTotals).sort((a, b) => b[1] - a[1]);
              let answer = `💳 ${cardFilter.name} in ${queryMonthLabel}: ${cs}${qTotalExp.toLocaleString('en-IN')} across ${qExpenses.length} transactions`;
              if (sortedCats.length > 0) {
                answer += `\n\nBy category:`;
                sortedCats.forEach(([cat, amt]) => {
                  const c = getCatLabel(cat);
                  const pct = qTotalExp > 0 ? Math.round((amt / qTotalExp) * 100) : 0;
                  answer += `\n${c.emoji} ${c.label}: ${cs}${amt.toLocaleString('en-IN')} (${pct}%)`;
                });
              }
              const sorted = [...qExpenses].sort((a, b) => (b.amount || 0) - (a.amount || 0));
              if (sorted.length > 0) {
                answer += `\n\nTop spends:`;
                sorted.slice(0, 5).forEach((e, i) => {
                  const c = getCatLabel(e.category);
                  answer += `\n${i + 1}. ${cs}${e.amount.toLocaleString('en-IN')} — ${e.description || c.label} (${c.emoji})`;
                });
              }
              return answer;
            }

            return null; // Not a recognized query
          };

          const isQuestion = (text) => {
            const lower = text.toLowerCase().trim();
            // Check if text mentions a credit card name (treat as query)
            const mentionsCard = (expenseData.creditCards || []).some(c => lower.includes(c.name.toLowerCase()) || (c.lastFour && lower.includes(c.lastFour)));
            return /\?$/.test(text.trim()) ||
              /^(what|how|which|where|when|who|why|show|tell|list|give|find|get|check|any|is\s+there|do\s+i|did\s+i|have\s+i|can\s+you)\b/i.test(lower) ||
              /\b(biggest|largest|highest|smallest|lowest|total|summary|overview|breakdown|compare|savings|balance)\b/i.test(lower) ||
              mentionsCard;
          };

          const addChatEntry = (parsed) => {
            setExpenseData(prev => ({ ...prev, chatLog: [parsed, ...(prev.chatLog || [])] }));
            const today = getDayKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
            if (parsed.type === 'in') {
              addIncome({ amount: parsed.amount, source: parsed.label, category: parsed.category, date: today, recurring: !!parsed.recurring, recurringFrequency: parsed.recurring || undefined, notes: parsed.text, smartLogSource: 'chatlog' });
            } else if (parsed.type === 'repayment') {
              addExpense({ amount: parsed.amount, description: parsed.label, category: 'emi', type: 'variable', date: today, paymentMethod: 'credit', creditCardId: parsed.cardId || undefined, isRepayment: true, notes: parsed.text, smartLogSource: 'chatlog' });
            } else {
              addExpense({ amount: parsed.amount, description: parsed.label, category: parsed.category, type: parsed.recurring ? 'fixed' : 'variable', date: today, paymentMethod: parsed.cardId ? 'credit' : 'cash', creditCardId: parsed.cardId || undefined, recurring: !!parsed.recurring, recurringFrequency: parsed.recurring || undefined, merchant: '', notes: parsed.text, smartLogSource: 'chatlog' });
            }
          };

          const handleChatSubmit = (force) => {
            // If force-adding a duplicate, use the pending entry
            if (force && chatDuplicateWarning) {
              addChatEntry(chatDuplicateWarning);
              setChatDuplicateWarning(null);
              if (chatInputRef.current) chatInputRef.current.value = '';
              return;
            }

            const text = (chatInputRef.current?.value || '').trim();
            if (!text) return;

            // Check if it's a question/query first
            if (isQuestion(text)) {
              const answer = answerExpenseQuery(text);
              if (answer) {
                const userEntry = { id: Date.now(), text, type: 'query', amount: 0, label: text, category: null, month: mk, timestamp: new Date().toISOString(), answer };
                setExpenseData(prev => ({ ...prev, chatLog: [userEntry, ...(prev.chatLog || [])] }));
                if (chatInputRef.current) chatInputRef.current.value = '';
                return;
              }
            }

            const parsed = parseChatMessage(text);
            if (!parsed) {
              const entry = { id: Date.now(), text, type: 'note', amount: 0, label: text, category: null, month: mk, timestamp: new Date().toISOString() };
              setExpenseData(prev => ({ ...prev, chatLog: [entry, ...(prev.chatLog || [])] }));
            } else {
              // Check for duplicate entry today
              const today = getDayKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
              const isDuplicate = parsed.type === 'in'
                ? (expenseData.income || []).some(i => i.amount === parsed.amount && i.date === today)
                : (expenseData.expenses || []).some(e => e.amount === parsed.amount && e.date === today && !e.isRepayment);

              if (isDuplicate && !force) {
                setChatDuplicateWarning(parsed);
                return;
              }
              setChatDuplicateWarning(null);
              addChatEntry(parsed);
            }
            if (chatInputRef.current) chatInputRef.current.value = '';
          };

          const deleteChat = (id) => {
            const entry = (expenseData.chatLog || []).find(e => e.id === id);
            if (entry && (entry.type === 'in' || entry.type === 'out' || entry.type === 'repayment')) {
              const ts = new Date(entry.timestamp).getTime();
              if (entry.type === 'in') {
                const linked = (expenseData.income || []).find(i => i.smartLogSource === 'chatlog' && Math.abs(new Date(i.createdAt).getTime() - ts) < 5000 && i.amount === entry.amount);
                if (linked) deleteIncome(linked.id);
              } else {
                const linked = (expenseData.expenses || []).find(e => e.smartLogSource === 'chatlog' && Math.abs(new Date(e.createdAt).getTime() - ts) < 5000 && e.amount === entry.amount);
                if (linked) deleteExpense(linked.id);
              }
            }
            setExpenseData(prev => ({ ...prev, chatLog: (prev.chatLog || []).filter(e => e.id !== id) }));
          };

          return (
          <div>
            {/* Chat Area */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ height: 560 }}>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 flex flex-col-reverse">
                {monthEntries.length === 0 && (
                  <div className="text-center py-12">
                    <MessageSquare size={40} className="mx-auto text-gray-200 mb-3" />
                    <p className="text-sm text-gray-400 font-medium">Log expenses or ask questions about your spending</p>
                    <div className="mt-4 space-y-1.5 text-[11px] text-gray-400">
                      <p className="text-[10px] font-bold text-gray-300 uppercase tracking-wider mb-1">Log expenses</p>
                      <p><span className="bg-gray-100 px-2 py-0.5 rounded font-mono">rent monthly 17500</span></p>
                      <p><span className="bg-gray-100 px-2 py-0.5 rounded font-mono">salary 85000</span></p>
                      <p><span className="bg-gray-100 px-2 py-0.5 rounded font-mono">electricity 2500 monthly</span></p>
                      <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider mb-1 mt-3">Card payments</p>
                      <p><span className="bg-blue-50 text-blue-500 px-2 py-0.5 rounded font-mono">paid HDFC 2100</span></p>
                      <p><span className="bg-blue-50 text-blue-500 px-2 py-0.5 rounded font-mono">tata neu bill 5000</span></p>
                      <p className="text-[10px] font-bold text-violet-300 uppercase tracking-wider mb-1 mt-3">Ask questions</p>
                      <p><span className="bg-violet-50 text-violet-500 px-2 py-0.5 rounded font-mono">what's my biggest spend?</span></p>
                      <p><span className="bg-violet-50 text-violet-500 px-2 py-0.5 rounded font-mono">tata neu total in february?</span></p>
                      <p><span className="bg-violet-50 text-violet-500 px-2 py-0.5 rounded font-mono">highest spend on hdfc?</span></p>
                    </div>
                  </div>
                )}
                {monthEntries.map(entry => (
                  <div key={entry.id} className="group">
                    <div className={`flex items-start gap-2 ${entry.type === 'note' ? '' : ''}`}>
                      <div className="flex-1">
                        {/* User message bubble */}
                        <div className="flex justify-end mb-1">
                          <div className="bg-indigo-500 text-white px-3 py-1.5 rounded-2xl rounded-br-sm text-sm max-w-[80%] break-words">
                            {entry.text}
                          </div>
                        </div>
                        {/* Parsed result */}
                        {(entry.type === 'in' || entry.type === 'out' || entry.type === 'repayment') && (
                          <div className="flex items-start gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${entry.type === 'repayment' ? 'bg-gradient-to-br from-blue-400 to-indigo-500' : 'bg-gradient-to-br from-emerald-400 to-teal-500'}`}>
                              <span className="text-[10px] text-white font-bold">{entry.type === 'repayment' ? '💸' : cs}</span>
                            </div>
                            <div className={`px-3 py-2 rounded-2xl rounded-bl-sm text-sm max-w-[85%] ${entry.type === 'repayment' ? 'bg-blue-50 border border-blue-200' : entry.type === 'in' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-base font-black ${entry.type === 'repayment' ? 'text-blue-600' : entry.type === 'in' ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {entry.type === 'in' ? '+' : '-'}{cs}{(entry.amount || 0).toLocaleString('en-IN')}
                                </span>
                                <span className="text-xs font-medium text-gray-600">{entry.label}</span>
                                {entry.type === 'repayment' && <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-bold">Repayment</span>}
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {entry.recurring && (
                                  <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full border border-blue-200 font-bold flex items-center gap-0.5">
                                    <RefreshCw size={8} /> {entry.recurring}
                                  </span>
                                )}
                                {entry.cardName && (
                                  <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-full border border-purple-200 font-medium">
                                    💳 {entry.cardName}
                                  </span>
                                )}
                                <span className="text-[9px] text-gray-300">{new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {entry.type === 'query' && entry.answer && (
                          <div className="flex items-start gap-2">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[10px] text-white font-bold">AI</span>
                            </div>
                            <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 text-sm text-gray-700 max-w-[85%] whitespace-pre-line leading-relaxed">
                              {entry.answer}
                            </div>
                          </div>
                        )}
                        {entry.type === 'note' && (
                          <div className="flex items-start gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[10px]">📝</span>
                            </div>
                            <div className="px-3 py-1.5 rounded-2xl rounded-bl-sm bg-gray-50 border border-gray-200 text-xs text-gray-500">
                              Noted (no amount detected)
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="relative opacity-0 group-hover:opacity-100 transition-all mt-1 flex-shrink-0">
                        <button onClick={() => deleteChat(entry.id)}
                          className="p-1 text-gray-300 hover:text-red-400 peer"
                          title={(entry.type === 'in' || entry.type === 'out' || entry.type === 'repayment') ? 'Undo — removes chat & transaction' : 'Remove chat entry'}>
                          <X size={12} />
                        </button>
                        {(entry.type === 'in' || entry.type === 'out' || entry.type === 'repayment') && (
                          <div className="absolute right-0 top-full mt-1 hidden peer-hover:block z-10 whitespace-nowrap bg-gray-800 text-white text-[9px] px-2 py-1 rounded-lg shadow-lg">
                            Also removes transaction
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              {/* Duplicate Warning */}
              {chatDuplicateWarning && (
                <div className="bg-amber-50 border-t border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-amber-700">Similar entry exists today</div>
                      <div className="text-[10px] text-amber-500 truncate">{cs}{(chatDuplicateWarning.amount || 0).toLocaleString('en-IN')} {chatDuplicateWarning.label} already logged. Add anyway?</div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => { handleChatSubmit(true); }} className="px-3 py-1 bg-amber-500 text-white rounded-lg text-[10px] font-bold hover:bg-amber-600 transition-colors">Add Anyway</button>
                    <button onClick={() => setChatDuplicateWarning(null)} className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[10px] hover:bg-gray-200 transition-colors">Cancel</button>
                  </div>
                </div>
              )}
              <div className="border-t border-gray-200 p-3 bg-gray-50/50">
                <div className="flex gap-2">
                  <input
                    type="text"
                    ref={chatInputRef}
                    defaultValue=""
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleChatSubmit(); } }}
                    placeholder="Log expense or ask: biggest spend? Tata Neu total?..."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  />
                  <button onClick={handleChatSubmit}
                    className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:from-emerald-600 hover:to-teal-600 transition-colors flex items-center gap-1.5">
                    <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400 flex-wrap">
                  <span>Try:</span>
                  {['rent 17500 monthly', 'salary 85000', 'biggest spend?', 'tata neu total?'].map(ex => (
                    <button key={ex} onClick={() => { if (chatInputRef.current) { chatInputRef.current.value = ex; chatInputRef.current.focus(); } }}
                      className={`px-2 py-0.5 rounded-full transition-colors font-mono ${ex.includes('?') || ex === 'summary' ? 'bg-violet-50 hover:bg-violet-100 text-violet-500' : 'bg-gray-100 hover:bg-gray-200'}`}>{ex}</button>
                  ))}
                </div>
              </div>
            </div>

          </div>
          );
        })()}

        {/* ===== INSIGHTS ===== */}
        {activeTab === 'insights' && (<div className="space-y-3">

          {/* ---- Calendar Heatmap ---- */}
          {(() => {
            const hmYear = selectedMonth.year;
            const hmMonth = selectedMonth.month;
            const hmDaysInMonth = new Date(hmYear, hmMonth + 1, 0).getDate();
            const hmFirstDow = new Date(hmYear, hmMonth, 1).getDay();
            const hmMk = `${hmYear}-${String(hmMonth + 1).padStart(2, '0')}`;

            const dailySpend = {};
            (expenseData.expenses || []).filter(e => e.date && e.date.startsWith(hmMk) && !e.isRepayment).forEach(e => {
              const day = parseInt(e.date.split('-')[2]);
              dailySpend[day] = (dailySpend[day] || 0) + (e.amount || 0);
            });
            const spendValues = Object.values(dailySpend);
            const avgDaily = spendValues.length > 0 ? spendValues.reduce((a, b) => a + b, 0) / spendValues.length : 0;

            const getHeatColor = (amt) => {
              if (!amt || amt === 0) return 'bg-gray-100';
              if (amt < avgDaily * 0.5) return 'bg-emerald-200';
              if (amt < avgDaily) return 'bg-yellow-300';
              if (amt < avgDaily * 2) return 'bg-orange-400';
              return 'bg-red-500';
            };
            const getHeatTextColor = (amt) => {
              if (!amt || amt === 0) return 'text-gray-400';
              if (amt < avgDaily * 0.5) return 'text-emerald-800';
              if (amt < avgDaily) return 'text-yellow-900';
              return 'text-white';
            };

            const cells = [];
            for (let i = 0; i < hmFirstDow; i++) cells.push(<div key={`he-${i}`} className="w-7 h-7" />);
            for (let d = 1; d <= hmDaysInMonth; d++) {
              const amt = dailySpend[d] || 0;
              const isSelected = selectedHeatmapDay === d;
              cells.push(
                <div key={`hd-${d}`} onClick={() => setSelectedHeatmapDay(isSelected ? null : d)}
                  className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold cursor-pointer transition-all ${getHeatColor(amt)} ${getHeatTextColor(amt)} ${isSelected ? 'ring-2 ring-indigo-500 scale-110' : 'hover:scale-105'}`}
                  title={`${emMonthNames[hmMonth]} ${d}: ₹${amt.toLocaleString('en-IN')}`}
                >{d}</div>
              );
            }

            return (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Calendar size={14} className="text-indigo-500" /> Spending Heatmap
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['S','M','T','W','T','F','S'].map((l, i) => (
                    <div key={i} className="w-7 h-5 flex items-center justify-center text-[9px] font-semibold text-gray-400">{l}</div>
                  ))}
                  {cells}
                </div>
                {selectedHeatmapDay && (
                  <div className="mt-2 px-3 py-2 bg-indigo-50 rounded-xl border border-indigo-100 text-xs">
                    <span className="font-bold text-indigo-700">{emMonthNames[hmMonth]} {selectedHeatmapDay}</span>
                    <span className="text-gray-500 mx-1.5">&mdash;</span>
                    <span className="font-bold text-gray-800">₹{(dailySpend[selectedHeatmapDay] || 0).toLocaleString('en-IN')}</span>
                    {dailySpend[selectedHeatmapDay] > 0 && avgDaily > 0 && (
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${dailySpend[selectedHeatmapDay] > avgDaily * 2 ? 'bg-red-100 text-red-600' : dailySpend[selectedHeatmapDay] > avgDaily ? 'bg-orange-100 text-orange-600' : dailySpend[selectedHeatmapDay] >= avgDaily * 0.5 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-600'}`}>
                        {(dailySpend[selectedHeatmapDay] / avgDaily).toFixed(1)}x avg
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  <span className="text-[9px] text-gray-400 mr-1">Less</span>
                  <div className="w-4 h-4 rounded bg-gray-100"></div>
                  <div className="w-4 h-4 rounded bg-emerald-200"></div>
                  <div className="w-4 h-4 rounded bg-yellow-300"></div>
                  <div className="w-4 h-4 rounded bg-orange-400"></div>
                  <div className="w-4 h-4 rounded bg-red-500"></div>
                  <span className="text-[9px] text-gray-400 ml-1">More</span>
                </div>
                {avgDaily > 0 && (
                  <div className="text-center text-[10px] text-gray-400 mt-1.5">Avg daily spend: ₹{Math.round(avgDaily).toLocaleString('en-IN')}</div>
                )}
              </div>
            );
          })()}

          {/* ---- Year in Review ---- */}
          {(() => {
            const yrYear = selectedMonth.year;
            const yrYearStr = String(yrYear);
            const yearExpenses = (expenseData.expenses || []).filter(e => e.date && e.date.startsWith(yrYearStr) && !e.isRepayment);
            const yearIncome = (expenseData.income || []).filter(i => i.date && i.date.startsWith(yrYearStr));
            const totalEarned = yearIncome.reduce((s, i) => s + (i.amount || 0), 0);
            const totalSpent = yearExpenses.reduce((s, e) => s + (e.amount || 0), 0);
            const totalSaved = totalEarned - totalSpent;

            const yrCatTotals = {};
            yearExpenses.forEach(e => { yrCatTotals[e.category] = (yrCatTotals[e.category] || 0) + (e.amount || 0); });
            const topCats = Object.entries(yrCatTotals).sort((a, b) => b[1] - a[1]).slice(0, 5);
            const topCatMax = topCats.length > 0 ? topCats[0][1] : 1;

            const biggestExpense = yearExpenses.length > 0 ? yearExpenses.reduce((max, e) => (e.amount || 0) > (max.amount || 0) ? e : max, yearExpenses[0]) : null;

            const monthlyData = [];
            for (let m = 0; m < 12; m++) {
              const mKey = `${yrYear}-${String(m + 1).padStart(2, '0')}`;
              const mExp = yearExpenses.filter(e => e.date && e.date.startsWith(mKey)).reduce((s, e) => s + (e.amount || 0), 0);
              const mInc = yearIncome.filter(i => i.date && i.date.startsWith(mKey)).reduce((s, i) => s + (i.amount || 0), 0);
              monthlyData.push({ month: m, expenses: mExp, income: mInc, savings: mInc - mExp });
            }
            const maxMonthExp = Math.max(...monthlyData.map(m => m.expenses), 1);
            const bestSavingsMonth = monthlyData.reduce((best, m, i) => m.savings > (best.savings || -Infinity) ? { ...m, idx: i } : best, { savings: -Infinity });
            const highestSpendMonth = monthlyData.reduce((worst, m, i) => m.expenses > (worst.expenses || 0) ? { ...m, idx: i } : worst, { expenses: 0 });

            return (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button onClick={() => setShowYearReport(!showYearReport)}
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-500 p-4 text-left flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                      <BarChart3 size={18} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">Year in Review {yrYear}</div>
                      <div className="text-[11px] text-white/70">Your annual money story</div>
                    </div>
                  </div>
                  <ChevronDown size={18} className={`text-white transition-transform ${showYearReport ? 'rotate-180' : ''}`} />
                </button>

                {showYearReport && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 text-center">
                        <div className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Earned</div>
                        <div className="text-base font-black text-emerald-700 mt-1">₹{totalEarned.toLocaleString('en-IN')}</div>
                      </div>
                      <div className="bg-rose-50 rounded-xl p-3 border border-rose-100 text-center">
                        <div className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Spent</div>
                        <div className="text-base font-black text-rose-700 mt-1">₹{totalSpent.toLocaleString('en-IN')}</div>
                      </div>
                      <div className={`${totalSaved >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'} rounded-xl p-3 border text-center`}>
                        <div className={`text-[10px] font-semibold ${totalSaved >= 0 ? 'text-blue-500' : 'text-red-500'} uppercase tracking-wider`}>Saved</div>
                        <div className={`text-base font-black ${totalSaved >= 0 ? 'text-blue-700' : 'text-red-700'} mt-1`}>{totalSaved < 0 ? '-' : ''}₹{Math.abs(totalSaved).toLocaleString('en-IN')}</div>
                      </div>
                    </div>

                    {topCats.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-500 mb-2.5 flex items-center gap-1.5"><Trophy size={13} className="text-amber-500" /> Top 5 Spending Categories</div>
                        <div className="space-y-2">
                          {topCats.map(([cat, amt], i) => {
                            const info = getCatInfo(cat);
                            return (
                              <div key={cat}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="font-medium text-gray-700">{info.emoji} {info.label}</span>
                                  <span className="font-bold text-gray-800">₹{amt.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${i === 0 ? 'bg-violet-500' : i === 1 ? 'bg-purple-400' : i === 2 ? 'bg-fuchsia-400' : i === 3 ? 'bg-pink-400' : 'bg-rose-300'}`}
                                    style={{ width: `${(amt / topCatMax) * 100}%` }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {biggestExpense && biggestExpense.amount > 0 && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-100">
                        <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Star size={11} /> Biggest Single Expense</div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-bold text-gray-800">{biggestExpense.description || biggestExpense.merchant || 'Unknown'}</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">{biggestExpense.date} &middot; {getCatInfo(biggestExpense.category).emoji} {getCatInfo(biggestExpense.category).label}</div>
                          </div>
                          <div className="text-lg font-black text-amber-700">₹{(biggestExpense.amount || 0).toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-2.5 flex items-center gap-1.5"><TrendingUp size={13} className="text-blue-500" /> Monthly Spending</div>
                      <div className="space-y-1.5">
                        {monthlyData.map((m, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className={`text-[10px] font-semibold w-8 text-right ${i === highestSpendMonth.idx ? 'text-red-500' : i === bestSavingsMonth.idx ? 'text-emerald-500' : 'text-gray-400'}`}>
                              {emMonthNames[i].substring(0, 3)}
                            </span>
                            <div className="flex-1 h-4 bg-gray-50 rounded-full overflow-hidden relative">
                              {m.expenses > 0 && (
                                <div className={`h-full rounded-full transition-all ${i === highestSpendMonth.idx ? 'bg-red-400' : i === bestSavingsMonth.idx ? 'bg-emerald-400' : 'bg-violet-300'}`}
                                  style={{ width: `${(m.expenses / maxMonthExp) * 100}%` }}></div>
                              )}
                            </div>
                            <span className="text-[10px] font-bold text-gray-600 w-16 text-right">
                              {m.expenses > 0 ? `₹${m.expenses >= 100000 ? `${(m.expenses / 1000).toFixed(0)}K` : m.expenses.toLocaleString('en-IN')}` : '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                        <div className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-1"><Award size={11} /> Best Saving Month</div>
                        <div className="text-sm font-black text-emerald-700 mt-1">{bestSavingsMonth.idx !== undefined ? emMonthNames[bestSavingsMonth.idx] : 'N/A'}</div>
                        {bestSavingsMonth.idx !== undefined && (
                          <div className="text-[10px] text-emerald-500 font-medium mt-0.5">Saved ₹{Math.max(0, bestSavingsMonth.savings || 0).toLocaleString('en-IN')}</div>
                        )}
                      </div>
                      <div className="bg-rose-50 rounded-xl p-3 border border-rose-100">
                        <div className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider flex items-center gap-1"><Flame size={11} /> Highest Spend Month</div>
                        <div className="text-sm font-black text-rose-700 mt-1">{highestSpendMonth.idx !== undefined ? emMonthNames[highestSpendMonth.idx] : 'N/A'}</div>
                        {highestSpendMonth.idx !== undefined && (
                          <div className="text-[10px] text-rose-500 font-medium mt-0.5">Spent ₹{(highestSpendMonth.expenses || 0).toLocaleString('en-IN')}</div>
                        )}
                      </div>
                    </div>

                    {(totalEarned === 0 && totalSpent === 0) && (
                      <div className="text-center text-gray-400 text-xs py-6">No transactions recorded for {yrYear}. Start tracking to see your year in review!</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">Month-over-Month</div>
            <div className="flex items-end gap-2 h-32">{(()=>{const months=[];for(let i=5;i>=0;i--){let m=selectedMonth.month-i;let y=selectedMonth.year;while(m<0){m+=12;y--;}const mk=`${y}-${String(m+1).padStart(2,'0')}`;months.push({mk,month:m,income:getMonthIncome(mk).reduce((s,e)=>s+e.amount,0),expenses:getMonthExpenses(mk).filter(e=>!e.isRepayment).reduce((s,e)=>s+e.amount,0)});}const maxVal=Math.max(...months.map(m=>Math.max(m.income,m.expenses)),1);
              return months.map((m,i)=>(<div key={i} className="flex-1 flex flex-col items-center gap-1"><div className="flex gap-0.5 items-end h-24 w-full justify-center"><div className="w-3 bg-emerald-400 rounded-t" style={{height:`${(m.income/maxVal)*100}%`,minHeight:m.income>0?'4px':'0'}}></div><div className="w-3 bg-rose-400 rounded-t" style={{height:`${(m.expenses/maxVal)*100}%`,minHeight:m.expenses>0?'4px':'0'}}></div></div><div className="text-[9px] text-gray-400 font-medium">{emMonthNames[m.month]?.substring(0,3)}</div></div>));})()}</div>
            <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-400"><span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded-sm"></span> Income</span><span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-400 rounded-sm"></span> Expenses</span></div>
          </div>
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center"><Flame size={18} className="text-white" /></div><div><div className="text-sm font-bold text-gray-800">Savings Streak</div>
              {(()=>{let streak=0;for(let i=0;i<12;i++){let m=selectedMonth.month-i;let y=selectedMonth.year;while(m<0){m+=12;y--;}const mk=`${y}-${String(m+1).padStart(2,'0')}`;const inc=getMonthIncome(mk).reduce((s,e)=>s+e.amount,0);const exp=getMonthExpenses(mk).filter(e=>!e.isRepayment).reduce((s,e)=>s+e.amount,0);if(inc>exp&&inc>0)streak++;else break;}
                return <div className="text-xs text-amber-600">{streak>0?`${streak} consecutive month${streak>1?'s':''} of positive savings! 🔥`:'Start saving to build your streak!'}</div>;})()}
            </div></div>
          </div>
          {Object.keys(expenseData.budgets||{}).filter(k=>(expenseData.budgets||{})[k]>0).length > 0 && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-3">Budget vs Actual</div>
              <div className="space-y-3">{Object.entries(expenseData.budgets||{}).filter(([,v])=>v>0).map(([cat,budget])=>{const spent=catTotals[cat]||0;const pct=Math.round((spent/budget)*100);const info=getCatInfo(cat);
                return (<div key={cat}><div className="flex justify-between text-xs mb-1"><span className="font-medium text-gray-700">{info.emoji} {info.label}</span><span className={`font-bold ${pct>100?'text-red-500':pct>80?'text-amber-500':'text-emerald-500'}`}>₹{spent.toLocaleString('en-IN')} / ₹{budget.toLocaleString('en-IN')} ({pct}%)</span></div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all ${pct>100?'bg-red-400':pct>80?'bg-amber-400':'bg-emerald-400'}`} style={{width:`${Math.min(120,pct)}%`}}></div></div></div>);})}</div>
            </div>)}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">Smart Alerts</div>
            <div className="space-y-2">{(()=>{const alerts=[];
              Object.entries(expenseData.budgets||{}).forEach(([cat,budget])=>{const spent=catTotals[cat]||0;if(budget>0&&spent>budget)alerts.push({type:'danger',text:`${getCatInfo(cat).emoji} ${getCatInfo(cat).label} is over budget by ₹${(spent-budget).toLocaleString('en-IN')}`});else if(budget>0&&spent>budget*0.8)alerts.push({type:'warning',text:`${getCatInfo(cat).emoji} ${getCatInfo(cat).label} is at ${Math.round((spent/budget)*100)}% of budget`});});
              (expenseData.creditCards||[]).forEach(card=>{const spent=getCardSpend(card.id,selectedMonth);const util=card.limit>0?(spent/card.limit)*100:0;if(util>70)alerts.push({type:'warning',text:`💳 ${card.name} is at ${Math.round(util)}% utilization`});});
              if(savingsRate>=30)alerts.push({type:'success',text:`🎉 Great job! You're saving ${savingsRate}% of your income`});else if(savingsRate<0)alerts.push({type:'danger',text:`⚠️ You're spending more than you earn this month`});
              let prevM=selectedMonth.month-1;let prevY=selectedMonth.year;if(prevM<0){prevM=11;prevY--;}const prevMk=`${prevY}-${String(prevM+1).padStart(2,'0')}`;const prevExp=getMonthExpenses(prevMk).filter(e=>!e.isRepayment).reduce((s,e)=>s+e.amount,0);
              if(prevExp>0&&totalExpenses>prevExp*1.2)alerts.push({type:'warning',text:`📈 Spending is ${Math.round(((totalExpenses-prevExp)/prevExp)*100)}% higher than last month`});
              if(alerts.length===0)alerts.push({type:'info',text:'No alerts — keep tracking to get personalized insights!'});
              return alerts.map((a,i)=>(<div key={i} className={`px-4 py-2.5 rounded-xl text-xs font-medium ${a.type==='danger'?'bg-red-50 text-red-700 border border-red-100':a.type==='warning'?'bg-amber-50 text-amber-700 border border-amber-100':a.type==='success'?'bg-emerald-50 text-emerald-700 border border-emerald-100':'bg-gray-50 text-gray-500 border border-gray-100'}`}>{a.text}</div>));})()}</div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">Spending by Day of Week</div>
            <div className="flex items-end gap-2 h-24">{(()=>{const dt=[0,0,0,0,0,0,0];const dl=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];curSpending.forEach(e=>{const d=new Date(e.date);if(!isNaN(d))dt[d.getDay()]+=e.amount;});const mx=Math.max(...dt,1);
              return dl.map((l,i)=>(<div key={i} className="flex-1 flex flex-col items-center gap-1"><div className="w-full bg-gradient-to-t from-blue-500 to-blue-300 rounded-t-lg" style={{height:`${(dt[i]/mx)*100}%`,minHeight:dt[i]>0?'6px':'3px'}}></div><div className="text-[10px] text-gray-400 font-semibold">{l}</div></div>));})()}</div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 mb-3">Top Merchants</div>
            {(()=>{const merchants={};curSpending.forEach(e=>{const m=e.merchant||e.description||'Unknown';merchants[m]=(merchants[m]||0)+e.amount;});const sorted=Object.entries(merchants).sort((a,b)=>b[1]-a[1]).slice(0,5);
              return sorted.length>0?(<div className="space-y-2.5">{sorted.map(([m,amt],i)=>(<div key={i} className="flex items-center justify-between py-1.5"><div className="flex items-center gap-3"><span className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">{i+1}</span><span className="text-sm font-medium text-gray-700">{m}</span></div><span className="text-sm font-bold text-gray-800">₹{amt.toLocaleString('en-IN')}</span></div>))}</div>):<div className="text-center text-gray-400 text-xs py-4">Add expenses to see merchant insights</div>;})()}
          </div>
        </div>)}
      </div>
    );
  };

  const CalendarView = () => {
    const [selectedColor, setSelectedColor] = useState('bg-blue-400');
    const [newMonthlyGoal, setNewMonthlyGoal] = useState('');
    const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
    const [showBannerForm, setShowBannerForm] = useState(false);
    const [editingBannerId, setEditingBannerId] = useState(null);
    const [bannerColor, setBannerColor] = useState('bg-purple-400');
    const [bannerEmoji, setBannerEmoji] = useState('');
    const [dayPanelPos, setDayPanelPos] = useState({ x: 80, y: 120 });
    const [dayPanelSize, setDayPanelSize] = useState({ w: 420, h: 380 });
    const [calendarMode, setCalendarMode] = useState('month'); // 'month' | 'week'
    const [viewingWeekStart, setViewingWeekStart] = useState(() => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      return { year: monday.getFullYear(), month: monday.getMonth(), day: monday.getDate() };
    });

    const bannerColors = [
      'bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-green-400', 'bg-teal-400',
      'bg-blue-400', 'bg-indigo-400', 'bg-purple-400', 'bg-pink-400'
    ];
    const bannerEmojis = ['🏖️','📚','🎄','✈️','🏠','🎂','💼','🏥','⚽','🎵','💊','🔧'];

    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const currentMonthGoals = getMonthlyGoals(currentYear, currentMonth);

    // Reusable day cell renderer
    const renderDayCell = (cellYear, cellMonth, cellDay, cellHeight) => {
      const highlight = getDayHighlight(cellYear, cellMonth, cellDay);
      const note = getDayNote(cellYear, cellMonth, cellDay);
      const hours = getDailyHours(cellYear, cellMonth, cellDay);
      const today = isToday(cellYear, cellMonth, cellDay);
      const tasks = getDayTasks(cellYear, cellMonth, cellDay);
      const totalDayTasks = (tasks.high?.length || 0) + (tasks.medium?.length || 0) + (tasks.low?.length || 0);
      const scheduleCount = Object.keys(getDaySchedule(cellYear, cellMonth, cellDay)).length;
      const hijri = getHijriDate(cellYear, cellMonth, cellDay);
      const holiday = getHoliday(cellYear, cellMonth, cellDay);
      const dayStr = getDayKey(cellYear, cellMonth, cellDay);
      const dayBanners = calendarBanners.filter(b => dayStr >= b.startDate && dayStr <= b.endDate);
      const isWeekMode = cellHeight === 'h-64';

      return (
        <div
          key={`${cellYear}-${cellMonth}-${cellDay}`}
          onClick={() => setSelectedCalendarDay(prev => prev && prev.day === cellDay && prev.month === cellMonth && prev.year === cellYear ? null : { year: cellYear, month: cellMonth, day: cellDay })}
          className={`${cellHeight} border border-gray-200 p-1.5 transition-all relative group cursor-pointer hover:shadow-lg ${highlight ? highlight : 'bg-white hover:bg-gray-50'
            } ${today ? 'ring-2 ring-blue-500' : ''} ${selectedCalendarDay && selectedCalendarDay.day === cellDay && selectedCalendarDay.month === cellMonth && selectedCalendarDay.year === cellYear ? 'ring-2 ring-indigo-500 shadow-lg' : ''}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-baseline gap-1">
              <span className={`${isWeekMode ? 'text-lg' : 'text-sm'} font-semibold ${today ? 'text-blue-600' : 'text-gray-700'}`}>
                {cellDay}
              </span>
              <span className="text-[9px] text-gray-400 font-medium">
                {hijri.day}{hijri.day === 1 || cellDay === 1 ? ` ${hijri.monthName.split(' ')[0]}` : ''}
              </span>
              {isWeekMode && (
                <span className="text-xs text-gray-400 font-medium ml-1">
                  {monthNames[cellMonth]}
                </span>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDayHighlight(cellYear, cellMonth, cellDay, selectedColor);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-white/50 rounded"
              title="Toggle highlight"
            >
              <div className={`w-3.5 h-3.5 rounded-full ${selectedColor} border border-gray-400`} />
            </button>
          </div>

          {holiday && (
            <div className={`text-[8px] font-semibold px-1 py-0.5 rounded border truncate mt-0.5 ${HOLIDAY_BADGE_STYLES[holiday.type]}`}>
              {holiday.name}
            </div>
          )}

          {today && !holiday && <div className="text-[9px] text-blue-600 font-medium mt-0.5">Today</div>}

          {/* Event banners */}
          {dayBanners.slice(0, isWeekMode ? 5 : 3).map(banner => {
            const isStart = dayStr === banner.startDate;
            const isEnd = dayStr === banner.endDate;
            return (
              <div key={banner.id}
                className={`text-[8px] font-bold text-white px-1 py-0 truncate mt-0.5 ${banner.color} ${isStart ? 'rounded-l-full ml-0' : '-ml-1.5'} ${isEnd ? 'rounded-r-full mr-0' : '-mr-1.5'}`}
                title={`${banner.title} (${banner.startDate} → ${banner.endDate})`}
              >
                {isStart ? `${banner.emoji || ''} ${banner.title}`.trim() : '\u00A0'}
              </div>
            );
          })}

          {scheduleCount > 0 && (
            <div className="text-[9px] font-medium text-blue-500 mt-0.5">
              {scheduleCount} event{scheduleCount > 1 ? 's' : ''}
            </div>
          )}

          {/* In week mode, show schedule details inline */}
          {isWeekMode && Object.entries(getDaySchedule(cellYear, cellMonth, cellDay)).sort(([a], [b]) => a - b).slice(0, 4).map(([hr, val]) => {
            const h = parseInt(hr);
            const timeStr = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
            return (
              <div key={hr} className="text-[9px] text-blue-600 truncate mt-0.5">
                <span className="font-mono text-blue-400">{timeStr}</span> {getScheduleText(val)}
              </div>
            );
          })}

          {totalDayTasks > 0 && (
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              {(tasks.high?.length || 0) > 0 && (
                <span className={`text-[8px] font-bold px-1 py-0 rounded ${tasks.high.filter(t=>t.completed).length === tasks.high.length ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}>
                  ⚡{tasks.high.filter(t=>t.completed).length}/{tasks.high.length}
                </span>
              )}
              {(tasks.medium?.length || 0) > 0 && (
                <span className={`text-[8px] font-bold px-1 py-0 rounded ${tasks.medium.filter(t=>t.completed).length === tasks.medium.length ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-500'}`}>
                  🔋{tasks.medium.filter(t=>t.completed).length}/{tasks.medium.length}
                </span>
              )}
              {(tasks.low?.length || 0) > 0 && (
                <span className={`text-[8px] font-bold px-1 py-0 rounded ${tasks.low.filter(t=>t.completed).length === tasks.low.length ? 'bg-green-100 text-green-700' : 'bg-emerald-50 text-emerald-500'}`}>
                  🪫{tasks.low.filter(t=>t.completed).length}/{tasks.low.length}
                </span>
              )}
            </div>
          )}

          {note && (
            <div className={`text-[9px] font-medium mt-1 px-1 py-0.5 rounded ${isWeekMode ? 'line-clamp-4' : 'line-clamp-2'} ${
              highlight
                ? 'bg-white/60 text-gray-800 backdrop-blur-sm'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}>
              {note}
            </div>
          )}

          {!scheduleCount && !totalDayTasks && !note && !hours && !holiday && (
            <div className="text-[9px] text-gray-400 italic mt-0.5">
              Click to plan
            </div>
          )}

          {/* Hover Tooltip */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white rounded-lg shadow-xl p-3 z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 text-xs">
            <div className="font-bold mb-1">{monthNames[cellMonth]} {cellDay}, {cellYear}</div>
            <div className="text-gray-300 mb-1">{hijri.day} {hijri.monthName} {hijri.year} AH</div>
            {holiday && (
              <div className="text-yellow-300 font-medium mb-1">{holiday.name}</div>
            )}
            {Object.entries(getDaySchedule(cellYear, cellMonth, cellDay)).sort(([a], [b]) => a - b).slice(0, 4).map(([hr, val]) => (
              <div key={hr} className="text-blue-300 truncate">
                {parseInt(hr) < 12 ? (parseInt(hr) === 0 ? '12 AM' : `${hr} AM`) : (parseInt(hr) === 12 ? '12 PM' : `${parseInt(hr) - 12} PM`)} — {getScheduleText(val)}
              </div>
            ))}
            {scheduleCount > 4 && <div className="text-gray-400">+{scheduleCount - 4} more events</div>}
            {totalDayTasks > 0 && (
              <div className="mt-1 space-y-0.5">
                {(tasks.high?.length || 0) > 0 && (
                  <div className={tasks.high.filter(t=>t.completed).length === tasks.high.length ? 'text-green-300' : 'text-red-300'}>
                    ⚡ High: {tasks.high.filter(t=>t.completed).length}/{tasks.high.length} done
                  </div>
                )}
                {(tasks.medium?.length || 0) > 0 && (
                  <div className={tasks.medium.filter(t=>t.completed).length === tasks.medium.length ? 'text-green-300' : 'text-amber-300'}>
                    🔋 Medium: {tasks.medium.filter(t=>t.completed).length}/{tasks.medium.length} done
                  </div>
                )}
                {(tasks.low?.length || 0) > 0 && (
                  <div className={tasks.low.filter(t=>t.completed).length === tasks.low.length ? 'text-green-300' : 'text-emerald-300'}>
                    🪫 Low: {tasks.low.filter(t=>t.completed).length}/{tasks.low.length} done
                  </div>
                )}
              </div>
            )}
            {note && (
              <div className="text-orange-300 mt-1 line-clamp-2">{note}</div>
            )}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      );
    };

    // Build days array based on mode
    const days = [];
    if (calendarMode === 'week') {
      const weekStart = new Date(viewingWeekStart.year, viewingWeekStart.month, viewingWeekStart.day);
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        days.push(renderDayCell(d.getFullYear(), d.getMonth(), d.getDate(), 'h-64'));
      }
    } else {
      // Add empty cells for days before month starts
      for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="h-24" />);
      }
      for (let day = 1; day <= daysInMonth; day++) {
        days.push(renderDayCell(currentYear, currentMonth, day, 'h-36'));
      }
    }

    // Compute week header info for week mode
    const weekStartDate = new Date(viewingWeekStart.year, viewingWeekStart.month, viewingWeekStart.day);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekStartDate.getDate() + 6);
    const weekNum = getWeekNumForDate(weekStartDate);
    const weekHeaderLabel = `Week ${weekNum} · ${monthNames[weekStartDate.getMonth()].slice(0,3)} ${weekStartDate.getDate()} – ${monthNames[weekEndDate.getMonth()].slice(0,3)} ${weekEndDate.getDate()}, ${weekEndDate.getFullYear()}`;

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setSelectedCalendarDay(null);
                if (calendarMode === 'week') {
                  const prev = new Date(viewingWeekStart.year, viewingWeekStart.month, viewingWeekStart.day);
                  prev.setDate(prev.getDate() - 7);
                  setViewingWeekStart({ year: prev.getFullYear(), month: prev.getMonth(), day: prev.getDate() });
                } else {
                  if (currentMonth === 0) {
                    setCurrentMonth(11);
                    setCurrentYear(currentYear - 1);
                  } else {
                    setCurrentMonth(currentMonth - 1);
                  }
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={24} />
            </button>

            <div className="text-center">
              {calendarMode === 'week' ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {weekHeaderLabel}
                  </h2>
                  <div className="text-xs text-green-700 font-medium mt-0.5">
                    {getHijriDate(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate()).day} {getHijriDate(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate()).monthName} – {getHijriDate(weekEndDate.getFullYear(), weekEndDate.getMonth(), weekEndDate.getDate()).day} {getHijriDate(weekEndDate.getFullYear(), weekEndDate.getMonth(), weekEndDate.getDate()).monthName}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {monthNames[currentMonth]} {currentYear}
                  </h2>
                  <div className="text-xs text-green-700 font-medium mt-0.5">
                    {getHijriMonthRange(currentMonth, currentYear)}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => {
                setSelectedCalendarDay(null);
                if (calendarMode === 'week') {
                  const next = new Date(viewingWeekStart.year, viewingWeekStart.month, viewingWeekStart.day);
                  next.setDate(next.getDate() + 7);
                  setViewingWeekStart({ year: next.getFullYear(), month: next.getMonth(), day: next.getDate() });
                } else {
                  if (currentMonth === 11) {
                    setCurrentMonth(0);
                    setCurrentYear(currentYear + 1);
                  } else {
                    setCurrentMonth(currentMonth + 1);
                  }
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Today button */}
            <button
              onClick={() => {
                const today = new Date();
                if (calendarMode === 'week') {
                  const dayOfWeek = today.getDay();
                  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                  const monday = new Date(today);
                  monday.setDate(today.getDate() + diff);
                  setViewingWeekStart({ year: monday.getFullYear(), month: monday.getMonth(), day: monday.getDate() });
                } else {
                  setCurrentMonth(today.getMonth());
                  setCurrentYear(today.getFullYear());
                }
                setSelectedCalendarDay(null);
              }}
              className="px-3 py-1.5 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200"
            >
              Today
            </button>
            {/* Week/Month toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => {
                  if (calendarMode !== 'week') {
                    // Switch to week: find Monday of current week or today's week
                    const today = new Date();
                    const refDate = (today.getMonth() === currentMonth && today.getFullYear() === currentYear) ? today : new Date(currentYear, currentMonth, 1);
                    const dayOfWeek = refDate.getDay();
                    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    const monday = new Date(refDate);
                    monday.setDate(refDate.getDate() + diff);
                    setViewingWeekStart({ year: monday.getFullYear(), month: monday.getMonth(), day: monday.getDate() });
                    setCalendarMode('week');
                  }
                }}
                className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${calendarMode === 'week' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Week
              </button>
              <button
                onClick={() => {
                  if (calendarMode !== 'month') {
                    // Switch to month: sync to month of the week being viewed
                    const ws = new Date(viewingWeekStart.year, viewingWeekStart.month, viewingWeekStart.day);
                    // Use the Thursday of the week to determine the month (so a week spanning Feb-Mar picks the right month)
                    const thu = new Date(ws);
                    thu.setDate(ws.getDate() + 3);
                    setCurrentMonth(thu.getMonth());
                    setCurrentYear(thu.getFullYear());
                    setCalendarMode('month');
                  }
                }}
                className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${calendarMode === 'month' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Month
              </button>
            </div>
          </div>
        </div>

        {/* Monthly Goals Section — only in month mode */}
        {calendarMode === 'month' && <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Target className="text-blue-500" size={20} />
            Monthly Goals - {monthNames[currentMonth]} {currentYear}
          </h3>

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newMonthlyGoal}
              onChange={(e) => setNewMonthlyGoal(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addMonthlyGoal(currentYear, currentMonth, newMonthlyGoal);
                  setNewMonthlyGoal('');
                }
              }}
              placeholder="Add a goal for this month..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              onClick={() => {
                addMonthlyGoal(currentYear, currentMonth, newMonthlyGoal);
                setNewMonthlyGoal('');
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {currentMonthGoals.map((goal) => (
              <div
                key={goal.id}
                className="flex items-center gap-2 p-2 bg-white rounded-lg"
              >
                <button
                  onClick={() => toggleMonthlyGoal(currentYear, currentMonth, goal.id)}
                  className={`p-1 rounded ${goal.completed ? 'bg-green-500 text-white' : goal.notDone ? 'bg-red-500 text-white' : 'bg-gray-300'}`}
                >
                  {goal.notDone ? <X size={14} /> : <Check size={14} />}
                </button>
                <span className={`flex-1 text-sm ${goal.completed ? 'line-through text-gray-500' : goal.notDone ? 'line-through text-red-500' : ''}`}>
                  {goal.text}
                </span>
                {!goal.completed && (
                  <button
                    onClick={() => markMonthlyGoalNotDone(currentYear, currentMonth, goal.id)}
                    className="p-1 text-orange-500 hover:bg-orange-100 rounded"
                    title="Mark as not done"
                  >
                    <XCircle size={14} />
                  </button>
                )}
                {!goal.completed && (
                  <button
                    onClick={() => moveMonthlyGoalToNextMonth(currentYear, currentMonth, goal.id)}
                    className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                    title="Move to next month"
                  >
                    <ArrowRight size={14} />
                  </button>
                )}
                <button
                  onClick={() => deleteMonthlyGoal(currentYear, currentMonth, goal.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {currentMonthGoals.length === 0 && (
              <p className="text-gray-400 text-sm col-span-2 text-center py-2">No monthly goals set</p>
            )}
          </div>
        </div>}

        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-center font-semibold text-gray-700 text-sm py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days}
        </div>

        {/* Floating Day Detail Popup — draggable & resizable */}
        {selectedCalendarDay && (() => {
          const sd = selectedCalendarDay;
          const sdKey = getDayKey(sd.year, sd.month, sd.day);
          const sdNote = getDayNote(sd.year, sd.month, sd.day);
          const sdSchedule = getDaySchedule(sd.year, sd.month, sd.day);
          const sdTasks = dayTasks[sdKey] || { high: [], medium: [], low: [] };
          const sdScheduleCount = Object.keys(sdSchedule).length;
          const sdTaskTotal = (sdTasks.high?.length || 0) + (sdTasks.medium?.length || 0) + (sdTasks.low?.length || 0);
          const sdTaskDone = (sdTasks.high?.filter(t => t.completed)?.length || 0) + (sdTasks.medium?.filter(t => t.completed)?.length || 0) + (sdTasks.low?.filter(t => t.completed)?.length || 0);
          const sdHighlight = getDayHighlight(sd.year, sd.month, sd.day);
          const sdDate = new Date(sd.year, sd.month, sd.day);
          const sdDisplayDate = sdDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          const sdIsToday = isToday(sd.year, sd.month, sd.day);

          return (
            <div
              style={{ position: 'fixed', left: dayPanelPos.x, top: dayPanelPos.y, width: dayPanelSize.w, height: dayPanelSize.h, zIndex: 9999 }}
              className="bg-white rounded-2xl border border-indigo-200 shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Draggable Header */}
              <div
                className="bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 px-4 py-3 cursor-move flex items-center justify-between flex-shrink-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX - dayPanelPos.x;
                  const startY = e.clientY - dayPanelPos.y;
                  const onMove = (ev) => setDayPanelPos({ x: Math.max(0, ev.clientX - startX), y: Math.max(0, ev.clientY - startY) });
                  const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm ${sdHighlight || 'bg-white/20'} text-white`}>
                    {sd.day}
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm">{sdDisplayDate}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {sdIsToday && <span className="text-[8px] bg-white/30 text-white px-1.5 py-0.5 rounded-full font-bold">TODAY</span>}
                      {sdScheduleCount > 0 && <span className="text-[8px] bg-white/20 text-white px-1.5 py-0.5 rounded-full font-medium">{sdScheduleCount} event{sdScheduleCount > 1 ? 's' : ''}</span>}
                      {sdTaskTotal > 0 && <span className="text-[8px] bg-white/20 text-white px-1.5 py-0.5 rounded-full font-medium">{sdTaskDone}/{sdTaskTotal} tasks</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { setActiveDayView({ year: sd.year, month: sd.month, day: sd.day }); window.scrollTo(0, 0); }}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-white/20 text-white rounded-lg text-[11px] font-bold hover:bg-white/30 transition-colors"
                  >
                    <Clock size={12} />
                    Day Planner
                    <ChevronRight size={12} />
                  </button>
                  <button
                    onClick={() => setSelectedCalendarDay(null)}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-indigo-50/50 to-white">
                {/* Quick schedule preview */}
                {sdScheduleCount > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Schedule</p>
                    <div className="space-y-1 max-h-28 overflow-y-auto">
                      {Object.entries(sdSchedule).sort(([a], [b]) => {
                        const pa = parseScheduleKey(a); const pb = parseScheduleKey(b);
                        return pa.hour !== pb.hour ? pa.hour - pb.hour : pa.minute - pb.minute;
                      }).slice(0, 5).map(([hr, evt]) => {
                        const { hour: eh, minute: em } = parseScheduleKey(hr);
                        const base = eh === 0 ? '12 AM' : eh < 12 ? `${eh} AM` : eh === 12 ? '12 PM' : `${eh - 12} PM`;
                        const timeStr = em ? base.replace(/( AM| PM)/, `:${String(em).padStart(2,'0')}$1`) : base;
                        return (
                          <div key={String(hr)} className="flex items-center gap-2 text-xs">
                            <span className="text-[10px] font-mono text-indigo-500 w-14 text-right flex-shrink-0">{timeStr}</span>
                            <div className="w-1 h-1 rounded-full bg-indigo-400 flex-shrink-0"></div>
                            <span className="text-gray-700 truncate">{getScheduleText(evt)}</span>
                          </div>
                        );
                      })}
                      {sdScheduleCount > 5 && <p className="text-[10px] text-gray-400 pl-16">+{sdScheduleCount - 5} more...</p>}
                    </div>
                  </div>
                )}

                {/* Day Note */}
                <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <FileText size={10} className="text-orange-500" />
                    Day Note
                  </p>
                  <textarea
                    key={sdKey}
                    defaultValue={sdNote}
                    onBlur={(e) => {
                      if (e.target.value !== sdNote) {
                        updateDayNote(sd.year, sd.month, sd.day, e.target.value);
                      }
                    }}
                    placeholder="Add a quick note for this day..."
                    className="w-full px-3 py-2 border border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none text-sm bg-gray-50/50"
                    style={{ minHeight: 80 }}
                  />
                </div>
              </div>

              {/* Resize Handle */}
              <div
                className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize flex items-center justify-center"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const startW = dayPanelSize.w;
                  const startH = dayPanelSize.h;
                  const onMove = (ev) => setDayPanelSize({ w: Math.max(320, startW + (ev.clientX - startX)), h: Math.max(250, startH + (ev.clientY - startY)) });
                  const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" className="text-gray-300">
                  <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          );
        })()}

        {/* Event Banners Management */}
        <div className="mt-4 mb-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <CalendarRange size={16} className="text-purple-500" />
              Event Banners
            </h3>
            <button onClick={() => { setShowBannerForm(!showBannerForm); setEditingBannerId(null); setBannerColor('bg-purple-400'); setBannerEmoji(''); }}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors">
              {showBannerForm ? 'Cancel' : '+ Add Banner'}
            </button>
          </div>

          {showBannerForm && (() => {
            const editBanner = editingBannerId ? calendarBanners.find(b => b.id === editingBannerId) : null;

            const parseEventText = (text) => {
              const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
              return lines.map(line => tryParseEvent(line)).filter(Boolean);
            };

            return (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200 mb-3 space-y-3">
              {/* Smart Paste */}
              {!editingBannerId && (
                <div className="bg-white rounded-lg p-3 border border-dashed border-purple-300">
                  <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <Sparkles size={10} /> Smart Paste — paste event text to auto-parse
                  </p>
                  <textarea
                    id="banner-smart-paste"
                    placeholder={"Paste or type events, one per line:\nSchool vacation March 8 - June 6\nEid holiday April 1 to April 3\nTrip to Dubai 05/15 - 05/22"}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none bg-gray-50/50"
                    rows={3}
                  />
                  <button
                    onClick={() => {
                      const txt = document.getElementById('banner-smart-paste')?.value;
                      if (!txt?.trim()) return;
                      const parsed = parseEventText(txt);
                      if (parsed.length === 0) return;
                      const colors = bannerColors;
                      const newBanners = parsed.map((p, i) => ({
                        id: Date.now() + i, title: p.title, startDate: p.startDate, endDate: p.endDate,
                        color: colors[(calendarBanners.length + i) % colors.length], emoji: ''
                      }));
                      setCalendarBanners(prev => [...prev, ...newBanners]);
                      document.getElementById('banner-smart-paste').value = '';
                    }}
                    className="mt-2 w-full px-3 py-1.5 bg-purple-500 text-white rounded-lg text-xs font-bold hover:bg-purple-600 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Zap size={12} /> Parse & Add Events
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-[9px] text-gray-400 font-medium uppercase">{editingBannerId ? 'Edit Banner' : 'or add manually'}</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              <input
                key={`bt-${editingBannerId || 'new'}`}
                id="banner-title"
                type="text"
                defaultValue={editBanner?.title || ''}
                placeholder="Event name (e.g., School Vacation)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Start Date</label>
                  <input key={`bs-${editingBannerId || 'new'}`} id="banner-start" type="date" defaultValue={editBanner?.startDate || ''} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">End Date</label>
                  <input key={`be-${editingBannerId || 'new'}`} id="banner-end" type="date" defaultValue={editBanner?.endDate || ''} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Color</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {bannerColors.map(c => (
                    <button key={c} onClick={() => setBannerColor(c)}
                      className={`w-6 h-6 rounded-full ${c} transition-all ${bannerColor === c ? 'ring-2 ring-offset-1 ring-gray-600 scale-110' : 'hover:scale-110'}`} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Emoji (optional)</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setBannerEmoji('')}
                    className={`w-7 h-7 rounded-lg text-xs border transition-all ${!bannerEmoji ? 'border-gray-600 bg-gray-100 font-bold' : 'border-gray-200 hover:bg-gray-50'}`}>—</button>
                  {bannerEmojis.map(e => (
                    <button key={e} onClick={() => setBannerEmoji(e)}
                      className={`w-7 h-7 rounded-lg text-sm border transition-all ${bannerEmoji === e ? 'border-gray-600 bg-gray-100 scale-110' : 'border-gray-200 hover:bg-gray-50 hover:scale-110'}`}>{e}</button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => {
                  const title = document.getElementById('banner-title')?.value?.trim();
                  const startDate = document.getElementById('banner-start')?.value;
                  const endDate = document.getElementById('banner-end')?.value;
                  if (!title || !startDate || !endDate) return;
                  if (endDate < startDate) return;
                  if (editingBannerId) {
                    setCalendarBanners(prev => prev.map(b => b.id === editingBannerId ? { ...b, title, startDate, endDate, color: bannerColor, emoji: bannerEmoji } : b));
                  } else {
                    setCalendarBanners(prev => [...prev, { id: Date.now(), title, startDate, endDate, color: bannerColor, emoji: bannerEmoji }]);
                  }
                  setEditingBannerId(null);
                  setBannerEmoji('');
                  setShowBannerForm(false);
                }}
                className="w-full px-4 py-2 bg-purple-500 text-white rounded-xl text-sm font-bold hover:bg-purple-600 transition-colors"
              >{editingBannerId ? 'Save Changes' : 'Add Banner'}</button>
            </div>
            );
          })()}

          {calendarBanners.length > 0 && (
            <div className="space-y-1.5">
              {calendarBanners.map(banner => (
                <div key={banner.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 group">
                  <div className={`w-3 h-3 rounded-full ${banner.color} flex-shrink-0`}></div>
                  <span className="font-medium text-gray-800">{banner.emoji} {banner.title}</span>
                  <span className="text-gray-400 flex-1 text-right">{banner.startDate} → {banner.endDate}</span>
                  <button
                    onClick={() => { setEditingBannerId(banner.id); setBannerColor(banner.color); setBannerEmoji(banner.emoji || ''); setShowBannerForm(true); }}
                    className="text-gray-300 hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-all"
                  ><Edit2 size={13} /></button>
                  <button
                    onClick={() => setCalendarBanners(prev => prev.filter(b => b.id !== banner.id))}
                    className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  ><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Floating Color Picker — always visible at bottom */}
        <div className="sticky bottom-0 mt-4 -mx-6 -mb-6 px-4 py-3 bg-white/90 backdrop-blur-md border-t border-gray-200 rounded-b-2xl z-10">
          <div className="flex items-center justify-center gap-2">
            <Palette size={16} className="text-gray-400 flex-shrink-0" />
            {highlightColors.map(c => (
              <button
                key={c.value}
                onClick={() => setSelectedColor(c.value)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                  selectedColor === c.value
                    ? `${c.value} text-white shadow-md scale-105 border-2 ${c.border}`
                    : `${c.bg} ${c.text} border border-gray-200 hover:scale-105`
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${selectedColor === c.value ? 'bg-white' : c.value} flex-shrink-0`} />
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const YearlyView = () => {
    const quarterGroups = [
      { name: 'Q1', months: [0, 1, 2], headerClass: 'from-blue-500 to-cyan-500', badgeClass: 'bg-blue-100 text-blue-700' },
      { name: 'Q2', months: [3, 4, 5], headerClass: 'from-emerald-500 to-teal-500', badgeClass: 'bg-emerald-100 text-emerald-700' },
      { name: 'Q3', months: [6, 7, 8], headerClass: 'from-amber-500 to-orange-500', badgeClass: 'bg-amber-100 text-amber-700' },
      { name: 'Q4', months: [9, 10, 11], headerClass: 'from-violet-500 to-fuchsia-500', badgeClass: 'bg-violet-100 text-violet-700' }
    ];

    const shortDayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    const getMonthStats = (monthIndex) => {
      const monthPrefix = `${currentYear}-${String(monthIndex + 1).padStart(2, '0')}-`;
      const monthGoalList = getMonthlyGoals(currentYear, monthIndex);
      const completedGoals = monthGoalList.filter(goal => goal.completed).length;

      const highlightedDays = Object.keys(dayHighlights).filter(key => key.startsWith(monthPrefix)).length;
      const notesCount = Object.keys(dayNotes).filter(key => key.startsWith(monthPrefix)).length;
      const plannedHours = Object.entries(dailyHours).reduce((sum, [key, value]) => {
        if (!key.startsWith(monthPrefix)) return sum;
        return sum + (parseFloat(value) || 0);
      }, 0);

      return {
        highlightedDays,
        notesCount,
        plannedHours,
        goalCount: monthGoalList.length,
        completedGoals
      };
    };

    const renderMiniMonth = (monthIndex) => {
      const daysInMonth = getDaysInMonth(monthIndex, currentYear);
      const firstDay = getFirstDayOfMonth(monthIndex, currentYear);
      const dayCells = [];

      for (let i = 0; i < firstDay; i++) {
        dayCells.push(<div key={`empty-${monthIndex}-${i}`} className="h-8 rounded-md" />);
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const highlight = getDayHighlight(currentYear, monthIndex, day);
        const note = getDayNote(currentYear, monthIndex, day);
        const hours = getDailyHours(currentYear, monthIndex, day);
        const today = isToday(currentYear, monthIndex, day);
        const holiday = getHoliday(currentYear, monthIndex, day);

        dayCells.push(
          <button
            key={`${monthIndex}-${day}`}
            onClick={() => {
              setCurrentMonth(monthIndex);
              setViewMode('calendar');
            }}
            className={`h-8 rounded-md text-xs font-semibold border transition-colors relative ${highlight ? `${highlight} border-transparent` : 'bg-white border-gray-200 hover:bg-gray-50'
              } ${today ? 'ring-2 ring-blue-400' : ''}`}
            title={holiday ? `${monthNames[monthIndex]} ${day} — ${holiday.name}` : `${monthNames[monthIndex]} ${day}, ${currentYear}`}
          >
            {day}
            {holiday && (
              <span className={`absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full ${
                holiday.type === 'islamic' ? 'bg-green-500' :
                holiday.type === 'national' ? 'bg-orange-500' :
                holiday.type === 'hindu' ? 'bg-amber-500' :
                'bg-purple-500'
              }`}></span>
            )}
            {(note || hours) && (
              <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-gray-700/70"></span>
            )}
          </button>
        );
      }

      const stats = getMonthStats(monthIndex);

      return (
        <div key={monthIndex} className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-800">{monthNames[monthIndex]}</h3>
            <button
              onClick={() => {
                setCurrentMonth(monthIndex);
                setViewMode('calendar');
              }}
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Open month
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {shortDayNames.map((dayName, idx) => (
              <div key={`${monthIndex}-${dayName}-${idx}`} className="text-[10px] text-gray-500 text-center font-medium">
                {dayName}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 mb-3">
            {dayCells}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-blue-50 text-blue-700 rounded-md px-2 py-1">
              Goals: {stats.completedGoals}/{stats.goalCount}
            </div>
            <div className="bg-purple-50 text-purple-700 rounded-md px-2 py-1">
              Hours: {stats.plannedHours}
            </div>
            <div className="bg-amber-50 text-amber-700 rounded-md px-2 py-1">
              Notes: {stats.notesCount}
            </div>
            <div className="bg-green-50 text-green-700 rounded-md px-2 py-1">
              Highlights: {stats.highlightedDays}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Yearly View - {currentYear}</h2>
          <p className="text-gray-600">All months grouped by quarter. Click a day or month to jump into detailed planning.</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {quarterGroups.map((quarter) => (
            <div key={quarter.name} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className={`bg-gradient-to-r ${quarter.headerClass} p-4 text-white flex items-center justify-between`}>
                <h3 className="text-lg font-bold">{quarter.name}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${quarter.badgeClass}`}>
                  {monthNames[quarter.months[0]]} - {monthNames[quarter.months[2]]}
                </span>
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {quarter.months.map((monthIndex) => renderMiniMonth(monthIndex))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const GoalInput = ({ week }) => {
    const [newGoal, setNewGoal] = useState('');

    return (
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newGoal}
          onChange={(e) => setNewGoal(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addGoal(week, newGoal);
              setNewGoal('');
            }
          }}
          placeholder="Add a goal..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => {
            addGoal(week, newGoal);
            setNewGoal('');
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus size={20} />
        </button>
      </div>
    );
  };

  const MilestoneInput = ({ week }) => {
    const [newMilestone, setNewMilestone] = useState('');
    const [newDate, setNewDate] = useState('');

    return (
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newMilestone}
          onChange={(e) => setNewMilestone(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addMilestone(week, newMilestone, newDate);
              setNewMilestone('');
              setNewDate('');
            }
          }}
          placeholder="Add milestone..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          onClick={() => {
            addMilestone(week, newMilestone, newDate);
            setNewMilestone('');
            setNewDate('');
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus size={20} />
        </button>
      </div>
    );
  };

  // ─── Financial Section Component (disabled for client version) ──────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const FinancialSection = ({ week }) => {
    const [newAmount, setNewAmount] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newType, setNewType] = useState('revenue');
    const [showHistory, setShowHistory] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [showStats, setShowStats] = useState(false);

    const data = getWeekData(week);
    const totalRev = getTotalRevenue(week);
    const totalExp = getTotalExpenses(week);
    const profit = totalRev - totalExp;
    const monthly = getMonthlyCumulativeFinancials(week);

    const currentWeekNum = getCurrentWeekNumber();
    let totalRevForecast = 0, weeksWithRev = 0;
    for (let w = 1; w <= currentWeekNum; w++) {
      const wd = getWeekData(w);
      if ((wd.revenueEntries || []).length > 0) {
        totalRevForecast += wd.revenue || 0;
        weeksWithRev++;
      }
    }
    const avgPerWeek = weeksWithRev > 0 ? totalRevForecast / weeksWithRev : 0;
    const projectedYearEnd = Math.round(avgPerWeek * 52);
    const projectedRemaining = Math.round(avgPerWeek * (52 - currentWeekNum));

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group">
        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign size={24} className="text-emerald-600" />
              <h2 className="text-xl font-bold text-gray-800">Weekly Finance</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setShowStats(!showStats); setShowAdd(false); setShowHistory(false); }}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${showStats ? 'bg-blue-500 text-white shadow-md shadow-blue-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
              >
                Stats
              </button>
              <button
                onClick={() => { setShowHistory(!showHistory); setShowAdd(false); setShowStats(false); }}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${showHistory ? 'bg-blue-500 text-white shadow-md shadow-blue-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
              >
                History
              </button>
              <button
                onClick={() => { setShowAdd(!showAdd); setShowHistory(false); setShowStats(false); }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${showAdd ? 'bg-gray-800 text-white rotate-45 shadow-lg shadow-gray-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Revenue</span>
              <span className="text-lg font-black text-gray-800 tracking-tight leading-none">{'\u20B9'}{totalRev.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Expenses</span>
              <span className="text-lg font-black text-gray-800 tracking-tight leading-none">{'\u20B9'}{totalExp.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 whitespace-nowrap">Net Profit</span>
              <div className={`text-lg font-black tracking-tight leading-none ${profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {profit >= 0 ? '+' : ''}{'\u20B9'}{profit.toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          {/* Minimal Progress Bar */}
          <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden flex shadow-inner">
            <div className="h-full bg-emerald-500 shadow-[2px_0_10px_rgba(16,185,129,0.4)]" style={{ width: `${(totalRev / (totalRev + totalExp || 1)) * 100}%` }}></div>
            <div className="h-full bg-rose-500 shadow-[-2px_0_10px_rgba(244,63,94,0.4)]" style={{ width: `${(totalExp / (totalRev + totalExp || 1)) * 100}%` }}></div>
          </div>
        </div>

        {/* Monthly Stats Drawer */}
        {showStats && (
          <div className="p-3 bg-blue-50/30 border-t border-blue-100 animate-in fade-in slide-in-from-top-2">
            <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-2 px-1">Cumulative {monthly.month}</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white p-2 rounded-xl border border-blue-100/50 shadow-sm">
                <span className="text-[9px] font-bold text-gray-400 uppercase leading-none block mb-1">Total Revenue</span>
                <span className="text-xs font-black text-emerald-600">{'\u20B9'}{monthly.revenue.toLocaleString('en-IN')}</span>
              </div>
              <div className="bg-white p-2 rounded-xl border border-blue-100/50 shadow-sm">
                <span className="text-[9px] font-bold text-gray-400 uppercase leading-none block mb-1">Total Expense</span>
                <span className="text-xs font-black text-rose-600">{'\u20B9'}{monthly.expenses.toLocaleString('en-IN')}</span>
              </div>
              <div className="bg-white p-2 rounded-xl border border-blue-100/50 shadow-sm">
                <span className="text-[9px] font-bold text-gray-400 uppercase leading-none block mb-1">Net Month</span>
                <span className={`text-xs font-black ${monthly.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {'\u20B9'}{monthly.profit.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* History Drawer */}
        {showHistory && (
          <div className="p-3 bg-gray-50 border-t border-gray-100 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2">
            <div className="space-y-1">
              {[...data.revenueEntries, ...data.expenseEntries.map(e => ({ ...e, isExp: true }))]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map(entry => (
                  <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded-lg text-[11px] group">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${entry.isExp ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                      <span className="text-gray-400 font-mono">{new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                      <span className="font-bold text-gray-700">{entry.description || (entry.isExp ? 'Expense' : 'Revenue')}</span>
                      {entry.source === 'expense-manager' && <span className="px-1 py-0.5 rounded bg-emerald-50 text-emerald-500 text-[8px] font-bold">EM</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-black ${entry.isExp ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {entry.isExp ? '-' : '+'}{'\u20B9'}{entry.amount.toLocaleString('en-IN')}
                      </span>
                      <button
                        onClick={() => deleteFinancialEntry(week, entry.isExp ? 'expenses' : 'revenue', entry.id)}
                        className="p-1 text-gray-300 hover:text-rose-500 opacity-0 group-hover:opacity-100"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              {data.revenueEntries.length === 0 && data.expenseEntries.length === 0 && (
                <div className="text-center py-4 text-gray-400 text-xs">No transactions this week</div>
              )}
            </div>
          </div>
        )}

        {/* Add Drawer */}
        {showAdd && (
          <div className="p-3 bg-blue-50/50 border-t border-blue-100 animate-in fade-in slide-in-from-top-2">
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setNewType('revenue')}
                className={`flex-1 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${newType === 'revenue' ? 'bg-emerald-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-emerald-50'}`}
              >
                Inflow
              </button>
              <button
                onClick={() => setNewType('expenses')}
                className={`flex-1 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${newType === 'expenses' ? 'bg-rose-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-rose-50'}`}
              >
                Outflow
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                placeholder="Amount"
                className="w-20 px-2 py-1.5 border border-blue-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Description"
                className="flex-1 px-2 py-1.5 border border-blue-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                onClick={() => {
                  if (newAmount && newDesc) {
                    addFinancialEntry(week, newType, parseFloat(newAmount), newDesc, new Date().toISOString().split('T')[0]);
                    setNewAmount('');
                    setNewDesc('');
                    setShowAdd(false);
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-md transition-colors ${newType === 'revenue' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Efficiency Score */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mx-6 mt-3 mb-4">
          <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3"><BarChart3 size={16} className="text-indigo-500" /> Efficiency Score</h3>
          {(() => {
            const last4 = [];
            for (let w = Math.max(1, currentWeekNum - 3); w <= currentWeekNum; w++) {
              const wk = weekData[w] || {};
              const goals = wk.goals || [];
              const totalG = goals.length;
              const doneG = goals.filter(g => g.completed).length;
              const score = totalG > 0 ? Math.round((doneG / totalG) * 100) : 0;
              last4.push({ week: w, total: totalG, done: doneG, score });
            }
            const avgScore = last4.length > 0 ? Math.round(last4.reduce((s,w) => s + w.score, 0) / last4.length) : 0;
            const bestWeek = last4.reduce((b, w) => w.score > (b?.score || 0) ? w : b, null);
            return (
              <div>
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke={avgScore >= 70 ? '#22c55e' : avgScore >= 40 ? '#f59e0b' : '#ef4444'} strokeWidth="3" strokeDasharray={`${avgScore * 0.942} 100`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-sm font-black text-gray-700">{avgScore}%</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-800">{avgScore >= 70 ? 'Great!' : avgScore >= 40 ? 'Getting there' : 'Room to grow'}</div>
                    <div className="text-xs text-gray-500">Avg efficiency over last {last4.length} weeks</div>
                    {bestWeek && bestWeek.score > 0 && <div className="text-[10px] text-indigo-500 font-medium mt-0.5">Best: Week {bestWeek.week} at {bestWeek.score}%</div>}
                  </div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {last4.map(w => (
                    <div key={w.week} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="w-full bg-gray-100 rounded-t-sm relative" style={{ height: '40px' }}>
                        <div className={`absolute bottom-0 w-full rounded-t-sm transition-all ${w.score >= 70 ? 'bg-green-400' : w.score >= 40 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ height: `${Math.max(w.score, 4)}%` }} />
                      </div>
                      <span className="text-[9px] text-gray-400 font-medium">W{w.week}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-gray-400 mt-2 text-center">{last4.reduce((s,w) => s+w.done, 0)} of {last4.reduce((s,w) => s+w.total, 0)} goals completed across {last4.length} weeks</div>
              </div>
            );
          })()}
        </div>

        {/* Year-End Revenue Forecast */}
        {avgPerWeek > 0 && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mt-3 mx-6 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={15} className="text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">Year-End Revenue Forecast</span>
            </div>
            <div className="text-2xl font-black text-emerald-700">{'\u20B9'}{projectedYearEnd.toLocaleString('en-IN')}</div>
            <div className="text-[11px] text-emerald-600 mt-0.5">
              Based on {'\u20B9'}{Math.round(avgPerWeek).toLocaleString('en-IN')}/wk avg · {'\u20B9'}{projectedRemaining.toLocaleString('en-IN')} more to go this year
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Notes Section Component ─────────────────────────────────────────
  const NotesSection = ({ week }) => {
    const [newNote, setNewNote] = useState('');
    const [editingNoteId, setEditingNoteId] = useState(null);
    const data = getWeekData(week);

    const formatTimestamp = (isoString) => {
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    const reversedEntries = [...data.noteEntries].reverse();

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="text-orange-600" size={22} />
            <h2 className="text-lg font-bold text-gray-800">Notes & Reflections</h2>
            {reversedEntries.length > 0 && (
              <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-2 py-0.5 rounded-full">{reversedEntries.length}</span>
            )}
          </div>
        </div>

        {/* Weekly Reflection Prompt */}
        {(() => {
          const prompts = [
            "What did I learn this week?",
            "Who did I help or connect with?",
            "What am I most proud of this week?",
            "What would I do differently?",
            "What challenge did I overcome?",
            "What habit or routine served me well?",
            "What should I do more of next week?",
            "What am I grateful for this week?",
            "What progress did I make on my goals?",
            "What drained my energy and how can I reduce it?",
            "What surprised me this week?",
            "How did I take care of myself?",
          ];
          const prompt = prompts[(currentWeek - 1) % prompts.length];
          return (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <span className="text-lg flex-shrink-0">💭</span>
                <div>
                  <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-1">Week {currentWeek} Reflection</p>
                  <p className="text-sm font-medium text-amber-900">{prompt}</p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Compact Add Note — inline row */}
        <div className="flex gap-2 mb-4">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Write your thoughts, learnings, and reflections..."
            rows={2}
            className="flex-1 px-3 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm"
          />
          <button
            onClick={() => {
              addNoteEntry(week, newNote);
              setNewNote('');
            }}
            disabled={!newNote.trim()}
            className="self-end px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Legacy notes display */}
        {data.notes && data.notes.trim() !== '' && data.noteEntries.length === 0 && (
          <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-1 text-xs text-orange-600 mb-1">
              <Clock size={12} />
              <span>Previous notes (legacy)</span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.notes}</p>
          </div>
        )}

        {/* Note Entries — 2-column grid, scrollable */}
        {reversedEntries.length === 0 && (!data.notes || data.notes.trim() === '') ? (
          <p className="text-gray-400 text-center py-6 text-sm">No notes yet for this week</p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reversedEntries.map(entry => (
                <div key={entry.id} className="p-3 bg-orange-50 rounded-lg border border-orange-100 group hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1 text-[11px] text-orange-600 font-medium">
                      <Clock size={11} />
                      <span>{formatTimestamp(entry.timestamp)}</span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditingNoteId(editingNoteId === entry.id ? null : entry.id)}
                        className="p-0.5 text-orange-600 hover:bg-orange-200 rounded"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this note?')) {
                            deleteNoteEntry(week, entry.id);
                          }
                        }}
                        className="p-0.5 text-red-600 hover:bg-red-100 rounded"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  {editingNoteId === entry.id ? (
                    <textarea
                      defaultValue={entry.text}
                      onBlur={(e) => {
                        updateNoteEntry(week, entry.id, e.target.value);
                        setEditingNoteId(null);
                      }}
                      autoFocus
                      className="w-full h-20 px-2 py-1.5 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm"
                    />
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">{entry.text}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" style={getSeasonalStyle()}>
      <div className={`mx-auto ${viewMode === 'braindump' ? 'max-w-full px-2' : 'max-w-7xl'}`}>
        {/* Title Card - hidden in full-screen views */}
        {(
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="text-blue-500" size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight leading-tight text-gray-800">
                  52 Week Planner <span className="text-blue-500">2026</span>
                </h1>
                <p className="text-gray-400 text-sm font-light tracking-wide">
                  {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Your'}'s — Work & Planning
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-50 text-blue-600 px-3 py-1 rounded-full border border-blue-100">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                Week {currentWeek}/52
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-600 px-3 py-1 rounded-full border border-blue-100">
                {Math.round((currentWeek / 52) * 100)}%
              </span>
              <button
                onClick={() => { setCmdPaletteOpen(true); setCmdInput(''); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 px-3 py-1.5 rounded-full border border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-colors cursor-pointer"
                title="Quick Command: schedule events, add goals, todos, event banners"
              >
                <Search size={13} />
                <span className="hidden sm:inline">Quick Add</span>
                <kbd className="text-[9px] bg-gray-100 rounded px-1 py-0.5 font-mono">⌘K</kbd>
              </button>
              {onSignOut && (
                <button
                  onClick={onSignOut}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        {!zenMode && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 mb-6">
          <div className="flex gap-2 flex-wrap justify-center">
            <button
              onClick={() => {
                const today = new Date();
                setActiveDayView({ year: today.getFullYear(), month: today.getMonth(), day: today.getDate() });
                setViewMode('daily');
                window.scrollTo(0, 0);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'daily'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <Clock size={18} />
              Daily
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'week'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <LayoutGrid size={18} />
              Week
            </button>
            <button
              onClick={() => { setActiveDayView(null); setViewMode('calendar'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'calendar'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <Calendar size={18} />
              Monthly
            </button>
            <button
              onClick={() => setViewMode('yearly')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'yearly'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <CalendarRange size={18} />
              Yearly
            </button>
            <button
              onClick={() => setViewMode('habits')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'habits'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <Check size={18} />
              Habits
            </button>
            <button
              onClick={() => setViewMode('productivity')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'productivity'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <TrendingUp size={18} />
              Dashboard
            </button>
            <button
              onClick={() => setViewMode('braindump')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'braindump'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <Brain size={18} />
              Brain Dump
            </button>
            <button
              onClick={() => setViewMode('todo')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'todo'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <ListTodo size={18} />
              To Do
            </button>
            <button
              onClick={() => setViewMode('journal')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'journal'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <BookOpen size={18} />
              Journal
            </button>
            <button
              onClick={() => setViewMode('lifelab')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'lifelab'
                ? 'bg-purple-500 text-white'
                : 'text-gray-500 hover:text-purple-500 hover:bg-purple-50'
                }`}
            >
              <Sparkles size={18} />
              Life Lab
            </button>
          </div>
        </div>
        )}

        {viewMode === 'week' && (
          <>
            {/* Smart Daily Briefing */}
            {(() => {
              const today = new Date();
              const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
              const yesterdayD = new Date(today); yesterdayD.setDate(yesterdayD.getDate()-1);
              const yesterdayKey = `${yesterdayD.getFullYear()}-${String(yesterdayD.getMonth()+1).padStart(2,'0')}-${String(yesterdayD.getDate()).padStart(2,'0')}`;
              const todaySchedule = daySchedule[todayKey] || {};
              const scheduleCount = Object.keys(todaySchedule).length;
              const weekGoals = weekData[currentWeek]?.goals || [];
              const completedGoals = weekGoals.filter(g => g.completed).length;
              const yesterdayTracking = habitTracking[yesterdayKey] || {};
              const habitsAtRisk = habits.filter(h => !yesterdayTracking[h.id]);
              const upcomingBills = (expenseData.expenses || []).filter(e => { // eslint-disable-line no-unused-vars
                if (!e.recurring || !e.nextDueDate) return false;
                const due = new Date(e.nextDueDate);
                const diff = Math.ceil((due - today) / (1000*60*60*24));
                return diff >= 0 && diff <= 7;
              });
              const quotes = [
                "The secret of getting ahead is getting started.",
                "Small daily improvements lead to stunning results.",
                "What you do today can improve all your tomorrows.",
                "Focus on progress, not perfection.",
                "A year from now you'll wish you had started today.",
                "The only way to do great work is to love what you do.",
                "Don't count the days, make the days count.",
                "Success is the sum of small efforts repeated daily.",
                "Your future is created by what you do today.",
                "Discipline is choosing what you want most over what you want now.",
                "Every expert was once a beginner.",
                "It always seems impossible until it's done.",
                "The best time to start was yesterday. The next best time is now.",
                "Believe you can and you're halfway there.",
                "Push yourself, because no one else is going to do it for you.",
                "Dreams don't work unless you do.",
                "Stay focused. Stay humble. Stay hungry.",
                "The harder you work, the luckier you get.",
                "Make each day your masterpiece.",
                "You don't have to be great to start, but you have to start to be great."
              ];
              const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 1)) / (1000*60*60*24));
              const quote = quotes[dayOfYear % quotes.length];
              return (
                <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-lg p-5 mb-4 text-white">
                  <div className="flex items-start justify-between mb-3 gap-4">
                    <div className="flex-shrink-0">
                      <h2 className="text-lg font-bold flex items-center gap-2"><Sparkles size={20} /> Good {today.getHours() < 12 ? 'Morning' : today.getHours() < 17 ? 'Afternoon' : 'Evening'}</h2>
                      <p className="text-white/70 text-sm">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div className="flex-1 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 via-yellow-300/20 to-orange-400/20 rounded-2xl blur-sm"></div>
                      <div className="relative bg-gradient-to-br from-amber-500/20 via-yellow-400/15 to-orange-500/20 backdrop-blur-sm rounded-2xl px-6 py-5 border border-amber-300/30 overflow-hidden">
                        <div className="absolute -top-2 -left-1 text-7xl font-serif text-amber-300/40 leading-none select-none">&ldquo;</div>
                        <p className="text-lg font-bold text-white leading-relaxed relative z-10 pl-5 pr-2" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif", letterSpacing: '0.01em' }}>{quote}</p>
                        <div className="flex items-center justify-end gap-2 mt-3 relative z-10">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300/40 to-transparent"></div>
                          <Sparkles size={10} className="text-amber-300/70" />
                          <p className="text-[10px] text-amber-200/80 font-bold tracking-widest uppercase">Thought of the Day</p>
                        </div>
                        <div className="absolute -bottom-3 -right-1 text-7xl font-serif text-amber-300/40 leading-none select-none rotate-180">&rdquo;</div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-2xl font-black">Week {currentWeek}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                      <div className="text-white/60 text-[10px] font-bold uppercase tracking-wider">Today's Events</div>
                      <div className="text-xl font-black mt-0.5">{scheduleCount}</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                      <div className="text-white/60 text-[10px] font-bold uppercase tracking-wider">Goals</div>
                      <div className="text-xl font-black mt-0.5">{completedGoals}<span className="text-sm font-medium text-white/50">/{weekGoals.length}</span></div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                      <div className="text-white/60 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">{habitsAtRisk.length > 0 && <AlertTriangle size={10} />}Habits at Risk</div>
                      <div className="text-xl font-black mt-0.5">{habitsAtRisk.length}</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Week Navigation - Minimalist Redesign */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentWeek(Math.max(1, currentWeek - 1))}
                  disabled={currentWeek === 1}
                  className="w-10 h-10 flex items-center justify-center bg-blue-50 text-blue-500 rounded-xl hover:bg-blue-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed group shadow-sm"
                >
                  <ChevronLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>

                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-3">
                    {currentData.cover?.emoji && (
                      <span className="text-3xl leading-none">{currentData.cover.emoji}</span>
                    )}
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center relative overflow-hidden group">
                      <span className="text-xl font-black z-10">{currentWeek}</span>
                      <div
                        className="absolute bottom-0 left-0 w-full bg-blue-100 transition-all duration-700 ease-out"
                        style={{ height: `${(currentWeek / 52) * 100}%`, opacity: 0.5 }}
                      ></div>
                    </div>
                    <div className="flex flex-col">
                      <h2 className="text-sm font-black text-gray-800 tracking-tight leading-none mb-1">
                        WEEK {currentWeek} <span className="text-blue-600 font-black ml-1">/ 52</span>
                      </h2>
                      <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest leading-none">
                        {getWeekDateRange(currentWeek)}
                      </p>
                      {currentData.cover?.title && (
                        <p className="text-xs font-semibold text-indigo-600 mt-0.5 truncate max-w-[160px]">{currentData.cover.title}</p>
                      )}
                      <button
                        onClick={() => setEditingCover(v => !v)}
                        className="text-[10px] text-gray-400 hover:text-indigo-500 flex items-center gap-0.5 mt-1"
                      >
                        <Edit2 size={10} /> {editingCover ? 'Done' : 'Customize week'}
                      </button>
                      {editingCover && (
                        <div className="mt-3 p-3 bg-white rounded-xl border border-indigo-100 shadow-sm space-y-3 w-64">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-12 flex-shrink-0">Emoji</span>
                            <input
                              type="text"
                              defaultValue={currentData.cover?.emoji || ''}
                              placeholder="🚀"
                              maxLength={4}
                              onBlur={e => {
                                const val = e.target.value.trim();
                                setWeekData(prev => ({
                                  ...prev,
                                  [currentWeek]: { ...(prev[currentWeek] || getWeekData(currentWeek)), cover: { ...(prev[currentWeek]?.cover || {}), emoji: val } }
                                }));
                              }}
                              className="flex-1 text-lg border border-gray-200 rounded px-2 py-0.5 focus:outline-none focus:border-indigo-300 w-16"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-12 flex-shrink-0">Title</span>
                            <input
                              type="text"
                              defaultValue={currentData.cover?.title || ''}
                              placeholder="e.g. Launch Week"
                              onBlur={e => {
                                const val = e.target.value.trim();
                                setWeekData(prev => ({
                                  ...prev,
                                  [currentWeek]: { ...(prev[currentWeek] || getWeekData(currentWeek)), cover: { ...(prev[currentWeek]?.cover || {}), title: val } }
                                }));
                              }}
                              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-indigo-300"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block mb-1.5">Color</span>
                            <div className="flex gap-1.5 flex-wrap">
                              {[
                                { label: 'Default', bg: 'bg-blue-50' },
                                { label: 'Purple', bg: 'bg-purple-50' },
                                { label: 'Green', bg: 'bg-emerald-50' },
                                { label: 'Orange', bg: 'bg-orange-50' },
                                { label: 'Rose', bg: 'bg-rose-50' },
                                { label: 'Amber', bg: 'bg-amber-50' },
                              ].map(c => (
                                <button
                                  key={c.bg}
                                  onClick={() => setWeekData(prev => ({
                                    ...prev,
                                    [currentWeek]: { ...(prev[currentWeek] || getWeekData(currentWeek)), cover: { ...(prev[currentWeek]?.cover || {}), colorClass: c.bg } }
                                  }))}
                                  className={`w-6 h-6 rounded-full ${c.bg} border-2 ${(currentData.cover?.colorClass === c.bg) ? 'border-indigo-400' : 'border-gray-200'} hover:border-indigo-300`}
                                  title={c.label}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setCurrentWeek(Math.min(52, currentWeek + 1))}
                  disabled={currentWeek === 52}
                  className="w-10 h-10 flex items-center justify-center bg-blue-50 text-blue-500 rounded-xl hover:bg-blue-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed group shadow-sm"
                >
                  <ChevronRight size={20} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              {/* Sophisticated Progress Strip */}
              <div className="mt-4 flex items-center gap-3 px-1">
                {(() => {
                  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24)) + 1;
                  const totalDays = ((new Date().getFullYear() % 4 === 0 && new Date().getFullYear() % 100 !== 0) || new Date().getFullYear() % 400 === 0) ? 366 : 365;
                  const pct = Math.round((dayOfYear / totalDays) * 100);
                  return (
                    <>
                      <div className="flex flex-col items-start leading-none shrink-0">
                        <span className="text-[10px] font-bold text-gray-500 tabular-nums">Day {dayOfYear}<span className="text-gray-300">/{totalDays}</span></span>
                      </div>
                      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden flex shadow-inner">
                        <div
                          className="h-full bg-blue-500 transition-all duration-700 ease-out"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                      <div className="flex flex-col items-end leading-none">
                        <span className="text-xs font-black text-emerald-600 tabular-nums">
                          {pct}%
                        </span>
                        <span className="text-[7px] font-black text-blue-400 uppercase tracking-tighter">
                          Year Progress
                        </span>
                      </div>
                    </>
                  );
                })()}
                <button
                  onClick={() => printWeekReport(currentWeek)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-500 hover:text-indigo-600 border border-gray-200 hover:border-indigo-200 rounded-lg transition-all"
                  title="Print week report as PDF"
                >
                  <span>🖨</span> PDF Report
                </button>
              </div>
            </div>

            {/* Quick Week Selector - Modern Timeline Redesign */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-2 px-1">
                <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Jump to Week</h3>
                <div className="h-px flex-1 bg-blue-50"></div>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pt-2 pb-4 custom-scrollbar snap-x">
                {[...Array(52)].map((_, i) => {
                  const weekNum = i + 1;
                  const status = getWeekStatus(weekNum);
                  const isSelected = currentWeek === weekNum;

                  let ringColor = 'ring-transparent';
                  let bgColor = 'bg-white';
                  let textColor = 'text-gray-400';
                  let scale = 'scale-100';
                  let shadow = 'shadow-none';

                  if (isSelected) {
                    bgColor = 'bg-blue-500';
                    textColor = 'text-white';
                    scale = 'scale-110';
                    shadow = 'shadow-lg shadow-blue-100';
                  } else if (status === 'current') {
                    bgColor = 'bg-emerald-500';
                    textColor = 'text-white';
                    ringColor = 'ring-4 ring-emerald-50';
                  } else if (status === 'past') {
                    bgColor = 'bg-gray-100';
                    textColor = 'text-gray-400 font-bold';
                  } else {
                    bgColor = 'bg-amber-400';
                    textColor = 'text-amber-950 font-black';
                    shadow = 'shadow-sm shadow-amber-100';
                  }

                  return (
                    <button
                      key={weekNum}
                      onClick={() => setCurrentWeek(weekNum)}
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-300 snap-center ${bgColor} ${textColor} ${scale} ${shadow} ${ringColor} hover:scale-110 active:scale-90`}
                    >
                      {weekNum}
                    </button>
                  );
                })}
              </div>

              {/* Minimalist Legend */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 px-1">
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(79,70,229,0.5)]"></div>
                  <span className="text-[9px] font-black text-blue-600 uppercase tracking-tighter">Selected</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                  <span className="text-[9px] font-black text-emerald-700 uppercase tracking-tighter">Current Week</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]"></div>
                  <span className="text-[9px] font-black text-amber-700 uppercase tracking-tighter">Future</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter text-opacity-60">Past</span>
                </div>
              </div>
            </div>
          </>
        )}

        {viewMode === 'daily' ? (
          <DayPlannerView
            year={activeDayView ? activeDayView.year : new Date().getFullYear()}
            month={activeDayView ? activeDayView.month : new Date().getMonth()}
            day={activeDayView ? activeDayView.day : new Date().getDate()}
            onBack={() => setViewMode('week')}
          />
        ) : viewMode === 'calendar' ? (
          activeDayView ? (
            <DayPlannerView
              year={activeDayView.year}
              month={activeDayView.month}
              day={activeDayView.day}
              onBack={() => setActiveDayView(null)}
            />
          ) : (
            <CalendarView />
          )
        ) : viewMode === 'yearly' ? (
          <YearlyView />
        ) : viewMode === 'habits' ? (
          <HabitsView />
        ) : viewMode === 'productivity' ? (
          <ProductivityView />
        ) : viewMode === 'journal' ? (
          JournalView()
        ) : viewMode === 'braindump' ? (
          BrainDumpView()
        ) : viewMode === 'todo' ? (
          TodoListView()
        ) : viewMode === 'lifelab' ? (
          <LifeLabView />
        ) : (
          <>
            {/* Week Day Strip */}
            {(() => {
              // Calculate the Monday of the selected week
              const startDate = new Date(2026, 0, 1);
              const startDay = startDate.getDay();
              const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
              const firstMonday = new Date(2026, 0, daysToMonday);
              const weekMonday = new Date(firstMonday);
              weekMonday.setDate(firstMonday.getDate() + (currentWeek - 1) * 7);

              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
              const weekDays = [];
              for (let i = 0; i < 7; i++) {
                const d = new Date(weekMonday);
                d.setDate(weekMonday.getDate() + i);
                weekDays.push(d);
              }

              return (
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-white rounded-full shadow-sm border border-gray-100 p-1.5 flex gap-1 items-center">
                    {weekDays.map((date, idx) => {
                      const dateOnly = new Date(date);
                      dateOnly.setHours(0, 0, 0, 0);
                      const isToday = dateOnly.getTime() === today.getTime();
                      const isPast = dateOnly < today;

                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setViewMode('calendar');
                            setCurrentMonth(date.getMonth());
                            setCurrentYear(date.getFullYear());
                            setActiveDayView({
                              year: date.getFullYear(),
                              month: date.getMonth(),
                              day: date.getDate()
                            });
                          }}
                          className={`group relative flex flex-col items-center justify-center w-12 h-12 rounded-full transition-all duration-300 ${isToday
                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-200 ring-4 ring-blue-50'
                            : isPast
                              ? 'text-gray-300 hover:text-gray-500'
                              : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          <span className={`text-[8px] font-black uppercase tracking-tighter mb-0.5 ${isToday ? 'text-white/60' : 'text-gray-400'}`}>
                            {dayNames[idx]}
                          </span>
                          <span className={`text-sm font-black ${isToday ? 'text-white' : 'text-gray-800'}`}>
                            {date.getDate()}
                          </span>
                          {isToday && (
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
                          )}
                          {!isToday && !isPast && (
                            <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400 border border-white"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Goals & Tasks */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="text-blue-600" size={24} />
                  <h2 className="text-xl font-bold text-gray-800">Weekly Goals & Tasks</h2>
                </div>

                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">Week {currentWeek} Focus</h3>
                  <p className="text-xs text-blue-600">Set specific goals for this week to track your progress</p>
                </div>

                <GoalInput week={currentWeek} />

                <div className="space-y-2">
                  {currentData.goals.map((goal) => (
                    <div
                      key={goal.id}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {editingGoal === goal.id ? (
                        <>
                          <input
                            type="text"
                            defaultValue={goal.text}
                            onBlur={(e) => {
                              updateGoal(currentWeek, goal.id, e.target.value);
                              setEditingGoal(null);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                updateGoal(currentWeek, goal.id, e.target.value);
                                setEditingGoal(null);
                              }
                            }}
                            autoFocus
                            className="flex-1 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input
                            type="date"
                            defaultValue={goal.deadline || ''}
                            onBlur={e => {
                              const dl = e.target.value;
                              setWeekData(prev => {
                                const cur = prev[currentWeek] || getWeekData(currentWeek);
                                return {
                                  ...prev,
                                  [currentWeek]: {
                                    ...cur,
                                    goals: (cur.goals || []).map(g =>
                                      g.id === goal.id ? { ...g, deadline: dl || undefined } : g
                                    )
                                  }
                                };
                              });
                            }}
                            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 focus:outline-none focus:border-blue-300"
                          />
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => toggleGoal(currentWeek, goal.id)}
                            className={`p-1 rounded ${goal.completed ? 'bg-green-500 text-white' : goal.notDone ? 'bg-red-500 text-white' : 'bg-gray-300'}`}
                          >
                            {goal.notDone ? <X size={16} /> : <Check size={16} />}
                          </button>
                          <span className={`flex-1 ${goal.completed ? 'line-through text-gray-500' : goal.notDone ? 'line-through text-red-500' : ''}`}>
                            {goal.text}
                            {goal.deadline && (() => {
                              const daysLeft = Math.ceil((new Date(goal.deadline) - new Date()) / 86400000);
                              if (daysLeft > 7) return <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full ml-1">📅 {daysLeft}d left</span>;
                              if (daysLeft > 0) return <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full ml-1">⚡ {daysLeft}d left</span>;
                              if (daysLeft === 0) return <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full ml-1">🔴 Due today</span>;
                              return <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full ml-1">⚠ Overdue {Math.abs(daysLeft)}d</span>;
                            })()}
                          </span>
                          {!goal.completed && (
                            <button
                              onClick={() => markGoalNotDone(currentWeek, goal.id)}
                              className="p-1 text-orange-500 hover:bg-orange-100 rounded"
                              title="Mark as not done"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                          {!goal.completed && (
                            <button
                              onClick={() => moveGoalToNextWeek(currentWeek, goal.id)}
                              className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                              title="Move to next week"
                            >
                              <ArrowRight size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => setEditingGoal(goal.id)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => deleteGoal(currentWeek, goal.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {currentData.goals.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No goals set for this week</p>
                  )}
                </div>
              </div>

              {/* Revenue & Expenses - disabled for client version */}
              {/* <FinancialSection week={currentWeek} /> */}



              {/* Project Milestones */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="text-blue-500" size={24} />
                  <h2 className="text-xl font-bold text-gray-800">Project Milestones</h2>
                </div>

                <MilestoneInput week={currentWeek} />

                <div className="space-y-2">
                  {currentData.milestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className="flex items-center gap-2 p-3 bg-gray-50/50 rounded-xl hover:bg-gray-100/50 border border-gray-100 transition-colors"
                    >
                      {editingMilestone === milestone.id ? (
                        <>
                          <input
                            type="text"
                            defaultValue={milestone.text}
                            onBlur={(e) => {
                              updateMilestone(currentWeek, milestone.id, e.target.value, milestone.date);
                              setEditingMilestone(null);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                updateMilestone(currentWeek, milestone.id, e.target.value, milestone.date);
                                setEditingMilestone(null);
                              }
                            }}
                            autoFocus
                            className="flex-1 px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => toggleMilestone(currentWeek, milestone.id)}
                            className={`p-1 rounded ${milestone.completed ? 'bg-purple-500 text-white' : milestone.notDone ? 'bg-red-500 text-white' : 'bg-gray-300'}`}
                          >
                            {milestone.notDone ? <X size={16} /> : <Check size={16} />}
                          </button>
                          <div className="flex-1">
                            <span className={`${milestone.completed ? 'line-through text-gray-500' : milestone.notDone ? 'line-through text-red-500' : ''}`}>
                              {milestone.text}
                            </span>
                            {milestone.date && (
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(milestone.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            )}
                          </div>
                          {!milestone.completed && (
                            <button
                              onClick={() => markMilestoneNotDone(currentWeek, milestone.id)}
                              className="p-1 text-orange-500 hover:bg-orange-100 rounded"
                              title="Mark as not done"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                          {!milestone.completed && (
                            <button
                              onClick={() => moveMilestoneToNextWeek(currentWeek, milestone.id)}
                              className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                              title="Move to next week"
                            >
                              <ArrowRight size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => setEditingMilestone(milestone.id)}
                            className="p-1 text-purple-600 hover:bg-purple-200 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => deleteMilestone(currentWeek, milestone.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {currentData.milestones.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No milestones for this week</p>
                  )}
                </div>
              </div>

              {/* Habit Tracker */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Check className="text-teal-600" size={24} />
                  <h2 className="text-xl font-bold text-gray-800">Daily Habits</h2>
                </div>

                {habits.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4">No habits tracked yet</p>
                    <button
                      onClick={() => setViewMode('habits')}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Add Your First Habit
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <button
                        onClick={() => setViewMode('habits')}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                      >
                        Manage Habits
                      </button>
                    </div>

                    <div className="space-y-3">
                      {habits.map(habit => {
                        const weekStart = new Date(2026, 0, 1);
                        const startDay = weekStart.getDay();
                        const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7 + 1;
                        const firstMonday = new Date(2026, 0, daysToMonday);

                        const weekStartDate = new Date(firstMonday);
                        weekStartDate.setDate(firstMonday.getDate() + (currentWeek - 1) * 7);

                        const completedDays = [];
                        for (let i = 0; i < 7; i++) {
                          const date = new Date(weekStartDate);
                          date.setDate(weekStartDate.getDate() + i);
                          if (isHabitCompletedForDay(habit.id, date.getFullYear(), date.getMonth(), date.getDate())) {
                            completedDays.push(i);
                          }
                        }

                        return (
                          <div key={habit.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-800">{habit.name}</span>
                              <span className="text-sm text-gray-600">{completedDays.length}/7 days</span>
                            </div>
                            <div className="flex gap-1">
                              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                                const date = new Date(weekStartDate);
                                date.setDate(weekStartDate.getDate() + index);
                                const isCompleted = completedDays.includes(index);

                                return (
                                  <button
                                    key={index}
                                    onClick={() => toggleHabitForDay(habit.id, date.getFullYear(), date.getMonth(), date.getDate())}
                                    className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${isCompleted
                                      ? `${habit.color} text-white`
                                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                      }`}
                                    title={`${day} - ${date.toLocaleDateString()}`}
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

            </div>

            {/* Notes & Reflections — full width */}
            <NotesSection week={currentWeek} />
          </>
        )}

        {/* Summary Stats — only on Week (home) view */}
        {viewMode === 'week' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Overall Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">Total Goals</div>
              <div className="text-2xl font-bold text-blue-600">
                {Object.values(weekData).reduce((sum, week) => sum + (week.goals?.length || 0), 0)}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600">Completed Goals</div>
              <div className="text-2xl font-bold text-green-600">
                {Object.values(weekData).reduce((sum, week) =>
                  sum + (week.goals?.filter(g => g.completed).length || 0), 0
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ⌘K Command Palette */}
        {cmdPaletteOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
            onClick={() => setCmdPaletteOpen(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100">
                <Search size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  value={cmdInput}
                  onChange={e => setCmdInput(e.target.value)}
                  placeholder='Try "9am dentist", "goal: launch", "event: vacation Mar 2 to 9"...'
                  className="flex-1 text-sm text-gray-800 focus:outline-none bg-transparent"
                  onKeyDown={e => {
                    if (e.key !== 'Enter' || !cmdInput.trim()) return;
                    const p = parseCommandInput(cmdInput);
                    if (p.type === 'todo') setTodoList(prev => [{ id: Date.now(), text: p.text, completed: false, createdAt: new Date().toISOString() }, ...prev]);
                    else if (p.type === 'goal') setWeekData(prev => {
                      const cur = prev[currentWeek] || getWeekData(currentWeek);
                      return { ...prev, [currentWeek]: { ...cur, goals: [...(cur.goals || []), { id: Date.now(), text: p.text, completed: false }] } };
                    });
                    else if (p.type === 'schedule') updateScheduleBlock(p.year, p.month, p.date, makeScheduleKey(p.hour, p.minute), { text: p.text, duration: 60, status: null });
                    else if (p.type === 'event') {
                      const colors = ['bg-red-400','bg-orange-400','bg-amber-400','bg-green-400','bg-teal-400','bg-blue-400','bg-indigo-400','bg-purple-400','bg-pink-400'];
                      setCalendarBanners(prev => [...prev, { id: Date.now(), title: p.title, startDate: p.startDate, endDate: p.endDate, color: colors[prev.length % colors.length], emoji: '' }]);
                    }
                    setCmdPaletteOpen(false);
                    setCmdInput('');
                  }}
                />
                <kbd className="text-[10px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">ESC</kbd>
              </div>
              {!cmdInput.trim() && (
                <div className="p-3 space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Quick Commands</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-green-50 border border-green-100">
                      <span className="text-sm">📅</span>
                      <div><p className="text-[11px] font-semibold text-green-800">9am Meeting</p><p className="text-[9px] text-green-600">Schedule an event</p></div>
                    </div>
                    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-purple-50 border border-purple-100">
                      <span className="text-sm">🎯</span>
                      <div><p className="text-[11px] font-semibold text-purple-800">goal: Launch app</p><p className="text-[9px] text-purple-600">Add weekly goal</p></div>
                    </div>
                    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-blue-50 border border-blue-100">
                      <span className="text-sm">✓</span>
                      <div><p className="text-[11px] font-semibold text-blue-800">todo: Buy groceries</p><p className="text-[9px] text-blue-600">Add to To-Do list</p></div>
                    </div>
                    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-orange-50 border border-orange-100">
                      <span className="text-sm">🗓️</span>
                      <div><p className="text-[11px] font-semibold text-orange-800">event: Leave Mar 8 to Apr 3</p><p className="text-[9px] text-orange-600">Calendar event banner</p></div>
                    </div>
                  </div>
                  <p className="text-[9px] text-gray-400 text-center pt-1">Type anything and press ↵ Enter</p>
                </div>
              )}
              {cmdInput.trim() && (() => {
                const p = parseCommandInput(cmdInput);
                const results = [];
                todoList.filter(t => t.text?.toLowerCase().includes(cmdInput.toLowerCase())).slice(0, 3)
                  .forEach(t => results.push({ label: t.text, sub: 'To-Do', icon: '✓', action: () => setViewMode('todo') }));
                (brainDump.cards || []).filter(c => c.text?.toLowerCase().includes(cmdInput.toLowerCase())).slice(0, 2)
                  .forEach(c => results.push({ label: c.text, sub: 'Brain Dump', icon: '💡', action: () => setViewMode('braindump') }));
                Object.entries(weekData).forEach(([wk, wd]) => {
                  (wd.goals || []).filter(g => g.text?.toLowerCase().includes(cmdInput.toLowerCase())).slice(0, 2)
                    .forEach(g => results.push({ label: g.text, sub: `Week ${wk} Goal`, icon: '🎯',
                      action: () => { setCurrentWeek(parseInt(wk)); setViewMode('week'); } }));
                });
                // Search schedule entries
                const q = cmdInput.toLowerCase();
                Object.entries(daySchedule).forEach(([dayKey, hours]) => {
                  Object.entries(hours).forEach(([hr, evt]) => {
                    const txt = typeof evt === 'string' ? evt : evt?.text || '';
                    if (txt.toLowerCase().includes(q)) {
                      const parts = dayKey.split('-');
                      const y = +parts[0]; const m = +parts[1] - 1; const d = +parts[2];
                      results.push({ label: txt, sub: `${dayKey} schedule`, icon: '📅',
                        action: () => { setActiveDayView({ year: y, month: m, day: d }); window.scrollTo(0, 0); } });
                    }
                  });
                });
                // Search day notes
                Object.entries(dayNotes).forEach(([dayKey, note]) => {
                  if (note && note.toLowerCase().includes(q)) {
                    const parts = dayKey.split('-');
                    const y = +parts[0]; const m = +parts[1] - 1;
                    results.push({ label: note.length > 60 ? note.substring(0, 60) + '...' : note, sub: `${dayKey} note`, icon: '📝',
                      action: () => { setViewMode('calendar'); setCurrentMonth(m); setCurrentYear(y); } });
                  }
                });
                // Search event banners
                calendarBanners.filter(b => b.title.toLowerCase().includes(q)).forEach(b => {
                  results.push({ label: `${b.emoji || ''} ${b.title}`.trim(), sub: `${b.startDate} → ${b.endDate}`, icon: '🗓️',
                    action: () => { setViewMode('calendar'); const parts = b.startDate.split('-'); setCurrentMonth(+parts[1] - 1); setCurrentYear(+parts[0]); } });
                });
                const typeColor = { todo: 'bg-blue-50 text-blue-700', goal: 'bg-purple-50 text-purple-700', schedule: 'bg-green-50 text-green-700', event: 'bg-orange-50 text-orange-700', event_hint: 'bg-orange-50 text-orange-500' };
                return (
                  <div className="p-2 space-y-1 max-h-72 overflow-y-auto">
                    <button
                      onClick={() => {
                        const p2 = parseCommandInput(cmdInput);
                        if (p2.type === 'todo') setTodoList(prev => [{ id: Date.now(), text: p2.text, completed: false, createdAt: new Date().toISOString() }, ...prev]);
                        else if (p2.type === 'goal') setWeekData(prev => { const cur = prev[currentWeek] || getWeekData(currentWeek); return { ...prev, [currentWeek]: { ...cur, goals: [...(cur.goals || []), { id: Date.now(), text: p2.text, completed: false }] } }; });
                        else if (p2.type === 'schedule') updateScheduleBlock(p2.year, p2.month, p2.date, makeScheduleKey(p2.hour, p2.minute), { text: p2.text, duration: 60, status: null });
                        else if (p2.type === 'event') {
                          const colors = ['bg-red-400','bg-orange-400','bg-amber-400','bg-green-400','bg-teal-400','bg-blue-400','bg-indigo-400','bg-purple-400','bg-pink-400'];
                          setCalendarBanners(prev => [...prev, { id: Date.now(), title: p2.title, startDate: p2.startDate, endDate: p2.endDate, color: colors[prev.length % colors.length], emoji: '' }]);
                        }
                        setCmdPaletteOpen(false);
                        setCmdInput('');
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium ${typeColor[p.type] || typeColor.todo}`}
                    >
                      <span>{p.type === 'todo' ? '✓' : p.type === 'goal' ? '🎯' : p.type === 'event' || p.type === 'event_hint' ? '🗓️' : '📅'}</span>
                      <span className="flex-1 text-left truncate">{p.text}</span>
                      <span className="text-[10px] opacity-60 flex-shrink-0">{p.preview} · ↵</span>
                    </button>
                    {results.length > 0 && (
                      <>
                        <p className="text-[10px] text-gray-400 px-3 pt-1">Search results</p>
                        {results.map((r, i) => (
                          <button
                            key={i}
                            onClick={() => { r.action(); setCmdPaletteOpen(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-left text-sm text-gray-700"
                          >
                            <span>{r.icon}</span>
                            <span className="flex-1 truncate">{r.label}</span>
                            <span className="text-[10px] text-gray-400">{r.sub}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Zen Mode float button */}
        <button
          onClick={() => setZenMode(z => !z)}
          className={`fixed bottom-4 right-4 z-40 px-3 py-1.5 rounded-full text-[11px] font-medium shadow-lg transition-all ${zenMode ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-indigo-300 hover:text-indigo-500'}`}
        >
          {zenMode ? '✕ Exit Zen' : '◎ Zen'}
        </button>
      </div>
    </div>
  );
}
